import { useState, useEffect, useCallback } from "react";
import apiClient from "../../context/apiClient";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions as MuiDialogActions,
  IconButton,
  Chip,
  Paper,
  Avatar,
  Skeleton,
  Fab,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  ReportProblem as ReportProblemIcon,
  Laptop as LaptopIcon,
  Business as BusinessIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { useNotification } from "../../context/NotificationContext";

const formatApiData = (apiInterview) => {
  let scheduledAt = null;
  let rawDate = "";
  let rawTime = "";
  let status = (apiInterview.status || "unknown").toLowerCase();
  let isExpired = false;

  if (apiInterview.interview_date && apiInterview.interview_time) {
    const timeWithSeconds =
      apiInterview.interview_time.length === 5
        ? `${apiInterview.interview_time}:00`
        : apiInterview.interview_time;
    scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`);
    rawDate = apiInterview.interview_date;
    rawTime = apiInterview.interview_time.substring(0, 5);
  } else if (apiInterview.scheduled_at) {
    scheduledAt = new Date(apiInterview.scheduled_at);
    const isoString = scheduledAt.toISOString();
    rawDate = isoString.split("T")[0];
    rawTime = isoString.substring(11, 16);
  }

  if (["scheduled", "rescheduled"].includes(status) && rawDate && rawTime) {
    const now = new Date();
    
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const currentDate = `${year}-${month}-${day}`;

    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const currentTime = `${hours}:${minutes}`;

    if (rawDate < currentDate) {
      status = "cancelled";
      isExpired = true;
    } else if (rawDate === currentDate && rawTime < currentTime) {
      status = "cancelled";
      isExpired = true;
    }
  }

  const location = apiInterview.location || "";
  let address = "";
  let interviewUrl = "";
  let formType = "in-person";

  const isVideoUrl =
    location.startsWith("http://") || location.startsWith("https://");
  const isGenericVideoCall = ["remote", "video", "video call"].includes(
    location.toLowerCase()
  );

  if (isVideoUrl) {
    formType = "video";
    interviewUrl = location;
  } else if (isGenericVideoCall) {
    formType = "video";
  } else {
    formType = "in-person";
    const locationParts = location.split(" | Map: ");
    address = locationParts[0];
  }

  return {
    id: apiInterview.id,
    applicationId:
      apiInterview.application || apiInterview.applicationId || apiInterview.id,
    candidateName: apiInterview.candidate_name || "N/A",
    position: apiInterview.job_title || "N/A",
    status: status,
    isExpired: isExpired,
    date: scheduledAt
      ? scheduledAt.toLocaleDateString("en-US", {
          weekday: "short",
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "N/A",
    time: scheduledAt
      ? scheduledAt.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "N/A",
    rawDate: rawDate,
    rawTime: rawTime,
    interviewType: formType === "video" ? "Online Meeting" : "Inperson Interview",
    formType: formType,
    skills: apiInterview.skills || [],
    notes:
      apiInterview.notes || "No specific notes provided for this interview.",
    avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
    address: address,
    interviewUrl: interviewUrl,
  };
};

const InterviewCardSkeleton = () => (
  <Grid item xs={12} sm={6}>
    <Card sx={{ height: 380, display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="60%" height={20} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={70}
            height={24}
            sx={{ borderRadius: 1 }}
          />
        </Box>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={120}
          sx={{ borderRadius: 1, mb: 2 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={60}
          sx={{ borderRadius: 1 }}
        />
      </CardContent>
      <CardActions sx={{ p: 2.5, pt: 0 }}>
        <Skeleton
          variant="rectangular"
          width={80}
          height={32}
          sx={{ borderRadius: 1, mr: 1 }}
        />
        <Skeleton
          variant="rectangular"
          width={60}
          height={32}
          sx={{ borderRadius: 1 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Skeleton variant="circular" width={32} height={32} />
      </CardActions>
    </Card>
  </Grid>
);

function InterviewScheduler() {
  const { showNotification } = useNotification();
  const theme = useTheme();
  const [interviews, setInterviews] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [formData, setFormData] = useState({
    applicationId: "",
    date: "",
    time: "",
    type: "video",
    notes: "",
    locationDetail: "",
  });

  const fetchInterviews = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const interviewsRes = await apiClient.get("/interviews/");
      const formattedData = interviewsRes.data.map(formatApiData);
      setInterviews(formattedData);
    } catch (err) {
      setError("Could not load interviews.");
      showNotification("Failed to fetch interviews.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]);

  const fetchCandidates = useCallback(async () => {
    try {
      const response = await apiClient.get("/applications/");
      const formattedCandidates = response.data.map((app) => ({
        applicationId: app.id,
        talentId: app.talent,
        name:
          `${app.talent_details?.user?.first_name || ""} ${
            app.talent_details?.user?.last_name || ""
          }`.trim() || `Talent #${app.talent}`,
        position: app.job_posting_details?.title || "Applied Position",
        avatar:
          app.talent_details?.profile_picture ||
          "/placeholder.svg?height=40&width=40",
      }));
      setCandidates(formattedCandidates);
    } catch (err) {
      showNotification("Could not load candidate list for scheduling.", "warning");
    }
  }, [showNotification]);

  useEffect(() => {
    fetchInterviews();
    fetchCandidates();
  }, [fetchInterviews, fetchCandidates]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (
      !selectedInterview &&
      (!formData.applicationId || !formData.date || !formData.time)
    ) {
      showNotification(
        "Please select a candidate, date, and time for the new interview.",
        "error"
      );
      return;
    }
    if (formData.type === "in-person" && !formData.locationDetail.trim()) {
      showNotification(
        "Please provide an address for the in-person interview.",
        "error"
      );
      return;
    }

    try {
      if (selectedInterview && tabValue === 2) {
        const locationValue =
          formData.locationDetail.trim() ||
          (formData.type === "video" ? "Video" : "");
        const scheduledAtISO = new Date(
          `${formData.date}T${formData.time}`
        ).toISOString();
        const payload = {
          application: formData.applicationId,
          interview_date: formData.date,
          interview_time: formData.time,
          scheduled_at: scheduledAtISO,
          location: locationValue,
          notes: formData.notes,
          status: "scheduled",
        };
        await apiClient.post("/interviews/", payload);
        showNotification("Interview rescheduled successfully!", "success");
      } else if (selectedInterview) {
        const locationValue =
          formData.locationDetail.trim() ||
          (formData.type === "video" ? "Video" : "");
        const payload = {
          interview_date: formData.date,
          interview_time: formData.time,
          location: locationValue,
          notes: formData.notes,
        };
        await apiClient.patch(`/interviews/${selectedInterview.id}/`, payload);
        showNotification("Interview updated successfully!", "success");
      } else {
        const locationValue =
          formData.locationDetail.trim() ||
          (formData.type === "video" ? "Video" : "");
        const scheduledAtISO = new Date(
          `${formData.date}T${formData.time}`
        ).toISOString();
        const payload = {
          application: formData.applicationId,
          interview_date: formData.date,
          interview_time: formData.time,
          scheduled_at: scheduledAtISO,
          location: locationValue,
          notes: formData.notes,
          status: "scheduled",
        };
        await apiClient.post("/interviews/", payload);
        showNotification("Interview scheduled successfully!", "success");
      }
      fetchInterviews();
      handleCloseDialog();
    } catch (err) {
      const errorData = err.response?.data;
      let message = "An error occurred.";
      if (errorData) {
        message =
          errorData.detail || JSON.stringify(errorData).substring(0, 100);
      }
      showNotification(message, "error");
    }
  };

  const handleDeleteInterview = async (id) => {
    if (
      window.confirm("Are you sure you want to permanently delete this interview record?")
    ) {
      try {
        await apiClient.delete(`/interviews/${id}/`);
        setInterviews(interviews.filter((i) => i.id !== id));
        showNotification("Interview deleted successfully!", "success");
      } catch (err) {
        showNotification("Failed to delete interview.", "error");
      }
    }
  };

  const handleStartInterview = (interviewUrl) => {
    if (
      interviewUrl &&
      (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))
    ) {
      window.open(interviewUrl, "_blank");
    } else {
      showNotification("No valid interview URL available.", "warning");
    }
  };

  const filteredInterviews = interviews.filter((interview) => {
    const status = interview.status.toLowerCase();
    if (tabValue === 0) return ["scheduled", "rescheduled"].includes(status);
    if (tabValue === 1) return status === "completed";
    if (tabValue === 2) return status === "cancelled";
    return true;
  });

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleCloseDialog = () => setOpenDialog(false);

  const handleOpenDialog = (interview = null) => {
    if (interview) {
      setSelectedInterview(interview);
      setFormData({
        applicationId: interview.applicationId,
        date: interview.rawDate,
        time: interview.rawTime,
        type: interview.formType,
        notes:
          interview.notes === "No specific notes provided for this interview."
            ? ""
            : interview.notes,
        locationDetail:
          interview.formType === "video"
            ? interview.interviewUrl
            : interview.address,
      });
    } else {
      setSelectedInterview(null);
      setFormData({
        applicationId: "",
        date: "",
        time: "",
        type: "video",
        notes: "",
        locationDetail: "",
      });
    }
    setOpenDialog(true);
  };
    
  const handleOpenMap = (address) => {
    const encodedAddress = encodeURIComponent(address);
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapUrl, "_blank");
  };

  const getStatusChipStyle = (status, isExpired) => {
    let styles = {
      borderRadius: "50px",
      fontWeight: 600,
      fontSize: "0.75rem",
      height: 24,
      textTransform: "capitalize",
    };
    if (isExpired) {
      return {
        ...styles,
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        label: "Expired",
      };
    }
    switch (status.toLowerCase()) {
      case "scheduled":
      case "rescheduled":
        return {
          ...styles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        };
      case "completed":
        return {
          ...styles,
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
        };
      case "cancelled":
        return {
          ...styles,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        };
      default:
        return {
          ...styles,
          backgroundColor: theme.palette.text.disabled,
          color: theme.palette.getContrastText(theme.palette.text.disabled),
        };
    }
  };

  const renderTabButton = (label, index, colorKey) => (
    <Button
      onClick={(e) => handleTabChange(e, index)}
      sx={{
        flex: 1,
        py: 1.5,
        mx: 1,
        borderRadius: "12px",
        fontSize: "1rem",
        fontWeight: "bold",
        color:
          tabValue === index
            ? theme.palette[colorKey].contrastText
            : theme.palette[colorKey].main,
        backgroundColor:
          tabValue === index
            ? theme.palette[colorKey].main
            : theme.palette.background.paper,
        border: `1px solid ${theme.palette[colorKey].main}`,
        "&:hover": {
          backgroundColor:
            tabValue === index
              ? alpha(theme.palette[colorKey].main, 0.9)
              : alpha(theme.palette[colorKey].main, 0.1),
        },
      }}
    >
      {label}
    </Button>
  );

  return (
    <Layout title="Interview Scheduler">
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            backgroundColor: "mint.main",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            fontWeight="700"
            color="text.primary"
          >
            Interview Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track all your candidate interviews
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          {renderTabButton("Scheduled", 0, "primary")}
          {renderTabButton("Completed", 1, "success")}
          {renderTabButton("Cancelled", 2, "error")}
        </Box>

        {isLoading ? (
          <Grid container spacing={3}>
            <InterviewCardSkeleton />
            <InterviewCardSkeleton />
          </Grid>
        ) : error ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <ReportProblemIcon
              sx={{ fontSize: 64, color: "error.main", mb: 2 }}
            />
            <Typography variant="h6" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={fetchInterviews}>
              Try Again
            </Button>
          </Paper>
        ) : (
          <Grid
            container
            spacing={3}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              alignItems: "stretch",
            }}
          >
            {filteredInterviews.length > 0 ? (
              filteredInterviews.map((interview) => (
                <Box
                  key={interview.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <Card
                    sx={{
                      backgroundColor: "mint.main",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      height: "100%",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          src={interview.avatar}
                          sx={{ width: 56, height: 56, mr: 2 }}
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            fontWeight="700"
                            color="text.primary"
                          >
                            {interview.candidateName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {interview.position}
                          </Typography>
                        </Box>
                        <Chip
                          label={
                            interview.isExpired ? "Expired" : interview.status
                          }
                          size="small"
                          sx={getStatusChipStyle(
                            interview.status,
                            interview.isExpired
                          )}
                        />
                      </Box>

                      <Grid container spacing={1.5} sx={{ my: 2 }}>
                        {[
                          {
                            icon: <CalendarIcon fontSize="small" />,
                            text: interview.date,
                          },
                          {
                            icon: <TimeIcon fontSize="small" />,
                            text: interview.time,
                          },
                          {
                            icon: <ScheduleIcon fontSize="small" />,
                            text: "Technical Round",
                          },
                          {
                            icon:
                              interview.formType === "video" ? (
                                <LaptopIcon fontSize="small" />
                              ) : (
                                <BusinessIcon fontSize="small" />
                              ),
                            text: interview.interviewType,
                          },
                        ].map((item, index) => (
                          <Grid
                            item
                            xs={6}
                            key={index}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Box
                              sx={{
                                color: "text.secondary",
                                mr: 1.5,
                                display: "flex",
                              }}
                            >
                              {item.icon}
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight="500"
                              color="text.primary"
                            >
                              {item.text}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      {interview.skills && interview.skills.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {interview.skills.map((skill) => (
                              <Chip
                                key={skill}
                                label={skill}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {interview.address && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{ mb: 0.5 }}
                          >
                            Address:
                          </Typography>
                          <Tooltip
                            title={interview.address}
                            placement="top"
                            arrow
                          >
                            <Typography variant="body2" color="text.secondary">
                              {interview.address.length > 80
                                ? `${interview.address.substring(0, 80)}...`
                                : interview.address}
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}

                      <Box
                        sx={{
                          flexGrow: 1,
                          p: 1.5,
                          backgroundColor: "background.paper",
                          borderRadius: "8px",
                          minHeight: "60px",
                          border: `1px solid ${theme.palette.divider}`,
                          mt: "auto",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          {interview.notes}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions
                      sx={{
                        p: 2,
                        justifyContent: "space-between",
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        {tabValue === 0 &&
                          interview.formType === "video" && (
                            <Button
                              variant="contained"
                              size="medium"
                              color="primary"
                              onClick={() =>
                                handleStartInterview(interview.interviewUrl)
                              }
                            >
                              Start Interview
                            </Button>
                          )}
                        {tabValue === 0 &&
                          interview.formType === "in-person" && (
                            <Button
                              variant="contained"
                              size="medium"
                              color="primary"
                              onClick={() => handleOpenMap(interview.address)}
                            >
                              Map Location
                            </Button>
                          )}
                        {tabValue === 2 && (
                          <Button
                            variant="contained"
                            size="medium"
                            color="primary"
                            startIcon={<ReplayIcon />}
                            onClick={() => handleOpenDialog(interview)}
                          >
                            Reschedule
                          </Button>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteInterview(interview.id)}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))
            ) : (
              <Box
                sx={{ gridColumn: "1 / -1", textAlign: "center", py: 5 }}
              >
                <ScheduleIcon
                  sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No{" "}
                  {tabValue === 0
                    ? "scheduled"
                    : tabValue === 1
                    ? "completed"
                    : "cancelled"}{" "}
                  interviews found
                </Typography>
              </Box>
            )}
          </Grid>
        )}
      </Box>

      <Fab
        onClick={() => handleOpenDialog()}
        color="primary"
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
        }}
        aria-label="schedule interview"
      >
        <AddIcon />
      </Fab>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {selectedInterview && tabValue === 2
            ? "Reschedule Interview"
            : selectedInterview
            ? "Edit Interview"
            : "Schedule New Interview"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={!!selectedInterview}>
                <InputLabel>Candidate Application</InputLabel>
                <Select
                  name="applicationId"
                  value={formData.applicationId}
                  onChange={handleInputChange}
                  label="Candidate Application"
                >
                  {candidates.map((candidate) => (
                    <MenuItem
                      key={candidate.applicationId}
                      value={candidate.applicationId}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          src={candidate.avatar}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {candidate.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {candidate.position}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                name="time"
                type="time"
                value={formData.time}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Type"
                >
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="in-person">In-Person</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.type === "video" ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Interview URL"
                  name="locationDetail"
                  value={formData.locationDetail}
                  onChange={handleInputChange}
                  placeholder="https://meet.google.com/abc-defg-hij"
                  helperText="Enter the meeting URL for the video interview"
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location / Address"
                  name="locationDetail"
                  value={formData.locationDetail}
                  onChange={handleInputChange}
                  placeholder="e.g., 123 Main St, Anytown, USA"
                  helperText="Enter the physical address for the in-person interview"
                  required
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes / Feedback"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <MuiDialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedInterview && tabValue === 2
              ? "Confirm Reschedule"
              : selectedInterview
              ? "Save Changes"
              : "Schedule Interview"}
          </Button>
        </MuiDialogActions>
      </Dialog>
    </Layout>
  );
}

export default InterviewScheduler;



// import { useState, useEffect, useCallback } from "react";
// import apiClient from "../../context/apiClient";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Paper,
//   Avatar,
//   Skeleton,
//   Fab,
//   Tooltip,
//   alpha,
// } from "@mui/material";
// import {
//   Add as AddIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   Laptop as LaptopIcon,
//   Business as BusinessIcon,
//   Replay as ReplayIcon,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";

// const palette = {
//   primary: "#4F46BA",
//   background: "#F1FFFA",
//   completed: "#2FAD43",
//   cancelled: "#E63946",
//   textDark: "#000000",
//   textMuted: "#A9A5A5",
// };

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null;
//   let rawDate = "";
//   let rawTime = "";
//   let status = (apiInterview.status || "unknown").toLowerCase();
//   let isExpired = false;

//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds =
//       apiInterview.interview_time.length === 5
//         ? `${apiInterview.interview_time}:00`
//         : apiInterview.interview_time;
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`);
//     rawDate = apiInterview.interview_date;
//     rawTime = apiInterview.interview_time.substring(0, 5);
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at);
//     const isoString = scheduledAt.toISOString();
//     rawDate = isoString.split("T")[0];
//     rawTime = isoString.substring(11, 16);
//   }

//   if (["scheduled", "rescheduled"].includes(status) && rawDate && rawTime) {
//     const now = new Date();
    
//     const year = now.getFullYear();
//     const month = String(now.getMonth() + 1).padStart(2, '0');
//     const day = String(now.getDate()).padStart(2, '0');
//     const currentDate = `${year}-${month}-${day}`;

//     const hours = String(now.getHours()).padStart(2, '0');
//     const minutes = String(now.getMinutes()).padStart(2, '0');
//     const currentTime = `${hours}:${minutes}`;

//     if (rawDate < currentDate) {
//       status = "cancelled";
//       isExpired = true;
//     } else if (rawDate === currentDate && rawTime < currentTime) {
//       status = "cancelled";
//       isExpired = true;
//     }
//   }

//   const location = apiInterview.location || "";
//   let address = "";
//   let interviewUrl = "";
//   let formType = "in-person";

//   const isVideoUrl =
//     location.startsWith("http://") || location.startsWith("https://");
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(
//     location.toLowerCase()
//   );

//   if (isVideoUrl) {
//     formType = "video";
//     interviewUrl = location;
//   } else if (isGenericVideoCall) {
//     formType = "video";
//   } else {
//     formType = "in-person";
//     const locationParts = location.split(" | Map: ");
//     address = locationParts[0];
//   }

//   return {
//     id: apiInterview.id,
//     applicationId:
//       apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     status: status,
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           day: "numeric",
//           month: "long",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "numeric",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Online Meeting" : "Inperson Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes:
//       apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   };
// };

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6}>
//     <Card sx={{ height: 380, display: "flex", flexDirection: "column" }}>
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton
//             variant="rectangular"
//             width={70}
//             height={24}
//             sx={{ borderRadius: 1 }}
//           />
//         </Box>
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={120}
//           sx={{ borderRadius: 1, mb: 2 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={60}
//           sx={{ borderRadius: 1 }}
//         />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton
//           variant="rectangular"
//           width={80}
//           height={32}
//           sx={{ borderRadius: 1, mr: 1 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width={60}
//           height={32}
//           sx={{ borderRadius: 1 }}
//         />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// );

// function InterviewScheduler() {
//   const { showNotification } = useNotification();
//   const [interviews, setInterviews] = useState([]);
//   const [candidates, setCandidates] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [openDialog, setOpenDialog] = useState(false);
//   const [tabValue, setTabValue] = useState(0);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   });

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true);
//     setError(null);
//     try {
//       const interviewsRes = await apiClient.get("/interviews/");
//       const formattedData = interviewsRes.data.map(formatApiData);
//       setInterviews(formattedData);
//     } catch (err) {
//       setError("Could not load interviews.");
//       showNotification("Failed to fetch interviews.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   }, [showNotification]); 

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/");
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${
//             app.talent_details?.user?.last_name || ""
//           }`.trim() || `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar:
//           app.talent_details?.profile_picture ||
//           "/placeholder.svg?height=40&width=40",
//       }));
//       setCandidates(formattedCandidates);
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning");
//     }
//   }, [showNotification]);

//   useEffect(() => {
//     fetchInterviews();
//     fetchCandidates();
//   }, [fetchInterviews, fetchCandidates]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async () => {
//     if (
//       !selectedInterview &&
//       (!formData.applicationId || !formData.date || !formData.time)
//     ) {
//       showNotification(
//         "Please select a candidate, date, and time for the new interview.",
//         "error"
//       );
//       return;
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification(
//         "Please provide an address for the in-person interview.",
//         "error"
//       );
//       return;
//     }

//     try {
//       if (selectedInterview && tabValue === 2) {
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled",
//         };
//         await apiClient.post("/interviews/", payload);
//         showNotification("Interview rescheduled successfully!", "success");
//       } else if (selectedInterview) {
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const payload = {
//           interview_date: formData.date,
//           interview_time: formData.time,
//           location: locationValue,
//           notes: formData.notes,
//         };
//         await apiClient.patch(`/interviews/${selectedInterview.id}/`, payload);
//         showNotification("Interview updated successfully!", "success");
//       } else {
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled",
//         };
//         await apiClient.post("/interviews/", payload);
//         showNotification("Interview scheduled successfully!", "success");
//       }
//       fetchInterviews();
//       handleCloseDialog();
//     } catch (err) {
//       const errorData = err.response?.data;
//       let message = "An error occurred.";
//       if (errorData) {
//         message =
//           errorData.detail || JSON.stringify(errorData).substring(0, 100);
//       }
//       showNotification(message, "error");
//     }
//   };

//   const handleDeleteInterview = async (id) => {
//     if (
//       window.confirm("Are you sure you want to permanently delete this interview record?")
//     ) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`);
//         setInterviews(interviews.filter((i) => i.id !== id));
//         showNotification("Interview deleted successfully!", "success");
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error");
//       }
//     }
//   };

//   const handleStartInterview = (interviewUrl) => {
//     if (
//       interviewUrl &&
//       (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))
//     ) {
//       window.open(interviewUrl, "_blank");
//     } else {
//       showNotification("No valid interview URL available.", "warning");
//     }
//   };

//   const filteredInterviews = interviews.filter((interview) => {
//     const status = interview.status.toLowerCase();
//     if (tabValue === 0) return ["scheduled", "rescheduled"].includes(status);
//     if (tabValue === 1) return status === "completed";
//     if (tabValue === 2) return status === "cancelled";
//     return true;
//   });

//   const handleTabChange = (event, newValue) => setTabValue(newValue);

//   const handleCloseDialog = () => setOpenDialog(false);

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview);
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes:
//           interview.notes === "No specific notes provided for this interview."
//             ? ""
//             : interview.notes,
//         locationDetail:
//           interview.formType === "video"
//             ? interview.interviewUrl
//             : interview.address,
//       });
//     } else {
//       setSelectedInterview(null);
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       });
//     }
//     setOpenDialog(true);
//   };
    
//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address);
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
//     window.open(mapUrl, "_blank");
//   };

//   const getStatusChipStyle = (status, isExpired) => {
//     let styles = {
//       color: "#FFFFFF",
//       borderRadius: "50px",
//       fontWeight: 600,
//       fontSize: "0.75rem",
//       height: 24,
//       textTransform: "capitalize",
//     };
//     if (isExpired) {
//       return { ...styles, backgroundColor: palette.cancelled, label: "Expired" };
//     }
//     switch (status.toLowerCase()) {
//       case "scheduled":
//       case "rescheduled":
//         return { ...styles, backgroundColor: palette.primary };
//       case "completed":
//         return { ...styles, backgroundColor: palette.completed };
//       case "cancelled":
//         return { ...styles, backgroundColor: palette.cancelled };
//       default:
//         return { ...styles, backgroundColor: palette.textMuted };
//     }
//   };

//   const renderTabButton = (label, index, color) => (
//     <Button
//       onClick={(e) => handleTabChange(e, index)}
//       sx={{
//         flex: 1,
//         py: 1.5,
//         mx: 1,
//         borderRadius: "12px",
//         fontSize: "1rem",
//         fontWeight: 'bold',
//         color: tabValue === index ? "#FFFFFF" : color,
//         backgroundColor: tabValue === index ? color : "#FFFFFF",
//         border: `1px solid ${color}`,
//         "&:hover": {
//           backgroundColor:
//             tabValue === index ? alpha(color, 0.9) : alpha(color, 0.1),
//         },
//       }}
//     >
//       {label}
//     </Button>
//   );

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//         <Box 
//           sx={{ 
//             mb: 4, 
//             p: 2.5, 
//             backgroundColor: palette.background, 
//             borderRadius: '12px', 
//             boxShadow: '0 4px 12px 0 rgba(0,0,0,0.08)' 
//           }}
//         >
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary">
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
//           {renderTabButton("Scheduled", 0, palette.primary)}
//           {renderTabButton("Completed", 1, palette.completed)}
//           {renderTabButton("Cancelled", 2, palette.cancelled)}
//         </Box>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//          <Grid
//   container
//   spacing={3}
//   sx={{
//     display: "grid",
//     gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
//     alignItems: "stretch",
//   }}
// >
//   {filteredInterviews.length > 0 ? (
//     filteredInterviews.map((interview) => (
//       <Box
//         key={interview.id}
//         sx={{
//           display: "flex",
//           flexDirection: "column",
//           height: "100%",
//           width: "100%",
//         }}
//       >
//         <Card
//           sx={{
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "space-between",
//             borderRadius: "16px",
//             boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
//             backgroundColor: palette.background,
//             height: "100%",
//             transition: "transform 0.2s ease, box-shadow 0.2s ease",
//             "&:hover": {
//               transform: "translateY(-4px)",
//               boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
//             },
//           }}
//         >
//           <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
//             <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//               <Avatar src={interview.avatar} sx={{ width: 56, height: 56, mr: 2 }} />
//               <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                 <Typography variant="h6" fontWeight="700" color={palette.textDark}>
//                   {interview.candidateName}
//                 </Typography>
//                 <Typography variant="body2" color={palette.textMuted}>
//                   {interview.position}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={interview.isExpired ? "Expired" : interview.status}
//                 size="small"
//                 sx={getStatusChipStyle(interview.status, interview.isExpired)}
//               />
//             </Box>

//             <Grid container spacing={1.5} sx={{ my: 2 }}>
//               {[
//                 { icon: <CalendarIcon fontSize="small" />, text: interview.date },
//                 { icon: <TimeIcon fontSize="small" />, text: interview.time },
//                 { icon: <ScheduleIcon fontSize="small" />, text: "Technical Round" },
//                 {
//                   icon:
//                     interview.formType === "video" ? (
//                       <LaptopIcon fontSize="small" />
//                     ) : (
//                       <BusinessIcon fontSize="small" />
//                     ),
//                   text: interview.interviewType,
//                 },
//               ].map((item, index) => (
//                 <Grid
//                   item
//                   xs={6}
//                   key={index}
//                   sx={{ display: "flex", alignItems: "center" }}
//                 >
//                   <Box
//                     sx={{
//                       color: palette.textMuted,
//                       mr: 1.5,
//                       display: "flex",
//                     }}
//                   >
//                     {item.icon}
//                   </Box>
//                   <Typography variant="body2" fontWeight="500" color={palette.textDark}>
//                     {item.text}
//                   </Typography>
//                 </Grid>
//               ))}
//             </Grid>

//             {interview.skills && interview.skills.length > 0 && (
//               <Box sx={{ mb: 2 }}>
//                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
//                   {interview.skills.map((skill) => (
//                     <Chip key={skill} label={skill} size="small" variant="outlined" />
//                   ))}
//                 </Box>
//               </Box>
//             )}

//             {interview.address && (
//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
//                   Address:
//                 </Typography>
//                 <Tooltip title={interview.address} placement="top" arrow>
//                   <Typography variant="body2" color={palette.textMuted}>
//                     {interview.address.length > 80
//                       ? `${interview.address.substring(0, 80)}...`
//                       : interview.address}
//                   </Typography>
//                 </Tooltip>
//               </Box>
//             )}

//             <Box
//               sx={{
//                 flexGrow: 1,
//                 p: 1.5,
//                 backgroundColor: "#FFFFFF",
//                 borderRadius: "8px",
//                 minHeight: "60px",
//                 border: "1px solid #E0E0E0",
//                 mt: "auto",
//               }}
//             >
//               <Typography
//                 variant="body2"
//                 color={palette.textMuted}
//                 sx={{ fontStyle: "italic" }}
//               >
//                 {interview.notes}
//               </Typography>
//             </Box>
//           </CardContent>

//           <CardActions
//             sx={{
//               p: 2,
//               justifyContent: "space-between",
//               borderTop: "1px solid #eee",
//             }}
//           >
//             <Box sx={{ display: "flex", gap: 1.5 }}>
//               {tabValue === 0 && interview.formType === "video" && (
//                 <Button
//                   variant="contained"
//                   size="medium"
//                   onClick={() => handleStartInterview(interview.interviewUrl)}
//                   sx={{
//                     borderRadius: "50px",
//                     textTransform: "none",
//                     backgroundColor: palette.primary,
//                     px: 3,
//                   }}
//                 >
//                   Start Interview
//                 </Button>
//               )}
//               {tabValue === 0 && interview.formType === "in-person" && (
//                 <Button
//                   variant="contained"
//                   size="medium"
//                   onClick={() => handleOpenMap(interview.address)}
//                   sx={{
//                     borderRadius: "50px",
//                     textTransform: "none",
//                     backgroundColor: palette.primary,
//                     px: 3,
//                   }}
//                 >
//                   Map Location
//                 </Button>
//               )}
//               {tabValue === 2 && (
//                 <Button
//                   variant="contained"
//                   size="medium"
//                   startIcon={<ReplayIcon />}
//                   onClick={() => handleOpenDialog(interview)}
//                   sx={{
//                     borderRadius: "50px",
//                     textTransform: "none",
//                     backgroundColor: palette.primary,
//                     px: 3,
//                   }}
//                 >
//                   Reschedule
//                 </Button>
//               )}
//             </Box>
//             <IconButton
//               size="small"
//               onClick={() => handleDeleteInterview(interview.id)}
//               sx={{ color: palette.cancelled }}
//             >
//               <DeleteIcon />
//             </IconButton>
//           </CardActions>
//         </Card>
//       </Box>
//     ))
//   ) : (
//     <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 5 }}>
//       <ScheduleIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
//       <Typography variant="h6" color="textSecondary">
//         No{" "}
//         {tabValue === 0
//           ? "scheduled"
//           : tabValue === 1
//           ? "completed"
//           : "cancelled"}{" "}
//         interviews found
//       </Typography>
//     </Box>
//   )}
// </Grid>

//         )}
//       </Box>

//       <Fab
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           backgroundColor: palette.primary,
//           color: "#FFFFFF",
//           "&:hover": { backgroundColor: alpha(palette.primary, 0.9) },
//         }}
//         aria-label="schedule interview"
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview && tabValue === 2
//             ? "Reschedule Interview"
//             : selectedInterview
//             ? "Edit Interview"
//             : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem
//                       key={candidate.applicationId}
//                       value={candidate.applicationId}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">{candidate.name}</Typography>
//                           <Typography variant="caption" color="textSecondary">{candidate.position}</Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>
//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: palette.primary }}>
//             {selectedInterview && tabValue === 2
//               ? "Confirm Reschedule"
//               : selectedInterview
//               ? "Save Changes"
//               : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   );
// }

// export default InterviewScheduler;





// import { useState, useEffect, useCallback } from "react";
// import apiClient from "../../context/apiClient";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Paper,
//   Avatar,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material";
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
//   Laptop as LaptopIcon,
//   Business as BusinessIcon,
//   Replay as ReplayIcon,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";

// const palette = {
//   primary: "#4F46BA",
//   background: "#F1FFFA",
//   completed: "#2FAD43",
//   cancelled: "#E63946",
//   textDark: "#000000",
//   textMuted: "#A9A5A5",
// };

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null;
//   let rawDate = "";
//   let rawTime = "";
//   let status = (apiInterview.status || "unknown").toLowerCase();
//   let isExpired = false;

//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds =
//       apiInterview.interview_time.length === 5
//         ? `${apiInterview.interview_time}:00`
//         : apiInterview.interview_time;
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`);
//     rawDate = apiInterview.interview_date;
//     rawTime = apiInterview.interview_time.substring(0, 5);
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at);
//     const isoString = scheduledAt.toISOString();
//     rawDate = isoString.split("T")[0];
//     rawTime = isoString.substring(11, 16);
//   }

//   // --- FIX --- Now checks both 'scheduled' and 'rescheduled' statuses for expiration
//   if (["scheduled", "rescheduled"].includes(status) && rawDate && rawTime) {
//     const now = new Date();
    
//     const year = now.getFullYear();
//     const month = String(now.getMonth() + 1).padStart(2, '0');
//     const day = String(now.getDate()).padStart(2, '0');
//     const currentDate = `${year}-${month}-${day}`;

//     const hours = String(now.getHours()).padStart(2, '0');
//     const minutes = String(now.getMinutes()).padStart(2, '0');
//     const currentTime = `${hours}:${minutes}`;

//     if (rawDate < currentDate) {
//       status = "cancelled";
//       isExpired = true;
//     } else if (rawDate === currentDate && rawTime < currentTime) {
//       status = "cancelled";
//       isExpired = true;
//     }
//   }

//   const location = apiInterview.location || "";
//   let address = "";
//   let interviewUrl = "";
//   let formType = "in-person";

//   const isVideoUrl =
//     location.startsWith("http://") || location.startsWith("https://");
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(
//     location.toLowerCase()
//   );

//   if (isVideoUrl) {
//     formType = "video";
//     interviewUrl = location;
//   } else if (isGenericVideoCall) {
//     formType = "video";
//   } else {
//     formType = "in-person";
//     const locationParts = location.split(" | Map: ");
//     address = locationParts[0];
//   }

//   return {
//     id: apiInterview.id,
//     applicationId:
//       apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     status: status,
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           day: "numeric",
//           month: "long",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "numeric",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Online Meeting" : "Inperson Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes:
//       apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   };
// };

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6}>
//     <Card sx={{ height: 380, display: "flex", flexDirection: "column" }}>
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton
//             variant="rectangular"
//             width={70}
//             height={24}
//             sx={{ borderRadius: 1 }}
//           />
//         </Box>
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={120}
//           sx={{ borderRadius: 1, mb: 2 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={60}
//           sx={{ borderRadius: 1 }}
//         />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton
//           variant="rectangular"
//           width={80}
//           height={32}
//           sx={{ borderRadius: 1, mr: 1 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width={60}
//           height={32}
//           sx={{ borderRadius: 1 }}
//         />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// );

// function InterviewScheduler() {
//   const { showNotification } = useNotification();
//   const [interviews, setInterviews] = useState([]);
//   const [candidates, setCandidates] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [openDialog, setOpenDialog] = useState(false);
//   const [tabValue, setTabValue] = useState(0);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   });

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true);
//     setError(null);
//     try {
//       const interviewsRes = await apiClient.get("/interviews/");
//       const formattedData = interviewsRes.data.map(formatApiData);
//       setInterviews(formattedData);
//     } catch (err) {
//       setError("Could not load interviews.");
//       showNotification("Failed to fetch interviews.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   }, []); 

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/");
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${
//             app.talent_details?.user?.last_name || ""
//           }`.trim() || `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar:
//           app.talent_details?.profile_picture ||
//           "/placeholder.svg?height=40&width=40",
//       }));
//       setCandidates(formattedCandidates);
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning");
//     }
//   }, []);

//   useEffect(() => {
//     fetchInterviews();
//     fetchCandidates();
//   }, [fetchInterviews, fetchCandidates]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async () => {
//     if (
//       !selectedInterview &&
//       (!formData.applicationId || !formData.date || !formData.time)
//     ) {
//       showNotification(
//         "Please select a candidate, date, and time for the new interview.",
//         "error"
//       );
//       return;
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification(
//         "Please provide an address for the in-person interview.",
//         "error"
//       );
//       return;
//     }

//     try {
//       if (selectedInterview && tabValue === 2) {
//         // This is a RESCHEDULE action from the Cancelled tab
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled", // Rescheduling sets it back to scheduled
//         };
//         await apiClient.post("/interviews/", payload); // Create a new one
//         showNotification("Interview rescheduled successfully!", "success");
//       } else if (selectedInterview) {
//         // This is an EDIT action from the Scheduled tab
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const payload = {
//           interview_date: formData.date,
//           interview_time: formData.time,
//           location: locationValue,
//           notes: formData.notes,
//         };
//         await apiClient.patch(`/interviews/${selectedInterview.id}/`, payload);
//         showNotification("Interview updated successfully!", "success");
//       } else {
//         // This is a NEW interview
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled",
//         };
//         await apiClient.post("/interviews/", payload);
//         showNotification("Interview scheduled successfully!", "success");
//       }
//       fetchInterviews();
//       handleCloseDialog();
//     } catch (err) {
//       const errorData = err.response?.data;
//       let message = "An error occurred.";
//       if (errorData) {
//         message =
//           errorData.detail || JSON.stringify(errorData).substring(0, 100);
//       }
//       showNotification(message, "error");
//     }
//   };

//   const handleDeleteInterview = async (id) => {
//     if (
//       window.confirm("Are you sure you want to permanently delete this interview record?")
//     ) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`);
//         setInterviews(interviews.filter((i) => i.id !== id));
//         showNotification("Interview deleted successfully!", "success");
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error");
//       }
//     }
//   };

//   const handleStartInterview = (interviewUrl) => {
//     if (
//       interviewUrl &&
//       (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))
//     ) {
//       window.open(interviewUrl, "_blank");
//     } else {
//       showNotification("No valid interview URL available.", "warning");
//     }
//   };

//   const filteredInterviews = interviews.filter((interview) => {
//     const status = interview.status.toLowerCase();
//     if (tabValue === 0) return ["scheduled", "rescheduled"].includes(status);
//     if (tabValue === 1) return status === "completed";
//     if (tabValue === 2) return status === "cancelled";
//     return true;
//   });

//   const handleTabChange = (event, newValue) => setTabValue(newValue);

//   const handleCloseDialog = () => setOpenDialog(false);

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview);
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes:
//           interview.notes === "No specific notes provided for this interview."
//             ? ""
//             : interview.notes,
//         locationDetail:
//           interview.formType === "video"
//             ? interview.interviewUrl
//             : interview.address,
//       });
//     } else {
//       setSelectedInterview(null);
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       });
//     }
//     setOpenDialog(true);
//   };
    
//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address);
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
//     window.open(mapUrl, "_blank");
//   };

//   const getStatusChipStyle = (status, isExpired) => {
//     let styles = {
//       color: "#FFFFFF",
//       borderRadius: "50px",
//       fontWeight: 600,
//       fontSize: "0.75rem",
//       height: 24,
//       textTransform: "capitalize",
//     };
//     if (isExpired) {
//       return { ...styles, backgroundColor: palette.cancelled, label: "Expired" };
//     }
//     switch (status.toLowerCase()) {
//       case "scheduled":
//       case "rescheduled":
//         return { ...styles, backgroundColor: palette.primary };
//       case "completed":
//         return { ...styles, backgroundColor: palette.completed };
//       case "cancelled":
//         return { ...styles, backgroundColor: palette.cancelled };
//       default:
//         return { ...styles, backgroundColor: palette.textMuted };
//     }
//   };

//   const renderTabButton = (label, index, color) => (
//     <Button
//       onClick={(e) => handleTabChange(e, index)}
//       sx={{
//         flex: 1,
//         py: 1.5,
//         mx: 1,
//         borderRadius: "12px",
//         fontSize: "1rem",
//         fontWeight: 'bold',
//         color: tabValue === index ? "#FFFFFF" : color,
//         backgroundColor: tabValue === index ? color : "#FFFFFF",
//         border: `1px solid ${color}`,
//         "&:hover": {
//           backgroundColor:
//             tabValue === index ? alpha(color, 0.9) : alpha(color, 0.1),
//         },
//       }}
//     >
//       {label}
//     </Button>
//   );

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//         <Box 
//           sx={{ 
//             mb: 4, 
//             p: 2.5, 
//             backgroundColor: palette.background, 
//             borderRadius: '12px', 
//             boxShadow: '0 4px 12px 0 rgba(0,0,0,0.08)' 
//           }}
//         >
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary">
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
//           {renderTabButton("Scheduled", 0, palette.primary)}
//           {renderTabButton("Completed", 1, palette.completed)}
//           {renderTabButton("Cancelled", 2, palette.cancelled)}
//         </Box>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} md={6} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                       borderRadius: "16px",
//                       boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)",
//                       backgroundColor: palette.background,
//                     }}
//                   >
//                     <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar src={interview.avatar} sx={{ width: 56, height: 56, mr: 2 }} />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Typography variant="h6" fontWeight="700" color={palette.textDark}>
//                             {interview.candidateName}
//                           </Typography>
//                           <Typography variant="body2" color={palette.textMuted}>
//                             {interview.position}
//                           </Typography>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={getStatusChipStyle(interview.status, interview.isExpired)}
//                         />
//                       </Box>

//                       <Grid container spacing={1.5} sx={{ my: 2 }}>
//                         {[
//                           { icon: <CalendarIcon fontSize="small" />, text: interview.date },
//                           { icon: <TimeIcon fontSize="small" />, text: interview.time },
//                           { icon: <ScheduleIcon fontSize="small" />, text: "Technical Round" },
//                           { icon: interview.formType === 'video' ? <LaptopIcon fontSize="small"/> : <BusinessIcon fontSize="small"/>, text: interview.interviewType },
//                         ].map((item, index) => (
//                           <Grid item xs={6} key={index} sx={{ display: "flex", alignItems: "center" }}>
//                             <Box sx={{ color: palette.textMuted, mr: 1.5, display: "flex" }}>{item.icon}</Box>
//                             <Typography variant="body2" fontWeight="500" color={palette.textDark}>
//                               {item.text}
//                             </Typography>
//                           </Grid>
//                         ))}
//                       </Grid>

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
//                             {interview.skills.map((skill) => (
//                               <Chip key={skill} label={skill} size="small" variant="outlined"/>
//                             ))}
//                           </Box>
//                         </Box>
//                       )}

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>Address:</Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography variant="body2" color={palette.textMuted}>
//                               {interview.address.length > 80 ? `${interview.address.substring(0, 80)}...` : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       <Box sx={{
//                           flexGrow: 1,
//                           p: 1.5,
//                           backgroundColor: "#FFFFFF",
//                           borderRadius: "8px",
//                           minHeight: "50px",
//                           border: "1px solid #E0E0E0",
//                         }}
//                       >
//                         <Typography variant="body2" color={palette.textMuted} sx={{ fontStyle: "italic" }}>
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                        <Box sx={{ display: "flex", gap: 1.5 }}>
//                         {tabValue === 0 && interview.formType === "video" && (
//                           <Button variant="contained" size="medium" onClick={() => handleStartInterview(interview.interviewUrl)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Start Interview
//                           </Button>
//                         )}
//                         {tabValue === 0 && interview.formType === "in-person" && (
//                           <Button variant="contained" size="medium" onClick={() => handleOpenMap(interview.address)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Map Location
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button variant="contained" size="medium" startIcon={<ReplayIcon />} onClick={() => handleOpenDialog(interview)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       <Box>
//                          <IconButton size="small" onClick={() => handleDeleteInterview(interview.id)} sx={{ color: palette.cancelled }}>
//                            <DeleteIcon />
//                          </IconButton>
//                       </Box>
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}{tabValue === 0 ? "scheduled" : tabValue === 1 ? "completed" : "cancelled"}{" "}
//                     interviews found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           backgroundColor: palette.primary,
//           color: "#FFFFFF",
//           "&:hover": { backgroundColor: alpha(palette.primary, 0.9) },
//         }}
//         aria-label="schedule interview"
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview && tabValue === 2
//             ? "Reschedule Interview"
//             : selectedInterview
//             ? "Edit Interview"
//             : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem
//                       key={candidate.applicationId}
//                       value={candidate.applicationId}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">{candidate.name}</Typography>
//                           <Typography variant="caption" color="textSecondary">{candidate.position}</Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>
//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: palette.primary }}>
//             {selectedInterview && tabValue === 2
//               ? "Confirm Reschedule"
//               : selectedInterview
//               ? "Save Changes"
//               : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   );
// }

// export default InterviewScheduler;
// import { useState, useEffect, useCallback } from "react";
// import apiClient from "../../context/apiClient";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Paper,
//   Avatar,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material";
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
//   Laptop as LaptopIcon,
//   Business as BusinessIcon,
//   Replay as ReplayIcon,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";

// const palette = {
//   primary: "#4F46BA",
//   background: "#F1FFFA",
//   completed: "#2FAD43",
//   cancelled: "#E63946",
//   textDark: "#000000",
//   textMuted: "#A9A5A5",
// };

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null;
//   let rawDate = "";
//   let rawTime = "";
//   let status = (apiInterview.status || "unknown").toLowerCase();
//   let isExpired = false;

//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds =
//       apiInterview.interview_time.length === 5
//         ? `${apiInterview.interview_time}:00`
//         : apiInterview.interview_time;
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`);
//     rawDate = apiInterview.interview_date;
//     rawTime = apiInterview.interview_time.substring(0, 5);
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at);
//     const isoString = scheduledAt.toISOString();
//     rawDate = isoString.split("T")[0];
//     rawTime = isoString.substring(11, 16);
//   }

//   if (status === "scheduled" && rawDate && rawTime) {
//     const now = new Date();
    
//     const year = now.getFullYear();
//     const month = String(now.getMonth() + 1).padStart(2, '0');
//     const day = String(now.getDate()).padStart(2, '0');
//     const currentDate = `${year}-${month}-${day}`;

//     const hours = String(now.getHours()).padStart(2, '0');
//     const minutes = String(now.getMinutes()).padStart(2, '0');
//     const currentTime = `${hours}:${minutes}`;

//     if (rawDate < currentDate) {
//       status = "cancelled";
//       isExpired = true;
//     } else if (rawDate === currentDate && rawTime < currentTime) {
//       status = "cancelled";
//       isExpired = true;
//     }
//   }

//   const location = apiInterview.location || "";
//   let address = "";
//   let interviewUrl = "";
//   let formType = "in-person";

//   const isVideoUrl =
//     location.startsWith("http://") || location.startsWith("https://");
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(
//     location.toLowerCase()
//   );

//   if (isVideoUrl) {
//     formType = "video";
//     interviewUrl = location;
//   } else if (isGenericVideoCall) {
//     formType = "video";
//   } else {
//     formType = "in-person";
//     const locationParts = location.split(" | Map: ");
//     address = locationParts[0];
//   }

//   return {
//     id: apiInterview.id,
//     applicationId:
//       apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     status: status,
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           day: "numeric",
//           month: "long",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "numeric",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Online Meeting" : "Inperson Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes:
//       apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   };
// };

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6}>
//     <Card sx={{ height: 380, display: "flex", flexDirection: "column" }}>
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton
//             variant="rectangular"
//             width={70}
//             height={24}
//             sx={{ borderRadius: 1 }}
//           />
//         </Box>
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={120}
//           sx={{ borderRadius: 1, mb: 2 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={60}
//           sx={{ borderRadius: 1 }}
//         />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton
//           variant="rectangular"
//           width={80}
//           height={32}
//           sx={{ borderRadius: 1, mr: 1 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width={60}
//           height={32}
//           sx={{ borderRadius: 1 }}
//         />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// );

// function InterviewScheduler() {
//   const { showNotification } = useNotification();
//   const [interviews, setInterviews] = useState([]);
//   const [candidates, setCandidates] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [openDialog, setOpenDialog] = useState(false);
//   const [tabValue, setTabValue] = useState(0);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   });

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true);
//     setError(null);
//     try {
//       const interviewsRes = await apiClient.get("/interviews/");
//       const formattedData = interviewsRes.data.map(formatApiData);
//       setInterviews(formattedData);
//     } catch (err) {
//       setError("Could not load interviews.");
//       showNotification("Failed to fetch interviews.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   // --- FIX --- Removed showNotification from the dependency array to prevent re-fetch loop on error
//   }, []); 

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/");
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${
//             app.talent_details?.user?.last_name || ""
//           }`.trim() || `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar:
//           app.talent_details?.profile_picture ||
//           "/placeholder.svg?height=40&width=40",
//       }));
//       setCandidates(formattedCandidates);
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning");
//     }
//   // --- FIX --- Removed showNotification from the dependency array
//   }, []);

//   useEffect(() => {
//     fetchInterviews();
//     fetchCandidates();
//   }, [fetchInterviews, fetchCandidates]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async () => {
//     if (
//       !selectedInterview &&
//       (!formData.applicationId || !formData.date || !formData.time)
//     ) {
//       showNotification(
//         "Please select a candidate, date, and time for the new interview.",
//         "error"
//       );
//       return;
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification(
//         "Please provide an address for the in-person interview.",
//         "error"
//       );
//       return;
//     }

//     try {
//       if (selectedInterview && tabValue === 2) {
//         // This is a RESCHEDULE action from the Cancelled tab
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled", // Rescheduling sets it back to scheduled
//         };
//         await apiClient.post("/interviews/", payload); // Create a new one
//         showNotification("Interview rescheduled successfully!", "success");
//       } else if (selectedInterview) {
//         // This is an EDIT action from the Scheduled tab
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const payload = {
//           interview_date: formData.date,
//           interview_time: formData.time,
//           location: locationValue,
//           notes: formData.notes,
//         };
//         await apiClient.patch(`/interviews/${selectedInterview.id}/`, payload);
//         showNotification("Interview updated successfully!", "success");
//       } else {
//         // This is a NEW interview
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled",
//         };
//         await apiClient.post("/interviews/", payload);
//         showNotification("Interview scheduled successfully!", "success");
//       }
//       fetchInterviews();
//       handleCloseDialog();
//     } catch (err) {
//       const errorData = err.response?.data;
//       let message = "An error occurred.";
//       if (errorData) {
//         message =
//           errorData.detail || JSON.stringify(errorData).substring(0, 100);
//       }
//       showNotification(message, "error");
//     }
//   };

//   const handleDeleteInterview = async (id) => {
//     if (
//       window.confirm("Are you sure you want to permanently delete this interview record?")
//     ) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`);
//         setInterviews(interviews.filter((i) => i.id !== id));
//         showNotification("Interview deleted successfully!", "success");
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error");
//       }
//     }
//   };

//   const handleStartInterview = (interviewUrl) => {
//     if (
//       interviewUrl &&
//       (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))
//     ) {
//       window.open(interviewUrl, "_blank");
//     } else {
//       showNotification("No valid interview URL available.", "warning");
//     }
//   };

//   const filteredInterviews = interviews.filter((interview) => {
//     const status = interview.status.toLowerCase();
//     if (tabValue === 0) return ["scheduled", "rescheduled"].includes(status);
//     if (tabValue === 1) return status === "completed";
//     if (tabValue === 2) return status === "cancelled";
//     return true;
//   });

//   const handleTabChange = (event, newValue) => setTabValue(newValue);

//   const handleCloseDialog = () => setOpenDialog(false);

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview);
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes:
//           interview.notes === "No specific notes provided for this interview."
//             ? ""
//             : interview.notes,
//         locationDetail:
//           interview.formType === "video"
//             ? interview.interviewUrl
//             : interview.address,
//       });
//     } else {
//       setSelectedInterview(null);
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       });
//     }
//     setOpenDialog(true);
//   };
    
//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address);
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
//     window.open(mapUrl, "_blank");
//   };

//   const getStatusChipStyle = (status, isExpired) => {
//     let styles = {
//       color: "#FFFFFF",
//       borderRadius: "50px",
//       fontWeight: 600,
//       fontSize: "0.75rem",
//       height: 24,
//       textTransform: "capitalize",
//     };
//     if (isExpired) {
//       return { ...styles, backgroundColor: palette.cancelled, label: "Expired" };
//     }
//     switch (status.toLowerCase()) {
//       case "scheduled":
//       case "rescheduled":
//         return { ...styles, backgroundColor: palette.primary };
//       case "completed":
//         return { ...styles, backgroundColor: palette.completed };
//       case "cancelled":
//         return { ...styles, backgroundColor: palette.cancelled };
//       default:
//         return { ...styles, backgroundColor: palette.textMuted };
//     }
//   };

//   const renderTabButton = (label, index, color) => (
//     <Button
//       onClick={(e) => handleTabChange(e, index)}
//       sx={{
//         flex: 1,
//         py: 1.5,
//         mx: 1,
//         borderRadius: "12px",
//         fontSize: "1rem",
//         fontWeight: 'bold',
//         color: tabValue === index ? "#FFFFFF" : color,
//         backgroundColor: tabValue === index ? color : "#FFFFFF",
//         border: `1px solid ${color}`,
//         "&:hover": {
//           backgroundColor:
//             tabValue === index ? alpha(color, 0.9) : alpha(color, 0.1),
//         },
//       }}
//     >
//       {label}
//     </Button>
//   );

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//         <Box 
//           sx={{ 
//             mb: 4, 
//             p: 2.5, 
//             backgroundColor: palette.background, 
//             borderRadius: '12px', 
//             boxShadow: '0 4px 12px 0 rgba(0,0,0,0.08)' 
//           }}
//         >
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary">
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
//           {renderTabButton("Scheduled", 0, palette.primary)}
//           {renderTabButton("Completed", 1, palette.completed)}
//           {renderTabButton("Cancelled", 2, palette.cancelled)}
//         </Box>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} md={6} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                       borderRadius: "16px",
//                       boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)",
//                       backgroundColor: palette.background,
//                     }}
//                   >
//                     <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar src={interview.avatar} sx={{ width: 56, height: 56, mr: 2 }} />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Typography variant="h6" fontWeight="700" color={palette.textDark}>
//                             {interview.candidateName}
//                           </Typography>
//                           <Typography variant="body2" color={palette.textMuted}>
//                             {interview.position}
//                           </Typography>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={getStatusChipStyle(interview.status, interview.isExpired)}
//                         />
//                       </Box>

//                       <Grid container spacing={1.5} sx={{ my: 2 }}>
//                         {[
//                           { icon: <CalendarIcon fontSize="small" />, text: interview.date },
//                           { icon: <TimeIcon fontSize="small" />, text: interview.time },
//                           { icon: <ScheduleIcon fontSize="small" />, text: "Technical Round" },
//                           { icon: interview.formType === 'video' ? <LaptopIcon fontSize="small"/> : <BusinessIcon fontSize="small"/>, text: interview.interviewType },
//                         ].map((item, index) => (
//                           <Grid item xs={6} key={index} sx={{ display: "flex", alignItems: "center" }}>
//                             <Box sx={{ color: palette.textMuted, mr: 1.5, display: "flex" }}>{item.icon}</Box>
//                             <Typography variant="body2" fontWeight="500" color={palette.textDark}>
//                               {item.text}
//                             </Typography>
//                           </Grid>
//                         ))}
//                       </Grid>

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
//                             {interview.skills.map((skill) => (
//                               <Chip key={skill} label={skill} size="small" variant="outlined"/>
//                             ))}
//                           </Box>
//                         </Box>
//                       )}

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>Address:</Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography variant="body2" color={palette.textMuted}>
//                               {interview.address.length > 80 ? `${interview.address.substring(0, 80)}...` : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       <Box sx={{
//                           flexGrow: 1,
//                           p: 1.5,
//                           backgroundColor: "#FFFFFF",
//                           borderRadius: "8px",
//                           minHeight: "50px",
//                           border: "1px solid #E0E0E0",
//                         }}
//                       >
//                         <Typography variant="body2" color={palette.textMuted} sx={{ fontStyle: "italic" }}>
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                        <Box sx={{ display: "flex", gap: 1.5 }}>
//                         {tabValue === 0 && interview.formType === "video" && (
//                           <Button variant="contained" size="medium" onClick={() => handleStartInterview(interview.interviewUrl)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Start Interview
//                           </Button>
//                         )}
//                         {tabValue === 0 && interview.formType === "in-person" && (
//                           <Button variant="contained" size="medium" onClick={() => handleOpenMap(interview.address)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Map Location
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button variant="contained" size="medium" startIcon={<ReplayIcon />} onClick={() => handleOpenDialog(interview)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       <Box>
//                          <IconButton size="small" onClick={() => handleDeleteInterview(interview.id)} sx={{ color: palette.cancelled }}>
//                            <DeleteIcon />
//                          </IconButton>
//                       </Box>
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}{tabValue === 0 ? "scheduled" : tabValue === 1 ? "completed" : "cancelled"}{" "}
//                     interviews found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           backgroundColor: palette.primary,
//           color: "#FFFFFF",
//           "&:hover": { backgroundColor: alpha(palette.primary, 0.9) },
//         }}
//         aria-label="schedule interview"
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview && tabValue === 2
//             ? "Reschedule Interview"
//             : selectedInterview
//             ? "Edit Interview"
//             : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem
//                       key={candidate.applicationId}
//                       value={candidate.applicationId}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">{candidate.name}</Typography>
//                           <Typography variant="caption" color="textSecondary">{candidate.position}</Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>
//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: palette.primary }}>
//             {selectedInterview && tabValue === 2
//               ? "Confirm Reschedule"
//               : selectedInterview
//               ? "Save Changes"
//               : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   );
// }

// export default InterviewScheduler;
// import { useState, useEffect, useCallback } from "react";
// import apiClient from "../../context/apiClient";
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Paper,
//   Avatar,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material";
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
//   Laptop as LaptopIcon,
//   Business as BusinessIcon,
//   Replay as ReplayIcon,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";

// const palette = {
//   primary: "#4F46BA",
//   background: "#F1FFFA",
//   completed: "#2FAD43",
//   cancelled: "#E63946",
//   textDark: "#000000",
//   textMuted: "#A9A5A5",
// };

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null;
//   let rawDate = "";
//   let rawTime = "";
//   let status = (apiInterview.status || "unknown").toLowerCase();
//   let isExpired = false;

//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds =
//       apiInterview.interview_time.length === 5
//         ? `${apiInterview.interview_time}:00`
//         : apiInterview.interview_time;
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}Z`);
//     rawDate = apiInterview.interview_date;
//     rawTime = apiInterview.interview_time.substring(0, 5);
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at);
//     const isoString = scheduledAt.toISOString();
//     rawDate = isoString.split("T")[0];
//     rawTime = isoString.substring(11, 16);
//   }

//   const now = new Date();
//   if (scheduledAt && scheduledAt < now && status === "scheduled") {
//     status = "cancelled";
//     isExpired = true;
//   }

//   const location = apiInterview.location || "";
//   let address = "";
//   let interviewUrl = "";
//   let formType = "in-person";

//   const isVideoUrl =
//     location.startsWith("http://") || location.startsWith("https://");
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(
//     location.toLowerCase()
//   );

//   if (isVideoUrl) {
//     formType = "video";
//     interviewUrl = location;
//   } else if (isGenericVideoCall) {
//     formType = "video";
//   } else {
//     formType = "in-person";
//     const locationParts = location.split(" | Map: ");
//     address = locationParts[0];
//   }

//   return {
//     id: apiInterview.id,
//     applicationId:
//       apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     status: status,
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           day: "numeric",
//           month: "long",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "numeric",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Online Meeting" : "Inperson Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes:
//       apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   };
// };

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6}>
//     <Card sx={{ height: 380, display: "flex", flexDirection: "column" }}>
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton
//             variant="rectangular"
//             width={70}
//             height={24}
//             sx={{ borderRadius: 1 }}
//           />
//         </Box>
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={120}
//           sx={{ borderRadius: 1, mb: 2 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={60}
//           sx={{ borderRadius: 1 }}
//         />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton
//           variant="rectangular"
//           width={80}
//           height={32}
//           sx={{ borderRadius: 1, mr: 1 }}
//         />
//         <Skeleton
//           variant="rectangular"
//           width={60}
//           height={32}
//           sx={{ borderRadius: 1 }}
//         />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// );

// function InterviewScheduler() {
//   const { showNotification } = useNotification();
//   const [interviews, setInterviews] = useState([]);
//   const [candidates, setCandidates] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [openDialog, setOpenDialog] = useState(false);
//   const [tabValue, setTabValue] = useState(0);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   });

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true);
//     setError(null);
//     try {
//       const interviewsRes = await apiClient.get("/interviews/");
//       const formattedData = interviewsRes.data.map(formatApiData);
//       setInterviews(formattedData);
//     } catch (err) {
//       setError("Could not load interviews.");
//       showNotification("Failed to fetch interviews.", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   }, [showNotification]);

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/");
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${
//             app.talent_details?.user?.last_name || ""
//           }`.trim() || `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar:
//           app.talent_details?.profile_picture ||
//           "/placeholder.svg?height=40&width=40",
//       }));
//       setCandidates(formattedCandidates);
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning");
//     }
//   }, [showNotification]);

//   useEffect(() => {
//     fetchInterviews();
//     fetchCandidates();
//   }, [fetchInterviews, fetchCandidates]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async () => {
//     if (
//       !selectedInterview &&
//       (!formData.applicationId || !formData.date || !formData.time)
//     ) {
//       showNotification(
//         "Please select a candidate, date, and time for the new interview.",
//         "error"
//       );
//       return;
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification(
//         "Please provide an address for the in-person interview.",
//         "error"
//       );
//       return;
//     }

//     try {
//       if (selectedInterview && tabValue === 2) {
//         // This is a RESCHEDULE action from the Cancelled tab
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled", // Rescheduling sets it back to scheduled
//         };
//         await apiClient.post("/interviews/", payload); // Create a new one
//         showNotification("Interview rescheduled successfully!", "success");
//       } else if (selectedInterview) {
//         // This is an EDIT action from the Scheduled tab
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const payload = {
//           interview_date: formData.date,
//           interview_time: formData.time,
//           location: locationValue,
//           notes: formData.notes,
//         };
//         await apiClient.patch(`/interviews/${selectedInterview.id}/`, payload);
//         showNotification("Interview updated successfully!", "success");
//       } else {
//         // This is a NEW interview
//         const locationValue =
//           formData.locationDetail.trim() ||
//           (formData.type === "video" ? "Video" : "");
//         const scheduledAtISO = new Date(
//           `${formData.date}T${formData.time}`
//         ).toISOString();
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled",
//         };
//         await apiClient.post("/interviews/", payload);
//         showNotification("Interview scheduled successfully!", "success");
//       }
//       fetchInterviews();
//       handleCloseDialog();
//     } catch (err) {
//       const errorData = err.response?.data;
//       let message = "An error occurred.";
//       if (errorData) {
//         message =
//           errorData.detail || JSON.stringify(errorData).substring(0, 100);
//       }
//       showNotification(message, "error");
//     }
//   };

//   const handleDeleteInterview = async (id) => {
//     if (
//       window.confirm("Are you sure you want to permanently delete this interview record?")
//     ) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`);
//         setInterviews(interviews.filter((i) => i.id !== id));
//         showNotification("Interview deleted successfully!", "success");
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error");
//       }
//     }
//   };

//   const handleStartInterview = (interviewUrl) => {
//     if (
//       interviewUrl &&
//       (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))
//     ) {
//       window.open(interviewUrl, "_blank");
//     } else {
//       showNotification("No valid interview URL available.", "warning");
//     }
//   };

//   const filteredInterviews = interviews.filter((interview) => {
//     const status = interview.status.toLowerCase();
//     if (tabValue === 0) return ["scheduled", "rescheduled"].includes(status);
//     if (tabValue === 1) return status === "completed";
//     if (tabValue === 2) return status === "cancelled";
//     return true;
//   });

//   const handleTabChange = (event, newValue) => setTabValue(newValue);

//   const handleCloseDialog = () => setOpenDialog(false);

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview);
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes:
//           interview.notes === "No specific notes provided for this interview."
//             ? ""
//             : interview.notes,
//         locationDetail:
//           interview.formType === "video"
//             ? interview.interviewUrl
//             : interview.address,
//       });
//     } else {
//       setSelectedInterview(null);
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       });
//     }
//     setOpenDialog(true);
//   };
    
//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address);
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
//     window.open(mapUrl, "_blank");
//   };

//   const getStatusChipStyle = (status, isExpired) => {
//     let styles = {
//       color: "#FFFFFF",
//       borderRadius: "50px",
//       fontWeight: 600,
//       fontSize: "0.75rem",
//       height: 24,
//       textTransform: "capitalize",
//     };
//     if (isExpired) {
//       return { ...styles, backgroundColor: palette.cancelled, label: "Expired" };
//     }
//     switch (status.toLowerCase()) {
//       case "scheduled":
//       case "rescheduled":
//         return { ...styles, backgroundColor: palette.primary };
//       case "completed":
//         return { ...styles, backgroundColor: palette.completed };
//       case "cancelled":
//         return { ...styles, backgroundColor: palette.cancelled };
//       default:
//         return { ...styles, backgroundColor: palette.textMuted };
//     }
//   };

//   const renderTabButton = (label, index, color) => (
//     <Button
//       onClick={(e) => handleTabChange(e, index)}
//       sx={{
//         flex: 1,
//         py: 1.5,
//         mx: 1,
//         borderRadius: "12px",
//         fontSize: "1rem",
//         fontWeight: 'bold',
//         color: tabValue === index ? "#FFFFFF" : color,
//         backgroundColor: tabValue === index ? color : "#FFFFFF",
//         border: `1px solid ${color}`,
//         "&:hover": {
//           backgroundColor:
//             tabValue === index ? alpha(color, 0.9) : alpha(color, 0.1),
//         },
//       }}
//     >
//       {label}
//     </Button>
//   );

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
//         <Box 
//           sx={{ 
//             mb: 4, 
//             p: 2.5, 
//             backgroundColor: palette.background, 
//             borderRadius: '12px', 
//             boxShadow: '0 4px 12px 0 rgba(0,0,0,0.08)' 
//           }}
//         >
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary">
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
//           {renderTabButton("Scheduled", 0, palette.primary)}
//           {renderTabButton("Completed", 1, palette.completed)}
//           {renderTabButton("Cancelled", 2, palette.cancelled)}
//         </Box>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} md={6} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                       borderRadius: "16px",
//                       boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)",
//                       backgroundColor: palette.background,
//                     }}
//                   >
//                     <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar src={interview.avatar} sx={{ width: 56, height: 56, mr: 2 }} />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Typography variant="h6" fontWeight="700" color={palette.textDark}>
//                             {interview.candidateName}
//                           </Typography>
//                           <Typography variant="body2" color={palette.textMuted}>
//                             {interview.position}
//                           </Typography>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={getStatusChipStyle(interview.status, interview.isExpired)}
//                         />
//                       </Box>

//                       <Grid container spacing={1.5} sx={{ my: 2 }}>
//                         {[
//                           { icon: <CalendarIcon fontSize="small" />, text: interview.date },
//                           { icon: <TimeIcon fontSize="small" />, text: interview.time },
//                           { icon: <ScheduleIcon fontSize="small" />, text: "Technical Round" },
//                           { icon: interview.formType === 'video' ? <LaptopIcon fontSize="small"/> : <BusinessIcon fontSize="small"/>, text: interview.interviewType },
//                         ].map((item, index) => (
//                           <Grid item xs={6} key={index} sx={{ display: "flex", alignItems: "center" }}>
//                             <Box sx={{ color: palette.textMuted, mr: 1.5, display: "flex" }}>{item.icon}</Box>
//                             <Typography variant="body2" fontWeight="500" color={palette.textDark}>
//                               {item.text}
//                             </Typography>
//                           </Grid>
//                         ))}
//                       </Grid>

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
//                             {interview.skills.map((skill) => (
//                               <Chip key={skill} label={skill} size="small" variant="outlined"/>
//                             ))}
//                           </Box>
//                         </Box>
//                       )}

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>Address:</Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography variant="body2" color={palette.textMuted}>
//                               {interview.address.length > 80 ? `${interview.address.substring(0, 80)}...` : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       <Box sx={{
//                           flexGrow: 1,
//                           p: 1.5,
//                           backgroundColor: "#FFFFFF",
//                           borderRadius: "8px",
//                           minHeight: "50px",
//                           border: "1px solid #E0E0E0",
//                         }}
//                       >
//                         <Typography variant="body2" color={palette.textMuted} sx={{ fontStyle: "italic" }}>
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                        <Box sx={{ display: "flex", gap: 1.5 }}>
//                         {tabValue === 0 && interview.formType === "video" && (
//                           <Button variant="contained" size="medium" onClick={() => handleStartInterview(interview.interviewUrl)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Start Interview
//                           </Button>
//                         )}
//                         {tabValue === 0 && interview.formType === "in-person" && (
//                           <Button variant="contained" size="medium" onClick={() => handleOpenMap(interview.address)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Map Location
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button variant="contained" size="medium" startIcon={<ReplayIcon />} onClick={() => handleOpenDialog(interview)} sx={{ borderRadius: "50px", textTransform: "none", backgroundColor: palette.primary, px: 3 }}>
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       <Box>
//                          <IconButton size="small" onClick={() => handleDeleteInterview(interview.id)} sx={{ color: palette.cancelled }}>
//                            <DeleteIcon />
//                          </IconButton>
//                       </Box>
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}{tabValue === 0 ? "scheduled" : tabValue === 1 ? "completed" : "cancelled"}{" "}
//                     interviews found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           backgroundColor: palette.primary,
//           color: "#FFFFFF",
//           "&:hover": { backgroundColor: alpha(palette.primary, 0.9) },
//         }}
//         aria-label="schedule interview"
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview && tabValue === 2
//             ? "Reschedule Interview"
//             : selectedInterview
//             ? "Edit Interview"
//             : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem
//                       key={candidate.applicationId}
//                       value={candidate.applicationId}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">{candidate.name}</Typography>
//                           <Typography variant="caption" color="textSecondary">{candidate.position}</Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>
//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: palette.primary }}>
//             {selectedInterview && tabValue === 2
//               ? "Confirm Reschedule"
//               : selectedInterview
//               ? "Save Changes"
//               : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   );
// }

// export default InterviewScheduler;
// import { useState, useEffect, useCallback } from "react"
// import apiClient from "../../context/apiClient"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Tabs,
//   Tab,
//   Paper,
//   Avatar,
//   Badge,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material"
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
// } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import { useNotification } from "../../context/NotificationContext"

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null
//   let rawDate = ""
//   let rawTime = ""
//   let status = (apiInterview.status || "unknown").toLowerCase()
//   let isExpired = false

//   // Logic for parsing date and time from API response
//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
//     // Treat the incoming date/time as UTC to ensure correct comparison across timezones
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}Z`)
//     rawDate = apiInterview.interview_date
//     rawTime = apiInterview.interview_time.substring(0, 5)
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at)
//     const isoString = scheduledAt.toISOString()
//     rawDate = isoString.split("T")[0]
//     rawTime = isoString.substring(11, 16)
//   }

//   // --- This is the key logic to update the status of expired interviews ---
//   const now = new Date()
//   if (scheduledAt && scheduledAt < now && status === "scheduled") {
//     // If a 'scheduled' interview's date/time is in the past, update its status.
//     status = "cancelled"
//     isExpired = true // Flag this for the UI to show an "Expired" label.
//   }
//   // --------------------------------------------------------------------------

//   const location = apiInterview.location || ""
//   let address = ""
//   let interviewUrl = ""
//   let formType = "in-person"

//   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

//   if (isVideoUrl) {
//     formType = "video"
//     interviewUrl = location
//   } else if (isGenericVideoCall) {
//     formType = "video"
//   } else {
//     formType = "in-person"
//     const locationParts = location.split(" | Map: ")
//     address = locationParts[0]
//   }

//   return {
//     id: apiInterview.id,
//     applicationId: apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     experience: apiInterview.experience_level || "N/A",
//     status: status, // The status will now correctly be 'cancelled' for expired interviews
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           month: "short",
//           day: "numeric",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "2-digit",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes: apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   }
// }

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6} lg={4}>
//     <Card
//       sx={{
//         height: 380,
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
//         </Box>
//         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
//         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
//         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// )

// function InterviewScheduler() {
//   const { showNotification } = useNotification()
//   const [interviews, setInterviews] = useState([])
//   const [candidates, setCandidates] = useState([])
//   const [isLoading, setIsLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [openDialog, setOpenDialog] = useState(false)
//   const [tabValue, setTabValue] = useState(0)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   })

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true)
//     setError(null)
//     try {
//       const interviewsRes = await apiClient.get("/interviews/")
//       const formattedData = interviewsRes.data.map(formatApiData)
//       setInterviews(formattedData)
//     } catch (err) {
//       setError("Could not load interviews.")
//       showNotification("Failed to fetch interviews.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }, [showNotification])

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/")
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
//           `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
//       }))
//       setCandidates(formattedCandidates)
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning")
//     }
//   }, [showNotification])

//   useEffect(() => {
//     fetchInterviews()
//     fetchCandidates()
//   }, [fetchInterviews, fetchCandidates])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleSubmit = async () => {
//     // Validation for new interviews
//     if (!selectedInterview && (!formData.applicationId || !formData.date || !formData.time)) {
//       showNotification("Please select a candidate, date, and time for the new interview.", "error")
//       return
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification("Please provide an address for the in-person interview.", "error")
//       return
//     }

//     try {
//       if (selectedInterview) {
//         // For an existing interview, only update its status to "Rescheduled"
//         await apiClient.patch(`/interviews/${selectedInterview.id}/update-status/`, {
//           interview_status: "Rescheduled",
//         })
//         showNotification("Interview has been rescheduled!", "success")
//       } else {
//         // For a new interview, create it with "scheduled" status
//         const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")
//         const scheduledAtISO = new Date(`${formData.date}T${formData.time}`).toISOString()
//         const payload = {
//           application: formData.applicationId,
//           interview_date: formData.date,
//           interview_time: formData.time,
//           scheduled_at: scheduledAtISO,
//           location: locationValue,
//           notes: formData.notes,
//           status: "scheduled",
//         }
//         await apiClient.post("/interviews/", payload)
//         showNotification("Interview scheduled successfully!", "success")
//       }
//       fetchInterviews() // Refresh the interviews list
//       handleCloseDialog()
//     } catch (err) {
//       const errorData = err.response?.data
//       let message = "An error occurred."
//       if (errorData) {
//         if (errorData.scheduled_at) {
//           message = `Scheduled At: ${errorData.scheduled_at.join(", ")}`
//         } else if (errorData.detail) {
//           message = errorData.detail
//         } else {
//           const firstErrorKey = Object.keys(errorData)[0]
//           if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
//             const fieldName = firstErrorKey.charAt(0).toUpperCase() + firstErrorKey.slice(1).replace(/_/g, " ")
//             message = `${fieldName}: ${errorData[firstErrorKey].join(", ")}`
//           }
//         }
//       }
//       showNotification(message, "error")
//     }
//   }

//   const handleDeleteInterview = async (id) => {
//     if (window.confirm("Are you sure you want to permanently delete this interview record?")) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`)
//         setInterviews(interviews.filter((i) => i.id !== id))
//         showNotification("Interview deleted successfully!", "success")
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error")
//       }
//     }
//   }

//   const handleStartInterview = (interviewUrl) => {
//     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
//       window.open(interviewUrl, "_blank")
//     } else {
//       showNotification("No valid interview URL available.", "warning")
//     }
//   }

//   // *** MODIFICATION START ***
//   // Updated filtering logic to include "rescheduled" interviews in the "Scheduled" tab
//   const filteredInterviews = interviews.filter((interview) => {
//     const status = interview.status.toLowerCase()
//     if (tabValue === 0) return ["scheduled", "rescheduled"].includes(status)
//     if (tabValue === 1) return status === "completed"
//     if (tabValue === 2) return status === "cancelled"
//     return true
//   })
//   // *** MODIFICATION END ***

//   const handleTabChange = (event, newValue) => setTabValue(newValue)

//   const handleCloseDialog = () => setOpenDialog(false)

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview)
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
//         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
//       })
//     } else {
//       setSelectedInterview(null)
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       })
//     }
//     setOpenDialog(true)
//   }

//   // *** MODIFICATION START ***
//   // Added a color for "rescheduled" status
//   const getStatusColor = (status) => {
//     switch (status.toLowerCase()) {
//       case "scheduled":
//         return "primary"
//       case "rescheduled":
//         return "info" // Using "info" color for rescheduled interviews
//       case "completed":
//         return "success"
//       case "cancelled":
//         return "error"
//       default:
//         return "default"
//     }
//   }
//   // *** MODIFICATION END ***

//   // *** MODIFICATION START ***
//   // Update badge count to include both scheduled and rescheduled interviews
//   const upcomingInterviews = interviews.filter(
//     (i) => i.status === "scheduled" || i.status === "rescheduled"
//   ).length
//   // *** MODIFICATION END ***

//   const completedInterviews = interviews.filter((i) => i.status === "completed").length
//   const cancelledInterviews = interviews.filter((i) => i.status === "cancelled").length

//   const tabIndicatorColors = {
//     0: "primary.main",
//     1: "success.main",
//     2: "error.main",
//   }

//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address)
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
//     window.open(mapUrl, "_blank")
//   }

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
//         <Box sx={{ mb: 3 }}>
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Paper
//           elevation={0}
//           sx={{
//             mb: 4,
//             borderRadius: 3,
//             background: (theme) => alpha(theme.palette.background.paper, 0.95),
//             backdropFilter: "blur(10px)",
//           }}
//         >
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             centered
//             sx={{
//               "& .MuiTabs-indicator": {
//                 backgroundColor: tabIndicatorColors[tabValue],
//               },
//               "& .MuiTab-root": {
//                 fontSize: "0.95rem",
//               },
//             }}
//           >
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={upcomingInterviews} color="primary" />
//                   <span>Scheduled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "primary.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={completedInterviews} color="success" />
//                   <span>Completed</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "success.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={cancelledInterviews} color="error" />
//                   <span>Cancelled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "error.main",
//                 },
//               }}
//             />
//           </Tabs>
//         </Paper>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                     }}
//                   >
//                     <CardContent
//                       sx={{
//                         flexGrow: 1,
//                         p: 2.5,
//                         pb: 1,
//                         display: "flex",
//                         flexDirection: "column",
//                       }}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar
//                           src={interview.avatar}
//                           sx={{
//                             width: 48,
//                             height: 48,
//                             mr: 2,
//                             border: (theme) => `2px solid ${theme.palette.background.default}`,
//                           }}
//                         />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Tooltip title={interview.candidateName} placement="top">
//                             <Typography
//                               variant="h6"
//                               fontWeight="600"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "1rem",
//                                 lineHeight: 1.3,
//                               }}
//                             >
//                               {interview.candidateName}
//                             </Typography>
//                           </Tooltip>
//                           <Tooltip title={interview.position} placement="top">
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "0.85rem",
//                               }}
//                             >
//                               {interview.position}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={(theme) => ({
//                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
//                             color: `${getStatusColor(interview.status)}.main`,
//                             fontWeight: 600,
//                             fontSize: "0.75rem",
//                             height: 24,
//                             textTransform: "capitalize",
//                           })}
//                         />
//                       </Box>

//                       <Divider sx={{ mb: 2 }} />

//                       <Box sx={{ mb: 2 }}>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.date}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.time}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           {interview.formType === "video" ? (
//                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
//                           ) : (
//                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
//                           )}
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.interviewType}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Address:
//                           </Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 fontSize: "0.8rem",
//                                 lineHeight: 1.3,
//                                 cursor: "pointer",
//                                 "&:hover": {
//                                   color: "primary.main",
//                                 },
//                               }}
//                             >
//                               {interview.address.length > 40
//                                 ? `${interview.address.substring(0, 40)}...`
//                                 : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Skills:
//                           </Typography>
//                           <Box
//                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
//                           >
//                             {interview.skills.slice(0, 3).map((skill) => (
//                               <Chip
//                                 key={skill}
//                                 label={skill}
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             ))}
//                             {interview.skills.length > 3 && (
//                               <Chip
//                                 label={`+${interview.skills.length - 3}`}
//                                 size="small"
//                                 variant="filled"
//                                 color="primary"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             )}
//                           </Box>
//                         </Box>
//                       )}

//                       <Box sx={{ flexGrow: 1 }}>
//                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                           Notes:
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           color="text.secondary"
//                           sx={{
//                             fontSize: "0.8rem",
//                             fontStyle: "italic",
//                             lineHeight: 1.3,
//                             overflow: "hidden",
//                             display: "-webkit-box",
//                             WebkitLineClamp: 2,
//                             WebkitBoxOrient: "vertical",
//                             height: "2.6em",
//                           }}
//                         >
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <Divider />

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                       <Box sx={{ display: "flex", gap: 1 }}>
//                         {(interview.status === "scheduled" || interview.status === "rescheduled") && interview.interviewUrl && (
//                           <Button
//                             variant="contained"
//                             size="small"
//                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleStartInterview(interview.interviewUrl)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Start
//                           </Button>
//                         )}
//                         {(interview.status === "scheduled" || interview.status === "rescheduled") && interview.address && (
//                           <Button
//                             variant="contained"
//                             color="secondary"
//                             size="small"
//                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenMap(interview.address)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Map
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button
//                             variant="outlined"
//                             size="small"
//                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenDialog(interview)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       <Box sx={{ display: "flex", gap: 0.5 }}>
//                         {tabValue === 0 && (
//                           <Tooltip title="Edit Interview">
//                             <IconButton
//                               size="small"
//                               onClick={() => handleOpenDialog(interview)}
//                               sx={{
//                                 "&:hover": {
//                                   color: "primary.main",
//                                   backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
//                                 },
//                               }}
//                             >
//                               <EditIcon sx={{ fontSize: 18 }} />
//                             </IconButton>
//                           </Tooltip>
//                         )}
//                         {(tabValue === 0 || tabValue === 2) && (
//                           <Tooltip title="Delete Interview">
//                             <IconButton
//                               size="small"
//                               color="error"
//                               onClick={() => handleDeleteInterview(interview.id)}
//                               sx={{
//                                 "&:hover": {
//                                   backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
//                                 },
//                               }}
//                             >
//                               <DeleteIcon sx={{ fontSize: 18 }} />
//                             </IconButton>
//                           </Tooltip>
//                         )}
//                       </Box>
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center" }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}
//                     {tabValue === 0
//                       ? "scheduled interviews"
//                       : tabValue === 1
//                       ? "completed interviews"
//                       : "cancelled interviews"}{" "}
//                     found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         color="primary"
//         aria-label="schedule interview"
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
//         }}
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>{selectedInterview ? "Reschedule Interview" : "Schedule New Interview"}</DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">
//                             {candidate.name}
//                           </Typography>
//                           <Typography variant="caption" color="textSecondary">
//                             {candidate.position}
//                           </Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//                 disabled={!!selectedInterview} // Disable if rescheduling
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//                 disabled={!!selectedInterview} // Disable if rescheduling
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>

//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                   disabled={!!selectedInterview}
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                   disabled={!!selectedInterview}
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//                 disabled={!!selectedInterview}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained">
//             {selectedInterview ? "Confirm Reschedule" : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   )
// }

// export default InterviewScheduler

// import { useState, useEffect, useCallback } from "react"
// import apiClient from "../../context/apiClient"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Tabs,
//   Tab,
//   Paper,
//   Avatar,
//   Badge,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material"
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
// } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import { useNotification } from "../../context/NotificationContext"

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null
//   let rawDate = ""
//   let rawTime = ""
//   let status = (apiInterview.status || "unknown").toLowerCase()
//   let isExpired = false

//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`)
//     rawDate = apiInterview.interview_date
//     rawTime = apiInterview.interview_time.substring(0, 5)
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at)
//     rawDate = scheduledAt.toISOString().split("T")[0]
//     rawTime = scheduledAt.toTimeString().substring(0, 5)
//   }

//   const now = new Date()
//   if (scheduledAt && scheduledAt < now && status === "scheduled") {
//     status = "cancelled"
//     isExpired = true
//   }

//   const location = apiInterview.location || ""
//   let address = ""
//   let interviewUrl = ""
//   let formType = "in-person"

//   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

//   if (isVideoUrl) {
//     formType = "video"
//     interviewUrl = location
//   } else if (isGenericVideoCall) {
//     formType = "video"
//   } else {
//     formType = "in-person"
//     const locationParts = location.split(" | Map: ")
//     address = locationParts[0]
//   }

//   return {
//     id: apiInterview.id,
//     applicationId: apiInterview.application || apiInterview.applicationId,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     experience: apiInterview.experience_level || "N/A",
//     status: status,
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           month: "short",
//           day: "numeric",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "2-digit",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes: apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   }
// }

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6} lg={4}>
//     <Card
//       sx={{
//         height: 380,
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
//         </Box>
//         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
//         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
//         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// )

// function InterviewScheduler() {
//   const { showNotification } = useNotification()
//   const [interviews, setInterviews] = useState([])
//   const [candidates, setCandidates] = useState([])
//   const [isLoading, setIsLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [openDialog, setOpenDialog] = useState(false)
//   const [tabValue, setTabValue] = useState(0)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   })

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true)
//     setError(null)
//     try {
//       const interviewsRes = await apiClient.get("/interviews/")
//       const formattedData = interviewsRes.data.map(formatApiData)
//       setInterviews(formattedData)
//     } catch (err) {
//       setError("Could not load interviews.")
//       showNotification("Failed to fetch interviews.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }, [showNotification])

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/")
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
//           `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
//       }))
//       setCandidates(formattedCandidates)
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning")
//     }
//   }, [showNotification])

//   useEffect(() => {
//     fetchInterviews()
//     fetchCandidates()
//   }, [fetchInterviews, fetchCandidates])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleSubmit = async () => {
//     if (!formData.applicationId || !formData.date || !formData.time) {
//       showNotification("Please select a candidate, date, and time.", "error")
//       return
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification("Please provide an address for the in-person interview.", "error")
//       return
//     }

//     const wasCancelled = selectedInterview && selectedInterview.status === "cancelled"
//     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")
//     const scheduledAtISO = new Date(`${formData.date}T${formData.time}`).toISOString()

//     const payload = {
//       application: formData.applicationId,
//       interview_date: formData.date,
//       interview_time: formData.time,
//       scheduled_at: scheduledAtISO,
//       location: locationValue,
//       notes: formData.notes,
//       status: "scheduled",
//     }

//     try {
//       if (selectedInterview) {
//         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
//       } else {
//         await apiClient.post("/interviews/", payload)
//       }

//       handleCloseDialog()
//       showNotification(selectedInterview ? "Interview updated successfully!" : "Interview scheduled successfully!", "success")

//       await fetchInterviews()

//       if (wasCancelled) {
//         setTabValue(0)
//       }
//     } catch (err) {
//       const errorData = err.response?.data
//       let message = "An error occurred."
//       if (errorData) {
//         if (errorData.application) {
//           message = `Application: ${errorData.application.join(", ")}`
//         } else if (errorData.scheduled_at) {
//           message = `Scheduled At: ${errorData.scheduled_at.join(", ")}`
//         } else if (errorData.detail) {
//           message = errorData.detail
//         } else {
//           const firstErrorKey = Object.keys(errorData)[0]
//           if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
//             const fieldName = firstErrorKey.charAt(0).toUpperCase() + firstErrorKey.slice(1).replace(/_/g, " ")
//             message = `${fieldName}: ${errorData[firstErrorKey].join(", ")}`
//           }
//         }
//       }
//       showNotification(message, "error")
//     }
//   }

//   const handleDeleteInterview = async (id) => {
//     if (window.confirm("Are you sure you want to permanently delete this interview record?")) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`)
//         setInterviews(interviews.filter((i) => i.id !== id))
//         showNotification("Interview deleted successfully!", "success")
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error")
//       }
//     }
//   }

//   const handleStartInterview = (interviewUrl) => {
//     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
//       window.open(interviewUrl, "_blank")
//     } else {
//       showNotification("No valid interview URL available.", "warning")
//     }
//   }

//   const filteredInterviews = interviews.filter((interview) => {
//     if (tabValue === 0) return interview.status === "scheduled"
//     if (tabValue === 1) return interview.status === "completed"
//     if (tabValue === 2) return interview.status === "cancelled"
//     return true
//   })

//   const handleTabChange = (event, newValue) => setTabValue(newValue)

//   const handleCloseDialog = () => setOpenDialog(false)

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview)
//       setFormData({
//         applicationId: interview.applicationId || "", // Use empty string if null/undefined
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
//         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
//       })
//     } else {
//       setSelectedInterview(null)
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       })
//     }
//     setOpenDialog(true)
//   }

//   const getStatusColor = (status) => {
//     switch (status) {
//       case "scheduled":
//         return "primary"
//       case "completed":
//         return "success"
//       case "cancelled":
//         return "error"
//       default:
//         return "default"
//     }
//   }

//   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length
//   const completedInterviews = interviews.filter((i) => i.status === "completed").length
//   const cancelledInterviews = interviews.filter((i) => i.status === "cancelled").length

//   const tabIndicatorColors = {
//     0: "primary.main",
//     1: "success.main",
//     2: "error.main",
//   }

//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address)
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
//     window.open(mapUrl, "_blank")
//   }

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
//         <Box sx={{ mb: 3 }}>
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Paper
//           elevation={0}
//           sx={{
//             mb: 4,
//             borderRadius: 3,
//             background: (theme) => alpha(theme.palette.background.paper, 0.95),
//             backdropFilter: "blur(10px)",
//           }}
//         >
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             centered
//             sx={{
//               "& .MuiTabs-indicator": {
//                 backgroundColor: tabIndicatorColors[tabValue],
//               },
//               "& .MuiTab-root": {
//                 fontSize: "0.95rem",
//               },
//             }}
//           >
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={upcomingInterviews} color="primary" />
//                   <span>Scheduled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "primary.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={completedInterviews} color="success" />
//                   <span>Completed</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "success.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={cancelledInterviews} color="error" />
//                   <span>Cancelled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "error.main",
//                 },
//               }}
//             />
//           </Tabs>
//         </Paper>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                     }}
//                   >
//                     <CardContent
//                       sx={{
//                         flexGrow: 1,
//                         p: 2.5,
//                         pb: 1,
//                         display: "flex",
//                         flexDirection: "column",
//                       }}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar
//                           src={interview.avatar}
//                           sx={{
//                             width: 48,
//                             height: 48,
//                             mr: 2,
//                             border: (theme) => `2px solid ${theme.palette.background.default}`,
//                           }}
//                         />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Tooltip title={interview.candidateName} placement="top">
//                             <Typography
//                               variant="h6"
//                               fontWeight="600"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "1rem",
//                                 lineHeight: 1.3,
//                               }}
//                             >
//                               {interview.candidateName}
//                             </Typography>
//                           </Tooltip>
//                           <Tooltip title={interview.position} placement="top">
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "0.85rem",
//                               }}
//                             >
//                               {interview.position}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={(theme) => ({
//                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
//                             color: `${getStatusColor(interview.status)}.main`,
//                             fontWeight: 600,
//                             fontSize: "0.75rem",
//                             height: 24,
//                             textTransform: "capitalize",
//                           })}
//                         />
//                       </Box>

//                       <Divider sx={{ mb: 2 }} />

//                       <Box sx={{ mb: 2 }}>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.date}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.time}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           {interview.formType === "video" ? (
//                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
//                           ) : (
//                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
//                           )}
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.interviewType}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Address:
//                           </Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 fontSize: "0.8rem",
//                                 lineHeight: 1.3,
//                                 cursor: "pointer",
//                                 "&:hover": {
//                                   color: "primary.main",
//                                 },
//                               }}
//                             >
//                               {interview.address.length > 40
//                                 ? `${interview.address.substring(0, 40)}...`
//                                 : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Skills:
//                           </Typography>
//                           <Box
//                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
//                           >
//                             {interview.skills.slice(0, 3).map((skill) => (
//                               <Chip
//                                 key={skill}
//                                 label={skill}
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             ))}
//                             {interview.skills.length > 3 && (
//                               <Chip
//                                 label={`+${interview.skills.length - 3}`}
//                                 size="small"
//                                 variant="filled"
//                                 color="primary"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             )}
//                           </Box>
//                         </Box>
//                       )}

//                       <Box sx={{ flexGrow: 1 }}>
//                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                           Notes:
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           color="text.secondary"
//                           sx={{
//                             fontSize: "0.8rem",
//                             fontStyle: "italic",
//                             lineHeight: 1.3,
//                             overflow: "hidden",
//                             display: "-webkit-box",
//                             WebkitLineClamp: 2,
//                             WebkitBoxOrient: "vertical",
//                             height: "2.6em",
//                           }}
//                         >
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <Divider />

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                       <Box sx={{ display: "flex", gap: 1 }}>
//                         {interview.status === "scheduled" && interview.interviewUrl && (
//                           <Button
//                             variant="contained"
//                             size="small"
//                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleStartInterview(interview.interviewUrl)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Start
//                           </Button>
//                         )}
//                         {interview.status === "scheduled" && interview.address && (
//                           <Button
//                             variant="contained"
//                             color="secondary"
//                             size="small"
//                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenMap(interview.address)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Map
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button
//                             variant="outlined"
//                             size="small"
//                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenDialog(interview)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       {(tabValue === 0 || tabValue === 2) && (
//                         <Tooltip title="Delete Interview">
//                           <IconButton
//                             size="small"
//                             color="error"
//                             onClick={() => handleDeleteInterview(interview.id)}
//                             sx={{
//                               "&:hover": {
//                                 backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
//                               },
//                             }}
//                           >
//                             <DeleteIcon sx={{ fontSize: 18 }} />
//                           </IconButton>
//                         </Tooltip>
//                       )}
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center" }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}
//                     {tabValue === 0
//                       ? "scheduled interviews"
//                       : tabValue === 1
//                       ? "completed interviews"
//                       : "cancelled interviews"}{" "}
//                     found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         color="primary"
//         aria-label="schedule interview"
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
//         }}
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview ? "Reschedule Interview" : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!(selectedInterview && selectedInterview.applicationId)}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">
//                             {candidate.name}
//                           </Typography>
//                           <Typography variant="caption" color="textSecondary">
//                             {candidate.position}
//                           </Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>

//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained">
//             {selectedInterview ? "Update Interview" : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   )
// }

// export default InterviewScheduler


// import { useState, useEffect, useCallback } from "react"
// import apiClient from "../../context/apiClient"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Tabs,
//   Tab,
//   Paper,
//   Avatar,
//   Badge,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material"
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
// } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import { useNotification } from "../../context/NotificationContext"

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null
//   let rawDate = ""
//   let rawTime = ""
//   let status = (apiInterview.status || "unknown").toLowerCase()
//   let isExpired = false

//   // Logic for parsing date and time from API response
//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
//     // FIX: Append 'Z' to treat the date/time as UTC. This prevents timezone errors
//     // during the expiry check, assuming the API provides date/time in UTC.
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}Z`)
//     rawDate = apiInterview.interview_date
//     rawTime = apiInterview.interview_time.substring(0, 5)
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at)
//     // Consistently derive rawDate and rawTime from the UTC representation for the form
//     const isoString = scheduledAt.toISOString()
//     rawDate = isoString.split("T")[0]
//     rawTime = isoString.substring(11, 16)
//   }

//   // --- This is the key logic to update the status of expired interviews ---
//   const now = new Date()
//   if (scheduledAt && scheduledAt < now && status === "scheduled") {
//     // If a 'scheduled' interview's date/time is in the past, update its status to 'cancelled'.
//     status = "cancelled"
//     isExpired = true // Flag this for the UI to show an "Expired" label.
//   }
//   // --------------------------------------------------------------------------

//   const location = apiInterview.location || ""
//   let address = ""
//   let interviewUrl = ""
//   let formType = "in-person"

//   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

//   if (isVideoUrl) {
//     formType = "video"
//     interviewUrl = location
//   } else if (isGenericVideoCall) {
//     formType = "video"
//   } else {
//     formType = "in-person"
//     const locationParts = location.split(" | Map: ")
//     address = locationParts[0]
//   }

//   return {
//     id: apiInterview.id,
//     applicationId: apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     experience: apiInterview.experience_level || "N/A",
//     status: status, // The status will now correctly be 'cancelled' for expired interviews
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           month: "short",
//           day: "numeric",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "2-digit",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes: apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   }
// }

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6} lg={4}>
//     <Card
//       sx={{
//         height: 380,
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
//         </Box>
//         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
//         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
//         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// )

// function InterviewScheduler() {
//   const { showNotification } = useNotification()
//   const [interviews, setInterviews] = useState([])
//   const [candidates, setCandidates] = useState([])
//   const [isLoading, setIsLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [openDialog, setOpenDialog] = useState(false)
//   const [tabValue, setTabValue] = useState(0)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   })

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true)
//     setError(null)
//     try {
//       const interviewsRes = await apiClient.get("/interviews/")
//       const formattedData = interviewsRes.data.map(formatApiData)
//       setInterviews(formattedData)
//     } catch (err) {
//       setError("Could not load interviews.")
//       showNotification("Failed to fetch interviews.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }, [showNotification])

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/")
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
//           `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
//       }))
//       setCandidates(formattedCandidates)
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning")
//     }
//   }, [showNotification])

//   useEffect(() => {
//     fetchInterviews()
//     fetchCandidates()
//   }, [fetchInterviews, fetchCandidates])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleSubmit = async () => {
//     if (!formData.applicationId || !formData.date || !formData.time) {
//       showNotification("Please select a candidate, date, and time.", "error")
//       return
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification("Please provide an address for the in-person interview.", "error")
//       return
//     }

//     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")

//     // Create a combined ISO string for the backend. The error indicates `scheduled_at` is required.
//     const scheduledAtISO = new Date(`${formData.date}T${formData.time}`).toISOString()

//     const payload = {
//       application: formData.applicationId,
//       interview_date: formData.date,
//       interview_time: formData.time,
//       scheduled_at: scheduledAtISO, // The crucial addition to fix the 400 error
//       location: locationValue,
//       notes: formData.notes,
//       status: "scheduled",
//     }

//     try {
//       if (selectedInterview) {
//         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
//         showNotification("Interview updated successfully!", "success")
//       } else {
//         await apiClient.post("/interviews/", payload)
//         showNotification("Interview scheduled successfully!", "success")
//       }
//       fetchInterviews()
//       handleCloseDialog()
//     } catch (err) {
//       const errorData = err.response?.data
//       let message = "An error occurred."
//       if (errorData) {
//         if (errorData.scheduled_at) {
//           message = `Scheduled At: ${errorData.scheduled_at.join(", ")}`
//         } else if (errorData.detail) {
//           message = errorData.detail
//         } else {
//           // Handle other potential validation errors
//           const firstErrorKey = Object.keys(errorData)[0]
//           if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
//             const fieldName = firstErrorKey.charAt(0).toUpperCase() + firstErrorKey.slice(1).replace(/_/g, " ")
//             message = `${fieldName}: ${errorData[firstErrorKey].join(", ")}`
//           }
//         }
//       }
//       showNotification(message, "error")
//     }
//   }

//   const handleDeleteInterview = async (id) => {
//     if (window.confirm("Are you sure you want to permanently delete this interview record?")) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`)
//         setInterviews(interviews.filter((i) => i.id !== id))
//         showNotification("Interview deleted successfully!", "success")
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error")
//       }
//     }
//   }

//   const handleStartInterview = (interviewUrl) => {
//     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
//       window.open(interviewUrl, "_blank")
//     } else {
//       showNotification("No valid interview URL available.", "warning")
//     }
//   }

//   const filteredInterviews = interviews.filter((interview) => {
//     if (tabValue === 0) return interview.status === "scheduled"
//     if (tabValue === 1) return interview.status === "completed"
//     if (tabValue === 2) return interview.status === "cancelled"
//     return true
//   })

//   const handleTabChange = (event, newValue) => setTabValue(newValue)

//   const handleCloseDialog = () => setOpenDialog(false)

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview)
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
//         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
//       })
//     } else {
//       setSelectedInterview(null)
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       })
//     }
//     setOpenDialog(true)
//   }

//   const getStatusColor = (status) => {
//     switch (status) {
//       case "scheduled":
//         return "primary"
//       case "completed":
//         return "success"
//       case "cancelled":
//         return "error"
//       default:
//         return "default"
//     }
//   }

//   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length
//   const completedInterviews = interviews.filter((i) => i.status === "completed").length
//   const cancelledInterviews = interviews.filter((i) => i.status === "cancelled").length

//   const tabIndicatorColors = {
//     0: "primary.main",
//     1: "success.main",
//     2: "error.main",
//   }

//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address)
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
//     window.open(mapUrl, "_blank")
//   }

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
//         <Box sx={{ mb: 3 }}>
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Paper
//           elevation={0}
//           sx={{
//             mb: 4,
//             borderRadius: 3,
//             background: (theme) => alpha(theme.palette.background.paper, 0.95),
//             backdropFilter: "blur(10px)",
//           }}
//         >
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             centered
//             sx={{
//               "& .MuiTabs-indicator": {
//                 backgroundColor: tabIndicatorColors[tabValue],
//               },
//               "& .MuiTab-root": {
//                 fontSize: "0.95rem",
//               },
//             }}
//           >
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={upcomingInterviews} color="primary" />
//                   <span>Scheduled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "primary.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={completedInterviews} color="success" />
//                   <span>Completed</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "success.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={cancelledInterviews} color="error" />
//                   <span>Cancelled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "error.main",
//                 },
//               }}
//             />
//           </Tabs>
//         </Paper>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                     }}
//                   >
//                     <CardContent
//                       sx={{
//                         flexGrow: 1,
//                         p: 2.5,
//                         pb: 1,
//                         display: "flex",
//                         flexDirection: "column",
//                       }}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar
//                           src={interview.avatar}
//                           sx={{
//                             width: 48,
//                             height: 48,
//                             mr: 2,
//                             border: (theme) => `2px solid ${theme.palette.background.default}`,
//                           }}
//                         />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Tooltip title={interview.candidateName} placement="top">
//                             <Typography
//                               variant="h6"
//                               fontWeight="600"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "1rem",
//                                 lineHeight: 1.3,
//                               }}
//                             >
//                               {interview.candidateName}
//                             </Typography>
//                           </Tooltip>
//                           <Tooltip title={interview.position} placement="top">
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "0.85rem",
//                               }}
//                             >
//                               {interview.position}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={(theme) => ({
//                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
//                             color: `${getStatusColor(interview.status)}.main`,
//                             fontWeight: 600,
//                             fontSize: "0.75rem",
//                             height: 24,
//                             textTransform: "capitalize",
//                           })}
//                         />
//                       </Box>

//                       <Divider sx={{ mb: 2 }} />

//                       <Box sx={{ mb: 2 }}>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.date}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.time}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           {interview.formType === "video" ? (
//                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
//                           ) : (
//                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
//                           )}
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.interviewType}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Address:
//                           </Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 fontSize: "0.8rem",
//                                 lineHeight: 1.3,
//                                 cursor: "pointer",
//                                 "&:hover": {
//                                   color: "primary.main",
//                                 },
//                               }}
//                             >
//                               {interview.address.length > 40
//                                 ? `${interview.address.substring(0, 40)}...`
//                                 : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Skills:
//                           </Typography>
//                           <Box
//                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
//                           >
//                             {interview.skills.slice(0, 3).map((skill) => (
//                               <Chip
//                                 key={skill}
//                                 label={skill}
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             ))}
//                             {interview.skills.length > 3 && (
//                               <Chip
//                                 label={`+${interview.skills.length - 3}`}
//                                 size="small"
//                                 variant="filled"
//                                 color="primary"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             )}
//                           </Box>
//                         </Box>
//                       )}

//                       <Box sx={{ flexGrow: 1 }}>
//                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                           Notes:
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           color="text.secondary"
//                           sx={{
//                             fontSize: "0.8rem",
//                             fontStyle: "italic",
//                             lineHeight: 1.3,
//                             overflow: "hidden",
//                             display: "-webkit-box",
//                             WebkitLineClamp: 2,
//                             WebkitBoxOrient: "vertical",
//                             height: "2.6em",
//                           }}
//                         >
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <Divider />

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                       <Box sx={{ display: "flex", gap: 1 }}>
//                         {interview.status === "scheduled" && interview.interviewUrl && (
//                           <Button
//                             variant="contained"
//                             size="small"
//                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleStartInterview(interview.interviewUrl)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Start
//                           </Button>
//                         )}
//                         {interview.status === "scheduled" && interview.address && (
//                           <Button
//                             variant="contained"
//                             color="secondary"
//                             size="small"
//                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenMap(interview.address)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Map
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button
//                             variant="outlined"
//                             size="small"
//                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenDialog(interview)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       {(tabValue === 0 || tabValue === 2) && (
//                         <Tooltip title="Delete Interview">
//                           <IconButton
//                             size="small"
//                             color="error"
//                             onClick={() => handleDeleteInterview(interview.id)}
//                             sx={{
//                               "&:hover": {
//                                 backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
//                               },
//                             }}
//                           >
//                             <DeleteIcon sx={{ fontSize: 18 }} />
//                           </IconButton>
//                         </Tooltip>
//                       )}
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center" }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}
//                     {tabValue === 0
//                       ? "scheduled interviews"
//                       : tabValue === 1
//                       ? "completed interviews"
//                       : "cancelled interviews"}{" "}
//                     found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         color="primary"
//         aria-label="schedule interview"
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
//         }}
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview ? "Reschedule Interview" : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">
//                             {candidate.name}
//                           </Typography>
//                           <Typography variant="caption" color="textSecondary">
//                             {candidate.position}
//                           </Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>

//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained">
//             {selectedInterview ? "Update Interview" : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   )
// }

// export default InterviewScheduler
// import { useState, useEffect, useCallback } from "react"
// import apiClient from "../../context/apiClient"
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions as MuiDialogActions,
//   IconButton,
//   Chip,
//   Tabs,
//   Tab,
//   Paper,
//   Avatar,
//   Badge,
//   Skeleton,
//   Fab,
//   Divider,
//   Tooltip,
//   alpha,
// } from "@mui/material"
// import {
//   Add as AddIcon,
//   VideoCall as VideoCallIcon,
//   CalendarMonth as CalendarIcon,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   Schedule as ScheduleIcon,
//   AccessTime as TimeIcon,
//   ReportProblem as ReportProblemIcon,
//   LocationOn as LocationOnIcon,
// } from "@mui/icons-material"
// import Layout from "../common/Layout"
// import { useNotification } from "../../context/NotificationContext"

// const formatApiData = (apiInterview) => {
//   let scheduledAt = null
//   let rawDate = ""
//   let rawTime = ""
//   let status = (apiInterview.status || "unknown").toLowerCase()
//   let isExpired = false

//   if (apiInterview.interview_date && apiInterview.interview_time) {
//     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
//     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`)
//     rawDate = apiInterview.interview_date
//     rawTime = apiInterview.interview_time.substring(0, 5)
//   } else if (apiInterview.scheduled_at) {
//     scheduledAt = new Date(apiInterview.scheduled_at)
//     rawDate = scheduledAt.toISOString().split("T")[0]
//     rawTime = scheduledAt.toTimeString().substring(0, 5)
//   }

//   const now = new Date()
//   if (scheduledAt && scheduledAt < now && status === "scheduled") {
//     status = "cancelled"
//     isExpired = true
//   }

//   const location = apiInterview.location || ""
//   let address = ""
//   let interviewUrl = ""
//   let formType = "in-person"

//   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
//   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

//   if (isVideoUrl) {
//     formType = "video"
//     interviewUrl = location
//   } else if (isGenericVideoCall) {
//     formType = "video"
//   } else {
//     formType = "in-person"
//     const locationParts = location.split(" | Map: ")
//     address = locationParts[0]
//   }

//   return {
//     id: apiInterview.id,
//     applicationId: apiInterview.application || apiInterview.applicationId || apiInterview.id,
//     candidateName: apiInterview.candidate_name || "N/A",
//     position: apiInterview.job_title || "N/A",
//     experience: apiInterview.experience_level || "N/A",
//     status: status,
//     isExpired: isExpired,
//     date: scheduledAt
//       ? scheduledAt.toLocaleDateString("en-US", {
//           weekday: "short",
//           month: "short",
//           day: "numeric",
//           year: "numeric",
//         })
//       : "N/A",
//     time: scheduledAt
//       ? scheduledAt.toLocaleTimeString("en-US", {
//           hour: "2-digit",
//           minute: "2-digit",
//           hour12: true,
//         })
//       : "N/A",
//     rawDate: rawDate,
//     rawTime: rawTime,
//     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
//     formType: formType,
//     skills: apiInterview.skills || [],
//     notes: apiInterview.notes || "No specific notes provided for this interview.",
//     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
//     address: address,
//     interviewUrl: interviewUrl,
//   }
// }

// const InterviewCardSkeleton = () => (
//   <Grid item xs={12} sm={6} lg={4}>
//     <Card
//       sx={{
//         height: 380,
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
//         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
//           <Box sx={{ flexGrow: 1 }}>
//             <Skeleton variant="text" width="80%" height={24} />
//             <Skeleton variant="text" width="60%" height={20} />
//           </Box>
//           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
//         </Box>
//         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
//         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
//       </CardContent>
//       <CardActions sx={{ p: 2.5, pt: 0 }}>
//         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
//         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
//         <Box sx={{ flexGrow: 1 }} />
//         <Skeleton variant="circular" width={32} height={32} />
//       </CardActions>
//     </Card>
//   </Grid>
// )

// function InterviewScheduler() {
//   const { showNotification } = useNotification()
//   const [interviews, setInterviews] = useState([])
//   const [candidates, setCandidates] = useState([])
//   const [isLoading, setIsLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [openDialog, setOpenDialog] = useState(false)
//   const [tabValue, setTabValue] = useState(0)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [formData, setFormData] = useState({
//     applicationId: "",
//     date: "",
//     time: "",
//     type: "video",
//     notes: "",
//     locationDetail: "",
//   })

//   const fetchInterviews = useCallback(async () => {
//     setIsLoading(true)
//     setError(null)
//     try {
//       const interviewsRes = await apiClient.get("/interviews/")
//       const formattedData = interviewsRes.data.map(formatApiData)
//       setInterviews(formattedData)
//     } catch (err) {
//       setError("Could not load interviews.")
//       showNotification("Failed to fetch interviews.", "error")
//     } finally {
//       setIsLoading(false)
//     }
//   }, [showNotification])

//   const fetchCandidates = useCallback(async () => {
//     try {
//       const response = await apiClient.get("/applications/")
//       const formattedCandidates = response.data.map((app) => ({
//         applicationId: app.id,
//         talentId: app.talent,
//         name:
//           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
//           `Talent #${app.talent}`,
//         position: app.job_posting_details?.title || "Applied Position",
//         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
//       }))
//       setCandidates(formattedCandidates)
//     } catch (err) {
//       showNotification("Could not load candidate list for scheduling.", "warning")
//     }
//   }, [showNotification])

//   useEffect(() => {
//     fetchInterviews()
//     fetchCandidates()
//   }, [fetchInterviews, fetchCandidates])

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleSubmit = async () => {
//     if (!formData.applicationId || !formData.date || !formData.time) {
//       showNotification("Please select a candidate, date, and time.", "error")
//       return
//     }
//     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
//       showNotification("Please provide an address for the in-person interview.", "error")
//       return
//     }

//     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")

//     // Create a combined ISO string for the backend. The error indicates `scheduled_at` is required.
//     const scheduledAtISO = new Date(`${formData.date}T${formData.time}`).toISOString()

//     const payload = {
//       application: formData.applicationId,
//       interview_date: formData.date,
//       interview_time: formData.time,
//       scheduled_at: scheduledAtISO, // The crucial addition to fix the 400 error
//       location: locationValue,
//       notes: formData.notes,
//       status: "scheduled",
//     }

//     try {
//       if (selectedInterview) {
//         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
//         showNotification("Interview updated successfully!", "success")
//       } else {
//         await apiClient.post("/interviews/", payload)
//         showNotification("Interview scheduled successfully!", "success")
//       }
//       fetchInterviews()
//       handleCloseDialog()
//     } catch (err) {
//       const errorData = err.response?.data
//       let message = "An error occurred."
//       if (errorData) {
//         if (errorData.scheduled_at) {
//           message = `Scheduled At: ${errorData.scheduled_at.join(", ")}`
//         } else if (errorData.detail) {
//           message = errorData.detail
//         } else {
//           // Handle other potential validation errors
//           const firstErrorKey = Object.keys(errorData)[0]
//           if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
//             const fieldName = firstErrorKey.charAt(0).toUpperCase() + firstErrorKey.slice(1).replace(/_/g, " ")
//             message = `${fieldName}: ${errorData[firstErrorKey].join(", ")}`
//           }
//         }
//       }
//       showNotification(message, "error")
//     }
//   }

//   const handleDeleteInterview = async (id) => {
//     if (window.confirm("Are you sure you want to permanently delete this interview record?")) {
//       try {
//         await apiClient.delete(`/interviews/${id}/`)
//         setInterviews(interviews.filter((i) => i.id !== id))
//         showNotification("Interview deleted successfully!", "success")
//       } catch (err) {
//         showNotification("Failed to delete interview.", "error")
//       }
//     }
//   }

//   const handleStartInterview = (interviewUrl) => {
//     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
//       window.open(interviewUrl, "_blank")
//     } else {
//       showNotification("No valid interview URL available.", "warning")
//     }
//   }

//   const filteredInterviews = interviews.filter((interview) => {
//     if (tabValue === 0) return interview.status === "scheduled"
//     if (tabValue === 1) return interview.status === "completed"
//     if (tabValue === 2) return interview.status === "cancelled"
//     return true
//   })

//   const handleTabChange = (event, newValue) => setTabValue(newValue)

//   const handleCloseDialog = () => setOpenDialog(false)

//   const handleOpenDialog = (interview = null) => {
//     if (interview) {
//       setSelectedInterview(interview)
//       setFormData({
//         applicationId: interview.applicationId,
//         date: interview.rawDate,
//         time: interview.rawTime,
//         type: interview.formType,
//         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
//         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
//       })
//     } else {
//       setSelectedInterview(null)
//       setFormData({
//         applicationId: "",
//         date: "",
//         time: "",
//         type: "video",
//         notes: "",
//         locationDetail: "",
//       })
//     }
//     setOpenDialog(true)
//   }

//   const getStatusColor = (status) => {
//     switch (status) {
//       case "scheduled":
//         return "primary"
//       case "completed":
//         return "success"
//       case "cancelled":
//         return "error"
//       default:
//         return "default"
//     }
//   }

//   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length
//   const completedInterviews = interviews.filter((i) => i.status === "completed").length
//   const cancelledInterviews = interviews.filter((i) => i.status === "cancelled").length

//   const tabIndicatorColors = {
//     0: "primary.main",
//     1: "success.main",
//     2: "error.main",
//   }

//   const handleOpenMap = (address) => {
//     const encodedAddress = encodeURIComponent(address)
//     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
//     window.open(mapUrl, "_blank")
//   }

//   return (
//     <Layout title="Interview Scheduler">
//       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
//         <Box sx={{ mb: 3 }}>
//           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
//             Interview Management
//           </Typography>
//           <Typography variant="body1" color="text.secondary">
//             Manage and track all your candidate interviews
//           </Typography>
//         </Box>

//         <Paper
//           elevation={0}
//           sx={{
//             mb: 4,
//             borderRadius: 3,
//             background: (theme) => alpha(theme.palette.background.paper, 0.95),
//             backdropFilter: "blur(10px)",
//           }}
//         >
//           <Tabs
//             value={tabValue}
//             onChange={handleTabChange}
//             centered
//             sx={{
//               "& .MuiTabs-indicator": {
//                 backgroundColor: tabIndicatorColors[tabValue],
//               },
//               "& .MuiTab-root": {
//                 fontSize: "0.95rem",
//               },
//             }}
//           >
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={upcomingInterviews} color="primary" />
//                   <span>Scheduled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "primary.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={completedInterviews} color="success" />
//                   <span>Completed</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "success.main",
//                 },
//               }}
//             />
//             <Tab
//               label={
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                   <Badge badgeContent={cancelledInterviews} color="error" />
//                   <span>Cancelled</span>
//                 </Box>
//               }
//               sx={{
//                 color: "text.secondary",
//                 "&.Mui-selected": {
//                   color: "error.main",
//                 },
//               }}
//             />
//           </Tabs>
//         </Paper>

//         {isLoading ? (
//           <Grid container spacing={3}>
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//             <InterviewCardSkeleton />
//           </Grid>
//         ) : error ? (
//           <Paper sx={{ p: 4, textAlign: "center" }}>
//             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
//             <Typography variant="h6" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//             <Button variant="contained" onClick={fetchInterviews}>
//               Try Again
//             </Button>
//           </Paper>
//         ) : (
//           <Grid container spacing={3}>
//             {filteredInterviews.length > 0 ? (
//               filteredInterviews.map((interview) => (
//                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
//                   <Card
//                     sx={{
//                       height: "100%",
//                       display: "flex",
//                       flexDirection: "column",
//                     }}
//                   >
//                     <CardContent
//                       sx={{
//                         flexGrow: 1,
//                         p: 2.5,
//                         pb: 1,
//                         display: "flex",
//                         flexDirection: "column",
//                       }}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
//                         <Avatar
//                           src={interview.avatar}
//                           sx={{
//                             width: 48,
//                             height: 48,
//                             mr: 2,
//                             border: (theme) => `2px solid ${theme.palette.background.default}`,
//                           }}
//                         />
//                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
//                           <Tooltip title={interview.candidateName} placement="top">
//                             <Typography
//                               variant="h6"
//                               fontWeight="600"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "1rem",
//                                 lineHeight: 1.3,
//                               }}
//                             >
//                               {interview.candidateName}
//                             </Typography>
//                           </Tooltip>
//                           <Tooltip title={interview.position} placement="top">
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 overflow: "hidden",
//                                 textOverflow: "ellipsis",
//                                 whiteSpace: "nowrap",
//                                 fontSize: "0.85rem",
//                               }}
//                             >
//                               {interview.position}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                         <Chip
//                           label={interview.isExpired ? "Expired" : interview.status}
//                           size="small"
//                           sx={(theme) => ({
//                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
//                             color: `${getStatusColor(interview.status)}.main`,
//                             fontWeight: 600,
//                             fontSize: "0.75rem",
//                             height: 24,
//                             textTransform: "capitalize",
//                           })}
//                         />
//                       </Box>

//                       <Divider sx={{ mb: 2 }} />

//                       <Box sx={{ mb: 2 }}>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.date}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.time}
//                           </Typography>
//                         </Box>
//                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
//                           {interview.formType === "video" ? (
//                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
//                           ) : (
//                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
//                           )}
//                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
//                             {interview.interviewType}
//                           </Typography>
//                         </Box>
//                       </Box>

//                       {interview.address && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Address:
//                           </Typography>
//                           <Tooltip title={interview.address} placement="top" arrow>
//                             <Typography
//                               variant="body2"
//                               color="text.secondary"
//                               sx={{
//                                 fontSize: "0.8rem",
//                                 lineHeight: 1.3,
//                                 cursor: "pointer",
//                                 "&:hover": {
//                                   color: "primary.main",
//                                 },
//                               }}
//                             >
//                               {interview.address.length > 40
//                                 ? `${interview.address.substring(0, 40)}...`
//                                 : interview.address}
//                             </Typography>
//                           </Tooltip>
//                         </Box>
//                       )}

//                       {interview.skills && interview.skills.length > 0 && (
//                         <Box sx={{ mb: 2 }}>
//                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                             Skills:
//                           </Typography>
//                           <Box
//                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
//                           >
//                             {interview.skills.slice(0, 3).map((skill) => (
//                               <Chip
//                                 key={skill}
//                                 label={skill}
//                                 size="small"
//                                 variant="outlined"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             ))}
//                             {interview.skills.length > 3 && (
//                               <Chip
//                                 label={`+${interview.skills.length - 3}`}
//                                 size="small"
//                                 variant="filled"
//                                 color="primary"
//                                 sx={{
//                                   fontSize: "0.7rem",
//                                   height: 22,
//                                   "& .MuiChip-label": { px: 1 },
//                                 }}
//                               />
//                             )}
//                           </Box>
//                         </Box>
//                       )}

//                       <Box sx={{ flexGrow: 1 }}>
//                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
//                           Notes:
//                         </Typography>
//                         <Typography
//                           variant="body2"
//                           color="text.secondary"
//                           sx={{
//                             fontSize: "0.8rem",
//                             fontStyle: "italic",
//                             lineHeight: 1.3,
//                             overflow: "hidden",
//                             display: "-webkit-box",
//                             WebkitLineClamp: 2,
//                             WebkitBoxOrient: "vertical",
//                             height: "2.6em",
//                           }}
//                         >
//                           {interview.notes}
//                         </Typography>
//                       </Box>
//                     </CardContent>

//                     <Divider />

//                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
//                       <Box sx={{ display: "flex", gap: 1 }}>
//                         {interview.status === "scheduled" && interview.interviewUrl && (
//                           <Button
//                             variant="contained"
//                             size="small"
//                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleStartInterview(interview.interviewUrl)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Start
//                           </Button>
//                         )}
//                         {interview.status === "scheduled" && interview.address && (
//                           <Button
//                             variant="contained"
//                             color="secondary"
//                             size="small"
//                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenMap(interview.address)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Map
//                           </Button>
//                         )}
//                         {tabValue === 2 && (
//                           <Button
//                             variant="outlined"
//                             size="small"
//                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
//                             onClick={() => handleOpenDialog(interview)}
//                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
//                           >
//                             Reschedule
//                           </Button>
//                         )}
//                       </Box>
//                       {(tabValue === 0 || tabValue === 2) && (
//                         <Tooltip title="Delete Interview">
//                           <IconButton
//                             size="small"
//                             color="error"
//                             onClick={() => handleDeleteInterview(interview.id)}
//                             sx={{
//                               "&:hover": {
//                                 backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
//                               },
//                             }}
//                           >
//                             <DeleteIcon sx={{ fontSize: 18 }} />
//                           </IconButton>
//                         </Tooltip>
//                       )}
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               ))
//             ) : (
//               <Grid item xs={12}>
//                 <Paper sx={{ p: 4, textAlign: "center" }}>
//                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
//                   <Typography variant="h6" color="textSecondary">
//                     No{" "}
//                     {tabValue === 0
//                       ? "scheduled interviews"
//                       : tabValue === 1
//                       ? "completed interviews"
//                       : "cancelled interviews"}{" "}
//                     found
//                   </Typography>
//                 </Paper>
//               </Grid>
//             )}
//           </Grid>
//         )}
//       </Box>

//       <Fab
//         color="primary"
//         aria-label="schedule interview"
//         onClick={() => handleOpenDialog()}
//         sx={{
//           position: "fixed",
//           bottom: 32,
//           right: 32,
//           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
//         }}
//       >
//         <AddIcon />
//       </Fab>

//       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
//         <DialogTitle sx={{ pb: 1 }}>
//           {selectedInterview ? "Reschedule Interview" : "Schedule New Interview"}
//         </DialogTitle>
//         <DialogContent>
//           <Grid container spacing={3} sx={{ pt: 1 }}>
//             <Grid item xs={12}>
//               <FormControl fullWidth disabled={!!selectedInterview}>
//                 <InputLabel>Candidate Application</InputLabel>
//                 <Select
//                   name="applicationId"
//                   value={formData.applicationId}
//                   onChange={handleInputChange}
//                   label="Candidate Application"
//                 >
//                   {candidates.map((candidate) => (
//                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
//                         <Box>
//                           <Typography variant="body2" fontWeight="bold">
//                             {candidate.name}
//                           </Typography>
//                           <Typography variant="caption" color="textSecondary">
//                             {candidate.position}
//                           </Typography>
//                         </Box>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Date"
//                 name="date"
//                 type="date"
//                 value={formData.date}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <TextField
//                 fullWidth
//                 label="Time"
//                 name="time"
//                 type="time"
//                 value={formData.time}
//                 onChange={handleInputChange}
//                 InputLabelProps={{ shrink: true }}
//                 required
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Type</InputLabel>
//                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
//                   <MenuItem value="video">Video</MenuItem>
//                   <MenuItem value="in-person">In-Person</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>

//             {formData.type === "video" ? (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Interview URL"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="https://meet.google.com/abc-defg-hij"
//                   helperText="Enter the meeting URL for the video interview"
//                 />
//               </Grid>
//             ) : (
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="Location / Address"
//                   name="locationDetail"
//                   value={formData.locationDetail}
//                   onChange={handleInputChange}
//                   placeholder="e.g., 123 Main St, Anytown, USA"
//                   helperText="Enter the physical address for the in-person interview"
//                   required
//                 />
//               </Grid>
//             )}
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Notes / Feedback"
//                 name="notes"
//                 value={formData.notes}
//                 onChange={handleInputChange}
//                 multiline
//                 rows={3}
//               />
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
//           <Button onClick={handleCloseDialog}>Cancel</Button>
//           <Button onClick={handleSubmit} variant="contained">
//             {selectedInterview ? "Update Interview" : "Schedule Interview"}
//           </Button>
//         </MuiDialogActions>
//       </Dialog>
//     </Layout>
//   )
// }

// export default InterviewScheduler

// // import { useState, useEffect, useCallback } from "react"
// // import apiClient from "../../context/apiClient"
// // import {
// //   Box,
// //   Typography,
// //   Grid,
// //   Card,
// //   CardContent,
// //   CardActions,
// //   Button,
// //   TextField,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions as MuiDialogActions,
// //   IconButton,
// //   Chip,
// //   Tabs,
// //   Tab,
// //   Paper,
// //   Avatar,
// //   Badge,
// //   Skeleton,
// //   Fab,
// //   Divider,
// //   Tooltip,
// //   alpha,
// // } from "@mui/material"
// // import {
// //   Add as AddIcon,
// //   VideoCall as VideoCallIcon,
// //   CalendarMonth as CalendarIcon,
// //   Edit as EditIcon,
// //   Schedule as ScheduleIcon,
// //   AccessTime as TimeIcon,
// //   ReportProblem as ReportProblemIcon,
// //   LocationOn as LocationOnIcon,
// // } from "@mui/icons-material"
// // import Layout from "../common/Layout"
// // import { useNotification } from "../../context/NotificationContext"

// // const formatApiData = (apiInterview) => {
// //   let scheduledAt = null
// //   let rawDate = ""
// //   let rawTime = ""
// //   let status = (apiInterview.status || "unknown").toLowerCase()
// //   let isExpired = false

// //   if (apiInterview.interview_date && apiInterview.interview_time) {
// //     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
// //     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`)
// //     rawDate = apiInterview.interview_date
// //     rawTime = apiInterview.interview_time.substring(0, 5)
// //   } else if (apiInterview.scheduled_at) {
// //     scheduledAt = new Date(apiInterview.scheduled_at)
// //     rawDate = scheduledAt.toISOString().split("T")[0]
// //     rawTime = scheduledAt.toTimeString().substring(0, 5)
// //   }

// //   const now = new Date()
// //   if (scheduledAt && scheduledAt < now && status === "scheduled") {
// //     status = "cancelled"
// //     isExpired = true
// //   }

// //   const location = apiInterview.location || ""
// //   let address = ""
// //   let interviewUrl = ""
// //   let formType = "in-person"

// //   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
// //   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

// //   if (isVideoUrl) {
// //     formType = "video"
// //     interviewUrl = location
// //   } else if (isGenericVideoCall) {
// //     formType = "video"
// //   } else {
// //     formType = "in-person"
// //     const locationParts = location.split(" | Map: ")
// //     address = locationParts[0]
// //   }

// //   return {
// //     id: apiInterview.id,
// //     applicationId: apiInterview.application || apiInterview.applicationId || apiInterview.id,
// //     candidateName: apiInterview.candidate_name || "N/A",
// //     position: apiInterview.job_title || "N/A",
// //     experience: apiInterview.experience_level || "N/A",
// //     status: status,
// //     isExpired: isExpired,
// //     date: scheduledAt
// //       ? scheduledAt.toLocaleDateString("en-US", {
// //           weekday: "short",
// //           month: "short",
// //           day: "numeric",
// //           year: "numeric",
// //         })
// //       : "N/A",
// //     time: scheduledAt
// //       ? scheduledAt.toLocaleTimeString("en-US", {
// //           hour: "2-digit",
// //           minute: "2-digit",
// //           hour12: true,
// //         })
// //       : "N/A",
// //     rawDate: rawDate,
// //     rawTime: rawTime,
// //     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
// //     formType: formType,
// //     skills: apiInterview.skills || [],
// //     notes: apiInterview.notes || "No specific notes provided for this interview.",
// //     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
// //     address: address,
// //     interviewUrl: interviewUrl,
// //   }
// // }

// // const InterviewCardSkeleton = () => (
// //   <Grid item xs={12} sm={6} lg={4}>
// //     <Card
// //       sx={{
// //         height: 380,
// //         display: "flex",
// //         flexDirection: "column",
// //       }}
// //     >
// //       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
// //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// //           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
// //           <Box sx={{ flexGrow: 1 }}>
// //             <Skeleton variant="text" width="80%" height={24} />
// //             <Skeleton variant="text" width="60%" height={20} />
// //           </Box>
// //           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
// //         </Box>
// //         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
// //         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
// //       </CardContent>
// //       <CardActions sx={{ p: 2.5, pt: 0 }}>
// //         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
// //         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
// //         <Box sx={{ flexGrow: 1 }} />
// //         <Skeleton variant="circular" width={32} height={32} />
// //       </CardActions>
// //     </Card>
// //   </Grid>
// // )

// // function InterviewScheduler() {
// //   const { showNotification } = useNotification()
// //   const [interviews, setInterviews] = useState([])
// //   const [candidates, setCandidates] = useState([])
// //   const [isLoading, setIsLoading] = useState(true)
// //   const [error, setError] = useState(null)
// //   const [openDialog, setOpenDialog] = useState(false)
// //   const [tabValue, setTabValue] = useState(0)
// //   const [selectedInterview, setSelectedInterview] = useState(null)
// //   const [formData, setFormData] = useState({
// //     applicationId: "",
// //     date: "",
// //     time: "",
// //     type: "video",
// //     notes: "",
// //     locationDetail: "",
// //   })

// //   const fetchInterviews = useCallback(async () => {
// //     setIsLoading(true)
// //     setError(null)
// //     try {
// //       const interviewsRes = await apiClient.get("/interviews/")
// //       const formattedData = interviewsRes.data.map(formatApiData)
// //       setInterviews(formattedData)
// //     } catch (err) {
// //       setError("Could not load interviews.")
// //       showNotification("Failed to fetch interviews.", "error")
// //     } finally {
// //       setIsLoading(false)
// //     }
// //   }, [showNotification])

// //   const fetchCandidates = useCallback(async () => {
// //     try {
// //       const response = await apiClient.get("/applications/")
// //       const formattedCandidates = response.data.map((app) => ({
// //         applicationId: app.id,
// //         talentId: app.talent,
// //         name:
// //           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
// //           `Talent #${app.talent}`,
// //         position: app.job_posting_details?.title || "Applied Position",
// //         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
// //       }))
// //       setCandidates(formattedCandidates)
// //     } catch (err) {
// //       showNotification("Could not load candidate list for scheduling.", "warning")
// //     }
// //   }, [showNotification])

// //   useEffect(() => {
// //     fetchInterviews()
// //     fetchCandidates()
// //   }, [fetchInterviews, fetchCandidates])

// //   const handleInputChange = (e) => {
// //     const { name, value } = e.target
// //     setFormData((prev) => ({ ...prev, [name]: value }))
// //   }

// //   const handleSubmit = async () => {
// //     if (!formData.applicationId || !formData.date || !formData.time) {
// //       showNotification("Please select a candidate, date, and time.", "error")
// //       return
// //     }
// //     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
// //       showNotification("Please provide an address for the in-person interview.", "error")
// //       return
// //     }

// //     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")

// //     const payload = {
// //       application: formData.applicationId,
// //       interview_date: formData.date,
// //       interview_time: formData.time,
// //       location: locationValue,
// //       notes: formData.notes,
// //       status: "scheduled",
// //     }

// //     try {
// //       if (selectedInterview) {
// //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
// //         showNotification("Interview updated successfully!", "success")
// //       } else {
// //         await apiClient.post("/interviews/", payload)
// //         showNotification("Interview scheduled successfully!", "success")
// //       }
// //       fetchInterviews()
// //       handleCloseDialog()
// //     } catch (err) {
// //       showNotification(err.response?.data?.detail || "An error occurred.", "error")
// //     }
// //   }

// //   const handleStartInterview = (interviewUrl) => {
// //     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
// //       window.open(interviewUrl, "_blank")
// //     } else {
// //       showNotification("No valid interview URL available.", "warning")
// //     }
// //   }

// //   const filteredInterviews = interviews.filter((interview) => {
// //     if (tabValue === 0) return interview.status === "scheduled"
// //     if (tabValue === 1) return interview.status === "completed"
// //     if (tabValue === 2) return interview.status === "cancelled"
// //     return true
// //   })

// //   const handleTabChange = (event, newValue) => setTabValue(newValue)

// //   const handleCloseDialog = () => setOpenDialog(false)

// //   const handleOpenDialog = (interview = null) => {
// //     if (interview) {
// //       setSelectedInterview(interview)
// //       setFormData({
// //         applicationId: interview.applicationId,
// //         date: interview.rawDate,
// //         time: interview.rawTime,
// //         type: interview.formType,
// //         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
// //         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
// //       })
// //     } else {
// //       setSelectedInterview(null)
// //       setFormData({
// //         applicationId: "",
// //         date: "",
// //         time: "",
// //         type: "video",
// //         notes: "",
// //         locationDetail: "",
// //       })
// //     }
// //     setOpenDialog(true)
// //   }

// //   const getStatusColor = (status) => {
// //     switch (status) {
// //       case "scheduled":
// //         return "primary"
// //       case "completed":
// //         return "success"
// //       case "cancelled":
// //         return "error"
// //       default:
// //         return "default"
// //     }
// //   }

// //   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length
// //   const completedInterviews = interviews.filter((i) => i.status === "completed").length
// //   const cancelledInterviews = interviews.filter((i) => i.status === "cancelled").length

// //   const tabIndicatorColors = {
// //     0: "primary.main",
// //     1: "success.main",
// //     2: "error.main",
// //   }

// //   const handleOpenMap = (address) => {
// //     const encodedAddress = encodeURIComponent(address)
// //     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
// //     window.open(mapUrl, "_blank")
// //   }

// //   return (
// //     <Layout title="Interview Scheduler">
// //       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
// //         <Box sx={{ mb: 3 }}>
// //           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
// //             Interview Management
// //           </Typography>
// //           <Typography variant="body1" color="text.secondary">
// //             Manage and track all your candidate interviews
// //           </Typography>
// //         </Box>

// //         <Paper
// //           elevation={0}
// //           sx={{
// //             mb: 4,
// //             borderRadius: 3,
// //             background: (theme) => alpha(theme.palette.background.paper, 0.95),
// //             backdropFilter: "blur(10px)",
// //           }}
// //         >
// //           <Tabs
// //             value={tabValue}
// //             onChange={handleTabChange}
// //             centered
// //             sx={{
// //               "& .MuiTabs-indicator": {
// //                 backgroundColor: tabIndicatorColors[tabValue],
// //               },
// //               "& .MuiTab-root": {
// //                 fontSize: "0.95rem",
// //               },
// //             }}
// //           >
// //             <Tab
// //               label={
// //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// //                   <Badge badgeContent={upcomingInterviews} color="primary" />
// //                   <span>Scheduled</span>
// //                 </Box>
// //               }
// //               sx={{
// //                 color: "text.secondary",
// //                 "&.Mui-selected": {
// //                   color: "primary.main",
// //                 },
// //               }}
// //             />
// //             <Tab
// //               label={
// //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// //                   <Badge badgeContent={completedInterviews} color="success" />
// //                   <span>Completed</span>
// //                 </Box>
// //               }
// //               sx={{
// //                 color: "text.secondary",
// //                 "&.Mui-selected": {
// //                   color: "success.main",
// //                 },
// //               }}
// //             />
// //             <Tab
// //               label={
// //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// //                   <Badge badgeContent={cancelledInterviews} color="error" />
// //                   <span>Cancelled</span>
// //                 </Box>
// //               }
// //               sx={{
// //                 color: "text.secondary",
// //                 "&.Mui-selected": {
// //                   color: "error.main",
// //                 },
// //               }}
// //             />
// //           </Tabs>
// //         </Paper>

// //         {isLoading ? (
// //           <Grid container spacing={3}>
// //             <InterviewCardSkeleton />
// //             <InterviewCardSkeleton />
// //             <InterviewCardSkeleton />
// //           </Grid>
// //         ) : error ? (
// //           <Paper sx={{ p: 4, textAlign: "center" }}>
// //             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
// //             <Typography variant="h6" sx={{ mb: 2 }}>
// //               {error}
// //             </Typography>
// //             <Button variant="contained" onClick={fetchInterviews}>
// //               Try Again
// //             </Button>
// //           </Paper>
// //         ) : (
// //           <Grid container spacing={3}>
// //             {filteredInterviews.length > 0 ? (
// //               filteredInterviews.map((interview) => (
// //                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
// //                   <Card
// //                     sx={{
// //                       height: 400,
// //                       display: "flex",
// //                       flexDirection: "column",
// //                     }}
// //                   >
// //                     <CardContent
// //                       sx={{
// //                         flexGrow: 1,
// //                         p: 2.5,
// //                         pb: 1,
// //                         display: "flex",
// //                         flexDirection: "column",
// //                       }}
// //                     >
// //                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// //                         <Avatar
// //                           src={interview.avatar}
// //                           sx={{
// //                             width: 48,
// //                             height: 48,
// //                             mr: 2,
// //                             border: (theme) => `2px solid ${theme.palette.background.default}`,
// //                           }}
// //                         />
// //                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
// //                           <Tooltip title={interview.candidateName} placement="top">
// //                             <Typography
// //                               variant="h6"
// //                               fontWeight="600"
// //                               sx={{
// //                                 overflow: "hidden",
// //                                 textOverflow: "ellipsis",
// //                                 whiteSpace: "nowrap",
// //                                 fontSize: "1rem",
// //                                 lineHeight: 1.3,
// //                               }}
// //                             >
// //                               {interview.candidateName}
// //                             </Typography>
// //                           </Tooltip>
// //                           <Tooltip title={interview.position} placement="top">
// //                             <Typography
// //                               variant="body2"
// //                               color="text.secondary"
// //                               sx={{
// //                                 overflow: "hidden",
// //                                 textOverflow: "ellipsis",
// //                                 whiteSpace: "nowrap",
// //                                 fontSize: "0.85rem",
// //                               }}
// //                             >
// //                               {interview.position}
// //                             </Typography>
// //                           </Tooltip>
// //                         </Box>
// //                         <Chip
// //                           label={interview.isExpired ? "Expired" : interview.status}
// //                           size="small"
// //                           sx={(theme) => ({
// //                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
// //                             color: `${getStatusColor(interview.status)}.main`,
// //                             fontWeight: 600,
// //                             fontSize: "0.75rem",
// //                             height: 24,
// //                             textTransform: "capitalize",
// //                           })}
// //                         />
// //                       </Box>

// //                       <Divider sx={{ mb: 2 }} />

// //                       <Box sx={{ mb: 2 }}>
// //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// //                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// //                             {interview.date}
// //                           </Typography>
// //                         </Box>
// //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// //                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// //                             {interview.time}
// //                           </Typography>
// //                         </Box>
// //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// //                           {interview.formType === "video" ? (
// //                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
// //                           ) : (
// //                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
// //                           )}
// //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// //                             {interview.interviewType}
// //                           </Typography>
// //                         </Box>
// //                       </Box>

// //                       {interview.address && (
// //                         <Box sx={{ mb: 2 }}>
// //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// //                             Address:
// //                           </Typography>
// //                           <Tooltip title={interview.address} placement="top" arrow>
// //                             <Typography
// //                               variant="body2"
// //                               color="text.secondary"
// //                               sx={{
// //                                 fontSize: "0.8rem",
// //                                 lineHeight: 1.3,
// //                                 cursor: "pointer",
// //                                 "&:hover": {
// //                                   color: "primary.main",
// //                                 },
// //                               }}
// //                             >
// //                               {interview.address.length > 40
// //                                 ? `${interview.address.substring(0, 40)}...`
// //                                 : interview.address}
// //                             </Typography>
// //                           </Tooltip>
// //                         </Box>
// //                       )}

// //                       {interview.skills && interview.skills.length > 0 && (
// //                         <Box sx={{ mb: 2 }}>
// //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// //                             Skills:
// //                           </Typography>
// //                           <Box
// //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
// //                           >
// //                             {interview.skills.slice(0, 3).map((skill) => (
// //                               <Chip
// //                                 key={skill}
// //                                 label={skill}
// //                                 size="small"
// //                                 variant="outlined"
// //                                 sx={{
// //                                   fontSize: "0.7rem",
// //                                   height: 22,
// //                                   "& .MuiChip-label": { px: 1 },
// //                                 }}
// //                               />
// //                             ))}
// //                             {interview.skills.length > 3 && (
// //                               <Chip
// //                                 label={`+${interview.skills.length - 3}`}
// //                                 size="small"
// //                                 variant="filled"
// //                                 color="primary"
// //                                 sx={{
// //                                   fontSize: "0.7rem",
// //                                   height: 22,
// //                                   "& .MuiChip-label": { px: 1 },
// //                                 }}
// //                               />
// //                             )}
// //                           </Box>
// //                         </Box>
// //                       )}

// //                       <Box sx={{ flexGrow: 1 }}>
// //                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// //                           Notes:
// //                         </Typography>
// //                         <Typography
// //                           variant="body2"
// //                           color="text.secondary"
// //                           sx={{
// //                             fontSize: "0.8rem",
// //                             fontStyle: "italic",
// //                             lineHeight: 1.3,
// //                             overflow: "hidden",
// //                             display: "-webkit-box",
// //                             WebkitLineClamp: 2,
// //                             WebkitBoxOrient: "vertical",
// //                             height: "2.6em",
// //                           }}
// //                         >
// //                           {interview.notes}
// //                         </Typography>
// //                       </Box>
// //                     </CardContent>

// //                     <Divider />

// //                     <CardActions sx={{ p: 2 }}>
// //                       <Box sx={{ display: "flex", gap: 1 }}>
// //                         {interview.status === "scheduled" && interview.interviewUrl && (
// //                           <Button
// //                             variant="contained"
// //                             size="small"
// //                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
// //                             onClick={() => handleStartInterview(interview.interviewUrl)}
// //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// //                           >
// //                             Start
// //                           </Button>
// //                         )}
// //                         {interview.status === "scheduled" && interview.address && (
// //                           <Button
// //                             variant="contained"
// //                             color="secondary"
// //                             size="small"
// //                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
// //                             onClick={() => handleOpenMap(interview.address)}
// //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// //                           >
// //                             Map
// //                           </Button>
// //                         )}
// //                         {interview.status === "scheduled" && (
// //                           <Button
// //                             variant="outlined"
// //                             size="small"
// //                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
// //                             onClick={() => handleOpenDialog(interview)}
// //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// //                           >
// //                             Edit
// //                           </Button>
// //                         )}
// //                         {interview.status === "cancelled" && (
// //                           <Button
// //                             variant="contained"
// //                             size="small"
// //                             startIcon={<ScheduleIcon sx={{ fontSize: 16 }} />}
// //                             onClick={() => handleOpenDialog(interview)}
// //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// //                           >
// //                             Reschedule
// //                           </Button>
// //                         )}
// //                       </Box>
// //                     </CardActions>
// //                   </Card>
// //                 </Grid>
// //               ))
// //             ) : (
// //               <Grid item xs={12}>
// //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// //                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
// //                   <Typography variant="h6" color="textSecondary">
// //                     No{" "}
// //                     {tabValue === 0
// //                       ? "scheduled interviews"
// //                       : tabValue === 1
// //                       ? "completed interviews"
// //                       : "cancelled interviews"}{" "}
// //                     found
// //                   </Typography>
// //                 </Paper>
// //               </Grid>
// //             )}
// //           </Grid>
// //         )}
// //       </Box>

// //       <Fab
// //         color="primary"
// //         aria-label="schedule interview"
// //         onClick={() => handleOpenDialog()}
// //         sx={{
// //           position: "fixed",
// //           bottom: 32,
// //           right: 32,
// //           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
// //         }}
// //       >
// //         <AddIcon />
// //       </Fab>

// //       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
// //         <DialogTitle sx={{ pb: 1 }}>{selectedInterview ? "Edit Interview" : "Schedule New Interview"}</DialogTitle>
// //         <DialogContent>
// //           <Grid container spacing={3} sx={{ pt: 1 }}>
// //             <Grid item xs={12}>
// //               <FormControl fullWidth disabled={!!selectedInterview}>
// //                 <InputLabel>Candidate Application</InputLabel>
// //                 <Select
// //                   name="applicationId"
// //                   value={formData.applicationId}
// //                   onChange={handleInputChange}
// //                   label="Candidate Application"
// //                 >
// //                   {candidates.map((candidate) => (
// //                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
// //                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// //                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
// //                         <Box>
// //                           <Typography variant="body2" fontWeight="bold">
// //                             {candidate.name}
// //                           </Typography>
// //                           <Typography variant="caption" color="textSecondary">
// //                             {candidate.position}
// //                           </Typography>
// //                         </Box>
// //                       </Box>
// //                     </MenuItem>
// //                   ))}
// //                 </Select>
// //               </FormControl>
// //             </Grid>
// //             <Grid item xs={12} sm={6}>
// //               <TextField
// //                 fullWidth
// //                 label="Date"
// //                 name="date"
// //                 type="date"
// //                 value={formData.date}
// //                 onChange={handleInputChange}
// //                 InputLabelProps={{ shrink: true }}
// //                 required
// //               />
// //             </Grid>
// //             <Grid item xs={12} sm={6}>
// //               <TextField
// //                 fullWidth
// //                 label="Time"
// //                 name="time"
// //                 type="time"
// //                 value={formData.time}
// //                 onChange={handleInputChange}
// //                 InputLabelProps={{ shrink: true }}
// //                 required
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <FormControl fullWidth>
// //                 <InputLabel>Type</InputLabel>
// //                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
// //                   <MenuItem value="video">Video</MenuItem>
// //                   <MenuItem value="in-person">In-Person</MenuItem>
// //                 </Select>
// //               </FormControl>
// //             </Grid>

// //             {formData.type === "video" ? (
// //               <Grid item xs={12}>
// //                 <TextField
// //                   fullWidth
// //                   label="Interview URL"
// //                   name="locationDetail"
// //                   value={formData.locationDetail}
// //                   onChange={handleInputChange}
// //                   placeholder="https://meet.google.com/abc-defg-hij"
// //                   helperText="Enter the meeting URL for the video interview"
// //                 />
// //               </Grid>
// //             ) : (
// //               <Grid item xs={12}>
// //                 <TextField
// //                   fullWidth
// //                   label="Location / Address"
// //                   name="locationDetail"
// //                   value={formData.locationDetail}
// //                   onChange={handleInputChange}
// //                   placeholder="e.g., 123 Main St, Anytown, USA"
// //                   helperText="Enter the physical address for the in-person interview"
// //                   required
// //                 />
// //               </Grid>
// //             )}
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Notes / Feedback"
// //                 name="notes"
// //                 value={formData.notes}
// //                 onChange={handleInputChange}
// //                 multiline
// //                 rows={3}
// //               />
// //             </Grid>
// //           </Grid>
// //         </DialogContent>
// //         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
// //           <Button onClick={handleCloseDialog}>Cancel</Button>
// //           <Button onClick={handleSubmit} variant="contained">
// //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// //           </Button>
// //         </MuiDialogActions>
// //       </Dialog>
// //     </Layout>
// //   )
// // }

// // export default InterviewScheduler

// // // import { useState, useEffect, useCallback } from "react"
// // // import apiClient from "../../context/apiClient"
// // // import {
// // //   Box,
// // //   Typography,
// // //   Grid,
// // //   Card,
// // //   CardContent,
// // //   CardActions,
// // //   Button,
// // //   TextField,
// // //   FormControl,
// // //   InputLabel,
// // //   Select,
// // //   MenuItem,
// // //   Dialog,
// // //   DialogTitle,
// // //   DialogContent,
// // //   DialogActions as MuiDialogActions,
// // //   IconButton,
// // //   Chip,
// // //   Tabs,
// // //   Tab,
// // //   Paper,
// // //   Avatar,
// // //   Badge,
// // //   Skeleton,
// // //   Fab,
// // //   Divider,
// // //   Tooltip,
// // //   alpha,
// // // } from "@mui/material"
// // // import {
// // //   Add as AddIcon,
// // //   VideoCall as VideoCallIcon,
// // //   CalendarMonth as CalendarIcon,
// // //   Delete as DeleteIcon,
// // //   Edit as EditIcon,
// // //   Schedule as ScheduleIcon,
// // //   AccessTime as TimeIcon,
// // //   ReportProblem as ReportProblemIcon,
// // //   LocationOn as LocationOnIcon,
// // // } from "@mui/icons-material"
// // // import Layout from "../common/Layout"
// // // import { useNotification } from "../../context/NotificationContext"

// // // const formatApiData = (apiInterview) => {
// // //   let scheduledAt = null
// // //   let rawDate = ""
// // //   let rawTime = ""

// // //   if (apiInterview.interview_date && apiInterview.interview_time) {
// // //     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
// // //     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`)
// // //     rawDate = apiInterview.interview_date
// // //     rawTime = apiInterview.interview_time.substring(0, 5)
// // //   } else if (apiInterview.scheduled_at) {
// // //     scheduledAt = new Date(apiInterview.scheduled_at)
// // //     rawDate = scheduledAt.toISOString().split("T")[0]
// // //     rawTime = scheduledAt.toTimeString().substring(0, 5)
// // //   }

// // //   const location = apiInterview.location || ""
// // //   let address = ""
// // //   let interviewUrl = ""
// // //   let formType = "in-person"

// // //   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
// // //   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

// // //   if (isVideoUrl) {
// // //     formType = "video"
// // //     interviewUrl = location
// // //   } else if (isGenericVideoCall) {
// // //     formType = "video"
// // //   } else {
// // //     formType = "in-person"
// // //     const locationParts = location.split(" | Map: ")
// // //     address = locationParts[0]
// // //   }

// // //   return {
// // //     id: apiInterview.id,
// // //     applicationId: apiInterview.application || apiInterview.applicationId || apiInterview.id,
// // //     candidateName: apiInterview.candidate_name || "N/A",
// // //     position: apiInterview.job_title || "N/A",
// // //     experience: apiInterview.experience_level || "N/A",
// // //     status: (apiInterview.status || "unknown").toLowerCase(),
// // //     date: scheduledAt
// // //       ? scheduledAt.toLocaleDateString("en-US", {
// // //           weekday: "short",
// // //           month: "short",
// // //           day: "numeric",
// // //           year: "numeric",
// // //         })
// // //       : "N/A",
// // //     time: scheduledAt
// // //       ? scheduledAt.toLocaleTimeString("en-US", {
// // //           hour: "2-digit",
// // //           minute: "2-digit",
// // //           hour12: true,
// // //         })
// // //       : "N/A",
// // //     rawDate: rawDate,
// // //     rawTime: rawTime,
// // //     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
// // //     formType: formType,
// // //     skills: apiInterview.skills || [],
// // //     notes: apiInterview.notes || "No specific notes provided for this interview.",
// // //     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
// // //     address: address,
// // //     interviewUrl: interviewUrl,
// // //   }
// // // }

// // // const InterviewCardSkeleton = () => (
// // //   <Grid item xs={12} sm={6} lg={4}>
// // //     <Card
// // //       sx={{
// // //         height: 380,
// // //         display: "flex",
// // //         flexDirection: "column",
// // //       }}
// // //     >
// // //       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
// // //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // //           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
// // //           <Box sx={{ flexGrow: 1 }}>
// // //             <Skeleton variant="text" width="80%" height={24} />
// // //             <Skeleton variant="text" width="60%" height={20} />
// // //           </Box>
// // //           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
// // //         </Box>
// // //         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
// // //         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
// // //       </CardContent>
// // //       <CardActions sx={{ p: 2.5, pt: 0 }}>
// // //         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
// // //         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
// // //         <Box sx={{ flexGrow: 1 }} />
// // //         <Skeleton variant="circular" width={32} height={32} />
// // //       </CardActions>
// // //     </Card>
// // //   </Grid>
// // // )

// // // function InterviewScheduler() {
// // //   const { showNotification } = useNotification()
// // //   const [interviews, setInterviews] = useState([])
// // //   const [candidates, setCandidates] = useState([])
// // //   const [isLoading, setIsLoading] = useState(true)
// // //   const [error, setError] = useState(null)
// // //   const [openDialog, setOpenDialog] = useState(false)
// // //   const [tabValue, setTabValue] = useState(0)
// // //   const [selectedInterview, setSelectedInterview] = useState(null)
// // //   const [formData, setFormData] = useState({
// // //     applicationId: "",
// // //     date: "",
// // //     time: "",
// // //     type: "video",
// // //     notes: "",
// // //     locationDetail: "",
// // //   })

// // //   const fetchInterviews = useCallback(async () => {
// // //     setIsLoading(true)
// // //     setError(null)
// // //     try {
// // //       const interviewsRes = await apiClient.get("/interviews/")
// // //       const formattedData = interviewsRes.data.map(formatApiData)
// // //       setInterviews(formattedData)
// // //     } catch (err) {
// // //       setError("Could not load interviews.")
// // //       showNotification("Failed to fetch interviews.", "error")
// // //     } finally {
// // //       setIsLoading(false)
// // //     }
// // //   }, [showNotification])

// // //   const fetchCandidates = useCallback(async () => {
// // //     try {
// // //       const response = await apiClient.get("/applications/")
// // //       const formattedCandidates = response.data.map((app) => ({
// // //         applicationId: app.id,
// // //         talentId: app.talent,
// // //         name:
// // //           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
// // //           `Talent #${app.talent}`,
// // //         position: app.job_posting_details?.title || "Applied Position",
// // //         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
// // //       }))
// // //       setCandidates(formattedCandidates)
// // //     } catch (err) {
// // //       showNotification("Could not load candidate list for scheduling.", "warning")
// // //     }
// // //   }, [showNotification])

// // //   useEffect(() => {
// // //     fetchInterviews()
// // //     fetchCandidates()
// // //   }, [fetchInterviews, fetchCandidates])

// // //   const handleInputChange = (e) => {
// // //     const { name, value } = e.target
// // //     setFormData((prev) => ({ ...prev, [name]: value }))
// // //   }

// // //   const handleSubmit = async () => {
// // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // //       showNotification("Please select a candidate, date, and time.", "error")
// // //       return
// // //     }
// // //     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
// // //       showNotification("Please provide an address for the in-person interview.", "error")
// // //       return
// // //     }

// // //     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")

// // //     const payload = {
// // //       application: formData.applicationId,
// // //       interview_date: formData.date,
// // //       interview_time: formData.time,
// // //       location: locationValue,
// // //       notes: formData.notes,
// // //       status: "scheduled",
// // //     }

// // //     try {
// // //       if (selectedInterview) {
// // //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
// // //         showNotification("Interview updated successfully!", "success")
// // //       } else {
// // //         await apiClient.post("/interviews/", payload)
// // //         showNotification("Interview scheduled successfully!", "success")
// // //       }
// // //       fetchInterviews()
// // //       handleCloseDialog()
// // //     } catch (err) {
// // //       showNotification(err.response?.data?.detail || "An error occurred.", "error")
// // //     }
// // //   }

// // //   const handleDeleteInterview = async (id) => {
// // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // //       try {
// // //         await apiClient.delete(`/interviews/${id}/`)
// // //         setInterviews(interviews.filter((i) => i.id !== id))
// // //         showNotification("Interview deleted successfully!", "success")
// // //       } catch (err) {
// // //         showNotification("Failed to delete interview.", "error")
// // //       }
// // //     }
// // //   }

// // //   const handleStartInterview = (interviewUrl) => {
// // //     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
// // //       window.open(interviewUrl, "_blank")
// // //     } else {
// // //       showNotification("No valid interview URL available.", "warning")
// // //     }
// // //   }

// // //   const filteredInterviews = interviews.filter((interview) => {
// // //     if (tabValue === 0) return interview.status === "scheduled"
// // //     if (tabValue === 1) return interview.status === "completed"
// // //     if (tabValue === 2) return interview.status === "cancelled"
// // //     return true
// // //   })

// // //   const handleTabChange = (event, newValue) => setTabValue(newValue)

// // //   const handleCloseDialog = () => setOpenDialog(false)

// // //   const handleOpenDialog = (interview = null) => {
// // //     if (interview) {
// // //       setSelectedInterview(interview)
// // //       setFormData({
// // //         applicationId: interview.applicationId,
// // //         date: interview.rawDate,
// // //         time: interview.rawTime,
// // //         type: interview.formType,
// // //         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
// // //         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
// // //       })
// // //     } else {
// // //       setSelectedInterview(null)
// // //       setFormData({
// // //         applicationId: "",
// // //         date: "",
// // //         time: "",
// // //         type: "video",
// // //         notes: "",
// // //         locationDetail: "",
// // //       })
// // //     }
// // //     setOpenDialog(true)
// // //   }

// // //   const getStatusColor = (status) => {
// // //     switch (status) {
// // //       case "scheduled":
// // //         return "primary"
// // //       case "completed":
// // //         return "success"
// // //       case "cancelled":
// // //         return "error"
// // //       default:
// // //         return "default"
// // //     }
// // //   }

// // //   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length

// // //   const tabIndicatorColors = {
// // //     0: "primary.main",
// // //     1: "success.main",
// // //     2: "error.main",
// // //   }

// // //   const handleOpenMap = (address) => {
// // //     const encodedAddress = encodeURIComponent(address)
// // //     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
// // //     window.open(mapUrl, "_blank")
// // //   }

// // //   return (
// // //     <Layout title="Interview Scheduler">
// // //       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
// // //         <Box sx={{ mb: 3 }}>
// // //           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
// // //             Interview Management
// // //           </Typography>
// // //           <Typography variant="body1" color="text.secondary">
// // //             Manage and track all your candidate interviews
// // //           </Typography>
// // //         </Box>

// // //         <Paper
// // //           elevation={0}
// // //           sx={{
// // //             mb: 4,
// // //             borderRadius: 3,
// // //             background: (theme) => alpha(theme.palette.background.paper, 0.95),
// // //             backdropFilter: "blur(10px)",
// // //           }}
// // //         >
// // //           <Tabs
// // //             value={tabValue}
// // //             onChange={handleTabChange}
// // //             centered
// // //             sx={{
// // //               "& .MuiTabs-indicator": {
// // //                 backgroundColor: tabIndicatorColors[tabValue],
// // //               },
// // //               "& .MuiTab-root": {
// // //                 fontSize: "0.95rem",
// // //               },
// // //             }}
// // //           >
// // //             <Tab
// // //               label={
// // //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// // //                   <Badge badgeContent={upcomingInterviews} color="primary" />
// // //                   <span>Scheduled</span>
// // //                 </Box>
// // //               }
// // //               sx={{
// // //                 color: "text.secondary",
// // //                 "&.Mui-selected": {
// // //                   color: "primary.main",
// // //                 },
// // //               }}
// // //             />
// // //             <Tab
// // //               label="Completed"
// // //               sx={{
// // //                 color: "text.secondary",
// // //                 "&.Mui-selected": {
// // //                   color: "success.main",
// // //                 },
// // //               }}
// // //             />
// // //             <Tab
// // //               label="Cancelled"
// // //               sx={{
// // //                 color: "text.secondary",
// // //                 "&.Mui-selected": {
// // //                   color: "error.main",
// // //                 },
// // //               }}
// // //             />
// // //           </Tabs>
// // //         </Paper>

// // //         {isLoading ? (
// // //           <Grid container spacing={3}>
// // //             <InterviewCardSkeleton />
// // //             <InterviewCardSkeleton />
// // //             <InterviewCardSkeleton />
// // //           </Grid>
// // //         ) : error ? (
// // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // //             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
// // //             <Typography variant="h6" sx={{ mb: 2 }}>
// // //               {error}
// // //             </Typography>
// // //             <Button variant="contained" onClick={fetchInterviews}>
// // //               Try Again
// // //             </Button>
// // //           </Paper>
// // //         ) : (
// // //           <Grid container spacing={3}>
// // //             {filteredInterviews.length > 0 ? (
// // //               filteredInterviews.map((interview) => (
// // //                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
// // //                   <Card
// // //                     sx={{
// // //                       height: 400,
// // //                       display: "flex",
// // //                       flexDirection: "column",
// // //                     }}
// // //                   >
// // //                     <CardContent
// // //                       sx={{
// // //                         flexGrow: 1,
// // //                         p: 2.5,
// // //                         pb: 1,
// // //                         display: "flex",
// // //                         flexDirection: "column",
// // //                       }}
// // //                     >
// // //                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // //                         <Avatar
// // //                           src={interview.avatar}
// // //                           sx={{
// // //                             width: 48,
// // //                             height: 48,
// // //                             mr: 2,
// // //                             border: (theme) => `2px solid ${theme.palette.background.default}`,
// // //                           }}
// // //                         />
// // //                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
// // //                           <Tooltip title={interview.candidateName} placement="top">
// // //                             <Typography
// // //                               variant="h6"
// // //                               fontWeight="600"
// // //                               sx={{
// // //                                 overflow: "hidden",
// // //                                 textOverflow: "ellipsis",
// // //                                 whiteSpace: "nowrap",
// // //                                 fontSize: "1rem",
// // //                                 lineHeight: 1.3,
// // //                               }}
// // //                             >
// // //                               {interview.candidateName}
// // //                             </Typography>
// // //                           </Tooltip>
// // //                           <Tooltip title={interview.position} placement="top">
// // //                             <Typography
// // //                               variant="body2"
// // //                               color="text.secondary"
// // //                               sx={{
// // //                                 overflow: "hidden",
// // //                                 textOverflow: "ellipsis",
// // //                                 whiteSpace: "nowrap",
// // //                                 fontSize: "0.85rem",
// // //                               }}
// // //                             >
// // //                               {interview.position}
// // //                             </Typography>
// // //                           </Tooltip>
// // //                         </Box>
// // //                         <Chip
// // //                           label={interview.status}
// // //                           size="small"
// // //                           sx={(theme) => ({
// // //                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
// // //                             color: `${getStatusColor(interview.status)}.main`,
// // //                             fontWeight: 600,
// // //                             fontSize: "0.75rem",
// // //                             height: 24,
// // //                             textTransform: "capitalize",
// // //                           })}
// // //                         />
// // //                       </Box>

// // //                       <Divider sx={{ mb: 2 }} />

// // //                       <Box sx={{ mb: 2 }}>
// // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // //                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // //                             {interview.date}
// // //                           </Typography>
// // //                         </Box>
// // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // //                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // //                             {interview.time}
// // //                           </Typography>
// // //                         </Box>
// // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // //                           {interview.formType === "video" ? (
// // //                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
// // //                           ) : (
// // //                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
// // //                           )}
// // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // //                             {interview.interviewType}
// // //                           </Typography>
// // //                         </Box>
// // //                       </Box>

// // //                       {interview.address && (
// // //                         <Box sx={{ mb: 2 }}>
// // //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // //                             Address:
// // //                           </Typography>
// // //                           <Tooltip title={interview.address} placement="top" arrow>
// // //                             <Typography
// // //                               variant="body2"
// // //                               color="text.secondary"
// // //                               sx={{
// // //                                 fontSize: "0.8rem",
// // //                                 lineHeight: 1.3,
// // //                                 cursor: "pointer",
// // //                                 "&:hover": {
// // //                                   color: "primary.main",
// // //                                 },
// // //                               }}
// // //                             >
// // //                               {interview.address.length > 40
// // //                                 ? `${interview.address.substring(0, 40)}...`
// // //                                 : interview.address}
// // //                             </Typography>
// // //                           </Tooltip>
// // //                         </Box>
// // //                       )}

// // //                       {interview.skills && interview.skills.length > 0 && (
// // //                         <Box sx={{ mb: 2 }}>
// // //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // //                             Skills:
// // //                           </Typography>
// // //                           <Box
// // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
// // //                           >
// // //                             {interview.skills.slice(0, 3).map((skill) => (
// // //                               <Chip
// // //                                 key={skill}
// // //                                 label={skill}
// // //                                 size="small"
// // //                                 variant="outlined"
// // //                                 sx={{
// // //                                   fontSize: "0.7rem",
// // //                                   height: 22,
// // //                                   "& .MuiChip-label": { px: 1 },
// // //                                 }}
// // //                               />
// // //                             ))}
// // //                             {interview.skills.length > 3 && (
// // //                               <Chip
// // //                                 label={`+${interview.skills.length - 3}`}
// // //                                 size="small"
// // //                                 variant="filled"
// // //                                 color="primary"
// // //                                 sx={{
// // //                                   fontSize: "0.7rem",
// // //                                   height: 22,
// // //                                   "& .MuiChip-label": { px: 1 },
// // //                                 }}
// // //                               />
// // //                             )}
// // //                           </Box>
// // //                         </Box>
// // //                       )}

// // //                       <Box sx={{ flexGrow: 1 }}>
// // //                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // //                           Notes:
// // //                         </Typography>
// // //                         <Typography
// // //                           variant="body2"
// // //                           color="text.secondary"
// // //                           sx={{
// // //                             fontSize: "0.8rem",
// // //                             fontStyle: "italic",
// // //                             lineHeight: 1.3,
// // //                             overflow: "hidden",
// // //                             display: "-webkit-box",
// // //                             WebkitLineClamp: 2,
// // //                             WebkitBoxOrient: "vertical",
// // //                             height: "2.6em",
// // //                           }}
// // //                         >
// // //                           {interview.notes}
// // //                         </Typography>
// // //                       </Box>
// // //                     </CardContent>

// // //                     <Divider />

// // //                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
// // //                       <Box sx={{ display: "flex", gap: 1 }}>
// // //                         {interview.status === "scheduled" && interview.interviewUrl && (
// // //                           <Button
// // //                             variant="contained"
// // //                             size="small"
// // //                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
// // //                             onClick={() => handleStartInterview(interview.interviewUrl)}
// // //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// // //                           >
// // //                             Start
// // //                           </Button>
// // //                         )}
// // //                         {interview.status === "scheduled" && interview.address && (
// // //                           <Button
// // //                             variant="contained"
// // //                             color="secondary"
// // //                             size="small"
// // //                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
// // //                             onClick={() => handleOpenMap(interview.address)}
// // //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// // //                           >
// // //                             Map
// // //                           </Button>
// // //                         )}
// // //                         {interview.status === "scheduled" && (
// // //                           <Button
// // //                             variant="outlined"
// // //                             size="small"
// // //                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
// // //                             onClick={() => handleOpenDialog(interview)}
// // //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// // //                           >
// // //                             Edit
// // //                           </Button>
// // //                         )}
// // //                       </Box>

// // //                       <Tooltip title="Delete Interview">
// // //                         <IconButton
// // //                           size="small"
// // //                           color="error"
// // //                           onClick={() => handleDeleteInterview(interview.id)}
// // //                           sx={{
// // //                             "&:hover": {
// // //                               backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
// // //                             },
// // //                           }}
// // //                         >
// // //                           <DeleteIcon sx={{ fontSize: 18 }} />
// // //                         </IconButton>
// // //                       </Tooltip>
// // //                     </CardActions>
// // //                   </Card>
// // //                 </Grid>
// // //               ))
// // //             ) : (
// // //               <Grid item xs={12}>
// // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // //                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
// // //                   <Typography variant="h6" color="textSecondary">
// // //                     No{" "}
// // //                     {tabValue === 0
// // //                       ? "scheduled interviews"
// // //                       : tabValue === 1
// // //                       ? "completed interviews"
// // //                       : "cancelled interviews"}{" "}
// // //                     found
// // //                   </Typography>
// // //                 </Paper>
// // //               </Grid>
// // //             )}
// // //           </Grid>
// // //         )}
// // //       </Box>

// // //       <Fab
// // //         color="primary"
// // //         aria-label="schedule interview"
// // //         onClick={() => handleOpenDialog()}
// // //         sx={{
// // //           position: "fixed",
// // //           bottom: 32,
// // //           right: 32,
// // //           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
// // //         }}
// // //       >
// // //         <AddIcon />
// // //       </Fab>

// // //       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
// // //         <DialogTitle sx={{ pb: 1 }}>{selectedInterview ? "Edit Interview" : "Schedule New Interview"}</DialogTitle>
// // //         <DialogContent>
// // //           <Grid container spacing={3} sx={{ pt: 1 }}>
// // //             <Grid item xs={12}>
// // //               <FormControl fullWidth disabled={!!selectedInterview}>
// // //                 <InputLabel>Candidate Application</InputLabel>
// // //                 <Select
// // //                   name="applicationId"
// // //                   value={formData.applicationId}
// // //                   onChange={handleInputChange}
// // //                   label="Candidate Application"
// // //                 >
// // //                   {candidates.map((candidate) => (
// // //                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
// // //                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// // //                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
// // //                         <Box>
// // //                           <Typography variant="body2" fontWeight="bold">
// // //                             {candidate.name}
// // //                           </Typography>
// // //                           <Typography variant="caption" color="textSecondary">
// // //                             {candidate.position}
// // //                           </Typography>
// // //                         </Box>
// // //                       </Box>
// // //                     </MenuItem>
// // //                   ))}
// // //                 </Select>
// // //               </FormControl>
// // //             </Grid>
// // //             <Grid item xs={12} sm={6}>
// // //               <TextField
// // //                 fullWidth
// // //                 label="Date"
// // //                 name="date"
// // //                 type="date"
// // //                 value={formData.date}
// // //                 onChange={handleInputChange}
// // //                 InputLabelProps={{ shrink: true }}
// // //                 required
// // //               />
// // //             </Grid>
// // //             <Grid item xs={12} sm={6}>
// // //               <TextField
// // //                 fullWidth
// // //                 label="Time"
// // //                 name="time"
// // //                 type="time"
// // //                 value={formData.time}
// // //                 onChange={handleInputChange}
// // //                 InputLabelProps={{ shrink: true }}
// // //                 required
// // //               />
// // //             </Grid>
// // //             <Grid item xs={12}>
// // //               <FormControl fullWidth>
// // //                 <InputLabel>Type</InputLabel>
// // //                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
// // //                   <MenuItem value="video">Video</MenuItem>
// // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // //                 </Select>
// // //               </FormControl>
// // //             </Grid>

// // //             {formData.type === "video" ? (
// // //               <Grid item xs={12}>
// // //                 <TextField
// // //                   fullWidth
// // //                   label="Interview URL"
// // //                   name="locationDetail"
// // //                   value={formData.locationDetail}
// // //                   onChange={handleInputChange}
// // //                   placeholder="https://meet.google.com/abc-defg-hij"
// // //                   helperText="Enter the meeting URL for the video interview"
// // //                 />
// // //               </Grid>
// // //             ) : (
// // //               <Grid item xs={12}>
// // //                 <TextField
// // //                   fullWidth
// // //                   label="Location / Address"
// // //                   name="locationDetail"
// // //                   value={formData.locationDetail}
// // //                   onChange={handleInputChange}
// // //                   placeholder="e.g., 123 Main St, Anytown, USA"
// // //                   helperText="Enter the physical address for the in-person interview"
// // //                   required
// // //                 />
// // //               </Grid>
// // //             )}
// // //             <Grid item xs={12}>
// // //               <TextField
// // //                 fullWidth
// // //                 label="Notes / Feedback"
// // //                 name="notes"
// // //                 value={formData.notes}
// // //                 onChange={handleInputChange}
// // //                 multiline
// // //                 rows={3}
// // //               />
// // //             </Grid>
// // //           </Grid>
// // //         </DialogContent>
// // //         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
// // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // //           <Button onClick={handleSubmit} variant="contained">
// // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // //           </Button>
// // //         </MuiDialogActions>
// // //       </Dialog>
// // //     </Layout>
// // //   )
// // // }

// // // export default InterviewScheduler

// // // // import { useState, useEffect, useCallback } from "react"
// // // // import apiClient from "../../context/apiClient"
// // // // import {
// // // //   Box,
// // // //   Typography,
// // // //   Grid,
// // // //   Card,
// // // //   CardContent,
// // // //   CardActions,
// // // //   Button,
// // // //   TextField,
// // // //   FormControl,
// // // //   InputLabel,
// // // //   Select,
// // // //   MenuItem,
// // // //   Dialog,
// // // //   DialogTitle,
// // // //   DialogContent,
// // // //   DialogActions as MuiDialogActions,
// // // //   IconButton,
// // // //   Chip,
// // // //   Tabs,
// // // //   Tab,
// // // //   Paper,
// // // //   Avatar,
// // // //   Badge,
// // // //   Skeleton,
// // // //   Fab,
// // // //   Divider,
// // // //   Tooltip,
// // // //   alpha,
// // // // } from "@mui/material"
// // // // import {
// // // //   Add as AddIcon,
// // // //   VideoCall as VideoCallIcon,
// // // //   CalendarMonth as CalendarIcon,
// // // //   Delete as DeleteIcon,
// // // //   Edit as EditIcon,
// // // //   Schedule as ScheduleIcon,
// // // //   AccessTime as TimeIcon,
// // // //   ReportProblem as ReportProblemIcon,
// // // //   LocationOn as LocationOnIcon,
// // // // } from "@mui/icons-material"
// // // // import Layout from "../common/Layout"
// // // // import { useNotification } from "../../context/NotificationContext"

// // // // const formatApiData = (apiInterview) => {
// // // //   let scheduledAt = null
// // // //   let rawDate = ""
// // // //   let rawTime = ""

// // // //   // Handle date and time from the current API response format
// // // //   if (apiInterview.interview_date && apiInterview.interview_time) {
// // // //     const timeWithSeconds = apiInterview.interview_time.length === 5 ? `${apiInterview.interview_time}:00` : apiInterview.interview_time
// // // //     scheduledAt = new Date(`${apiInterview.interview_date}T${timeWithSeconds}`)
// // // //     rawDate = apiInterview.interview_date
// // // //     rawTime = apiInterview.interview_time.substring(0, 5)
// // // //   } else if (apiInterview.scheduled_at) {
// // // //     // Fallback for older format
// // // //     scheduledAt = new Date(apiInterview.scheduled_at)
// // // //     rawDate = scheduledAt.toISOString().split("T")[0]
// // // //     rawTime = scheduledAt.toTimeString().substring(0, 5)
// // // //   }

// // // //   // Handle location, interview type, URL, and address
// // // //   const location = apiInterview.location || ""
// // // //   let address = ""
// // // //   let interviewUrl = ""
// // // //   let formType = "in-person"

// // // //   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")
// // // //   const isGenericVideoCall = ["remote", "video", "video call"].includes(location.toLowerCase())

// // // //   if (isVideoUrl) {
// // // //     formType = "video"
// // // //     interviewUrl = location
// // // //   } else if (isGenericVideoCall) {
// // // //     formType = "video"
// // // //   } else {
// // // //     formType = "in-person"
// // // //     const locationParts = location.split(" | Map: ")
// // // //     address = locationParts[0]
// // // //   }

// // // //   return {
// // // //     id: apiInterview.id,
// // // //     // Fallback for missing application ID to prevent errors
// // // //     applicationId: apiInterview.application || apiInterview.applicationId || apiInterview.id,
// // // //     // Use direct keys from the new API response
// // // //     candidateName: apiInterview.candidate_name || "N/A",
// // // //     position: apiInterview.job_title || "N/A",
// // // //     experience: apiInterview.experience_level || "N/A",
// // // //     status: (apiInterview.status || "unknown").toLowerCase(),
// // // //     date: scheduledAt
// // // //       ? scheduledAt.toLocaleDateString("en-US", {
// // // //           weekday: "short",
// // // //           month: "short",
// // // //           day: "numeric",
// // // //           year: "numeric",
// // // //         })
// // // //       : "N/A",
// // // //     time: scheduledAt
// // // //       ? scheduledAt.toLocaleTimeString("en-US", {
// // // //           hour: "2-digit",
// // // //           minute: "2-digit",
// // // //           hour12: true,
// // // //         })
// // // //       : "N/A",
// // // //     rawDate: rawDate,
// // // //     rawTime: rawTime,
// // // //     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
// // // //     formType: formType,
// // // //     skills: apiInterview.skills || [],
// // // //     notes: apiInterview.notes || "No specific notes provided for this interview.",
// // // //     // Use placeholder avatar as it's not in the API response
// // // //     avatar: apiInterview.avatar || "/placeholder.svg?height=56&width=56",
// // // //     address: address,
// // // //     interviewUrl: interviewUrl,
// // // //   }
// // // // }

// // // // const InterviewCardSkeleton = () => (
// // // //   <Grid item xs={12} sm={6} lg={4}>
// // // //     <Card
// // // //       sx={{
// // // //         height: 380,
// // // //         display: "flex",
// // // //         flexDirection: "column",
// // // //       }}
// // // //     >
// // // //       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
// // // //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // //           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
// // // //           <Box sx={{ flexGrow: 1 }}>
// // // //             <Skeleton variant="text" width="80%" height={24} />
// // // //             <Skeleton variant="text" width="60%" height={20} />
// // // //           </Box>
// // // //           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
// // // //         </Box>
// // // //         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
// // // //         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
// // // //       </CardContent>
// // // //       <CardActions sx={{ p: 2.5, pt: 0 }}>
// // // //         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
// // // //         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
// // // //         <Box sx={{ flexGrow: 1 }} />
// // // //         <Skeleton variant="circular" width={32} height={32} />
// // // //       </CardActions>
// // // //     </Card>
// // // //   </Grid>
// // // // )

// // // // function InterviewScheduler() {
// // // //   const { showNotification } = useNotification()
// // // //   const [interviews, setInterviews] = useState([])
// // // //   const [candidates, setCandidates] = useState([])
// // // //   const [isLoading, setIsLoading] = useState(true)
// // // //   const [error, setError] = useState(null)
// // // //   const [openDialog, setOpenDialog] = useState(false)
// // // //   const [tabValue, setTabValue] = useState(0)
// // // //   const [selectedInterview, setSelectedInterview] = useState(null)
// // // //   const [formData, setFormData] = useState({
// // // //     applicationId: "",
// // // //     date: "",
// // // //     time: "",
// // // //     type: "video",
// // // //     notes: "",
// // // //     locationDetail: "",
// // // //   })

// // // //   const fetchInterviews = useCallback(async () => {
// // // //     setIsLoading(true)
// // // //     setError(null)
// // // //     try {
// // // //       const interviewsRes = await apiClient.get("/interviews/")
// // // //       const formattedData = interviewsRes.data.map(formatApiData)
// // // //       setInterviews(formattedData)
// // // //     } catch (err) {
// // // //       setError("Could not load interviews.")
// // // //       showNotification("Failed to fetch interviews.", "error")
// // // //     } finally {
// // // //       setIsLoading(false)
// // // //     }
// // // //   }, [showNotification])

// // // //   const fetchCandidates = useCallback(async () => {
// // // //     try {
// // // //       const response = await apiClient.get("/applications/")
// // // //       const formattedCandidates = response.data.map((app) => ({
// // // //         applicationId: app.id,
// // // //         talentId: app.talent,
// // // //         name:
// // // //           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
// // // //           `Talent #${app.talent}`,
// // // //         position: app.job_posting_details?.title || "Applied Position",
// // // //         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
// // // //       }))
// // // //       setCandidates(formattedCandidates)
// // // //     } catch (err) {
// // // //       showNotification("Could not load candidate list for scheduling.", "warning")
// // // //     }
// // // //   }, [showNotification])

// // // //   useEffect(() => {
// // // //     fetchInterviews()
// // // //     fetchCandidates()
// // // //   }, [fetchInterviews, fetchCandidates])

// // // //   const handleInputChange = (e) => {
// // // //     const { name, value } = e.target
// // // //     setFormData((prev) => ({ ...prev, [name]: value }))
// // // //   }

// // // //   const handleSubmit = async () => {
// // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // //       showNotification("Please select a candidate, date, and time.", "error")
// // // //       return
// // // //     }
// // // //     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
// // // //       showNotification("Please provide an address for the in-person interview.", "error")
// // // //       return
// // // //     }

// // // //     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "Video" : "")

// // // //     const payload = {
// // // //       application: formData.applicationId,
// // // //       interview_date: formData.date,
// // // //       interview_time: formData.time,
// // // //       location: locationValue,
// // // //       notes: formData.notes,
// // // //       status: "scheduled",
// // // //     }

// // // //     try {
// // // //       if (selectedInterview) {
// // // //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
// // // //         showNotification("Interview updated successfully!", "success")
// // // //       } else {
// // // //         await apiClient.post("/interviews/", payload)
// // // //         showNotification("Interview scheduled successfully!", "success")
// // // //       }
// // // //       fetchInterviews()
// // // //       handleCloseDialog()
// // // //     } catch (err) {
// // // //       showNotification(err.response?.data?.detail || "An error occurred.", "error")
// // // //     }
// // // //   }

// // // //   const handleDeleteInterview = async (id) => {
// // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // //       try {
// // // //         await apiClient.delete(`/interviews/${id}/`)
// // // //         setInterviews(interviews.filter((i) => i.id !== id))
// // // //         showNotification("Interview deleted successfully!", "success")
// // // //       } catch (err) {
// // // //         showNotification("Failed to delete interview.", "error")
// // // //       }
// // // //     }
// // // //   }

// // // //   const handleStartInterview = (interviewUrl) => {
// // // //     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
// // // //       window.open(interviewUrl, "_blank")
// // // //     } else {
// // // //       showNotification("No valid interview URL available.", "warning")
// // // //     }
// // // //   }

// // // //   const filteredInterviews = interviews.filter((interview) => {
// // // //     if (tabValue === 0) return interview.status === "scheduled"
// // // //     if (tabValue === 1) return interview.status === "completed"
// // // //     if (tabValue === 2) return interview.status === "cancelled"
// // // //     return true
// // // //   })

// // // //   const handleTabChange = (event, newValue) => setTabValue(newValue)

// // // //   const handleCloseDialog = () => setOpenDialog(false)

// // // //   const handleOpenDialog = (interview = null) => {
// // // //     if (interview) {
// // // //       setSelectedInterview(interview)
// // // //       setFormData({
// // // //         applicationId: interview.applicationId,
// // // //         date: interview.rawDate,
// // // //         time: interview.rawTime,
// // // //         type: interview.formType,
// // // //         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
// // // //         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
// // // //       })
// // // //     } else {
// // // //       setSelectedInterview(null)
// // // //       setFormData({
// // // //         applicationId: "",
// // // //         date: "",
// // // //         time: "",
// // // //         type: "video",
// // // //         notes: "",
// // // //         locationDetail: "",
// // // //       })
// // // //     }
// // // //     setOpenDialog(true)
// // // //   }

// // // //   const getStatusColor = (status) => {
// // // //     switch (status) {
// // // //       case "scheduled":
// // // //         return "primary"
// // // //       case "completed":
// // // //         return "success"
// // // //       case "cancelled":
// // // //         return "error"
// // // //       default:
// // // //         return "default"
// // // //     }
// // // //   }

// // // //   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length

// // // //   const tabIndicatorColors = {
// // // //     0: "primary.main",
// // // //     1: "success.main",
// // // //     2: "error.main",
// // // //   }

// // // //   const handleOpenMap = (address) => {
// // // //     const encodedAddress = encodeURIComponent(address)
// // // //     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
// // // //     window.open(mapUrl, "_blank")
// // // //   }

// // // //   return (
// // // //     <Layout title="Interview Scheduler">
// // // //       <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
// // // //         <Box sx={{ mb: 3 }}>
// // // //           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
// // // //             Interview Management
// // // //           </Typography>
// // // //           <Typography variant="body1" color="text.secondary">
// // // //             Manage and track all your candidate interviews
// // // //           </Typography>
// // // //         </Box>

// // // //         <Paper
// // // //           elevation={0}
// // // //           sx={{
// // // //             mb: 4,
// // // //             borderRadius: 3,
// // // //             background: (theme) => alpha(theme.palette.background.paper, 0.95),
// // // //             backdropFilter: "blur(10px)",
// // // //           }}
// // // //         >
// // // //           <Tabs
// // // //             value={tabValue}
// // // //             onChange={handleTabChange}
// // // //             centered
// // // //             sx={{
// // // //               "& .MuiTabs-indicator": {
// // // //                 backgroundColor: tabIndicatorColors[tabValue],
// // // //               },
// // // //               "& .MuiTab-root": {
// // // //                 fontSize: "0.95rem",
// // // //               },
// // // //             }}
// // // //           >
// // // //             <Tab
// // // //               label={
// // // //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// // // //                   <Badge badgeContent={upcomingInterviews} color="primary" />
// // // //                   <span>Scheduled</span>
// // // //                 </Box>
// // // //               }
// // // //               sx={{
// // // //                 color: "text.secondary",
// // // //                 "&.Mui-selected": {
// // // //                   color: "primary.main",
// // // //                 },
// // // //               }}
// // // //             />
// // // //             <Tab
// // // //               label="Completed"
// // // //               sx={{
// // // //                 color: "text.secondary",
// // // //                 "&.Mui-selected": {
// // // //                   color: "success.main",
// // // //                 },
// // // //               }}
// // // //             />
// // // //             <Tab
// // // //               label="Cancelled"
// // // //               sx={{
// // // //                 color: "text.secondary",
// // // //                 "&.Mui-selected": {
// // // //                   color: "error.main",
// // // //                 },
// // // //               }}
// // // //             />
// // // //           </Tabs>
// // // //         </Paper>

// // // //         {isLoading ? (
// // // //           <Grid container spacing={3}>
// // // //             <InterviewCardSkeleton />
// // // //             <InterviewCardSkeleton />
// // // //             <InterviewCardSkeleton />
// // // //           </Grid>
// // // //         ) : error ? (
// // // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // // //             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
// // // //             <Typography variant="h6" sx={{ mb: 2 }}>
// // // //               {error}
// // // //             </Typography>
// // // //             <Button variant="contained" onClick={fetchInterviews}>
// // // //               Try Again
// // // //             </Button>
// // // //           </Paper>
// // // //         ) : (
// // // //           <Grid container spacing={3}>
// // // //             {filteredInterviews.length > 0 ? (
// // // //               filteredInterviews.map((interview) => (
// // // //                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
// // // //                   <Card
// // // //                     sx={{
// // // //                       height: 380,
// // // //                       display: "flex",
// // // //                       flexDirection: "column",
// // // //                     }}
// // // //                   >
// // // //                     <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
// // // //                       {/* Header Section */}
// // // //                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // //                         <Avatar
// // // //                           src={interview.avatar}
// // // //                           sx={{
// // // //                             width: 48,
// // // //                             height: 48,
// // // //                             mr: 2,
// // // //                             border: (theme) => `2px solid ${theme.palette.background.default}`,
// // // //                           }}
// // // //                         />
// // // //                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
// // // //                           <Tooltip title={interview.candidateName} placement="top">
// // // //                             <Typography
// // // //                               variant="h6"
// // // //                               fontWeight="600"
// // // //                               sx={{
// // // //                                 overflow: "hidden",
// // // //                                 textOverflow: "ellipsis",
// // // //                                 whiteSpace: "nowrap",
// // // //                                 fontSize: "1rem",
// // // //                                 lineHeight: 1.3,
// // // //                               }}
// // // //                             >
// // // //                               {interview.candidateName}
// // // //                             </Typography>
// // // //                           </Tooltip>
// // // //                           <Tooltip title={interview.position} placement="top">
// // // //                             <Typography
// // // //                               variant="body2"
// // // //                               color="text.secondary"
// // // //                               sx={{
// // // //                                 overflow: "hidden",
// // // //                                 textOverflow: "ellipsis",
// // // //                                 whiteSpace: "nowrap",
// // // //                                 fontSize: "0.85rem",
// // // //                               }}
// // // //                             >
// // // //                               {interview.position}
// // // //                             </Typography>
// // // //                           </Tooltip>
// // // //                         </Box>
// // // //                         <Chip
// // // //                           label={interview.status}
// // // //                           size="small"
// // // //                           sx={(theme) => ({
// // // //                             backgroundColor: alpha(theme.palette[getStatusColor(interview.status)].main, 0.1),
// // // //                             color: `${getStatusColor(interview.status)}.main`,
// // // //                             fontWeight: 600,
// // // //                             fontSize: "0.75rem",
// // // //                             height: 24,
// // // //                             textTransform: "capitalize",
// // // //                           })}
// // // //                         />
// // // //                       </Box>

// // // //                       <Divider sx={{ mb: 2 }} />

// // // //                       {/* Interview Details */}
// // // //                       <Box sx={{ mb: 2 }}>
// // // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // // //                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// // // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // // //                             {interview.date}
// // // //                           </Typography>
// // // //                         </Box>
// // // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // // //                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// // // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // // //                             {interview.time}
// // // //                           </Typography>
// // // //                         </Box>
// // // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // // //                           {interview.formType === "video" ? (
// // // //                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
// // // //                           ) : (
// // // //                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
// // // //                           )}
// // // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // // //                             {interview.interviewType}
// // // //                           </Typography>
// // // //                         </Box>
// // // //                       </Box>

// // // //                       {/* Address Section */}
// // // //                       {interview.address && (
// // // //                         <Box sx={{ mb: 2 }}>
// // // //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // // //                             Address:
// // // //                           </Typography>
// // // //                           <Tooltip title={interview.address} placement="top" arrow>
// // // //                             <Typography
// // // //                               variant="body2"
// // // //                               color="text.secondary"
// // // //                               sx={{
// // // //                                 fontSize: "0.8rem",
// // // //                                 lineHeight: 1.3,
// // // //                                 overflow: "hidden",
// // // //                                 textOverflow: "ellipsis",
// // // //                                 whiteSpace: "nowrap",
// // // //                                 cursor: "pointer",
// // // //                                 "&:hover": {
// // // //                                   color: "primary.main",
// // // //                                 },
// // // //                               }}
// // // //                             >
// // // //                               {interview.address}
// // // //                             </Typography>
// // // //                           </Tooltip>
// // // //                         </Box>
// // // //                       )}

// // // //                       {/* Skills Section */}
// // // //                       {interview.skills && interview.skills.length > 0 && (
// // // //                         <Box sx={{ mb: 2 }}>
// // // //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // // //                             Skills:
// // // //                           </Typography>
// // // //                           <Box
// // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
// // // //                           >
// // // //                             {interview.skills.slice(0, 3).map((skill) => (
// // // //                               <Chip
// // // //                                 key={skill}
// // // //                                 label={skill}
// // // //                                 size="small"
// // // //                                 variant="outlined"
// // // //                                 sx={{
// // // //                                   fontSize: "0.7rem",
// // // //                                   height: 22,
// // // //                                   "& .MuiChip-label": { px: 1 },
// // // //                                 }}
// // // //                               />
// // // //                             ))}
// // // //                             {interview.skills.length > 3 && (
// // // //                               <Chip
// // // //                                 label={`+${interview.skills.length - 3}`}
// // // //                                 size="small"
// // // //                                 variant="filled"
// // // //                                 color="primary"
// // // //                                 sx={{
// // // //                                   fontSize: "0.7rem",
// // // //                                   height: 22,
// // // //                                   "& .MuiChip-label": { px: 1 },
// // // //                                 }}
// // // //                               />
// // // //                             )}
// // // //                           </Box>
// // // //                         </Box>
// // // //                       )}

// // // //                       {/* Notes Section */}
// // // //                       <Box sx={{ flexGrow: 1 }}>
// // // //                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // // //                           Notes:
// // // //                         </Typography>
// // // //                         <Typography
// // // //                           variant="body2"
// // // //                           color="text.secondary"
// // // //                           sx={{
// // // //                             fontSize: "0.8rem",
// // // //                             fontStyle: "italic",
// // // //                             lineHeight: 1.3,
// // // //                             overflow: "hidden",
// // // //                             display: "-webkit-box",
// // // //                             WebkitLineClamp: 2,
// // // //                             WebkitBoxOrient: "vertical",
// // // //                             height: "2.6em",
// // // //                           }}
// // // //                         >
// // // //                           {interview.notes}
// // // //                         </Typography>
// // // //                       </Box>
// // // //                     </CardContent>

// // // //                     <Divider />

// // // //                     <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
// // // //                       <Box sx={{ display: "flex", gap: 1 }}>
// // // //                         {interview.status === "scheduled" && interview.interviewUrl && (
// // // //                           <Button
// // // //                             variant="contained"
// // // //                             size="small"
// // // //                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
// // // //                             onClick={() => handleStartInterview(interview.interviewUrl)}
// // // //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// // // //                           >
// // // //                             Start
// // // //                           </Button>
// // // //                         )}
// // // //                         {interview.status === "scheduled" && interview.address && (
// // // //                           <Button
// // // //                             variant="contained"
// // // //                             color="secondary"
// // // //                             size="small"
// // // //                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
// // // //                             onClick={() => handleOpenMap(interview.address)}
// // // //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// // // //                           >
// // // //                             Map
// // // //                           </Button>
// // // //                         )}
// // // //                         {interview.status === "scheduled" && (
// // // //                           <Button
// // // //                             variant="outlined"
// // // //                             size="small"
// // // //                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
// // // //                             onClick={() => handleOpenDialog(interview)}
// // // //                             sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
// // // //                           >
// // // //                             Edit
// // // //                           </Button>
// // // //                         )}
// // // //                       </Box>

// // // //                       <Tooltip title="Delete Interview">
// // // //                         <IconButton
// // // //                           size="small"
// // // //                           color="error"
// // // //                           onClick={() => handleDeleteInterview(interview.id)}
// // // //                           sx={{
// // // //                             "&:hover": {
// // // //                               backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
// // // //                             },
// // // //                           }}
// // // //                         >
// // // //                           <DeleteIcon sx={{ fontSize: 18 }} />
// // // //                         </IconButton>
// // // //                       </Tooltip>
// // // //                     </CardActions>
// // // //                   </Card>
// // // //                 </Grid>
// // // //               ))
// // // //             ) : (
// // // //               <Grid item xs={12}>
// // // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // // //                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
// // // //                   <Typography variant="h6" color="textSecondary">
// // // //                     No{" "}
// // // //                     {tabValue === 0
// // // //                       ? "scheduled interviews"
// // // //                       : tabValue === 1
// // // //                       ? "completed interviews"
// // // //                       : "cancelled interviews"}{" "}
// // // //                     found
// // // //                   </Typography>
// // // //                 </Paper>
// // // //               </Grid>
// // // //             )}
// // // //           </Grid>
// // // //         )}
// // // //       </Box>

// // // //       <Fab
// // // //         color="primary"
// // // //         aria-label="schedule interview"
// // // //         onClick={() => handleOpenDialog()}
// // // //         sx={{
// // // //           position: "fixed",
// // // //           bottom: 32,
// // // //           right: 32,
// // // //           boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
// // // //         }}
// // // //       >
// // // //         <AddIcon />
// // // //       </Fab>

// // // //       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
// // // //         <DialogTitle sx={{ pb: 1 }}>{selectedInterview ? "Edit Interview" : "Schedule New Interview"}</DialogTitle>
// // // //         <DialogContent>
// // // //           <Grid container spacing={3} sx={{ pt: 1 }}>
// // // //             <Grid item xs={12}>
// // // //               <FormControl fullWidth disabled={!!selectedInterview}>
// // // //                 <InputLabel>Candidate Application</InputLabel>
// // // //                 <Select
// // // //                   name="applicationId"
// // // //                   value={formData.applicationId}
// // // //                   onChange={handleInputChange}
// // // //                   label="Candidate Application"
// // // //                 >
// // // //                   {candidates.map((candidate) => (
// // // //                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
// // // //                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// // // //                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
// // // //                         <Box>
// // // //                           <Typography variant="body2" fontWeight="bold">
// // // //                             {candidate.name}
// // // //                           </Typography>
// // // //                           <Typography variant="caption" color="textSecondary">
// // // //                             {candidate.position}
// // // //                           </Typography>
// // // //                         </Box>
// // // //                       </Box>
// // // //                     </MenuItem>
// // // //                   ))}
// // // //                 </Select>
// // // //               </FormControl>
// // // //             </Grid>
// // // //             <Grid item xs={12} sm={6}>
// // // //               <TextField
// // // //                 fullWidth
// // // //                 label="Date"
// // // //                 name="date"
// // // //                 type="date"
// // // //                 value={formData.date}
// // // //                 onChange={handleInputChange}
// // // //                 InputLabelProps={{ shrink: true }}
// // // //                 required
// // // //               />
// // // //             </Grid>
// // // //             <Grid item xs={12} sm={6}>
// // // //               <TextField
// // // //                 fullWidth
// // // //                 label="Time"
// // // //                 name="time"
// // // //                 type="time"
// // // //                 value={formData.time}
// // // //                 onChange={handleInputChange}
// // // //                 InputLabelProps={{ shrink: true }}
// // // //                 required
// // // //               />
// // // //             </Grid>
// // // //             <Grid item xs={12}>
// // // //               <FormControl fullWidth>
// // // //                 <InputLabel>Type</InputLabel>
// // // //                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
// // // //                   <MenuItem value="video">Video</MenuItem>
// // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // //                 </Select>
// // // //               </FormControl>
// // // //             </Grid>

// // // //             {formData.type === "video" ? (
// // // //               <Grid item xs={12}>
// // // //                 <TextField
// // // //                   fullWidth
// // // //                   label="Interview URL"
// // // //                   name="locationDetail"
// // // //                   value={formData.locationDetail}
// // // //                   onChange={handleInputChange}
// // // //                   placeholder="https://meet.google.com/abc-defg-hij"
// // // //                   helperText="Enter the meeting URL for the video interview"
// // // //                 />
// // // //               </Grid>
// // // //             ) : (
// // // //               <Grid item xs={12}>
// // // //                 <TextField
// // // //                   fullWidth
// // // //                   label="Location / Address"
// // // //                   name="locationDetail"
// // // //                   value={formData.locationDetail}
// // // //                   onChange={handleInputChange}
// // // //                   placeholder="e.g., 123 Main St, Anytown, USA"
// // // //                   helperText="Enter the physical address for the in-person interview"
// // // //                   required
// // // //                 />
// // // //               </Grid>
// // // //             )}
// // // //             <Grid item xs={12}>
// // // //               <TextField
// // // //                 fullWidth
// // // //                 label="Notes / Feedback"
// // // //                 name="notes"
// // // //                 value={formData.notes}
// // // //                 onChange={handleInputChange}
// // // //                 multiline
// // // //                 rows={3}
// // // //               />
// // // //             </Grid>
// // // //           </Grid>
// // // //         </DialogContent>
// // // //         <MuiDialogActions sx={{ p: 3, pt: 2 }}>
// // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // //           <Button onClick={handleSubmit} variant="contained">
// // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // //           </Button>
// // // //         </MuiDialogActions>
// // // //       </Dialog>
// // // //     </Layout>
// // // //   )
// // // // }

// // // // export default InterviewScheduler

// // // // // // // // // // import { useState } from "react";
// // // // // // // // // // import {
// // // // // // // // // //   Box,
// // // // // // // // // //   Typography,
// // // // // // // // // //   Grid,
// // // // // // // // // //   Card,
// // // // // // // // // //   CardContent,
// // // // // // // // // //   Button,
// // // // // // // // // //   TextField,
// // // // // // // // // //   FormControl,
// // // // // // // // // //   InputLabel,
// // // // // // // // // //   Select,
// // // // // // // // // //   MenuItem,
// // // // // // // // // //   Dialog,
// // // // // // // // // //   DialogTitle,
// // // // // // // // // //   DialogContent,
// // // // // // // // // //   DialogActions,
// // // // // // // // // //   IconButton,
// // // // // // // // // //   Chip,
// // // // // // // // // //   Tabs,
// // // // // // // // // //   Tab,
// // // // // // // // // //   Paper,
// // // // // // // // // //   Divider,
// // // // // // // // // //   Avatar,
// // // // // // // // // //   List,
// // // // // // // // // //   ListItem,
// // // // // // // // // //   ListItemText,
// // // // // // // // // // } from "@mui/material";
// // // // // // // // // // import {
// // // // // // // // // //   Add as AddIcon,
// // // // // // // // // //   VideoCall as VideoCallIcon,
// // // // // // // // // //   Call as CallIcon,
// // // // // // // // // //   CalendarMonth as CalendarIcon,
// // // // // // // // // //   Delete as DeleteIcon,
// // // // // // // // // //   Edit as EditIcon,
// // // // // // // // // // } from "@mui/icons-material";
// // // // // // // // // // import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// // // // // // // // // // import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
// // // // // // // // // // import Layout from "../common/Layout";

// // // // // // // // // // // Mock data for interviews
// // // // // // // // // // const mockInterviews = [
// // // // // // // // // //   {
// // // // // // // // // //     id: 1,
// // // // // // // // // //     candidateName: "Priya Sharma",
// // // // // // // // // //     candidateId: "T1001",
// // // // // // // // // //     position: "AI Engineer",
// // // // // // // // // //     date: new Date(2023, 6, 15, 10, 0),
// // // // // // // // // //     type: "video",
// // // // // // // // // //     status: "scheduled",
// // // // // // // // // //     notes: "Focus on NLP experience",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/women/44.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: 2,
// // // // // // // // // //     candidateName: "Rahul Verma",
// // // // // // // // // //     candidateId: "T1002",
// // // // // // // // // //     position: "ML Specialist",
// // // // // // // // // //     date: new Date(2023, 6, 16, 14, 30),
// // // // // // // // // //     type: "audio",
// // // // // // // // // //     status: "scheduled",
// // // // // // // // // //     notes: "Check certification details",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/men/32.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: 3,
// // // // // // // // // //     candidateName: "Ananya Patel",
// // // // // // // // // //     candidateId: "T1003",
// // // // // // // // // //     position: "Data Scientist",
// // // // // // // // // //     date: new Date(2023, 6, 12, 11, 0),
// // // // // // // // // //     type: "video",
// // // // // // // // // //     status: "completed",
// // // // // // // // // //     notes: "Strong Python skills, good fit",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/women/68.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: 4,
// // // // // // // // // //     candidateName: "Vikram Singh",
// // // // // // // // // //     candidateId: "T1004",
// // // // // // // // // //     position: "NLP Engineer",
// // // // // // // // // //     date: new Date(2023, 6, 10, 15, 0),
// // // // // // // // // //     type: "video",
// // // // // // // // // //     status: "completed",
// // // // // // // // // //     notes: "Needs more experience with transformers",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/men/45.jpg",
// // // // // // // // // //   },
// // // // // // // // // // ];

// // // // // // // // // // // Mock data for candidates
// // // // // // // // // // const mockCandidates = [
// // // // // // // // // //   {
// // // // // // // // // //     id: "T1001",
// // // // // // // // // //     name: "Priya Sharma",
// // // // // // // // // //     position: "AI Engineer",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/women/44.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: "T1002",
// // // // // // // // // //     name: "Rahul Verma",
// // // // // // // // // //     position: "ML Specialist",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/men/32.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: "T1003",
// // // // // // // // // //     name: "Ananya Patel",
// // // // // // // // // //     position: "Data Scientist",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/women/68.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: "T1004",
// // // // // // // // // //     name: "Vikram Singh",
// // // // // // // // // //     position: "NLP Engineer",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/men/45.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: "T1005",
// // // // // // // // // //     name: "Neha Gupta",
// // // // // // // // // //     position: "Computer Vision Specialist",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/women/22.jpg",
// // // // // // // // // //   },
// // // // // // // // // //   {
// // // // // // // // // //     id: "T1006",
// // // // // // // // // //     name: "Arjun Kumar",
// // // // // // // // // //     position: "AI Research Scientist",
// // // // // // // // // //     avatar: "https://randomuser.me/api/portraits/men/67.jpg",
// // // // // // // // // //   },
// // // // // // // // // // ];

// // // // // // // // // // function InterviewScheduler() {
// // // // // // // // // //   const [interviews, setInterviews] = useState(mockInterviews);
// // // // // // // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // // // // // // //   const [tabValue, setTabValue] = useState(0);
// // // // // // // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // // // // // // //   const [openVideoCall, setOpenVideoCall] = useState(false);

// // // // // // // // // //   // Form state
// // // // // // // // // //   const [formData, setFormData] = useState({
// // // // // // // // // //     candidateId: "",
// // // // // // // // // //     position: "",
// // // // // // // // // //     date: new Date(),
// // // // // // // // // //     type: "video",
// // // // // // // // // //     notes: "",
// // // // // // // // // //   });

// // // // // // // // // //   // Filter interviews based on tab
// // // // // // // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // // // // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // // // // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // // // // // // //     return true;
// // // // // // // // // //   });

// // // // // // // // // //   const handleTabChange = (event, newValue) => {
// // // // // // // // // //     setTabValue(newValue);
// // // // // // // // // //   };

// // // // // // // // // //   const handleOpenDialog = (interview = null) => {
// // // // // // // // // //     if (interview) {
// // // // // // // // // //       setSelectedInterview(interview);
// // // // // // // // // //       setFormData({
// // // // // // // // // //         candidateId: interview.candidateId,
// // // // // // // // // //         position: interview.position,
// // // // // // // // // //         date: interview.date,
// // // // // // // // // //         type: interview.type,
// // // // // // // // // //         notes: interview.notes,
// // // // // // // // // //       });
// // // // // // // // // //     } else {
// // // // // // // // // //       setSelectedInterview(null);
// // // // // // // // // //       setFormData({
// // // // // // // // // //         candidateId: "",
// // // // // // // // // //         position: "",
// // // // // // // // // //         date: new Date(),
// // // // // // // // // //         type: "video",
// // // // // // // // // //         notes: "",
// // // // // // // // // //       });
// // // // // // // // // //     }
// // // // // // // // // //     setOpenDialog(true);
// // // // // // // // // //   };

// // // // // // // // // //   const handleCloseDialog = () => {
// // // // // // // // // //     setOpenDialog(false);
// // // // // // // // // //   };

// // // // // // // // // //   const handleInputChange = (e) => {
// // // // // // // // // //     const { name, value } = e.target;
// // // // // // // // // //     setFormData({
// // // // // // // // // //       ...formData,
// // // // // // // // // //       [name]: value,
// // // // // // // // // //     });
// // // // // // // // // //   };

// // // // // // // // // //   const handleDateChange = (newDate) => {
// // // // // // // // // //     setFormData({
// // // // // // // // // //       ...formData,
// // // // // // // // // //       date: newDate,
// // // // // // // // // //     });
// // // // // // // // // //   };

// // // // // // // // // //   const handleSubmit = () => {
// // // // // // // // // //     const candidate = mockCandidates.find((c) => c.id === formData.candidateId);

// // // // // // // // // //     if (selectedInterview) {
// // // // // // // // // //       // Update existing interview
// // // // // // // // // //       const updatedInterviews = interviews.map((interview) =>
// // // // // // // // // //         interview.id === selectedInterview.id
// // // // // // // // // //           ? {
// // // // // // // // // //               ...interview,
// // // // // // // // // //               candidateName: candidate.name,
// // // // // // // // // //               position: formData.position,
// // // // // // // // // //               date: formData.date,
// // // // // // // // // //               type: formData.type,
// // // // // // // // // //               notes: formData.notes,
// // // // // // // // // //               avatar: candidate.avatar,
// // // // // // // // // //             }
// // // // // // // // // //           : interview
// // // // // // // // // //       );
// // // // // // // // // //       setInterviews(updatedInterviews);
// // // // // // // // // //     } else {
// // // // // // // // // //       // Add new interview
// // // // // // // // // //       const newInterview = {
// // // // // // // // // //         id: interviews.length + 1,
// // // // // // // // // //         candidateName: candidate.name,
// // // // // // // // // //         candidateId: formData.candidateId,
// // // // // // // // // //         position: formData.position,
// // // // // // // // // //         date: formData.date,
// // // // // // // // // //         type: formData.type,
// // // // // // // // // //         status: "scheduled",
// // // // // // // // // //         notes: formData.notes,
// // // // // // // // // //         avatar: candidate.avatar,
// // // // // // // // // //       };
// // // // // // // // // //       setInterviews([...interviews, newInterview]);
// // // // // // // // // //     }

// // // // // // // // // //     handleCloseDialog();
// // // // // // // // // //   };

// // // // // // // // // //   const handleDeleteInterview = (id) => {
// // // // // // // // // //     setInterviews(interviews.filter((interview) => interview.id !== id));
// // // // // // // // // //   };

// // // // // // // // // //   const handleStartInterview = (interview) => {
// // // // // // // // // //     setSelectedInterview(interview);
// // // // // // // // // //     setOpenVideoCall(true);
// // // // // // // // // //   };

// // // // // // // // // //   const handleCloseVideoCall = () => {
// // // // // // // // // //     setOpenVideoCall(false);

// // // // // // // // // //     // Mark interview as completed
// // // // // // // // // //     if (selectedInterview) {
// // // // // // // // // //       const updatedInterviews = interviews.map((interview) =>
// // // // // // // // // //         interview.id === selectedInterview.id
// // // // // // // // // //           ? { ...interview, status: "completed" }
// // // // // // // // // //           : interview
// // // // // // // // // //       );
// // // // // // // // // //       setInterviews(updatedInterviews);
// // // // // // // // // //     }
// // // // // // // // // //   };

// // // // // // // // // //   return (
// // // // // // // // // //     <Layout>
// // // // // // // // // //       <Box sx={{ p: 3 }}>
// // // // // // // // // //         <Box
// // // // // // // // // //           sx={{
// // // // // // // // // //             display: "flex",
// // // // // // // // // //             justifyContent: "space-between",
// // // // // // // // // //             alignItems: "center",
// // // // // // // // // //             mb: 3,
// // // // // // // // // //           }}
// // // // // // // // // //         >
// // // // // // // // // //           <Typography variant="h4" component="h1" gutterBottom>
// // // // // // // // // //             Interview Scheduler
// // // // // // // // // //           </Typography>
// // // // // // // // // //           <Button
// // // // // // // // // //             variant="contained"
// // // // // // // // // //             color="primary"
// // // // // // // // // //             startIcon={<AddIcon />}
// // // // // // // // // //             onClick={() => handleOpenDialog()}
// // // // // // // // // //           >
// // // // // // // // // //             Schedule New Interview
// // // // // // // // // //           </Button>
// // // // // // // // // //         </Box>

// // // // // // // // // //         <Paper sx={{ mb: 3 }}>
// // // // // // // // // //           <Tabs
// // // // // // // // // //             value={tabValue}
// // // // // // // // // //             onChange={handleTabChange}
// // // // // // // // // //             indicatorColor="primary"
// // // // // // // // // //             textColor="primary"
// // // // // // // // // //             centered
// // // // // // // // // //           >
// // // // // // // // // //             <Tab label="Upcoming Interviews" />
// // // // // // // // // //             <Tab label="Past Interviews" />
// // // // // // // // // //           </Tabs>
// // // // // // // // // //         </Paper>

// // // // // // // // // //         <Grid container spacing={3}>
// // // // // // // // // //           {filteredInterviews.length > 0 ? (
// // // // // // // // // //             filteredInterviews.map((interview) => (
// // // // // // // // // //               <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // // // // // // //                 <Card sx={{ height: "100%" }}>
// // // // // // // // // //                   <CardContent>
// // // // // // // // // //                     <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // // // // // // //                       <Avatar
// // // // // // // // // //                         src={interview.avatar}
// // // // // // // // // //                         sx={{ width: 56, height: 56, mr: 2 }}
// // // // // // // // // //                       />
// // // // // // // // // //                       <Box>
// // // // // // // // // //                         <Typography variant="h6">
// // // // // // // // // //                           {interview.candidateName}
// // // // // // // // // //                         </Typography>
// // // // // // // // // //                         <Typography variant="body2" color="textSecondary">
// // // // // // // // // //                           {interview.position}
// // // // // // // // // //                         </Typography>
// // // // // // // // // //                       </Box>
// // // // // // // // // //                     </Box>

// // // // // // // // // //                     <Divider sx={{ my: 2 }} />

// // // // // // // // // //                     <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
// // // // // // // // // //                       <CalendarIcon sx={{ mr: 1, color: "primary.main" }} />
// // // // // // // // // //                       <Typography variant="body2">
// // // // // // // // // //                         {interview.date.toLocaleString("en-US", {
// // // // // // // // // //                           weekday: "short",
// // // // // // // // // //                           year: "numeric",
// // // // // // // // // //                           month: "short",
// // // // // // // // // //                           day: "numeric",
// // // // // // // // // //                           hour: "2-digit",
// // // // // // // // // //                           minute: "2-digit",
// // // // // // // // // //                         })}
// // // // // // // // // //                       </Typography>
// // // // // // // // // //                     </Box>

// // // // // // // // // //                     <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // // // // // // //                       {interview.type === "video" ? (
// // // // // // // // // //                         <VideoCallIcon sx={{ mr: 1, color: "primary.main" }} />
// // // // // // // // // //                       ) : (
// // // // // // // // // //                         <CallIcon sx={{ mr: 1, color: "primary.main" }} />
// // // // // // // // // //                       )}
// // // // // // // // // //                       <Typography variant="body2">
// // // // // // // // // //                         {interview.type === "video"
// // // // // // // // // //                           ? "Video Interview"
// // // // // // // // // //                           : "Audio Interview"}
// // // // // // // // // //                       </Typography>
// // // // // // // // // //                     </Box>

// // // // // // // // // //                     {interview.notes && (
// // // // // // // // // //                       <Typography
// // // // // // // // // //                         variant="body2"
// // // // // // // // // //                         color="textSecondary"
// // // // // // // // // //                         sx={{ mb: 2 }}
// // // // // // // // // //                       >
// // // // // // // // // //                         <strong>Notes:</strong> {interview.notes}
// // // // // // // // // //                       </Typography>
// // // // // // // // // //                     )}

// // // // // // // // // //                     <Box
// // // // // // // // // //                       sx={{
// // // // // // // // // //                         display: "flex",
// // // // // // // // // //                         justifyContent: "space-between",
// // // // // // // // // //                         mt: 2,
// // // // // // // // // //                       }}
// // // // // // // // // //                     >
// // // // // // // // // //                       {interview.status === "scheduled" ? (
// // // // // // // // // //                         <Button
// // // // // // // // // //                           variant="contained"
// // // // // // // // // //                           color="primary"
// // // // // // // // // //                           startIcon={
// // // // // // // // // //                             interview.type === "video" ? (
// // // // // // // // // //                               <VideoCallIcon />
// // // // // // // // // //                             ) : (
// // // // // // // // // //                               <CallIcon />
// // // // // // // // // //                             )
// // // // // // // // // //                           }
// // // // // // // // // //                           onClick={() => handleStartInterview(interview)}
// // // // // // // // // //                         >
// // // // // // // // // //                           Start Interview
// // // // // // // // // //                         </Button>
// // // // // // // // // //                       ) : (
// // // // // // // // // //                         <Chip
// // // // // // // // // //                           label="Completed"
// // // // // // // // // //                           color="success"
// // // // // // // // // //                           variant="outlined"
// // // // // // // // // //                         />
// // // // // // // // // //                       )}

// // // // // // // // // //                       <Box>
// // // // // // // // // //                         <IconButton
// // // // // // // // // //                           size="small"
// // // // // // // // // //                           onClick={() => handleOpenDialog(interview)}
// // // // // // // // // //                           sx={{ mr: 1 }}
// // // // // // // // // //                         >
// // // // // // // // // //                           <EditIcon />
// // // // // // // // // //                         </IconButton>
// // // // // // // // // //                         <IconButton
// // // // // // // // // //                           size="small"
// // // // // // // // // //                           color="error"
// // // // // // // // // //                           onClick={() => handleDeleteInterview(interview.id)}
// // // // // // // // // //                         >
// // // // // // // // // //                           <DeleteIcon />
// // // // // // // // // //                         </IconButton>
// // // // // // // // // //                       </Box>
// // // // // // // // // //                     </Box>
// // // // // // // // // //                   </CardContent>
// // // // // // // // // //                 </Card>
// // // // // // // // // //               </Grid>
// // // // // // // // // //             ))
// // // // // // // // // //           ) : (
// // // // // // // // // //             <Grid item xs={12}>
// // // // // // // // // //               <Paper sx={{ p: 3, textAlign: "center" }}>
// // // // // // // // // //                 <Typography variant="h6" color="textSecondary">
// // // // // // // // // //                   No {tabValue === 0 ? "upcoming" : "past"} interviews found
// // // // // // // // // //                 </Typography>
// // // // // // // // // //               </Paper>
// // // // // // // // // //             </Grid>
// // // // // // // // // //           )}
// // // // // // // // // //         </Grid>
// // // // // // // // // //       </Box>

// // // // // // // // // //       {/* Schedule Interview Dialog */}
// // // // // // // // // //       <Dialog
// // // // // // // // // //         open={openDialog}
// // // // // // // // // //         onClose={handleCloseDialog}
// // // // // // // // // //         maxWidth="sm"
// // // // // // // // // //         fullWidth
// // // // // // // // // //       >
// // // // // // // // // //         <DialogTitle>
// // // // // // // // // //           {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // // // // // // //         </DialogTitle>
// // // // // // // // // //         <DialogContent>
// // // // // // // // // //           <Grid container spacing={2} sx={{ mt: 1 }}>
// // // // // // // // // //             <Grid item xs={12}>
// // // // // // // // // //               <FormControl fullWidth>
// // // // // // // // // //                 <InputLabel id="candidate-label">Candidate</InputLabel>
// // // // // // // // // //                 <Select
// // // // // // // // // //                   labelId="candidate-label"
// // // // // // // // // //                   name="candidateId"
// // // // // // // // // //                   value={formData.candidateId}
// // // // // // // // // //                   onChange={handleInputChange}
// // // // // // // // // //                   label="Candidate"
// // // // // // // // // //                 >
// // // // // // // // // //                   {mockCandidates.map((candidate) => (
// // // // // // // // // //                     <MenuItem key={candidate.id} value={candidate.id}>
// // // // // // // // // //                       <Box sx={{ display: "flex", alignItems: "center" }}>
// // // // // // // // // //                         <Avatar
// // // // // // // // // //                           src={candidate.avatar}
// // // // // // // // // //                           sx={{ width: 24, height: 24, mr: 1 }}
// // // // // // // // // //                         />
// // // // // // // // // //                         {candidate.name} - {candidate.position}
// // // // // // // // // //                       </Box>
// // // // // // // // // //                     </MenuItem>
// // // // // // // // // //                   ))}
// // // // // // // // // //                 </Select>
// // // // // // // // // //               </FormControl>
// // // // // // // // // //             </Grid>

// // // // // // // // // //             <Grid item xs={12}>
// // // // // // // // // //               <TextField
// // // // // // // // // //                 fullWidth
// // // // // // // // // //                 label="Position"
// // // // // // // // // //                 name="position"
// // // // // // // // // //                 value={formData.position}
// // // // // // // // // //                 onChange={handleInputChange}
// // // // // // // // // //               />
// // // // // // // // // //             </Grid>

// // // // // // // // // //             <Grid item xs={12}>
// // // // // // // // // //               <LocalizationProvider dateAdapter={AdapterDateFns}>
// // // // // // // // // //                 <DateTimePicker
// // // // // // // // // //                   label="Interview Date & Time"
// // // // // // // // // //                   value={formData.date}
// // // // // // // // // //                   onChange={handleDateChange}
// // // // // // // // // //                   renderInput={(params) => <TextField {...params} fullWidth />}
// // // // // // // // // //                 />
// // // // // // // // // //               </LocalizationProvider>
// // // // // // // // // //             </Grid>

// // // // // // // // // //             <Grid item xs={12}>
// // // // // // // // // //               <FormControl fullWidth>
// // // // // // // // // //                 <InputLabel id="type-label">Interview Type</InputLabel>
// // // // // // // // // //                 <Select
// // // // // // // // // //                   labelId="type-label"
// // // // // // // // // //                   name="type"
// // // // // // // // // //                   value={formData.type}
// // // // // // // // // //                   onChange={handleInputChange}
// // // // // // // // // //                   label="Interview Type"
// // // // // // // // // //                 >
// // // // // // // // // //                   <MenuItem value="video">Video Interview</MenuItem>
// // // // // // // // // //                   <MenuItem value="audio">Audio Interview</MenuItem>
// // // // // // // // // //                 </Select>
// // // // // // // // // //               </FormControl>
// // // // // // // // // //             </Grid>

// // // // // // // // // //             <Grid item xs={12}>
// // // // // // // // // //               <TextField
// // // // // // // // // //                 fullWidth
// // // // // // // // // //                 label="Notes"
// // // // // // // // // //                 name="notes"
// // // // // // // // // //                 value={formData.notes}
// // // // // // // // // //                 onChange={handleInputChange}
// // // // // // // // // //                 multiline
// // // // // // // // // //                 rows={3}
// // // // // // // // // //               />
// // // // // // // // // //             </Grid>
// // // // // // // // // //           </Grid>
// // // // // // // // // //         </DialogContent>
// // // // // // // // // //         <DialogActions>
// // // // // // // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // // // // // // //           <Button onClick={handleSubmit} variant="contained" color="primary">
// // // // // // // // // //             {selectedInterview ? "Update" : "Schedule"}
// // // // // // // // // //           </Button>
// // // // // // // // // //         </DialogActions>
// // // // // // // // // //       </Dialog>

// // // // // // // // // //       {/* Video Call Dialog */}
// // // // // // // // // //       <Dialog
// // // // // // // // // //         open={openVideoCall}
// // // // // // // // // //         onClose={handleCloseVideoCall}
// // // // // // // // // //         maxWidth="md"
// // // // // // // // // //         fullWidth
// // // // // // // // // //       >
// // // // // // // // // //         <DialogTitle>
// // // // // // // // // //           {selectedInterview && (
// // // // // // // // // //             <Box
// // // // // // // // // //               sx={{
// // // // // // // // // //                 display: "flex",
// // // // // // // // // //                 alignItems: "center",
// // // // // // // // // //                 justifyContent: "space-between",
// // // // // // // // // //               }}
// // // // // // // // // //             >
// // // // // // // // // //               <Typography variant="h6">
// // // // // // // // // //                 Interview with {selectedInterview.candidateName}
// // // // // // // // // //               </Typography>
// // // // // // // // // //               <Chip
// // // // // // // // // //                 label="Live"
// // // // // // // // // //                 color="error"
// // // // // // // // // //                 size="small"
// // // // // // // // // //                 sx={{
// // // // // // // // // //                   "&::after": {
// // // // // // // // // //                     content: '""',
// // // // // // // // // //                     position: "absolute",
// // // // // // // // // //                     top: "50%",
// // // // // // // // // //                     left: "10px",
// // // // // // // // // //                     transform: "translateY(-50%)",
// // // // // // // // // //                     width: "8px",
// // // // // // // // // //                     height: "8px",
// // // // // // // // // //                     borderRadius: "50%",
// // // // // // // // // //                     backgroundColor: "error.main",
// // // // // // // // // //                     animation: "pulse 1.5s infinite",
// // // // // // // // // //                   },
// // // // // // // // // //                   pl: 3,
// // // // // // // // // //                   position: "relative",
// // // // // // // // // //                 }}
// // // // // // // // // //               />
// // // // // // // // // //             </Box>
// // // // // // // // // //           )}
// // // // // // // // // //         </DialogTitle>
// // // // // // // // // //         <DialogContent>
// // // // // // // // // //           <Box
// // // // // // // // // //             sx={{
// // // // // // // // // //               height: 400,
// // // // // // // // // //               bgcolor: "black",
// // // // // // // // // //               display: "flex",
// // // // // // // // // //               alignItems: "center",
// // // // // // // // // //               justifyContent: "center",
// // // // // // // // // //               position: "relative",
// // // // // // // // // //               mb: 2,
// // // // // // // // // //             }}
// // // // // // // // // //           >
// // // // // // // // // //             {selectedInterview && (
// // // // // // // // // //               <>
// // // // // // // // // //                 <Box
// // // // // // // // // //                   component="img"
// // // // // // // // // //                   src={selectedInterview.avatar}
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     width: "100%",
// // // // // // // // // //                     height: "100%",
// // // // // // // // // //                     objectFit: "cover",
// // // // // // // // // //                     opacity: 0.7,
// // // // // // // // // //                   }}
// // // // // // // // // //                 />
// // // // // // // // // //                 <Typography
// // // // // // // // // //                   variant="h5"
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     position: "absolute",
// // // // // // // // // //                     color: "white",
// // // // // // // // // //                     textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
// // // // // // // // // //                   }}
// // // // // // // // // //                 >
// // // // // // // // // //                   Video call simulation
// // // // // // // // // //                 </Typography>
// // // // // // // // // //                 <Box
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     position: "absolute",
// // // // // // // // // //                     bottom: 16,
// // // // // // // // // //                     right: 16,
// // // // // // // // // //                     width: 150,
// // // // // // // // // //                     height: 100,
// // // // // // // // // //                     bgcolor: "grey.800",
// // // // // // // // // //                     border: "2px solid white",
// // // // // // // // // //                     borderRadius: 1,
// // // // // // // // // //                     backgroundImage:
// // // // // // // // // //                       "url(https://randomuser.me/api/portraits/men/85.jpg)",
// // // // // // // // // //                     backgroundSize: "cover",
// // // // // // // // // //                   }}
// // // // // // // // // //                 />
// // // // // // // // // //               </>
// // // // // // // // // //             )}
// // // // // // // // // //           </Box>

// // // // // // // // // //           <Paper sx={{ p: 2, mb: 2 }}>
// // // // // // // // // //             <Typography variant="subtitle2" gutterBottom>
// // // // // // // // // //               Interview Questions
// // // // // // // // // //             </Typography>
// // // // // // // // // //             <List dense>
// // // // // // // // // //               <ListItem>
// // // // // // // // // //                 <ListItemText primary="1. Tell me about your experience with AI/ML technologies." />
// // // // // // // // // //               </ListItem>
// // // // // // // // // //               <ListItem>
// // // // // // // // // //                 <ListItemText primary="2. How have you implemented NLP in your previous projects?" />
// // // // // // // // // //               </ListItem>
// // // // // // // // // //               <ListItem>
// // // // // // // // // //                 <ListItemText primary="3. What challenges did you face in your last project and how did you overcome them?" />
// // // // // // // // // //               </ListItem>
// // // // // // // // // //               <ListItem>
// // // // // // // // // //                 <ListItemText primary="4. How do you stay updated with the latest AI research?" />
// // // // // // // // // //               </ListItem>
// // // // // // // // // //             </List>
// // // // // // // // // //           </Paper>
// // // // // // // // // //         </DialogContent>
// // // // // // // // // //         <DialogActions>
// // // // // // // // // //           <Button
// // // // // // // // // //             variant="contained"
// // // // // // // // // //             color="error"
// // // // // // // // // //             onClick={handleCloseVideoCall}
// // // // // // // // // //           >
// // // // // // // // // //             End Interview
// // // // // // // // // //           </Button>
// // // // // // // // // //         </DialogActions>
// // // // // // // // // //       </Dialog>
// // // // // // // // // //     </Layout>
// // // // // // // // // //   );
// // // // // // // // // // }

// // // // // // // // // // export default InterviewScheduler;

// // // // // // // // //

// // // // // // // // // import { useState } from "react";
// // // // // // // // // import {
// // // // // // // // //   Box,
// // // // // // // // //   Typography,
// // // // // // // // //   Grid,
// // // // // // // // //   Card,
// // // // // // // // //   CardContent,
// // // // // // // // //   Button,
// // // // // // // // //   TextField,
// // // // // // // // //   FormControl,
// // // // // // // // //   InputLabel,
// // // // // // // // //   Select,
// // // // // // // // //   MenuItem,
// // // // // // // // //   Dialog,
// // // // // // // // //   DialogTitle,
// // // // // // // // //   DialogContent,
// // // // // // // // //   DialogActions,
// // // // // // // // //   IconButton,
// // // // // // // // //   Chip,
// // // // // // // // //   Tabs,
// // // // // // // // //   Tab,
// // // // // // // // //   Paper,
// // // // // // // // //   Divider,
// // // // // // // // //   Avatar,
// // // // // // // // //   List,
// // // // // // // // //   ListItem,
// // // // // // // // //   ListItemText,
// // // // // // // // //   Badge,
// // // // // // // // //   Alert,
// // // // // // // // //   LinearProgress,
// // // // // // // // // } from "@mui/material";
// // // // // // // // // import {
// // // // // // // // //   Add as AddIcon,
// // // // // // // // //   VideoCall as VideoCallIcon,
// // // // // // // // //   Call as CallIcon,
// // // // // // // // //   CalendarMonth as CalendarIcon,
// // // // // // // // //   Delete as DeleteIcon,
// // // // // // // // //   Edit as EditIcon,
// // // // // // // // //   Schedule as ScheduleIcon,
// // // // // // // // //   Person as PersonIcon,
// // // // // // // // //   AccessTime as TimeIcon,
// // // // // // // // //   CheckCircle as CheckCircleIcon,
// // // // // // // // //   Cancel as CancelIcon,
// // // // // // // // //   Notifications as NotificationsIcon,
// // // // // // // // // } from "@mui/icons-material";
// // // // // // // // // import Layout from "../common/Layout";
// // // // // // // // // import { useNotification } from "../../context/NotificationContext";

// // // // // // // // // // Mock data for interviews
// // // // // // // // // const mockInterviews = [
// // // // // // // // //   {
// // // // // // // // //     id: 1,
// // // // // // // // //     candidateName: "Priya Sharma",
// // // // // // // // //     candidateId: "T1001",
// // // // // // // // //     position: "AI Engineer",
// // // // // // // // //     date: "2024-01-20",
// // // // // // // // //     time: "10:00",
// // // // // // // // //     type: "video",
// // // // // // // // //     status: "scheduled",
// // // // // // // // //     notes: "Focus on NLP experience and TensorFlow projects",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "TensorFlow", "NLP"],
// // // // // // // // //     experience: "5 years",
// // // // // // // // //     location: "Bangalore",
// // // // // // // // //   },
// // // // // // // // //   {
// // // // // // // // //     id: 2,
// // // // // // // // //     candidateName: "Rahul Verma",
// // // // // // // // //     candidateId: "T1002",
// // // // // // // // //     position: "ML Specialist",
// // // // // // // // //     date: "2024-01-21",
// // // // // // // // //     time: "14:30",
// // // // // // // // //     type: "audio",
// // // // // // // // //     status: "scheduled",
// // // // // // // // //     notes: "Check AWS certification and cloud experience",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "AWS", "Machine Learning"],
// // // // // // // // //     experience: "4 years",
// // // // // // // // //     location: "Mumbai",
// // // // // // // // //   },
// // // // // // // // //   {
// // // // // // // // //     id: 3,
// // // // // // // // //     candidateName: "Ananya Patel",
// // // // // // // // //     candidateId: "T1003",
// // // // // // // // //     position: "Data Scientist",
// // // // // // // // //     date: "2024-01-18",
// // // // // // // // //     time: "11:00",
// // // // // // // // //     type: "video",
// // // // // // // // //     status: "completed",
// // // // // // // // //     notes: "Strong Python skills, excellent analytical thinking",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "R", "Statistics"],
// // // // // // // // //     experience: "6 years",
// // // // // // // // //     location: "Pune",
// // // // // // // // //     rating: 4.5,
// // // // // // // // //     feedback: "Excellent candidate with strong technical skills",
// // // // // // // // //   },
// // // // // // // // //   {
// // // // // // // // //     id: 4,
// // // // // // // // //     candidateName: "Vikram Singh",
// // // // // // // // //     candidateId: "T1004",
// // // // // // // // //     position: "NLP Engineer",
// // // // // // // // //     date: "2024-01-17",
// // // // // // // // //     time: "15:00",
// // // // // // // // //     type: "video",
// // // // // // // // //     status: "completed",
// // // // // // // // //     notes:
// // // // // // // // //       "Good understanding of transformers, needs more practical experience",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "NLP", "Transformers"],
// // // // // // // // //     experience: "3 years",
// // // // // // // // //     location: "Delhi",
// // // // // // // // //     rating: 3.5,
// // // // // // // // //     feedback: "Good potential, needs more hands-on experience",
// // // // // // // // //   },
// // // // // // // // // ];

// // // // // // // // // // Mock data for candidates
// // // // // // // // // const mockCandidates = [
// // // // // // // // //   {
// // // // // // // // //     id: "T1005",
// // // // // // // // //     name: "Neha Gupta",
// // // // // // // // //     position: "Computer Vision Specialist",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "OpenCV", "Deep Learning"],
// // // // // // // // //     experience: "4 years",
// // // // // // // // //     location: "Chennai",
// // // // // // // // //   },
// // // // // // // // //   {
// // // // // // // // //     id: "T1006",
// // // // // // // // //     name: "Arjun Kumar",
// // // // // // // // //     position: "AI Research Scientist",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "Research", "Publications"],
// // // // // // // // //     experience: "7 years",
// // // // // // // // //     location: "Hyderabad",
// // // // // // // // //   },
// // // // // // // // //   {
// // // // // // // // //     id: "T1007",
// // // // // // // // //     name: "Kavya Reddy",
// // // // // // // // //     position: "MLOps Engineer",
// // // // // // // // //     avatar: "/placeholder.svg?height=40&width=40",
// // // // // // // // //     skills: ["Python", "Docker", "Kubernetes"],
// // // // // // // // //     experience: "5 years",
// // // // // // // // //     location: "Bangalore",
// // // // // // // // //   },
// // // // // // // // // ];

// // // // // // // // // function InterviewScheduler() {
// // // // // // // // //   const { showNotification } = useNotification();
// // // // // // // // //   const [interviews, setInterviews] = useState(mockInterviews);
// // // // // // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // // // // // //   const [tabValue, setTabValue] = useState(0);
// // // // // // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // // // // // //   const [openVideoCall, setOpenVideoCall] = useState(false);
// // // // // // // // //   const [interviewInProgress, setInterviewInProgress] = useState(false);

// // // // // // // // //   // Form state
// // // // // // // // //   const [formData, setFormData] = useState({
// // // // // // // // //     candidateId: "",
// // // // // // // // //     position: "",
// // // // // // // // //     date: "",
// // // // // // // // //     time: "",
// // // // // // // // //     type: "video",
// // // // // // // // //     notes: "",
// // // // // // // // //   });

// // // // // // // // //   // Filter interviews based on tab
// // // // // // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // // // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // // // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // // // // // //     if (tabValue === 2) return interview.status === "cancelled";
// // // // // // // // //     return true;
// // // // // // // // //   });

// // // // // // // // //   const handleTabChange = (event, newValue) => {
// // // // // // // // //     setTabValue(newValue);
// // // // // // // // //   };

// // // // // // // // //   const handleOpenDialog = (interview = null) => {
// // // // // // // // //     if (interview) {
// // // // // // // // //       setSelectedInterview(interview);
// // // // // // // // //       setFormData({
// // // // // // // // //         candidateId: interview.candidateId,
// // // // // // // // //         position: interview.position,
// // // // // // // // //         date: interview.date,
// // // // // // // // //         time: interview.time,
// // // // // // // // //         type: interview.type,
// // // // // // // // //         notes: interview.notes,
// // // // // // // // //       });
// // // // // // // // //     } else {
// // // // // // // // //       setSelectedInterview(null);
// // // // // // // // //       setFormData({
// // // // // // // // //         candidateId: "",
// // // // // // // // //         position: "",
// // // // // // // // //         date: "",
// // // // // // // // //         time: "",
// // // // // // // // //         type: "video",
// // // // // // // // //         notes: "",
// // // // // // // // //       });
// // // // // // // // //     }
// // // // // // // // //     setOpenDialog(true);
// // // // // // // // //   };

// // // // // // // // //   const handleCloseDialog = () => {
// // // // // // // // //     setOpenDialog(false);
// // // // // // // // //   };

// // // // // // // // //   const handleInputChange = (e) => {
// // // // // // // // //     const { name, value } = e.target;
// // // // // // // // //     setFormData({
// // // // // // // // //       ...formData,
// // // // // // // // //       [name]: value,
// // // // // // // // //     });
// // // // // // // // //   };

// // // // // // // // //   const handleSubmit = () => {
// // // // // // // // //     const candidate = mockCandidates.find((c) => c.id === formData.candidateId);

// // // // // // // // //     if (selectedInterview) {
// // // // // // // // //       // Update existing interview
// // // // // // // // //       const updatedInterviews = interviews.map((interview) =>
// // // // // // // // //         interview.id === selectedInterview.id
// // // // // // // // //           ? {
// // // // // // // // //               ...interview,
// // // // // // // // //               candidateName: candidate?.name || selectedInterview.candidateName,
// // // // // // // // //               position: formData.position,
// // // // // // // // //               date: formData.date,
// // // // // // // // //               time: formData.time,
// // // // // // // // //               type: formData.type,
// // // // // // // // //               notes: formData.notes,
// // // // // // // // //               avatar: candidate?.avatar || selectedInterview.avatar,
// // // // // // // // //               skills: candidate?.skills || selectedInterview.skills,
// // // // // // // // //               experience: candidate?.experience || selectedInterview.experience,
// // // // // // // // //               location: candidate?.location || selectedInterview.location,
// // // // // // // // //             }
// // // // // // // // //           : interview
// // // // // // // // //       );
// // // // // // // // //       setInterviews(updatedInterviews);
// // // // // // // // //       showNotification("Interview updated successfully!", "success");
// // // // // // // // //     } else {
// // // // // // // // //       // Add new interview
// // // // // // // // //       const newInterview = {
// // // // // // // // //         id: interviews.length + 1,
// // // // // // // // //         candidateName: candidate?.name || "Unknown Candidate",
// // // // // // // // //         candidateId: formData.candidateId,
// // // // // // // // //         position: formData.position,
// // // // // // // // //         date: formData.date,
// // // // // // // // //         time: formData.time,
// // // // // // // // //         type: formData.type,
// // // // // // // // //         status: "scheduled",
// // // // // // // // //         notes: formData.notes,
// // // // // // // // //         avatar: candidate?.avatar || "/placeholder.svg?height=40&width=40",
// // // // // // // // //         skills: candidate?.skills || [],
// // // // // // // // //         experience: candidate?.experience || "N/A",
// // // // // // // // //         location: candidate?.location || "N/A",
// // // // // // // // //       };
// // // // // // // // //       setInterviews([...interviews, newInterview]);
// // // // // // // // //       showNotification("Interview scheduled successfully!", "success");
// // // // // // // // //     }

// // // // // // // // //     handleCloseDialog();
// // // // // // // // //   };

// // // // // // // // //   const handleDeleteInterview = (id) => {
// // // // // // // // //     setInterviews(interviews.filter((interview) => interview.id !== id));
// // // // // // // // //     showNotification("Interview deleted successfully!", "success");
// // // // // // // // //   };

// // // // // // // // //   const handleCancelInterview = (id) => {
// // // // // // // // //     const updatedInterviews = interviews.map((interview) =>
// // // // // // // // //       interview.id === id ? { ...interview, status: "cancelled" } : interview
// // // // // // // // //     );
// // // // // // // // //     setInterviews(updatedInterviews);
// // // // // // // // //     showNotification("Interview cancelled", "warning");
// // // // // // // // //   };

// // // // // // // // //   const handleStartInterview = (interview) => {
// // // // // // // // //     setSelectedInterview(interview);
// // // // // // // // //     setOpenVideoCall(true);
// // // // // // // // //     setInterviewInProgress(true);
// // // // // // // // //   };

// // // // // // // // //   const handleEndInterview = () => {
// // // // // // // // //     setInterviewInProgress(false);
// // // // // // // // //     setOpenVideoCall(false);

// // // // // // // // //     // Mark interview as completed
// // // // // // // // //     if (selectedInterview) {
// // // // // // // // //       const updatedInterviews = interviews.map((interview) =>
// // // // // // // // //         interview.id === selectedInterview.id
// // // // // // // // //           ? {
// // // // // // // // //               ...interview,
// // // // // // // // //               status: "completed",
// // // // // // // // //               rating: 4.0,
// // // // // // // // //               feedback: "Interview completed successfully",
// // // // // // // // //             }
// // // // // // // // //           : interview
// // // // // // // // //       );
// // // // // // // // //       setInterviews(updatedInterviews);
// // // // // // // // //       showNotification("Interview completed successfully!", "success");
// // // // // // // // //     }
// // // // // // // // //   };

// // // // // // // // //   const getStatusColor = (status) => {
// // // // // // // // //     switch (status) {
// // // // // // // // //       case "scheduled":
// // // // // // // // //         return "primary";
// // // // // // // // //       case "completed":
// // // // // // // // //         return "success";
// // // // // // // // //       case "cancelled":
// // // // // // // // //         return "error";
// // // // // // // // //       default:
// // // // // // // // //         return "default";
// // // // // // // // //     }
// // // // // // // // //   };

// // // // // // // // //   const upcomingInterviews = interviews.filter(
// // // // // // // // //     (i) => i.status === "scheduled"
// // // // // // // // //   ).length;
// // // // // // // // //   const completedInterviews = interviews.filter(
// // // // // // // // //     (i) => i.status === "completed"
// // // // // // // // //   ).length;
// // // // // // // // //   const todayInterviews = interviews.filter(
// // // // // // // // //     (i) =>
// // // // // // // // //       i.status === "scheduled" &&
// // // // // // // // //       i.date === new Date().toISOString().split("T")[0]
// // // // // // // // //   ).length;

// // // // // // // // //   return (
// // // // // // // // //     <Layout title="Interview Scheduler">
// // // // // // // // //       <Box sx={{ p: 3 }}>
// // // // // // // // //         {/* Header with Stats */}
// // // // // // // // //         <Grid container spacing={3} sx={{ mb: 3 }}>
// // // // // // // // //           <Grid item xs={12} md={8}>
// // // // // // // // //             <Box
// // // // // // // // //               sx={{
// // // // // // // // //                 display: "flex",
// // // // // // // // //                 justifyContent: "space-between",
// // // // // // // // //                 alignItems: "center",
// // // // // // // // //               }}
// // // // // // // // //             >
// // // // // // // // //               <Typography variant="h4" component="h1" fontWeight="bold">
// // // // // // // // //                 Interview Management
// // // // // // // // //               </Typography>
// // // // // // // // //               <Button
// // // // // // // // //                 variant="contained"
// // // // // // // // //                 startIcon={<AddIcon />}
// // // // // // // // //                 onClick={() => handleOpenDialog()}
// // // // // // // // //                 size="large"
// // // // // // // // //               >
// // // // // // // // //                 Schedule Interview
// // // // // // // // //               </Button>
// // // // // // // // //             </Box>
// // // // // // // // //           </Grid>
// // // // // // // // //           <Grid item xs={12} md={4}>
// // // // // // // // //             <Grid container spacing={2}>
// // // // // // // // //               <Grid item xs={4}>
// // // // // // // // //                 <Card sx={{ textAlign: "center", bgcolor: "primary.50" }}>
// // // // // // // // //                   <CardContent sx={{ py: 2 }}>
// // // // // // // // //                     <Typography variant="h4" color="primary">
// // // // // // // // //                       {upcomingInterviews}
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Typography variant="caption">Upcoming</Typography>
// // // // // // // // //                   </CardContent>
// // // // // // // // //                 </Card>
// // // // // // // // //               </Grid>
// // // // // // // // //               <Grid item xs={4}>
// // // // // // // // //                 <Card sx={{ textAlign: "center", bgcolor: "success.50" }}>
// // // // // // // // //                   <CardContent sx={{ py: 2 }}>
// // // // // // // // //                     <Typography variant="h4" color="success.main">
// // // // // // // // //                       {completedInterviews}
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Typography variant="caption">Completed</Typography>
// // // // // // // // //                   </CardContent>
// // // // // // // // //                 </Card>
// // // // // // // // //               </Grid>
// // // // // // // // //               <Grid item xs={4}>
// // // // // // // // //                 <Card sx={{ textAlign: "center", bgcolor: "warning.50" }}>
// // // // // // // // //                   <CardContent sx={{ py: 2 }}>
// // // // // // // // //                     <Typography variant="h4" color="warning.main">
// // // // // // // // //                       {todayInterviews}
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Typography variant="caption">Today</Typography>
// // // // // // // // //                   </CardContent>
// // // // // // // // //                 </Card>
// // // // // // // // //               </Grid>
// // // // // // // // //             </Grid>
// // // // // // // // //           </Grid>
// // // // // // // // //         </Grid>

// // // // // // // // //         {/* Today's Schedule Alert */}
// // // // // // // // //         {todayInterviews > 0 && (
// // // // // // // // //           <Alert severity="info" sx={{ mb: 3 }} icon={<NotificationsIcon />}>
// // // // // // // // //             You have {todayInterviews} interview{todayInterviews > 1 ? "s" : ""}{" "}
// // // // // // // // //             scheduled for today!
// // // // // // // // //           </Alert>
// // // // // // // // //         )}

// // // // // // // // //         {/* Tabs */}
// // // // // // // // //         <Paper sx={{ mb: 3 }}>
// // // // // // // // //           <Tabs
// // // // // // // // //             value={tabValue}
// // // // // // // // //             onChange={handleTabChange}
// // // // // // // // //             indicatorColor="primary"
// // // // // // // // //             textColor="primary"
// // // // // // // // //             centered
// // // // // // // // //           >
// // // // // // // // //             <Tab
// // // // // // // // //               icon={<Badge badgeContent={upcomingInterviews} color="primary" />}
// // // // // // // // //               label="Scheduled"
// // // // // // // // //               iconPosition="start"
// // // // // // // // //             />
// // // // // // // // //             <Tab
// // // // // // // // //               icon={
// // // // // // // // //                 <Badge badgeContent={completedInterviews} color="success" />
// // // // // // // // //               }
// // // // // // // // //               label="Completed"
// // // // // // // // //               iconPosition="start"
// // // // // // // // //             />
// // // // // // // // //             <Tab icon={<CancelIcon />} label="Cancelled" iconPosition="start" />
// // // // // // // // //           </Tabs>
// // // // // // // // //         </Paper>

// // // // // // // // //         {/* Interview Cards */}
// // // // // // // // //         <Grid container spacing={3}>
// // // // // // // // //           {filteredInterviews.length > 0 ? (
// // // // // // // // //             filteredInterviews.map((interview) => (
// // // // // // // // //               <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // // // // // //                 <Card
// // // // // // // // //                   sx={{
// // // // // // // // //                     height: "100%",
// // // // // // // // //                     transition: "all 0.3s ease",
// // // // // // // // //                     "&:hover": {
// // // // // // // // //                       transform: "translateY(-4px)",
// // // // // // // // //                       boxShadow: 4,
// // // // // // // // //                     },
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <CardContent>
// // // // // // // // //                     {/* Header */}
// // // // // // // // //                     <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // // // // // //                       <Avatar
// // // // // // // // //                         src={interview.avatar}
// // // // // // // // //                         sx={{ width: 56, height: 56, mr: 2 }}
// // // // // // // // //                       />
// // // // // // // // //                       <Box sx={{ flexGrow: 1 }}>
// // // // // // // // //                         <Typography variant="h6" fontWeight="bold">
// // // // // // // // //                           {interview.candidateName}
// // // // // // // // //                         </Typography>
// // // // // // // // //                         <Typography variant="body2" color="textSecondary">
// // // // // // // // //                           {interview.position}
// // // // // // // // //                         </Typography>
// // // // // // // // //                         <Typography variant="caption" color="textSecondary">
// // // // // // // // //                           {interview.experience} • {interview.location}
// // // // // // // // //                         </Typography>
// // // // // // // // //                       </Box>
// // // // // // // // //                       <Chip
// // // // // // // // //                         label={interview.status}
// // // // // // // // //                         color={getStatusColor(interview.status)}
// // // // // // // // //                         size="small"
// // // // // // // // //                         variant="outlined"
// // // // // // // // //                       />
// // // // // // // // //                     </Box>

// // // // // // // // //                     <Divider sx={{ my: 2 }} />

// // // // // // // // //                     {/* Interview Details */}
// // // // // // // // //                     <Box sx={{ mb: 2 }}>
// // // // // // // // //                       <Box
// // // // // // // // //                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
// // // // // // // // //                       >
// // // // // // // // //                         <CalendarIcon
// // // // // // // // //                           sx={{ mr: 1, color: "primary.main", fontSize: 20 }}
// // // // // // // // //                         />
// // // // // // // // //                         <Typography variant="body2">
// // // // // // // // //                           {new Date(interview.date).toLocaleDateString(
// // // // // // // // //                             "en-US",
// // // // // // // // //                             {
// // // // // // // // //                               weekday: "short",
// // // // // // // // //                               month: "short",
// // // // // // // // //                               day: "numeric",
// // // // // // // // //                               year: "numeric",
// // // // // // // // //                             }
// // // // // // // // //                           )}
// // // // // // // // //                         </Typography>
// // // // // // // // //                       </Box>

// // // // // // // // //                       <Box
// // // // // // // // //                         sx={{ display: "flex", alignItems: "center", mb: 1 }}
// // // // // // // // //                       >
// // // // // // // // //                         <TimeIcon
// // // // // // // // //                           sx={{ mr: 1, color: "primary.main", fontSize: 20 }}
// // // // // // // // //                         />
// // // // // // // // //                         <Typography variant="body2">
// // // // // // // // //                           {interview.time}
// // // // // // // // //                         </Typography>
// // // // // // // // //                       </Box>

// // // // // // // // //                       <Box
// // // // // // // // //                         sx={{ display: "flex", alignItems: "center", mb: 2 }}
// // // // // // // // //                       >
// // // // // // // // //                         {interview.type === "video" ? (
// // // // // // // // //                           <VideoCallIcon
// // // // // // // // //                             sx={{ mr: 1, color: "primary.main", fontSize: 20 }}
// // // // // // // // //                           />
// // // // // // // // //                         ) : (
// // // // // // // // //                           <CallIcon
// // // // // // // // //                             sx={{ mr: 1, color: "primary.main", fontSize: 20 }}
// // // // // // // // //                           />
// // // // // // // // //                         )}
// // // // // // // // //                         <Typography variant="body2">
// // // // // // // // //                           {interview.type === "video"
// // // // // // // // //                             ? "Video Interview"
// // // // // // // // //                             : "Audio Interview"}
// // // // // // // // //                         </Typography>
// // // // // // // // //                       </Box>

// // // // // // // // //                       {/* Skills */}
// // // // // // // // //                       <Box sx={{ mb: 2 }}>
// // // // // // // // //                         <Typography
// // // // // // // // //                           variant="caption"
// // // // // // // // //                           color="textSecondary"
// // // // // // // // //                           gutterBottom
// // // // // // // // //                         >
// // // // // // // // //                           Skills:
// // // // // // // // //                         </Typography>
// // // // // // // // //                         <Box
// // // // // // // // //                           sx={{
// // // // // // // // //                             display: "flex",
// // // // // // // // //                             flexWrap: "wrap",
// // // // // // // // //                             gap: 0.5,
// // // // // // // // //                             mt: 0.5,
// // // // // // // // //                           }}
// // // // // // // // //                         >
// // // // // // // // //                           {interview.skills?.map((skill, index) => (
// // // // // // // // //                             <Chip
// // // // // // // // //                               key={index}
// // // // // // // // //                               label={skill}
// // // // // // // // //                               size="small"
// // // // // // // // //                               variant="outlined"
// // // // // // // // //                             />
// // // // // // // // //                           ))}
// // // // // // // // //                         </Box>
// // // // // // // // //                       </Box>

// // // // // // // // //                       {/* Notes */}
// // // // // // // // //                       {interview.notes && (
// // // // // // // // //                         <Box sx={{ mb: 2 }}>
// // // // // // // // //                           <Typography
// // // // // // // // //                             variant="caption"
// // // // // // // // //                             color="textSecondary"
// // // // // // // // //                             gutterBottom
// // // // // // // // //                           >
// // // // // // // // //                             Notes:
// // // // // // // // //                           </Typography>
// // // // // // // // //                           <Typography
// // // // // // // // //                             variant="body2"
// // // // // // // // //                             sx={{ fontStyle: "italic" }}
// // // // // // // // //                           >
// // // // // // // // //                             {interview.notes}
// // // // // // // // //                           </Typography>
// // // // // // // // //                         </Box>
// // // // // // // // //                       )}

// // // // // // // // //                       {/* Rating for completed interviews */}
// // // // // // // // //                       {interview.status === "completed" && interview.rating && (
// // // // // // // // //                         <Box sx={{ mb: 2 }}>
// // // // // // // // //                           <Typography
// // // // // // // // //                             variant="caption"
// // // // // // // // //                             color="textSecondary"
// // // // // // // // //                             gutterBottom
// // // // // // // // //                           >
// // // // // // // // //                             Rating:
// // // // // // // // //                           </Typography>
// // // // // // // // //                           <Box
// // // // // // // // //                             sx={{
// // // // // // // // //                               display: "flex",
// // // // // // // // //                               alignItems: "center",
// // // // // // // // //                               gap: 1,
// // // // // // // // //                             }}
// // // // // // // // //                           >
// // // // // // // // //                             <Typography variant="body2" fontWeight="bold">
// // // // // // // // //                               {interview.rating}/5.0
// // // // // // // // //                             </Typography>
// // // // // // // // //                             <Box sx={{ display: "flex" }}>
// // // // // // // // //                               {[...Array(5)].map((_, i) => (
// // // // // // // // //                                 <Box
// // // // // // // // //                                   key={i}
// // // // // // // // //                                   sx={{
// // // // // // // // //                                     width: 16,
// // // // // // // // //                                     height: 16,
// // // // // // // // //                                     borderRadius: "50%",
// // // // // // // // //                                     bgcolor:
// // // // // // // // //                                       i < Math.floor(interview.rating)
// // // // // // // // //                                         ? "warning.main"
// // // // // // // // //                                         : "grey.300",
// // // // // // // // //                                     mr: 0.5,
// // // // // // // // //                                   }}
// // // // // // // // //                                 />
// // // // // // // // //                               ))}
// // // // // // // // //                             </Box>
// // // // // // // // //                           </Box>
// // // // // // // // //                         </Box>
// // // // // // // // //                       )}
// // // // // // // // //                     </Box>

// // // // // // // // //                     {/* Actions */}
// // // // // // // // //                     <Box
// // // // // // // // //                       sx={{
// // // // // // // // //                         display: "flex",
// // // // // // // // //                         justifyContent: "space-between",
// // // // // // // // //                         alignItems: "center",
// // // // // // // // //                       }}
// // // // // // // // //                     >
// // // // // // // // //                       {interview.status === "scheduled" ? (
// // // // // // // // //                         <Button
// // // // // // // // //                           variant="contained"
// // // // // // // // //                           startIcon={
// // // // // // // // //                             interview.type === "video" ? (
// // // // // // // // //                               <VideoCallIcon />
// // // // // // // // //                             ) : (
// // // // // // // // //                               <CallIcon />
// // // // // // // // //                             )
// // // // // // // // //                           }
// // // // // // // // //                           onClick={() => handleStartInterview(interview)}
// // // // // // // // //                           size="small"
// // // // // // // // //                         >
// // // // // // // // //                           Start Interview
// // // // // // // // //                         </Button>
// // // // // // // // //                       ) : interview.status === "completed" ? (
// // // // // // // // //                         <Chip
// // // // // // // // //                           icon={<CheckCircleIcon />}
// // // // // // // // //                           label="Completed"
// // // // // // // // //                           color="success"
// // // // // // // // //                           variant="outlined"
// // // // // // // // //                         />
// // // // // // // // //                       ) : (
// // // // // // // // //                         <Chip
// // // // // // // // //                           icon={<CancelIcon />}
// // // // // // // // //                           label="Cancelled"
// // // // // // // // //                           color="error"
// // // // // // // // //                           variant="outlined"
// // // // // // // // //                         />
// // // // // // // // //                       )}

// // // // // // // // //                       <Box>
// // // // // // // // //                         {interview.status === "scheduled" && (
// // // // // // // // //                           <IconButton
// // // // // // // // //                             size="small"
// // // // // // // // //                             onClick={() => handleCancelInterview(interview.id)}
// // // // // // // // //                             color="warning"
// // // // // // // // //                           >
// // // // // // // // //                             <CancelIcon />
// // // // // // // // //                           </IconButton>
// // // // // // // // //                         )}
// // // // // // // // //                         <IconButton
// // // // // // // // //                           size="small"
// // // // // // // // //                           onClick={() => handleOpenDialog(interview)}
// // // // // // // // //                         >
// // // // // // // // //                           <EditIcon />
// // // // // // // // //                         </IconButton>
// // // // // // // // //                         <IconButton
// // // // // // // // //                           size="small"
// // // // // // // // //                           color="error"
// // // // // // // // //                           onClick={() => handleDeleteInterview(interview.id)}
// // // // // // // // //                         >
// // // // // // // // //                           <DeleteIcon />
// // // // // // // // //                         </IconButton>
// // // // // // // // //                       </Box>
// // // // // // // // //                     </Box>
// // // // // // // // //                   </CardContent>
// // // // // // // // //                 </Card>
// // // // // // // // //               </Grid>
// // // // // // // // //             ))
// // // // // // // // //           ) : (
// // // // // // // // //             <Grid item xs={12}>
// // // // // // // // //               <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // // // // //                 <ScheduleIcon
// // // // // // // // //                   sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
// // // // // // // // //                 />
// // // // // // // // //                 <Typography variant="h6" color="textSecondary" gutterBottom>
// // // // // // // // //                   No{" "}
// // // // // // // // //                   {tabValue === 0
// // // // // // // // //                     ? "scheduled"
// // // // // // // // //                     : tabValue === 1
// // // // // // // // //                     ? "completed"
// // // // // // // // //                     : "cancelled"}{" "}
// // // // // // // // //                   interviews found
// // // // // // // // //                 </Typography>
// // // // // // // // //                 <Typography
// // // // // // // // //                   variant="body2"
// // // // // // // // //                   color="textSecondary"
// // // // // // // // //                   sx={{ mb: 2 }}
// // // // // // // // //                 >
// // // // // // // // //                   {tabValue === 0 &&
// // // // // // // // //                     "Schedule your first interview to get started"}
// // // // // // // // //                 </Typography>
// // // // // // // // //                 {tabValue === 0 && (
// // // // // // // // //                   <Button
// // // // // // // // //                     variant="contained"
// // // // // // // // //                     startIcon={<AddIcon />}
// // // // // // // // //                     onClick={() => handleOpenDialog()}
// // // // // // // // //                   >
// // // // // // // // //                     Schedule Interview
// // // // // // // // //                   </Button>
// // // // // // // // //                 )}
// // // // // // // // //               </Paper>
// // // // // // // // //             </Grid>
// // // // // // // // //           )}
// // // // // // // // //         </Grid>
// // // // // // // // //       </Box>

// // // // // // // // //       {/* Schedule Interview Dialog */}
// // // // // // // // //       <Dialog
// // // // // // // // //         open={openDialog}
// // // // // // // // //         onClose={handleCloseDialog}
// // // // // // // // //         maxWidth="md"
// // // // // // // // //         fullWidth
// // // // // // // // //       >
// // // // // // // // //         <DialogTitle>
// // // // // // // // //           <Typography variant="h6">
// // // // // // // // //             {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // // // // // //           </Typography>
// // // // // // // // //         </DialogTitle>
// // // // // // // // //         <DialogContent>
// // // // // // // // //           <Grid container spacing={3} sx={{ mt: 1 }}>
// // // // // // // // //             <Grid item xs={12}>
// // // // // // // // //               <FormControl fullWidth>
// // // // // // // // //                 <InputLabel>Candidate</InputLabel>
// // // // // // // // //                 <Select
// // // // // // // // //                   name="candidateId"
// // // // // // // // //                   value={formData.candidateId}
// // // // // // // // //                   onChange={handleInputChange}
// // // // // // // // //                   label="Candidate"
// // // // // // // // //                 >
// // // // // // // // //                   {mockCandidates.map((candidate) => (
// // // // // // // // //                     <MenuItem key={candidate.id} value={candidate.id}>
// // // // // // // // //                       <Box
// // // // // // // // //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
// // // // // // // // //                       >
// // // // // // // // //                         <Avatar
// // // // // // // // //                           src={candidate.avatar}
// // // // // // // // //                           sx={{ width: 32, height: 32 }}
// // // // // // // // //                         />
// // // // // // // // //                         <Box>
// // // // // // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // // // // // //                             {candidate.name}
// // // // // // // // //                           </Typography>
// // // // // // // // //                           <Typography variant="caption" color="textSecondary">
// // // // // // // // //                             {candidate.position} • {candidate.experience}
// // // // // // // // //                           </Typography>
// // // // // // // // //                         </Box>
// // // // // // // // //                       </Box>
// // // // // // // // //                     </MenuItem>
// // // // // // // // //                   ))}
// // // // // // // // //                 </Select>
// // // // // // // // //               </FormControl>
// // // // // // // // //             </Grid>

// // // // // // // // //             <Grid item xs={12} md={6}>
// // // // // // // // //               <TextField
// // // // // // // // //                 fullWidth
// // // // // // // // //                 label="Position"
// // // // // // // // //                 name="position"
// // // // // // // // //                 value={formData.position}
// // // // // // // // //                 onChange={handleInputChange}
// // // // // // // // //                 required
// // // // // // // // //               />
// // // // // // // // //             </Grid>

// // // // // // // // //             <Grid item xs={12} md={6}>
// // // // // // // // //               <FormControl fullWidth>
// // // // // // // // //                 <InputLabel>Interview Type</InputLabel>
// // // // // // // // //                 <Select
// // // // // // // // //                   name="type"
// // // // // // // // //                   value={formData.type}
// // // // // // // // //                   onChange={handleInputChange}
// // // // // // // // //                   label="Interview Type"
// // // // // // // // //                 >
// // // // // // // // //                   <MenuItem value="video">
// // // // // // // // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // // // // // // //                       <VideoCallIcon />
// // // // // // // // //                       Video Interview
// // // // // // // // //                     </Box>
// // // // // // // // //                   </MenuItem>
// // // // // // // // //                   <MenuItem value="audio">
// // // // // // // // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // // // // // // //                       <CallIcon />
// // // // // // // // //                       Audio Interview
// // // // // // // // //                     </Box>
// // // // // // // // //                   </MenuItem>
// // // // // // // // //                 </Select>
// // // // // // // // //               </FormControl>
// // // // // // // // //             </Grid>

// // // // // // // // //             <Grid item xs={12} md={6}>
// // // // // // // // //               <TextField
// // // // // // // // //                 fullWidth
// // // // // // // // //                 label="Date"
// // // // // // // // //                 name="date"
// // // // // // // // //                 type="date"
// // // // // // // // //                 value={formData.date}
// // // // // // // // //                 onChange={handleInputChange}
// // // // // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // // // // //                 required
// // // // // // // // //               />
// // // // // // // // //             </Grid>

// // // // // // // // //             <Grid item xs={12} md={6}>
// // // // // // // // //               <TextField
// // // // // // // // //                 fullWidth
// // // // // // // // //                 label="Time"
// // // // // // // // //                 name="time"
// // // // // // // // //                 type="time"
// // // // // // // // //                 value={formData.time}
// // // // // // // // //                 onChange={handleInputChange}
// // // // // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // // // // //                 required
// // // // // // // // //               />
// // // // // // // // //             </Grid>

// // // // // // // // //             <Grid item xs={12}>
// // // // // // // // //               <TextField
// // // // // // // // //                 fullWidth
// // // // // // // // //                 label="Interview Notes"
// // // // // // // // //                 name="notes"
// // // // // // // // //                 value={formData.notes}
// // // // // // // // //                 onChange={handleInputChange}
// // // // // // // // //                 multiline
// // // // // // // // //                 rows={3}
// // // // // // // // //                 placeholder="Add any specific topics to focus on, questions to ask, or preparation notes..."
// // // // // // // // //               />
// // // // // // // // //             </Grid>
// // // // // // // // //           </Grid>
// // // // // // // // //         </DialogContent>
// // // // // // // // //         <DialogActions sx={{ p: 3 }}>
// // // // // // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // // // // // //           <Button onClick={handleSubmit} variant="contained" size="large">
// // // // // // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // // // // // //           </Button>
// // // // // // // // //         </DialogActions>
// // // // // // // // //       </Dialog>

// // // // // // // // //       {/* Video Call Dialog */}
// // // // // // // // //       <Dialog
// // // // // // // // //         open={openVideoCall}
// // // // // // // // //         onClose={handleEndInterview}
// // // // // // // // //         maxWidth="lg"
// // // // // // // // //         fullWidth
// // // // // // // // //       >
// // // // // // // // //         <DialogTitle>
// // // // // // // // //           {selectedInterview && (
// // // // // // // // //             <Box
// // // // // // // // //               sx={{
// // // // // // // // //                 display: "flex",
// // // // // // // // //                 alignItems: "center",
// // // // // // // // //                 justifyContent: "space-between",
// // // // // // // // //               }}
// // // // // // // // //             >
// // // // // // // // //               <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// // // // // // // // //                 <Avatar src={selectedInterview.avatar} />
// // // // // // // // //                 <Box>
// // // // // // // // //                   <Typography variant="h6">
// // // // // // // // //                     Interview with {selectedInterview.candidateName}
// // // // // // // // //                   </Typography>
// // // // // // // // //                   <Typography variant="body2" color="textSecondary">
// // // // // // // // //                     {selectedInterview.position}
// // // // // // // // //                   </Typography>
// // // // // // // // //                 </Box>
// // // // // // // // //               </Box>
// // // // // // // // //               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // // // // // // //                 {interviewInProgress && <LinearProgress sx={{ width: 100 }} />}
// // // // // // // // //                 <Chip
// // // // // // // // //                   label={interviewInProgress ? "Live" : "Connecting..."}
// // // // // // // // //                   color={interviewInProgress ? "error" : "warning"}
// // // // // // // // //                   size="small"
// // // // // // // // //                   sx={{
// // // // // // // // //                     animation: interviewInProgress
// // // // // // // // //                       ? "pulse 1.5s infinite"
// // // // // // // // //                       : "none",
// // // // // // // // //                   }}
// // // // // // // // //                 />
// // // // // // // // //               </Box>
// // // // // // // // //             </Box>
// // // // // // // // //           )}
// // // // // // // // //         </DialogTitle>
// // // // // // // // //         <DialogContent>
// // // // // // // // //           {/* Video Interface */}
// // // // // // // // //           <Box
// // // // // // // // //             sx={{
// // // // // // // // //               height: 400,
// // // // // // // // //               bgcolor: "black",
// // // // // // // // //               display: "flex",
// // // // // // // // //               alignItems: "center",
// // // // // // // // //               justifyContent: "center",
// // // // // // // // //               position: "relative",
// // // // // // // // //               mb: 3,
// // // // // // // // //               borderRadius: 2,
// // // // // // // // //               overflow: "hidden",
// // // // // // // // //             }}
// // // // // // // // //           >
// // // // // // // // //             {selectedInterview && (
// // // // // // // // //               <>
// // // // // // // // //                 {/* Main video area */}
// // // // // // // // //                 <Box
// // // // // // // // //                   sx={{
// // // // // // // // //                     width: "100%",
// // // // // // // // //                     height: "100%",
// // // // // // // // //                     background: `linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)`,
// // // // // // // // //                     display: "flex",
// // // // // // // // //                     alignItems: "center",
// // // // // // // // //                     justifyContent: "center",
// // // // // // // // //                     position: "relative",
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <Avatar
// // // // // // // // //                     src={selectedInterview.avatar}
// // // // // // // // //                     sx={{ width: 120, height: 120 }}
// // // // // // // // //                   />
// // // // // // // // //                   <Typography
// // // // // // // // //                     variant="h6"
// // // // // // // // //                     sx={{
// // // // // // // // //                       position: "absolute",
// // // // // // // // //                       bottom: 20,
// // // // // // // // //                       left: 20,
// // // // // // // // //                       color: "white",
// // // // // // // // //                       textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
// // // // // // // // //                     }}
// // // // // // // // //                   >
// // // // // // // // //                     {selectedInterview.candidateName}
// // // // // // // // //                   </Typography>
// // // // // // // // //                 </Box>

// // // // // // // // //                 {/* Picture-in-picture for interviewer */}
// // // // // // // // //                 <Box
// // // // // // // // //                   sx={{
// // // // // // // // //                     position: "absolute",
// // // // // // // // //                     bottom: 16,
// // // // // // // // //                     right: 16,
// // // // // // // // //                     width: 150,
// // // // // // // // //                     height: 100,
// // // // // // // // //                     bgcolor: "grey.800",
// // // // // // // // //                     border: "2px solid white",
// // // // // // // // //                     borderRadius: 1,
// // // // // // // // //                     display: "flex",
// // // // // // // // //                     alignItems: "center",
// // // // // // // // //                     justifyContent: "center",
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <PersonIcon sx={{ color: "white", fontSize: 40 }} />
// // // // // // // // //                 </Box>

// // // // // // // // //                 {/* Controls overlay */}
// // // // // // // // //                 <Box
// // // // // // // // //                   sx={{
// // // // // // // // //                     position: "absolute",
// // // // // // // // //                     bottom: 0,
// // // // // // // // //                     left: 0,
// // // // // // // // //                     right: 0,
// // // // // // // // //                     background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
// // // // // // // // //                     p: 2,
// // // // // // // // //                     display: "flex",
// // // // // // // // //                     justifyContent: "center",
// // // // // // // // //                     gap: 2,
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <IconButton
// // // // // // // // //                     sx={{ color: "white", bgcolor: "rgba(255,255,255,0.2)" }}
// // // // // // // // //                   >
// // // // // // // // //                     <VideoCallIcon />
// // // // // // // // //                   </IconButton>
// // // // // // // // //                   <IconButton
// // // // // // // // //                     sx={{ color: "white", bgcolor: "rgba(255,255,255,0.2)" }}
// // // // // // // // //                   >
// // // // // // // // //                     <CallIcon />
// // // // // // // // //                   </IconButton>
// // // // // // // // //                 </Box>
// // // // // // // // //               </>
// // // // // // // // //             )}
// // // // // // // // //           </Box>

// // // // // // // // //           {/* Interview Questions Panel */}
// // // // // // // // //           <Grid container spacing={3}>
// // // // // // // // //             <Grid item xs={12} md={6}>
// // // // // // // // //               <Paper sx={{ p: 2 }}>
// // // // // // // // //                 <Typography variant="h6" gutterBottom>
// // // // // // // // //                   Interview Questions
// // // // // // // // //                 </Typography>
// // // // // // // // //                 <List dense>
// // // // // // // // //                   <ListItem>
// // // // // // // // //                     <ListItemText
// // // // // // // // //                       primary="1. Tell me about your experience with AI/ML technologies."
// // // // // // // // //                       secondary="Focus on practical applications and projects"
// // // // // // // // //                     />
// // // // // // // // //                   </ListItem>
// // // // // // // // //                   <ListItem>
// // // // // // // // //                     <ListItemText
// // // // // // // // //                       primary="2. How do you approach debugging ML models?"
// // // // // // // // //                       secondary="Look for systematic problem-solving approach"
// // // // // // // // //                     />
// // // // // // // // //                   </ListItem>
// // // // // // // // //                   <ListItem>
// // // // // // // // //                     <ListItemText
// // // // // // // // //                       primary="3. Describe a challenging project you worked on."
// // // // // // // // //                       secondary="Assess technical depth and problem-solving skills"
// // // // // // // // //                     />
// // // // // // // // //                   </ListItem>
// // // // // // // // //                   <ListItem>
// // // // // // // // //                     <ListItemText
// // // // // // // // //                       primary="4. How do you stay updated with latest AI research?"
// // // // // // // // //                       secondary="Evaluate continuous learning mindset"
// // // // // // // // //                     />
// // // // // // // // //                   </ListItem>
// // // // // // // // //                 </List>
// // // // // // // // //               </Paper>
// // // // // // // // //             </Grid>

// // // // // // // // //             <Grid item xs={12} md={6}>
// // // // // // // // //               <Paper sx={{ p: 2 }}>
// // // // // // // // //                 <Typography variant="h6" gutterBottom>
// // // // // // // // //                   Candidate Profile
// // // // // // // // //                 </Typography>
// // // // // // // // //                 {selectedInterview && (
// // // // // // // // //                   <Box>
// // // // // // // // //                     <Typography variant="body2" gutterBottom>
// // // // // // // // //                       <strong>Experience:</strong>{" "}
// // // // // // // // //                       {selectedInterview.experience}
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Typography variant="body2" gutterBottom>
// // // // // // // // //                       <strong>Location:</strong> {selectedInterview.location}
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Typography variant="body2" gutterBottom>
// // // // // // // // //                       <strong>Skills:</strong>
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Box
// // // // // // // // //                       sx={{
// // // // // // // // //                         display: "flex",
// // // // // // // // //                         flexWrap: "wrap",
// // // // // // // // //                         gap: 0.5,
// // // // // // // // //                         mb: 2,
// // // // // // // // //                       }}
// // // // // // // // //                     >
// // // // // // // // //                       {selectedInterview.skills?.map((skill, index) => (
// // // // // // // // //                         <Chip
// // // // // // // // //                           key={index}
// // // // // // // // //                           label={skill}
// // // // // // // // //                           size="small"
// // // // // // // // //                           variant="outlined"
// // // // // // // // //                         />
// // // // // // // // //                       ))}
// // // // // // // // //                     </Box>
// // // // // // // // //                     <Typography variant="body2" gutterBottom>
// // // // // // // // //                       <strong>Notes:</strong>
// // // // // // // // //                     </Typography>
// // // // // // // // //                     <Typography variant="body2" sx={{ fontStyle: "italic" }}>
// // // // // // // // //                       {selectedInterview.notes}
// // // // // // // // //                     </Typography>
// // // // // // // // //                   </Box>
// // // // // // // // //                 )}
// // // // // // // // //               </Paper>
// // // // // // // // //             </Grid>
// // // // // // // // //           </Grid>
// // // // // // // // //         </DialogContent>
// // // // // // // // //         <DialogActions sx={{ p: 3 }}>
// // // // // // // // //           <Button variant="outlined" onClick={handleEndInterview}>
// // // // // // // // //             End Interview
// // // // // // // // //           </Button>
// // // // // // // // //           <Button
// // // // // // // // //             variant="contained"
// // // // // // // // //             color="error"
// // // // // // // // //             onClick={handleEndInterview}
// // // // // // // // //           >
// // // // // // // // //             Complete & Rate
// // // // // // // // //           </Button>
// // // // // // // // //         </DialogActions>
// // // // // // // // //       </Dialog>
// // // // // // // // //     </Layout>
// // // // // // // // //   );
// // // // // // // // // }

// // // // // // // // // export default InterviewScheduler;

// // // // // // // // import { useState, useEffect, useCallback } from "react";
// // // // // // // // import axios from "axios";
// // // // // // // // import {
// // // // // // // //   Box,
// // // // // // // //   Typography,
// // // // // // // //   Grid,
// // // // // // // //   Card,
// // // // // // // //   CardContent,
// // // // // // // //   CardActions,
// // // // // // // //   Button,
// // // // // // // //   TextField,
// // // // // // // //   FormControl,
// // // // // // // //   InputLabel,
// // // // // // // //   Select,
// // // // // // // //   MenuItem,
// // // // // // // //   Dialog,
// // // // // // // //   DialogTitle,
// // // // // // // //   DialogActions as MuiDialogActions,
// // // // // // // //   IconButton,
// // // // // // // //   Chip,
// // // // // // // //   Tabs,
// // // // // // // //   Tab,
// // // // // // // //   Paper,
// // // // // // // //   Divider,
// // // // // // // //   Avatar,
// // // // // // // //   Badge,
// // // // // // // //   Skeleton,
// // // // // // // //   Fab,
// // // // // // // // } from "@mui/material";
// // // // // // // // import {
// // // // // // // //   Add as AddIcon,
// // // // // // // //   VideoCall as VideoCallIcon,
// // // // // // // //   CalendarMonth as CalendarIcon,
// // // // // // // //   Delete as DeleteIcon,
// // // // // // // //   Edit as EditIcon,
// // // // // // // //   Schedule as ScheduleIcon,
// // // // // // // //   AccessTime as TimeIcon,
// // // // // // // //   ReportProblem as ReportProblemIcon,
// // // // // // // // } from "@mui/icons-material";
// // // // // // // // import Layout from "../common/Layout";
// // // // // // // // import { useNotification } from "../../context/NotificationContext";

// // // // // // // // const API_BASE_URL = "https://tdtlworld.com/gatep-backend/api";

// // // // // // // // const api = axios.create({
// // // // // // // //   baseURL: API_BASE_URL,
// // // // // // // // });

// // // // // // // // api.interceptors.request.use(
// // // // // // // //   (config) => {
// // // // // // // //     const token = localStorage.getItem("access_token");
// // // // // // // //     if (token) {
// // // // // // // //       config.headers["Authorization"] = `Bearer ${token}`;
// // // // // // // //     }
// // // // // // // //     return config;
// // // // // // // //   },
// // // // // // // //   (error) => {
// // // // // // // //     return Promise.reject(error);
// // // // // // // //   }
// // // // // // // // );

// // // // // // // // const formatApiData = (apiInterview) => {
// // // // // // // //   // Support both new and old API formats
// // // // // // // //   return {
// // // // // // // //     id: apiInterview.id,
// // // // // // // //     applicationId: apiInterview.application || apiInterview.applicationId,
// // // // // // // //     candidateName:
// // // // // // // //       apiInterview.candidate_name ||
// // // // // // // //       apiInterview.application_details?.talent_details?.user?.username ||
// // // // // // // //       apiInterview.interviewer_details?.username ||
// // // // // // // //       "N/A",
// // // // // // // //     position:
// // // // // // // //       apiInterview.job_title ||
// // // // // // // //       apiInterview.application_details?.job_posting_details?.title ||
// // // // // // // //       "N/A",
// // // // // // // //     experience:
// // // // // // // //       apiInterview.experience_level ||
// // // // // // // //       apiInterview.application_details?.job_posting_details
// // // // // // // //         ?.experience_level_display ||
// // // // // // // //       "N/A",
// // // // // // // //     location: apiInterview.location || "N/A",
// // // // // // // //     status: (
// // // // // // // //       apiInterview.status ||
// // // // // // // //       apiInterview.interview_status ||
// // // // // // // //       "unknown"
// // // // // // // //     ).toLowerCase(),
// // // // // // // //     date:
// // // // // // // //       apiInterview.interview_date ||
// // // // // // // //       (apiInterview.scheduled_at
// // // // // // // //         ? new Date(apiInterview.scheduled_at).toLocaleDateString("en-US", {
// // // // // // // //             weekday: "short",
// // // // // // // //             month: "short",
// // // // // // // //             day: "numeric",
// // // // // // // //             year: "numeric",
// // // // // // // //           })
// // // // // // // //         : "N/A"),
// // // // // // // //     time:
// // // // // // // //       apiInterview.interview_time ||
// // // // // // // //       (apiInterview.scheduled_at
// // // // // // // //         ? new Date(apiInterview.scheduled_at).toLocaleTimeString("en-US", {
// // // // // // // //             hour: "2-digit",
// // // // // // // //             minute: "2-digit",
// // // // // // // //             hour12: false,
// // // // // // // //           })
// // // // // // // //         : "N/A"),
// // // // // // // //     interviewType:
// // // // // // // //       apiInterview.interview_mode ||
// // // // // // // //       apiInterview.interview_type ||
// // // // // // // //       (apiInterview.location === "video" ? "Video Interview" : "Interview"),
// // // // // // // //     skills:
// // // // // // // //       apiInterview.skills ||
// // // // // // // //       apiInterview.application_details?.job_posting_details?.required_skills ||
// // // // // // // //       [],
// // // // // // // //     notes:
// // // // // // // //       apiInterview.notes ||
// // // // // // // //       apiInterview.feedback ||
// // // // // // // //       "No specific notes provided for this interview.",
// // // // // // // //     avatar:
// // // // // // // //       apiInterview.application_details?.talent_details?.profile_picture ||
// // // // // // // //       "/placeholder.svg?height=56&width=56",
// // // // // // // //     interviewUrl: apiInterview.location || "",
// // // // // // // //   };
// // // // // // // // };

// // // // // // // // const InterviewCardSkeleton = () => (
// // // // // // // //   <Grid item xs={12} md={6} lg={4}>
// // // // // // // //     <Card sx={{ height: "100%" }}>
// // // // // // // //       <CardContent>
// // // // // // // //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // // // // //           <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
// // // // // // // //           <Box sx={{ flexGrow: 1 }}>
// // // // // // // //             <Skeleton variant="text" width="60%" />
// // // // // // // //             <Skeleton variant="text" width="80%" />
// // // // // // // //           </Box>
// // // // // // // //         </Box>
// // // // // // // //         <Divider sx={{ my: 2 }} />
// // // // // // // //         <Skeleton variant="text" width="50%" sx={{ mb: 1 }} />
// // // // // // // //         <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
// // // // // // // //         <Skeleton variant="text" width="90%" />
// // // // // // // //       </CardContent>
// // // // // // // //     </Card>
// // // // // // // //   </Grid>
// // // // // // // // );

// // // // // // // // function InterviewScheduler() {
// // // // // // // //   const { showNotification } = useNotification();
// // // // // // // //   const [interviews, setInterviews] = useState([]);
// // // // // // // //   const [candidates, setCandidates] = useState([]);
// // // // // // // //   const [isLoading, setIsLoading] = useState(true);
// // // // // // // //   const [error, setError] = useState(null);
// // // // // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // // // // //   const [tabValue, setTabValue] = useState(0);
// // // // // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // // // // //   const [formData, setFormData] = useState({
// // // // // // // //     applicationId: "",
// // // // // // // //     date: "",
// // // // // // // //     time: "",
// // // // // // // //     type: "video",
// // // // // // // //     notes: "",
// // // // // // // //     interviewUrl: "",
// // // // // // // //   });

// // // // // // // //   const fetchInterviews = useCallback(async () => {
// // // // // // // //     setIsLoading(true);
// // // // // // // //     setError(null);
// // // // // // // //     try {
// // // // // // // //       const response = await api.get("/interviews/");
// // // // // // // //       const formattedData = response.data.map(formatApiData);
// // // // // // // //       setInterviews(formattedData);
// // // // // // // //     } catch (err) {
// // // // // // // //       setError("Could not load interviews.");
// // // // // // // //     } finally {
// // // // // // // //       setIsLoading(false);
// // // // // // // //     }
// // // // // // // //   }, []);

// // // // // // // //   const fetchCandidates = useCallback(async () => {
// // // // // // // //     try {
// // // // // // // //       const response = await api.get("/applications/");
// // // // // // // //       const formattedCandidates = response.data.map((app) => ({
// // // // // // // //         applicationId: app.id,
// // // // // // // //         talentId: app.talent,
// // // // // // // //         name:
// // // // // // // //           `${app.talent_details?.user?.first_name || ""} ${
// // // // // // // //             app.talent_details?.user?.last_name || ""
// // // // // // // //           }`.trim() || `Talent #${app.talent}`,
// // // // // // // //         position: app.job_posting_details?.title || "Applied Position",
// // // // // // // //         avatar:
// // // // // // // //           app.talent_details?.profile_picture ||
// // // // // // // //           "/placeholder.svg?height=40&width=40",
// // // // // // // //       }));
// // // // // // // //       setCandidates(formattedCandidates);
// // // // // // // //     } catch (err) {
// // // // // // // //       showNotification(
// // // // // // // //         "Could not load candidate list for scheduling.",
// // // // // // // //         "warning"
// // // // // // // //       );
// // // // // // // //     }
// // // // // // // //   }, [showNotification]);

// // // // // // // //   useEffect(() => {
// // // // // // // //     fetchInterviews();
// // // // // // // //     fetchCandidates();
// // // // // // // //   }, [fetchInterviews, fetchCandidates]);

// // // // // // // //   const handleInputChange = (e) => {
// // // // // // // //     const { name, value } = e.target;
// // // // // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // // // // //   };

// // // // // // // //   const handleSubmit = async () => {
// // // // // // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // // // // // //       showNotification("Please select a candidate, date, and time.", "error");
// // // // // // // //       return;
// // // // // // // //     }

// // // // // // // //     // If editing, keep the previous URL if the field is empty
// // // // // // // //     let locationValue = formData.interviewUrl;
// // // // // // // //     if (!locationValue && selectedInterview && selectedInterview.interviewUrl) {
// // // // // // // //       locationValue = selectedInterview.interviewUrl;
// // // // // // // //     }
// // // // // // // //     if (!locationValue) {
// // // // // // // //       locationValue = formData.type;
// // // // // // // //     }

// // // // // // // //     const payload = {
// // // // // // // //       application: formData.applicationId,
// // // // // // // //       scheduled_at: `${formData.date}T${formData.time}:00Z`,
// // // // // // // //       location: locationValue,
// // // // // // // //       feedback: formData.notes,
// // // // // // // //       interview_status: "Scheduled",
// // // // // // // //       interviewer: 3,
// // // // // // // //     };

// // // // // // // //     try {
// // // // // // // //       if (selectedInterview) {
// // // // // // // //         await api.put(`/interviews/${selectedInterview.id}/`, payload);
// // // // // // // //         showNotification("Interview updated successfully!", "success");
// // // // // // // //       } else {
// // // // // // // //         await api.post("/interviews/", payload);
// // // // // // // //         showNotification("Interview scheduled successfully!", "success");
// // // // // // // //       }
// // // // // // // //       fetchInterviews();
// // // // // // // //       handleCloseDialog();
// // // // // // // //     } catch (err) {
// // // // // // // //       showNotification(
// // // // // // // //         err.response?.data?.detail || "An error occurred.",
// // // // // // // //         "error"
// // // // // // // //       );
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   const handleDeleteInterview = async (id) => {
// // // // // // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // // // // // //       try {
// // // // // // // //         await api.delete(`/interviews/${id}/`);
// // // // // // // //         setInterviews(interviews.filter((i) => i.id !== id));
// // // // // // // //         showNotification("Interview deleted successfully!", "success");
// // // // // // // //       } catch (err) {
// // // // // // // //         showNotification("Failed to delete interview.", "error");
// // // // // // // //       }
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   const handleStartInterview = (interviewUrl) => {
// // // // // // // //     if (
// // // // // // // //       interviewUrl &&
// // // // // // // //       (interviewUrl.startsWith("http://") ||
// // // // // // // //         interviewUrl.startsWith("https://"))
// // // // // // // //     ) {
// // // // // // // //       window.open(interviewUrl, "_blank");
// // // // // // // //     } else {
// // // // // // // //       showNotification("No valid interview URL available.", "warning");
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // // // // //     if (tabValue === 2) return interview.status === "cancelled";
// // // // // // // //     return true;
// // // // // // // //   });

// // // // // // // //   const handleTabChange = (event, newValue) => setTabValue(newValue);

// // // // // // // //   const handleCloseDialog = () => setOpenDialog(false);

// // // // // // // //   const handleOpenDialog = (interview = null) => {
// // // // // // // //     if (interview) {
// // // // // // // //       setSelectedInterview(interview);
// // // // // // // //       setFormData({
// // // // // // // //         applicationId: interview.applicationId,
// // // // // // // //         date: interview.date,
// // // // // // // //         time: interview.time,
// // // // // // // //         type: interview.interviewType === "Video Interview" ? "video" : "audio",
// // // // // // // //         notes: interview.notes,
// // // // // // // //         interviewUrl: interview.interviewUrl || "",
// // // // // // // //       });
// // // // // // // //     } else {
// // // // // // // //       setSelectedInterview(null);
// // // // // // // //       setFormData({
// // // // // // // //         applicationId: "",
// // // // // // // //         date: "",
// // // // // // // //         time: "",
// // // // // // // //         type: "video",
// // // // // // // //         notes: "",
// // // // // // // //         interviewUrl: "",
// // // // // // // //       });
// // // // // // // //     }
// // // // // // // //     setOpenDialog(true);
// // // // // // // //   };

// // // // // // // //   const getStatusColor = (status) => {
// // // // // // // //     switch (status) {
// // // // // // // //       case "scheduled":
// // // // // // // //         return "primary";
// // // // // // // //       case "completed":
// // // // // // // //         return "success";
// // // // // // // //       case "cancelled":
// // // // // // // //         return "error";
// // // // // // // //       default:
// // // // // // // //         return "default";
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   const upcomingInterviews = interviews.filter(
// // // // // // // //     (i) => i.status === "scheduled"
// // // // // // // //   ).length;

// // // // // // // //   return (
// // // // // // // //     <Layout title="Interview Scheduler">
// // // // // // // //       <Box sx={{ p: 3, position: "relative" }}>
// // // // // // // //         <Grid container spacing={3} sx={{ mb: 3 }}>
// // // // // // // //           <Grid item xs={12}>
// // // // // // // //             <Typography variant="h4" component="h1" fontWeight="bold">
// // // // // // // //               Interview Management
// // // // // // // //             </Typography>
// // // // // // // //           </Grid>
// // // // // // // //         </Grid>

// // // // // // // //         <Paper sx={{ mb: 3 }}>
// // // // // // // //           <Tabs
// // // // // // // //             value={tabValue}
// // // // // // // //             onChange={handleTabChange}
// // // // // // // //             indicatorColor="primary"
// // // // // // // //             textColor="primary"
// // // // // // // //             centered
// // // // // // // //           >
// // // // // // // //             <Tab
// // // // // // // //               icon={<Badge badgeContent={upcomingInterviews} color="primary" />}
// // // // // // // //               label="Scheduled"
// // // // // // // //               iconPosition="start"
// // // // // // // //             />
// // // // // // // //             <Tab label="Completed" iconPosition="start" />
// // // // // // // //             <Tab label="Cancelled" iconPosition="start" />
// // // // // // // //           </Tabs>
// // // // // // // //         </Paper>

// // // // // // // //         {isLoading ? (
// // // // // // // //           <Grid container spacing={3}>
// // // // // // // //             <InterviewCardSkeleton />
// // // // // // // //             <InterviewCardSkeleton />
// // // // // // // //             <InterviewCardSkeleton />
// // // // // // // //           </Grid>
// // // // // // // //         ) : error ? (
// // // // // // // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // // // //             <ReportProblemIcon
// // // // // // // //               sx={{ fontSize: 64, color: "error.main", mb: 2 }}
// // // // // // // //             />
// // // // // // // //             <Typography variant="h6">{error}</Typography>
// // // // // // // //             <Button
// // // // // // // //               variant="contained"
// // // // // // // //               onClick={fetchInterviews}
// // // // // // // //               sx={{ mt: 2 }}
// // // // // // // //             >
// // // // // // // //               Try Again
// // // // // // // //             </Button>
// // // // // // // //           </Paper>
// // // // // // // //         ) : (
// // // // // // // //           <Grid container spacing={3}>
// // // // // // // //             {filteredInterviews.length > 0 ? (
// // // // // // // //               filteredInterviews.map((interview) => (
// // // // // // // //                 <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // // // // //                   <Card
// // // // // // // //                     sx={{
// // // // // // // //                       height: "100%",
// // // // // // // //                       display: "flex",
// // // // // // // //                       flexDirection: "column",
// // // // // // // //                       p: 1,
// // // // // // // //                     }}
// // // // // // // //                   >
// // // // // // // //                     <CardContent sx={{ flexGrow: 1 }}>
// // // // // // // //                       <Box
// // // // // // // //                         sx={{
// // // // // // // //                           display: "flex",
// // // // // // // //                           alignItems: "flex-start",
// // // // // // // //                           mb: 2,
// // // // // // // //                         }}
// // // // // // // //                       >
// // // // // // // //                         <Avatar
// // // // // // // //                           src={interview.avatar}
// // // // // // // //                           sx={{ width: 56, height: 56, mr: 2 }}
// // // // // // // //                         />
// // // // // // // //                         <Box sx={{ flexGrow: 1 }}>
// // // // // // // //                           <Typography variant="h6" fontWeight="bold">
// // // // // // // //                             {interview.candidateName}
// // // // // // // //                           </Typography>
// // // // // // // //                           <Typography variant="body2" color="text.secondary">
// // // // // // // //                             {interview.position}
// // // // // // // //                           </Typography>
// // // // // // // //                           <Typography variant="body2" color="text.secondary">
// // // // // // // //                             {interview.experience}
// // // // // // // //                           </Typography>
// // // // // // // //                         </Box>
// // // // // // // //                         <Chip
// // // // // // // //                           label={interview.status}
// // // // // // // //                           color={getStatusColor(interview.status)}
// // // // // // // //                           size="small"
// // // // // // // //                           variant="outlined"
// // // // // // // //                         />
// // // // // // // //                       </Box>

// // // // // // // //                       <Box sx={{ mb: 2 }}>
// // // // // // // //                         <Box
// // // // // // // //                           sx={{
// // // // // // // //                             display: "flex",
// // // // // // // //                             alignItems: "center",
// // // // // // // //                             gap: 1.5,
// // // // // // // //                             mb: 1,
// // // // // // // //                           }}
// // // // // // // //                         >
// // // // // // // //                           <CalendarIcon color="action" />
// // // // // // // //                           <Typography variant="body2">
// // // // // // // //                             {interview.date}
// // // // // // // //                           </Typography>
// // // // // // // //                         </Box>
// // // // // // // //                         <Box
// // // // // // // //                           sx={{
// // // // // // // //                             display: "flex",
// // // // // // // //                             alignItems: "center",
// // // // // // // //                             gap: 1.5,
// // // // // // // //                             mb: 1,
// // // // // // // //                           }}
// // // // // // // //                         >
// // // // // // // //                           <TimeIcon color="action" />
// // // // // // // //                           <Typography variant="body2">
// // // // // // // //                             {interview.time}
// // // // // // // //                           </Typography>
// // // // // // // //                         </Box>
// // // // // // // //                         <Box
// // // // // // // //                           sx={{
// // // // // // // //                             display: "flex",
// // // // // // // //                             alignItems: "center",
// // // // // // // //                             gap: 1.5,
// // // // // // // //                           }}
// // // // // // // //                         >
// // // // // // // //                           <VideoCallIcon color="action" />
// // // // // // // //                           <Typography variant="body2">
// // // // // // // //                             {interview.interviewType}
// // // // // // // //                           </Typography>
// // // // // // // //                         </Box>
// // // // // // // //                       </Box>

// // // // // // // //                       {interview.skills && interview.skills.length > 0 && (
// // // // // // // //                         <Box sx={{ mb: 2 }}>
// // // // // // // //                           <Typography
// // // // // // // //                             variant="body2"
// // // // // // // //                             fontWeight="medium"
// // // // // // // //                             gutterBottom
// // // // // // // //                           >
// // // // // // // //                             Skills:
// // // // // // // //                           </Typography>
// // // // // // // //                           <Box
// // // // // // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
// // // // // // // //                           >
// // // // // // // //                             {interview.skills.map((skill) => (
// // // // // // // //                               <Chip
// // // // // // // //                                 key={skill}
// // // // // // // //                                 label={skill}
// // // // // // // //                                 size="small"
// // // // // // // //                                 variant="outlined"
// // // // // // // //                               />
// // // // // // // //                             ))}
// // // // // // // //                           </Box>
// // // // // // // //                         </Box>
// // // // // // // //                       )}

// // // // // // // //                       <Box>
// // // // // // // //                         <Typography
// // // // // // // //                           variant="body2"
// // // // // // // //                           fontWeight="medium"
// // // // // // // //                           gutterBottom
// // // // // // // //                         >
// // // // // // // //                           Notes:
// // // // // // // //                         </Typography>
// // // // // // // //                         <Typography
// // // // // // // //                           variant="body2"
// // // // // // // //                           color="text.secondary"
// // // // // // // //                           sx={{ fontStyle: "italic" }}
// // // // // // // //                         >
// // // // // // // //                           {interview.notes}
// // // // // // // //                         </Typography>
// // // // // // // //                       </Box>
// // // // // // // //                     </CardContent>

// // // // // // // //                     <CardActions
// // // // // // // //                       sx={{
// // // // // // // //                         justifyContent: "space-between",
// // // // // // // //                         alignItems: "center",
// // // // // // // //                         px: 2,
// // // // // // // //                         pb: 2,
// // // // // // // //                       }}
// // // // // // // //                     >
// // // // // // // //                       {/* Group of action buttons on the left */}
// // // // // // // //                       <Box sx={{ display: "flex", gap: 1 }}>
// // // // // // // //                         {/* Start Interview Button - MODIFIED AS PER REQUEST */}
// // // // // // // //                         {interview.status === "scheduled" && (
// // // // // // // //                           <Button
// // // // // // // //                             variant="contained"
// // // // // // // //                             startIcon={<VideoCallIcon />}
// // // // // // // //                             onClick={() =>
// // // // // // // //                               handleStartInterview(interview.location)
// // // // // // // //                             }
// // // // // // // //                             size="medium"
// // // // // // // //                           >
// // // // // // // //                             Start interview
// // // // // // // //                           </Button>
// // // // // // // //                         )}
// // // // // // // //                         {/* Edit Button */}
// // // // // // // //                         {interview.status === "scheduled" && (
// // // // // // // //                           <Button
// // // // // // // //                             variant="outlined"
// // // // // // // //                             startIcon={<EditIcon />}
// // // // // // // //                             size="medium"
// // // // // // // //                             onClick={() => handleOpenDialog(interview)}
// // // // // // // //                           >
// // // // // // // //                             Edit
// // // // // // // //                           </Button>
// // // // // // // //                         )}
// // // // // // // //                       </Box>

// // // // // // // //                       {/* Delete button on the right */}
// // // // // // // //                       <IconButton
// // // // // // // //                         size="small"
// // // // // // // //                         color="error"
// // // // // // // //                         title="Delete Interview"
// // // // // // // //                         onClick={() => handleDeleteInterview(interview.id)}
// // // // // // // //                       >
// // // // // // // //                         <DeleteIcon />
// // // // // // // //                       </IconButton>
// // // // // // // //                     </CardActions>
// // // // // // // //                   </Card>
// // // // // // // //                 </Grid>
// // // // // // // //               ))
// // // // // // // //             ) : (
// // // // // // // //               <Grid item xs={12}>
// // // // // // // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // // // //                   <ScheduleIcon
// // // // // // // //                     sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
// // // // // // // //                   />
// // // // // // // //                   <Typography variant="h6" color="textSecondary">
// // // // // // // //                     No {tabValue === 0 ? "scheduled" : "interviews"} found
// // // // // // // //                   </Typography>
// // // // // // // //                 </Paper>
// // // // // // // //               </Grid>
// // // // // // // //             )}
// // // // // // // //           </Grid>
// // // // // // // //         )}
// // // // // // // //       </Box>

// // // // // // // //       <Fab
// // // // // // // //         color="primary"
// // // // // // // //         aria-label="schedule interview"
// // // // // // // //         onClick={() => handleOpenDialog()}
// // // // // // // //         sx={{ position: "fixed", bottom: 32, right: 32 }}
// // // // // // // //       >
// // // // // // // //         <AddIcon />
// // // // // // // //       </Fab>

// // // // // // // //       <Dialog
// // // // // // // //         open={openDialog}
// // // // // // // //         onClose={handleCloseDialog}
// // // // // // // //         maxWidth="sm"
// // // // // // // //         fullWidth
// // // // // // // //       >
// // // // // // // //         <DialogTitle>
// // // // // // // //           {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // // // // //         </DialogTitle>
// // // // // // // //         <MuiDialogActions>
// // // // // // // //           <Grid container spacing={3} sx={{ pt: 1, px: 3 }}>
// // // // // // // //             <Grid item xs={12}>
// // // // // // // //               <FormControl fullWidth>
// // // // // // // //                 <InputLabel>Candidate Application</InputLabel>
// // // // // // // //                 <Select
// // // // // // // //                   name="applicationId"
// // // // // // // //                   value={formData.applicationId}
// // // // // // // //                   onChange={handleInputChange}
// // // // // // // //                   label="Candidate Application"
// // // // // // // //                 >
// // // // // // // //                   {candidates.map((candidate) => (
// // // // // // // //                     <MenuItem
// // // // // // // //                       key={candidate.applicationId}
// // // // // // // //                       value={candidate.applicationId}
// // // // // // // //                     >
// // // // // // // //                       <Box
// // // // // // // //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
// // // // // // // //                       >
// // // // // // // //                         <Avatar
// // // // // // // //                           src={candidate.avatar}
// // // // // // // //                           sx={{ width: 32, height: 32 }}
// // // // // // // //                         />
// // // // // // // //                         <Box>
// // // // // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // // // // //                             {candidate.name}
// // // // // // // //                           </Typography>
// // // // // // // //                           <Typography variant="caption" color="textSecondary">
// // // // // // // //                             {candidate.position}
// // // // // // // //                           </Typography>
// // // // // // // //                         </Box>
// // // // // // // //                       </Box>
// // // // // // // //                     </MenuItem>
// // // // // // // //                   ))}
// // // // // // // //                 </Select>
// // // // // // // //               </FormControl>
// // // // // // // //             </Grid>

// // // // // // // //             <Grid item xs={12} sm={6}>
// // // // // // // //               <TextField
// // // // // // // //                 fullWidth
// // // // // // // //                 label="Date"
// // // // // // // //                 name="date"
// // // // // // // //                 type="date"
// // // // // // // //                 value={formData.date}
// // // // // // // //                 onChange={handleInputChange}
// // // // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // // // //                 required
// // // // // // // //               />
// // // // // // // //             </Grid>

// // // // // // // //             <Grid item xs={12} sm={6}>
// // // // // // // //               <TextField
// // // // // // // //                 fullWidth
// // // // // // // //                 label="Time"
// // // // // // // //                 name="time"
// // // // // // // //                 type="time"
// // // // // // // //                 value={formData.time}
// // // // // // // //                 onChange={handleInputChange}
// // // // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // // // //                 required
// // // // // // // //               />
// // // // // // // //             </Grid>

// // // // // // // //             <Grid item xs={12}>
// // // // // // // //               <FormControl fullWidth>
// // // // // // // //                 <InputLabel>Type</InputLabel>
// // // // // // // //                 <Select
// // // // // // // //                   name="type"
// // // // // // // //                   value={formData.type}
// // // // // // // //                   onChange={handleInputChange}
// // // // // // // //                   label="Type"
// // // // // // // //                 >
// // // // // // // //                   <MenuItem value="video">Video</MenuItem>
// // // // // // // //                   {/* <MenuItem value="audio">Audio</MenuItem> */}
// // // // // // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // // // // // //                 </Select>
// // // // // // // //               </FormControl>
// // // // // // // //             </Grid>

// // // // // // // //             <Grid item xs={12}>
// // // // // // // //               <TextField
// // // // // // // //                 fullWidth
// // // // // // // //                 label="Interview URL"
// // // // // // // //                 name="interviewUrl"
// // // // // // // //                 value={formData.interviewUrl}
// // // // // // // //                 onChange={handleInputChange}
// // // // // // // //                 placeholder="https://meet.google.com/abc-defg-hij or https://zoom.us/j/123456789"
// // // // // // // //                 helperText="Enter the meeting URL for the interview"
// // // // // // // //               />
// // // // // // // //             </Grid>

// // // // // // // //             <Grid item xs={12}>
// // // // // // // //               <TextField
// // // // // // // //                 fullWidth
// // // // // // // //                 label="Notes / Feedback"
// // // // // // // //                 name="notes"
// // // // // // // //                 value={formData.notes}
// // // // // // // //                 onChange={handleInputChange}
// // // // // // // //                 multiline
// // // // // // // //                 rows={3}
// // // // // // // //               />
// // // // // // // //             </Grid>
// // // // // // // //           </Grid>
// // // // // // // //         </MuiDialogActions>

// // // // // // // //         <MuiDialogActions sx={{ p: 3 }}>
// // // // // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // // // // //           <Button onClick={handleSubmit} variant="contained">
// // // // // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // // // // //           </Button>
// // // // // // // //         </MuiDialogActions>
// // // // // // // //       </Dialog>
// // // // // // // //     </Layout>
// // // // // // // //   );
// // // // // // // // }

// // // // // // // // export default InterviewScheduler;
// // // // // // // import { useState, useEffect, useCallback } from "react";
// // // // // // // import axios from "axios";
// // // // // // // import {
// // // // // // //   Box,
// // // // // // //   Typography,
// // // // // // //   Grid,
// // // // // // //   Card,
// // // // // // //   CardContent,
// // // // // // //   CardActions,
// // // // // // //   Button,
// // // // // // //   TextField,
// // // // // // //   FormControl,
// // // // // // //   InputLabel,
// // // // // // //   Select,
// // // // // // //   MenuItem,
// // // // // // //   Dialog,
// // // // // // //   DialogTitle,
// // // // // // //   DialogActions as MuiDialogActions,
// // // // // // //   IconButton,
// // // // // // //   Chip,
// // // // // // //   Tabs,
// // // // // // //   Tab,
// // // // // // //   Paper,
// // // // // // //   Divider,
// // // // // // //   Avatar,
// // // // // // //   Badge,
// // // // // // //   Skeleton,
// // // // // // //   Fab,
// // // // // // // } from "@mui/material";
// // // // // // // import {
// // // // // // //   Add as AddIcon,
// // // // // // //   VideoCall as VideoCallIcon,
// // // // // // //   CalendarMonth as CalendarIcon,
// // // // // // //   Delete as DeleteIcon,
// // // // // // //   Edit as EditIcon,
// // // // // // //   Schedule as ScheduleIcon,
// // // // // // //   AccessTime as TimeIcon,
// // // // // // //   ReportProblem as ReportProblemIcon,
// // // // // // // } from "@mui/icons-material";
// // // // // // // import Layout from "../common/Layout";
// // // // // // // import { useNotification } from "../../context/NotificationContext";

// // // // // // // const API_BASE_URL = "https://tdtlworld.com/gatep-backend/api";

// // // // // // // const api = axios.create({
// // // // // // //   baseURL: API_BASE_URL,
// // // // // // // });

// // // // // // // api.interceptors.request.use(
// // // // // // //   (config) => {
// // // // // // //     const token = localStorage.getItem("access_token");
// // // // // // //     if (token) {
// // // // // // //       config.headers["Authorization"] = `Bearer ${token}`;
// // // // // // //     }
// // // // // // //     return config;
// // // // // // //   },
// // // // // // //   (error) => {
// // // // // // //     return Promise.reject(error);
// // // // // // //   }
// // // // // // // );

// // // // // // // const formatApiData = (apiInterview) => {
// // // // // // //   // Support both new and old API formats
// // // // // // //   return {
// // // // // // //     id: apiInterview.id,
// // // // // // //     applicationId: apiInterview.application || apiInterview.applicationId,
// // // // // // //     candidateName:
// // // // // // //       apiInterview.candidate_name ||
// // // // // // //       apiInterview.application_details?.talent_details?.user?.username ||
// // // // // // //       apiInterview.interviewer_details?.username ||
// // // // // // //       "N/A",
// // // // // // //     position:
// // // // // // //       apiInterview.job_title ||
// // // // // // //       apiInterview.application_details?.job_posting_details?.title ||
// // // // // // //       "N/A",
// // // // // // //     experience:
// // // // // // //       apiInterview.experience_level ||
// // // // // // //       apiInterview.application_details?.job_posting_details
// // // // // // //         ?.experience_level_display ||
// // // // // // //       "N/A",
// // // // // // //     location: apiInterview.location || "N/A",
// // // // // // //     status: (
// // // // // // //       apiInterview.status ||
// // // // // // //       apiInterview.interview_status ||
// // // // // // //       "unknown"
// // // // // // //     ).toLowerCase(),
// // // // // // //     date:
// // // // // // //       apiInterview.interview_date ||
// // // // // // //       (apiInterview.scheduled_at
// // // // // // //         ? new Date(apiInterview.scheduled_at).toLocaleDateString("en-US", {
// // // // // // //             weekday: "short",
// // // // // // //             month: "short",
// // // // // // //             day: "numeric",
// // // // // // //             year: "numeric",
// // // // // // //           })
// // // // // // //         : "N/A"),
// // // // // // //     time:
// // // // // // //       apiInterview.interview_time ||
// // // // // // //       (apiInterview.scheduled_at
// // // // // // //         ? new Date(apiInterview.scheduled_at).toLocaleTimeString("en-US", {
// // // // // // //             hour: "2-digit",
// // // // // // //             minute: "2-digit",
// // // // // // //             hour12: false,
// // // // // // //           })
// // // // // // //         : "N/A"),
// // // // // // //     interviewType:
// // // // // // //       apiInterview.interview_mode ||
// // // // // // //       apiInterview.interview_type ||
// // // // // // //       (apiInterview.location === "video" ? "Video Interview" : "Interview"),
// // // // // // //     skills:
// // // // // // //       apiInterview.skills ||
// // // // // // //       apiInterview.application_details?.job_posting_details?.required_skills ||
// // // // // // //       [],
// // // // // // //     notes:
// // // // // // //       apiInterview.notes ||
// // // // // // //       apiInterview.feedback ||
// // // // // // //       "No specific notes provided for this interview.",
// // // // // // //     avatar:
// // // // // // //       apiInterview.application_details?.talent_details?.profile_picture ||
// // // // // // //       "/placeholder.svg?height=56&width=56",
// // // // // // //     interviewUrl: apiInterview.location || "",
// // // // // // //   };
// // // // // // // };

// // // // // // // const InterviewCardSkeleton = () => (
// // // // // // //   <Grid item xs={12} md={6} lg={4}>
// // // // // // //     <Card sx={{ height: "100%" }}>
// // // // // // //       <CardContent>
// // // // // // //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // // // //           <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
// // // // // // //           <Box sx={{ flexGrow: 1 }}>
// // // // // // //             <Skeleton variant="text" width="60%" />
// // // // // // //             <Skeleton variant="text" width="80%" />
// // // // // // //           </Box>
// // // // // // //         </Box>
// // // // // // //         <Divider sx={{ my: 2 }} />
// // // // // // //         <Skeleton variant="text" width="50%" sx={{ mb: 1 }} />
// // // // // // //         <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
// // // // // // //         <Skeleton variant="text" width="90%" />
// // // // // // //       </CardContent>
// // // // // // //     </Card>
// // // // // // //   </Grid>
// // // // // // // );

// // // // // // // function InterviewScheduler() {
// // // // // // //   const { showNotification } = useNotification();
// // // // // // //   const [interviews, setInterviews] = useState([]);
// // // // // // //   const [candidates, setCandidates] = useState([]);
// // // // // // //   const [isLoading, setIsLoading] = useState(true);
// // // // // // //   const [error, setError] = useState(null);
// // // // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // // // //   const [tabValue, setTabValue] = useState(0);
// // // // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // // // //   const [formData, setFormData] = useState({
// // // // // // //     applicationId: "",
// // // // // // //     date: "",
// // // // // // //     time: "",
// // // // // // //     type: "video",
// // // // // // //     notes: "",
// // // // // // //     interviewUrl: "",
// // // // // // //   });

// // // // // // //   const fetchInterviews = useCallback(async () => {
// // // // // // //     setIsLoading(true);
// // // // // // //     setError(null);
// // // // // // //     try {
// // // // // // //       const response = await api.get("/interviews/");
// // // // // // //       const formattedData = response.data.map(formatApiData);
// // // // // // //       setInterviews(formattedData);
// // // // // // //     } catch (err) {
// // // // // // //       setError("Could not load interviews.");
// // // // // // //     } finally {
// // // // // // //       setIsLoading(false);
// // // // // // //     }
// // // // // // //   }, []);

// // // // // // //   const fetchCandidates = useCallback(async () => {
// // // // // // //     try {
// // // // // // //       const response = await api.get("/applications/");
// // // // // // //       const formattedCandidates = response.data.map((app) => ({
// // // // // // //         applicationId: app.id,
// // // // // // //         talentId: app.talent,
// // // // // // //         name:
// // // // // // //           `${app.talent_details?.user?.first_name || ""} ${
// // // // // // //             app.talent_details?.user?.last_name || ""
// // // // // // //           }`.trim() || `Talent #${app.talent}`,
// // // // // // //         position: app.job_posting_details?.title || "Applied Position",
// // // // // // //         avatar:
// // // // // // //           app.talent_details?.profile_picture ||
// // // // // // //           "/placeholder.svg?height=40&width=40",
// // // // // // //       }));
// // // // // // //       setCandidates(formattedCandidates);
// // // // // // //     } catch (err) {
// // // // // // //       showNotification(
// // // // // // //         "Could not load candidate list for scheduling.",
// // // // // // //         "warning"
// // // // // // //       );
// // // // // // //     }
// // // // // // //   }, [showNotification]);

// // // // // // //   useEffect(() => {
// // // // // // //     fetchInterviews();
// // // // // // //     fetchCandidates();
// // // // // // //   }, [fetchInterviews, fetchCandidates]);

// // // // // // //   const handleInputChange = (e) => {
// // // // // // //     const { name, value } = e.target;
// // // // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // // // //   };

// // // // // // //   const handleSubmit = async () => {
// // // // // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // // // // //       showNotification("Please select a candidate, date, and time.", "error");
// // // // // // //       return;
// // // // // // //     }

// // // // // // //     // If editing, keep the previous URL if the field is empty
// // // // // // //     let locationValue = formData.interviewUrl;
// // // // // // //     if (!locationValue && selectedInterview && selectedInterview.interviewUrl) {
// // // // // // //       locationValue = selectedInterview.interviewUrl;
// // // // // // //     }
// // // // // // //     if (!locationValue) {
// // // // // // //       locationValue = formData.type;
// // // // // // //     }

// // // // // // //     const payload = {
// // // // // // //       application: formData.applicationId,
// // // // // // //       scheduled_at: `${formData.date}T${formData.time}:00Z`,
// // // // // // //       location: locationValue,
// // // // // // //       feedback: formData.notes,
// // // // // // //       interview_status: "Scheduled",
// // // // // // //       interviewer: 3,
// // // // // // //     };

// // // // // // //     try {
// // // // // // //       if (selectedInterview) {
// // // // // // //         await api.put(`/interviews/${selectedInterview.id}/`, payload);
// // // // // // //         showNotification("Interview updated successfully!", "success");
// // // // // // //       } else {
// // // // // // //         await api.post("/interviews/", payload);
// // // // // // //         showNotification("Interview scheduled successfully!", "success");
// // // // // // //       }
// // // // // // //       fetchInterviews();
// // // // // // //       handleCloseDialog();
// // // // // // //     } catch (err) {
// // // // // // //       showNotification(
// // // // // // //         err.response?.data?.detail || "An error occurred.",
// // // // // // //         "error"
// // // // // // //       );
// // // // // // //     }
// // // // // // //   };

// // // // // // //   const handleDeleteInterview = async (id) => {
// // // // // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // // // // //       try {
// // // // // // //         await api.delete(`/interviews/${id}/`);
// // // // // // //         setInterviews(interviews.filter((i) => i.id !== id));
// // // // // // //         showNotification("Interview deleted successfully!", "success");
// // // // // // //       } catch (err) {
// // // // // // //         showNotification("Failed to delete interview.", "error");
// // // // // // //       }
// // // // // // //     }
// // // // // // //   };

// // // // // // //   const handleStartInterview = (interviewUrl) => {
// // // // // // //     if (
// // // // // // //       interviewUrl &&
// // // // // // //       (interviewUrl.startsWith("http://") ||
// // // // // // //         interviewUrl.startsWith("https://"))
// // // // // // //     ) {
// // // // // // //       window.open(interviewUrl, "_blank");
// // // // // // //     } else {
// // // // // // //       showNotification("No valid interview URL available.", "warning");
// // // // // // //     }
// // // // // // //   };

// // // // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // // // //     if (tabValue === 2) return interview.status === "cancelled";
// // // // // // //     return true;
// // // // // // //   });

// // // // // // //   const handleTabChange = (event, newValue) => setTabValue(newValue);

// // // // // // //   const handleCloseDialog = () => setOpenDialog(false);

// // // // // // //   const handleOpenDialog = (interview = null) => {
// // // // // // //     if (interview) {
// // // // // // //       setSelectedInterview(interview);
// // // // // // //       setFormData({
// // // // // // //         applicationId: interview.applicationId,
// // // // // // //         date: interview.date,
// // // // // // //         time: interview.time,
// // // // // // //         type: interview.interviewType === "Video Interview" ? "video" : "audio",
// // // // // // //         notes: interview.notes,
// // // // // // //         interviewUrl: interview.interviewUrl || "",
// // // // // // //       });
// // // // // // //     } else {
// // // // // // //       setSelectedInterview(null);
// // // // // // //       setFormData({
// // // // // // //         applicationId: "",
// // // // // // //         date: "",
// // // // // // //         time: "",
// // // // // // //         type: "video",
// // // // // // //         notes: "",
// // // // // // //         interviewUrl: "",
// // // // // // //       });
// // // // // // //     }
// // // // // // //     setOpenDialog(true);
// // // // // // //   };

// // // // // // //   const getStatusColor = (status) => {
// // // // // // //     switch (status) {
// // // // // // //       case "scheduled":
// // // // // // //         return "primary";
// // // // // // //       case "completed":
// // // // // // //         return "success";
// // // // // // //       case "cancelled":
// // // // // // //         return "error";
// // // // // // //       default:
// // // // // // //         return "default";
// // // // // // //     }
// // // // // // //   };

// // // // // // //   const upcomingInterviews = interviews.filter(
// // // // // // //     (i) => i.status === "scheduled"
// // // // // // //   ).length;

// // // // // // //   return (
// // // // // // //     <Layout title="Interview Scheduler">
// // // // // // //       <Box sx={{ p: 3, position: "relative" }}>
// // // // // // //         <Grid container spacing={3} sx={{ mb: 3 }}>
// // // // // // //           <Grid item xs={12}>
// // // // // // //             <Typography variant="h4" component="h1" fontWeight="bold">
// // // // // // //               Interview Management
// // // // // // //             </Typography>
// // // // // // //           </Grid>
// // // // // // //         </Grid>

// // // // // // //         <Paper sx={{ mb: 3 }}>
// // // // // // //           <Tabs
// // // // // // //             value={tabValue}
// // // // // // //             onChange={handleTabChange}
// // // // // // //             indicatorColor="primary"
// // // // // // //             textColor="primary"
// // // // // // //             centered
// // // // // // //           >
// // // // // // //             <Tab
// // // // // // //               icon={<Badge badgeContent={upcomingInterviews} color="primary" />}
// // // // // // //               label="Scheduled"
// // // // // // //               iconPosition="start"
// // // // // // //             />
// // // // // // //             <Tab label="Completed" iconPosition="start" />
// // // // // // //             <Tab label="Cancelled" iconPosition="start" />
// // // // // // //           </Tabs>
// // // // // // //         </Paper>

// // // // // // //         {isLoading ? (
// // // // // // //           <Grid container spacing={3}>
// // // // // // //             <InterviewCardSkeleton />
// // // // // // //             <InterviewCardSkeleton />
// // // // // // //             <InterviewCardSkeleton />
// // // // // // //           </Grid>
// // // // // // //         ) : error ? (
// // // // // // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // // //             <ReportProblemIcon
// // // // // // //               sx={{ fontSize: 64, color: "error.main", mb: 2 }}
// // // // // // //             />
// // // // // // //             <Typography variant="h6">{error}</Typography>
// // // // // // //             <Button
// // // // // // //               variant="contained"
// // // // // // //               onClick={fetchInterviews}
// // // // // // //               sx={{ mt: 2 }}
// // // // // // //             >
// // // // // // //               Try Again
// // // // // // //             </Button>
// // // // // // //           </Paper>
// // // // // // //         ) : (
// // // // // // //           <Grid container spacing={3}>
// // // // // // //             {filteredInterviews.length > 0 ? (
// // // // // // //               filteredInterviews.map((interview) => (
// // // // // // //                 <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // // // //                   <Card
// // // // // // //                     sx={{
// // // // // // //                       height: "100%",
// // // // // // //                       display: "flex",
// // // // // // //                       flexDirection: "column",
// // // // // // //                       p: 1,
// // // // // // //                     }}
// // // // // // //                   >
// // // // // // //                     <CardContent sx={{ flexGrow: 1 }}>
// // // // // // //                       <Box
// // // // // // //                         sx={{
// // // // // // //                           display: "flex",
// // // // // // //                           alignItems: "flex-start",
// // // // // // //                           mb: 2,
// // // // // // //                         }}
// // // // // // //                       >
// // // // // // //                         <Avatar
// // // // // // //                           src={interview.avatar}
// // // // // // //                           sx={{ width: 56, height: 56, mr: 2 }}
// // // // // // //                         />
// // // // // // //                         <Box sx={{ flexGrow: 1 }}>
// // // // // // //                           <Typography variant="h6" fontWeight="bold">
// // // // // // //                             {interview.candidateName}
// // // // // // //                           </Typography>
// // // // // // //                           <Typography variant="body2" color="text.secondary">
// // // // // // //                             {interview.position}
// // // // // // //                           </Typography>
// // // // // // //                           <Typography variant="body2" color="text.secondary">
// // // // // // //                             {interview.experience}
// // // // // // //                           </Typography>
// // // // // // //                         </Box>
// // // // // // //                         <Chip
// // // // // // //                           label={interview.status}
// // // // // // //                           color={getStatusColor(interview.status)}
// // // // // // //                           size="small"
// // // // // // //                           variant="outlined"
// // // // // // //                         />
// // // // // // //                       </Box>

// // // // // // //                       <Box sx={{ mb: 2 }}>
// // // // // // //                         <Box
// // // // // // //                           sx={{
// // // // // // //                             display: "flex",
// // // // // // //                             alignItems: "center",
// // // // // // //                             gap: 1.5,
// // // // // // //                             mb: 1,
// // // // // // //                           }}
// // // // // // //                         >
// // // // // // //                           <CalendarIcon color="action" />
// // // // // // //                           <Typography variant="body2">
// // // // // // //                             {interview.date}
// // // // // // //                           </Typography>
// // // // // // //                         </Box>
// // // // // // //                         <Box
// // // // // // //                           sx={{
// // // // // // //                             display: "flex",
// // // // // // //                             alignItems: "center",
// // // // // // //                             gap: 1.5,
// // // // // // //                             mb: 1,
// // // // // // //                           }}
// // // // // // //                         >
// // // // // // //                           <TimeIcon color="action" />
// // // // // // //                           <Typography variant="body2">
// // // // // // //                             {interview.time}
// // // // // // //                           </Typography>
// // // // // // //                         </Box>
// // // // // // //                         <Box
// // // // // // //                           sx={{
// // // // // // //                             display: "flex",
// // // // // // //                             alignItems: "center",
// // // // // // //                             gap: 1.5,
// // // // // // //                           }}
// // // // // // //                         >
// // // // // // //                           <VideoCallIcon color="action" />
// // // // // // //                           <Typography variant="body2">
// // // // // // //                             {interview.interviewType}
// // // // // // //                           </Typography>
// // // // // // //                         </Box>
// // // // // // //                       </Box>

// // // // // // //                       {interview.skills && interview.skills.length > 0 && (
// // // // // // //                         <Box sx={{ mb: 2 }}>
// // // // // // //                           <Typography
// // // // // // //                             variant="body2"
// // // // // // //                             fontWeight="medium"
// // // // // // //                             gutterBottom
// // // // // // //                           >
// // // // // // //                             Skills:
// // // // // // //                           </Typography>
// // // // // // //                           <Box
// // // // // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
// // // // // // //                           >
// // // // // // //                             {interview.skills.map((skill) => (
// // // // // // //                               <Chip
// // // // // // //                                 key={skill}
// // // // // // //                                 label={skill}
// // // // // // //                                 size="small"
// // // // // // //                                 variant="outlined"
// // // // // // //                               />
// // // // // // //                             ))}
// // // // // // //                           </Box>
// // // // // // //                         </Box>
// // // // // // //                       )}

// // // // // // //                       <Box>
// // // // // // //                         <Typography
// // // // // // //                           variant="body2"
// // // // // // //                           fontWeight="medium"
// // // // // // //                           gutterBottom
// // // // // // //                         >
// // // // // // //                           Notes:
// // // // // // //                         </Typography>
// // // // // // //                         <Typography
// // // // // // //                           variant="body2"
// // // // // // //                           color="text.secondary"
// // // // // // //                           sx={{ fontStyle: "italic" }}
// // // // // // //                         >
// // // // // // //                           {interview.notes}
// // // // // // //                         </Typography>
// // // // // // //                       </Box>
// // // // // // //                     </CardContent>

// // // // // // //                     <CardActions
// // // // // // //                       sx={{
// // // // // // //                         justifyContent: "space-between",
// // // // // // //                         alignItems: "center",
// // // // // // //                         px: 2,
// // // // // // //                         pb: 2,
// // // // // // //                       }}
// // // // // // //                     >
// // // // // // //                       {/* Group of action buttons on the left */}
// // // // // // //                       <Box sx={{ display: "flex", gap: 1 }}>
// // // // // // //                         {/* Start Interview Button - MODIFIED AS PER REQUEST */}
// // // // // // //                         {interview.status === "scheduled" && (
// // // // // // //                           <Button
// // // // // // //                             variant="contained"
// // // // // // //                             startIcon={<VideoCallIcon />}
// // // // // // //                             onClick={() =>
// // // // // // //                               handleStartInterview(interview.location)
// // // // // // //                             }
// // // // // // //                             size="medium"
// // // // // // //                           >
// // // // // // //                             Start interview
// // // // // // //                           </Button>
// // // // // // //                         )}
// // // // // // //                         {/* Edit Button */}
// // // // // // //                         {interview.status === "scheduled" && (
// // // // // // //                           <Button
// // // // // // //                             variant="outlined"
// // // // // // //                             startIcon={<EditIcon />}
// // // // // // //                             size="medium"
// // // // // // //                             onClick={() => handleOpenDialog(interview)}
// // // // // // //                           >
// // // // // // //                             Edit
// // // // // // //                           </Button>
// // // // // // //                         )}
// // // // // // //                       </Box>

// // // // // // //                       {/* Delete button on the right */}
// // // // // // //                       <IconButton
// // // // // // //                         size="small"
// // // // // // //                         color="error"
// // // // // // //                         title="Delete Interview"
// // // // // // //                         onClick={() => handleDeleteInterview(interview.id)}
// // // // // // //                       >
// // // // // // //                         <DeleteIcon />
// // // // // // //                       </IconButton>
// // // // // // //                     </CardActions>
// // // // // // //                   </Card>
// // // // // // //                 </Grid>
// // // // // // //               ))
// // // // // // //             ) : (
// // // // // // //               <Grid item xs={12}>
// // // // // // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // // //                   <ScheduleIcon
// // // // // // //                     sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
// // // // // // //                   />
// // // // // // //                   <Typography variant="h6" color="textSecondary">
// // // // // // //                     No {tabValue === 0 ? "scheduled" : "interviews"} found
// // // // // // //                   </Typography>
// // // // // // //                 </Paper>
// // // // // // //               </Grid>
// // // // // // //             )}
// // // // // // //           </Grid>
// // // // // // //         )}
// // // // // // //       </Box>

// // // // // // //       <Fab
// // // // // // //         color="primary"
// // // // // // //         aria-label="schedule interview"
// // // // // // //         onClick={() => handleOpenDialog()}
// // // // // // //         sx={{ position: "fixed", bottom: 32, right: 32 }}
// // // // // // //       >
// // // // // // //         <AddIcon />
// // // // // // //       </Fab>

// // // // // // //       <Dialog
// // // // // // //         open={openDialog}
// // // // // // //         onClose={handleCloseDialog}
// // // // // // //         maxWidth="sm"
// // // // // // //         fullWidth
// // // // // // //       >
// // // // // // //         <DialogTitle>
// // // // // // //           {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // // // //         </DialogTitle>
// // // // // // //         <MuiDialogActions>
// // // // // // //           <Grid container spacing={3} sx={{ pt: 1, px: 3 }}>
// // // // // // //             <Grid item xs={12}>
// // // // // // //               <FormControl fullWidth>
// // // // // // //                 <InputLabel>Candidate Application</InputLabel>
// // // // // // //                 <Select
// // // // // // //                   name="applicationId"
// // // // // // //                   value={formData.applicationId}
// // // // // // //                   onChange={handleInputChange}
// // // // // // //                   label="Candidate Application"
// // // // // // //                 >
// // // // // // //                   {candidates.map((candidate) => (
// // // // // // //                     <MenuItem
// // // // // // //                       key={candidate.applicationId}
// // // // // // //                       value={candidate.applicationId}
// // // // // // //                     >
// // // // // // //                       <Box
// // // // // // //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
// // // // // // //                       >
// // // // // // //                         <Avatar
// // // // // // //                           src={candidate.avatar}
// // // // // // //                           sx={{ width: 32, height: 32 }}
// // // // // // //                         />
// // // // // // //                         <Box>
// // // // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // // // //                             {candidate.name}
// // // // // // //                           </Typography>
// // // // // // //                           <Typography variant="caption" color="textSecondary">
// // // // // // //                             {candidate.position}
// // // // // // //                           </Typography>
// // // // // // //                         </Box>
// // // // // // //                       </Box>
// // // // // // //                     </MenuItem>
// // // // // // //                   ))}
// // // // // // //                 </Select>
// // // // // // //               </FormControl>
// // // // // // //             </Grid>

// // // // // // //             <Grid item xs={12} sm={6}>
// // // // // // //               <TextField
// // // // // // //                 fullWidth
// // // // // // //                 label="Date"
// // // // // // //                 name="date"
// // // // // // //                 type="date"
// // // // // // //                 value={formData.date}
// // // // // // //                 onChange={handleInputChange}
// // // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // // //                 required
// // // // // // //               />
// // // // // // //             </Grid>

// // // // // // //             <Grid item xs={12} sm={6}>
// // // // // // //               <TextField
// // // // // // //                 fullWidth
// // // // // // //                 label="Time"
// // // // // // //                 name="time"
// // // // // // //                 type="time"
// // // // // // //                 value={formData.time}
// // // // // // //                 onChange={handleInputChange}
// // // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // // //                 required
// // // // // // //               />
// // // // // // //             </Grid>

// // // // // // //             <Grid item xs={12}>
// // // // // // //               <FormControl fullWidth>
// // // // // // //                 <InputLabel>Type</InputLabel>
// // // // // // //                 <Select
// // // // // // //                   name="type"
// // // // // // //                   value={formData.type}
// // // // // // //                   onChange={handleInputChange}
// // // // // // //                   label="Type"
// // // // // // //                 >
// // // // // // //                   <MenuItem value="video">Video</MenuItem>
// // // // // // //                   {/* <MenuItem value="audio">Audio</MenuItem> */}
// // // // // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // // // // //                 </Select>
// // // // // // //               </FormControl>
// // // // // // //             </Grid>

// // // // // // //             <Grid item xs={12}>
// // // // // // //               <TextField
// // // // // // //                 fullWidth
// // // // // // //                 label="Interview URL"
// // // // // // //                 name="interviewUrl"
// // // // // // //                 value={formData.interviewUrl}
// // // // // // //                 onChange={handleInputChange}
// // // // // // //                 placeholder="https://meet.google.com/abc-defg-hij or https://zoom.us/j/123456789"
// // // // // // //                 helperText="Enter the meeting URL for the interview"
// // // // // // //               />
// // // // // // //             </Grid>

// // // // // // //             <Grid item xs={12}>
// // // // // // //               <TextField
// // // // // // //                 fullWidth
// // // // // // //                 label="Notes / Feedback"
// // // // // // //                 name="notes"
// // // // // // //                 value={formData.notes}
// // // // // // //                 onChange={handleInputChange}
// // // // // // //                 multiline
// // // // // // //                 rows={3}
// // // // // // //               />
// // // // // // //             </Grid>
// // // // // // //           </Grid>
// // // // // // //         </MuiDialogActions>

// // // // // // //         <MuiDialogActions sx={{ p: 3 }}>
// // // // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // // // //           <Button onClick={handleSubmit} variant="contained">
// // // // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // // // //           </Button>
// // // // // // //         </MuiDialogActions>
// // // // // // //       </Dialog>
// // // // // // //     </Layout>
// // // // // // //   );
// // // // // // // }

// // // // // // // export default InterviewScheduler;
// // // // // // import { useState, useEffect, useCallback } from "react";
// // // // // // // REMOVED: import axios from "axios";
// // // // // // // ADDED: Import the centralized apiClient
// // // // // // import apiClient from "../../context/apiClient";
// // // // // // import {
// // // // // //   Box,
// // // // // //   Typography,
// // // // // //   Grid,
// // // // // //   Card,
// // // // // //   CardContent,
// // // // // //   CardActions,
// // // // // //   Button,
// // // // // //   TextField,
// // // // // //   FormControl,
// // // // // //   InputLabel,
// // // // // //   Select,
// // // // // //   MenuItem,
// // // // // //   Dialog,
// // // // // //   DialogTitle,
// // // // // //   DialogActions as MuiDialogActions,
// // // // // //   IconButton,
// // // // // //   Chip,
// // // // // //   Tabs,
// // // // // //   Tab,
// // // // // //   Paper,
// // // // // //   Divider,
// // // // // //   Avatar,
// // // // // //   Badge,
// // // // // //   Skeleton,
// // // // // //   Fab,
// // // // // // } from "@mui/material";
// // // // // // import {
// // // // // //   Add as AddIcon,
// // // // // //   VideoCall as VideoCallIcon,
// // // // // //   CalendarMonth as CalendarIcon,
// // // // // //   Delete as DeleteIcon,
// // // // // //   Edit as EditIcon,
// // // // // //   Schedule as ScheduleIcon,
// // // // // //   AccessTime as TimeIcon,
// // // // // //   ReportProblem as ReportProblemIcon,
// // // // // // } from "@mui/icons-material";
// // // // // // import Layout from "../common/Layout";
// // // // // // import { useNotification } from "../../context/NotificationContext";

// // // // // // // REMOVED: API_BASE_URL constant and local axios instance with interceptor.
// // // // // // // The apiClient handles all of this now.

// // // // // // const formatApiData = (apiInterview) => {
// // // // // //   // Support both new and old API formats
// // // // // //   return {
// // // // // //     id: apiInterview.id,
// // // // // //     applicationId: apiInterview.application || apiInterview.applicationId,
// // // // // //     candidateName:
// // // // // //       apiInterview.candidate_name ||
// // // // // //       apiInterview.application_details?.talent_details?.user?.username ||
// // // // // //       apiInterview.interviewer_details?.username ||
// // // // // //       "N/A",
// // // // // //     position:
// // // // // //       apiInterview.job_title ||
// // // // // //       apiInterview.application_details?.job_posting_details?.title ||
// // // // // //       "N/A",
// // // // // //     experience:
// // // // // //       apiInterview.experience_level ||
// // // // // //       apiInterview.application_details?.job_posting_details
// // // // // //         ?.experience_level_display ||
// // // // // //       "N/A",
// // // // // //     location: apiInterview.location || "N/A",
// // // // // //     status: (
// // // // // //       apiInterview.status ||
// // // // // //       apiInterview.interview_status ||
// // // // // //       "unknown"
// // // // // //     ).toLowerCase(),
// // // // // //     date:
// // // // // //       apiInterview.interview_date ||
// // // // // //       (apiInterview.scheduled_at
// // // // // //         ? new Date(apiInterview.scheduled_at).toLocaleDateString("en-US", {
// // // // // //             weekday: "short",
// // // // // //             month: "short",
// // // // // //             day: "numeric",
// // // // // //             year: "numeric",
// // // // // //           })
// // // // // //         : "N/A"),
// // // // // //     time:
// // // // // //       apiInterview.interview_time ||
// // // // // //       (apiInterview.scheduled_at
// // // // // //         ? new Date(apiInterview.scheduled_at).toLocaleTimeString("en-US", {
// // // // // //             hour: "2-digit",
// // // // // //             minute: "2-digit",
// // // // // //             hour12: false,
// // // // // //           })
// // // // // //         : "N/A"),
// // // // // //     interviewType:
// // // // // //       apiInterview.interview_mode ||
// // // // // //       apiInterview.interview_type ||
// // // // // //       (apiInterview.location === "video" ? "Video Interview" : "Interview"),
// // // // // //     skills:
// // // // // //       apiInterview.skills ||
// // // // // //       apiInterview.application_details?.job_posting_details?.required_skills ||
// // // // // //       [],
// // // // // //     notes:
// // // // // //       apiInterview.notes ||
// // // // // //       apiInterview.feedback ||
// // // // // //       "No specific notes provided for this interview.",
// // // // // //     avatar:
// // // // // //       apiInterview.application_details?.talent_details?.profile_picture ||
// // // // // //       "/placeholder.svg?height=56&width=56",
// // // // // //     interviewUrl: apiInterview.location || "",
// // // // // //   };
// // // // // // };

// // // // // // const InterviewCardSkeleton = () => (
// // // // // //   <Grid item xs={12} md={6} lg={4}>
// // // // // //     <Card sx={{ height: "100%" }}>
// // // // // //       <CardContent>
// // // // // //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // // //           <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
// // // // // //           <Box sx={{ flexGrow: 1 }}>
// // // // // //             <Skeleton variant="text" width="60%" />
// // // // // //             <Skeleton variant="text" width="80%" />
// // // // // //           </Box>
// // // // // //         </Box>
// // // // // //         <Divider sx={{ my: 2 }} />
// // // // // //         <Skeleton variant="text" width="50%" sx={{ mb: 1 }} />
// // // // // //         <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
// // // // // //         <Skeleton variant="text" width="90%" />
// // // // // //       </CardContent>
// // // // // //     </Card>
// // // // // //   </Grid>
// // // // // // );

// // // // // // function InterviewScheduler() {
// // // // // //   const { showNotification } = useNotification();
// // // // // //   const [interviews, setInterviews] = useState([]);
// // // // // //   const [candidates, setCandidates] = useState([]);
// // // // // //   const [isLoading, setIsLoading] = useState(true);
// // // // // //   const [error, setError] = useState(null);
// // // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // // //   const [tabValue, setTabValue] = useState(0);
// // // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // // //   const [formData, setFormData] = useState({
// // // // // //     applicationId: "",
// // // // // //     date: "",
// // // // // //     time: "",
// // // // // //     type: "video",
// // // // // //     notes: "",
// // // // // //     interviewUrl: "",
// // // // // //   });

// // // // // //   const fetchInterviews = useCallback(async () => {
// // // // // //     setIsLoading(true);
// // // // // //     setError(null);
// // // // // //     try {
// // // // // //       // CHANGED: Use apiClient instead of api
// // // // // //       const response = await apiClient.get("/interviews/");
// // // // // //       const formattedData = response.data.map(formatApiData);
// // // // // //       setInterviews(formattedData);
// // // // // //     } catch (err) {
// // // // // //       setError("Could not load interviews.");
// // // // // //     } finally {
// // // // // //       setIsLoading(false);
// // // // // //     }
// // // // // //   }, []);

// // // // // //   const fetchCandidates = useCallback(async () => {
// // // // // //     try {
// // // // // //       // CHANGED: Use apiClient instead of api
// // // // // //       const response = await apiClient.get("/applications/");
// // // // // //       const formattedCandidates = response.data.map((app) => ({
// // // // // //         applicationId: app.id,
// // // // // //         talentId: app.talent,
// // // // // //         name:
// // // // // //           `${app.talent_details?.user?.first_name || ""} ${
// // // // // //             app.talent_details?.user?.last_name || ""
// // // // // //           }`.trim() || `Talent #${app.talent}`,
// // // // // //         position: app.job_posting_details?.title || "Applied Position",
// // // // // //         avatar:
// // // // // //           app.talent_details?.profile_picture ||
// // // // // //           "/placeholder.svg?height=40&width=40",
// // // // // //       }));
// // // // // //       setCandidates(formattedCandidates);
// // // // // //     } catch (err) {
// // // // // //       showNotification(
// // // // // //         "Could not load candidate list for scheduling.",
// // // // // //         "warning"
// // // // // //       );
// // // // // //     }
// // // // // //   }, [showNotification]);

// // // // // //   useEffect(() => {
// // // // // //     fetchInterviews();
// // // // // //     fetchCandidates();
// // // // // //   }, [fetchInterviews, fetchCandidates]);

// // // // // //   const handleInputChange = (e) => {
// // // // // //     const { name, value } = e.target;
// // // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // // //   };

// // // // // //   const handleSubmit = async () => {
// // // // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // // // //       showNotification("Please select a candidate, date, and time.", "error");
// // // // // //       return;
// // // // // //     }

// // // // // //     let locationValue = formData.interviewUrl;
// // // // // //     if (!locationValue && selectedInterview && selectedInterview.interviewUrl) {
// // // // // //       locationValue = selectedInterview.interviewUrl;
// // // // // //     }
// // // // // //     if (!locationValue) {
// // // // // //       locationValue = formData.type;
// // // // // //     }

// // // // // //     const payload = {
// // // // // //       application: formData.applicationId,
// // // // // //       scheduled_at: `${formData.date}T${formData.time}:00Z`,
// // // // // //       location: locationValue,
// // // // // //       feedback: formData.notes,
// // // // // //       interview_status: "Scheduled",
// // // // // //       interviewer: 3,
// // // // // //     };

// // // // // //     try {
// // // // // //       if (selectedInterview) {
// // // // // //         // CHANGED: Use apiClient instead of api
// // // // // //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload);
// // // // // //         showNotification("Interview updated successfully!", "success");
// // // // // //       } else {
// // // // // //         // CHANGED: Use apiClient instead of api
// // // // // //         await apiClient.post("/interviews/", payload);
// // // // // //         showNotification("Interview scheduled successfully!", "success");
// // // // // //       }
// // // // // //       fetchInterviews();
// // // // // //       handleCloseDialog();
// // // // // //     } catch (err) {
// // // // // //       showNotification(
// // // // // //         err.response?.data?.detail || "An error occurred.",
// // // // // //         "error"
// // // // // //       );
// // // // // //     }
// // // // // //   };

// // // // // //   const handleDeleteInterview = async (id) => {
// // // // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // // // //       try {
// // // // // //         // CHANGED: Use apiClient instead of api
// // // // // //         await apiClient.delete(`/interviews/${id}/`);
// // // // // //         setInterviews(interviews.filter((i) => i.id !== id));
// // // // // //         showNotification("Interview deleted successfully!", "success");
// // // // // //       } catch (err) {
// // // // // //         showNotification("Failed to delete interview.", "error");
// // // // // //       }
// // // // // //     }
// // // // // //   };

// // // // // //   const handleStartInterview = (interviewUrl) => {
// // // // // //     if (
// // // // // //       interviewUrl &&
// // // // // //       (interviewUrl.startsWith("http://") ||
// // // // // //         interviewUrl.startsWith("https://"))
// // // // // //     ) {
// // // // // //       window.open(interviewUrl, "_blank");
// // // // // //     } else {
// // // // // //       showNotification("No valid interview URL available.", "warning");
// // // // // //     }
// // // // // //   };

// // // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // // //     if (tabValue === 2) return interview.status === "cancelled";
// // // // // //     return true;
// // // // // //   });

// // // // // //   const handleTabChange = (event, newValue) => setTabValue(newValue);

// // // // // //   const handleCloseDialog = () => setOpenDialog(false);

// // // // // //   const handleOpenDialog = (interview = null) => {
// // // // // //     if (interview) {
// // // // // //       setSelectedInterview(interview);
// // // // // //       setFormData({
// // // // // //         applicationId: interview.applicationId,
// // // // // //         date: interview.date,
// // // // // //         time: interview.time,
// // // // // //         type: interview.interviewType === "Video Interview" ? "video" : "audio",
// // // // // //         notes: interview.notes,
// // // // // //         interviewUrl: interview.interviewUrl || "",
// // // // // //       });
// // // // // //     } else {
// // // // // //       setSelectedInterview(null);
// // // // // //       setFormData({
// // // // // //         applicationId: "",
// // // // // //         date: "",
// // // // // //         time: "",
// // // // // //         type: "video",
// // // // // //         notes: "",
// // // // // //         interviewUrl: "",
// // // // // //       });
// // // // // //     }
// // // // // //     setOpenDialog(true);
// // // // // //   };

// // // // // //   const getStatusColor = (status) => {
// // // // // //     switch (status) {
// // // // // //       case "scheduled":
// // // // // //         return "primary";
// // // // // //       case "completed":
// // // // // //         return "success";
// // // // // //       case "cancelled":
// // // // // //         return "error";
// // // // // //       default:
// // // // // //         return "default";
// // // // // //     }
// // // // // //   };

// // // // // //   const upcomingInterviews = interviews.filter(
// // // // // //     (i) => i.status === "scheduled"
// // // // // //   ).length;

// // // // // //   return (
// // // // // //     <Layout title="Interview Scheduler">
// // // // // //       <Box sx={{ p: 3, position: "relative" }}>
// // // // // //         <Grid container spacing={3} sx={{ mb: 3 }}>
// // // // // //           <Grid item xs={12}>
// // // // // //             <Typography variant="h4" component="h1" fontWeight="bold">
// // // // // //               Interview Management
// // // // // //             </Typography>
// // // // // //           </Grid>
// // // // // //         </Grid>

// // // // // //         <Paper sx={{ mb: 3 }}>
// // // // // //           <Tabs
// // // // // //             value={tabValue}
// // // // // //             onChange={handleTabChange}
// // // // // //             indicatorColor="primary"
// // // // // //             textColor="primary"
// // // // // //             centered
// // // // // //           >
// // // // // //             <Tab
// // // // // //               icon={<Badge badgeContent={upcomingInterviews} color="primary" />}
// // // // // //               label="Scheduled"
// // // // // //               iconPosition="start"
// // // // // //             />
// // // // // //             <Tab label="Completed" iconPosition="start" />
// // // // // //             <Tab label="Cancelled" iconPosition="start" />
// // // // // //           </Tabs>
// // // // // //         </Paper>

// // // // // //         {isLoading ? (
// // // // // //           <Grid container spacing={3}>
// // // // // //             <InterviewCardSkeleton />
// // // // // //             <InterviewCardSkeleton />
// // // // // //             <InterviewCardSkeleton />
// // // // // //           </Grid>
// // // // // //         ) : error ? (
// // // // // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // //             <ReportProblemIcon
// // // // // //               sx={{ fontSize: 64, color: "error.main", mb: 2 }}
// // // // // //             />
// // // // // //             <Typography variant="h6">{error}</Typography>
// // // // // //             <Button
// // // // // //               variant="contained"
// // // // // //               onClick={fetchInterviews}
// // // // // //               sx={{ mt: 2 }}
// // // // // //             >
// // // // // //               Try Again
// // // // // //             </Button>
// // // // // //           </Paper>
// // // // // //         ) : (
// // // // // //           <Grid container spacing={3}>
// // // // // //             {filteredInterviews.length > 0 ? (
// // // // // //               filteredInterviews.map((interview) => (
// // // // // //                 <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // // //                   <Card
// // // // // //                     sx={{
// // // // // //                       height: "100%",
// // // // // //                       display: "flex",
// // // // // //                       flexDirection: "column",
// // // // // //                       p: 1,
// // // // // //                     }}
// // // // // //                   >
// // // // // //                     <CardContent sx={{ flexGrow: 1 }}>
// // // // // //                       <Box
// // // // // //                         sx={{
// // // // // //                           display: "flex",
// // // // // //                           alignItems: "flex-start",
// // // // // //                           mb: 2,
// // // // // //                         }}
// // // // // //                       >
// // // // // //                         <Avatar
// // // // // //                           src={interview.avatar}
// // // // // //                           sx={{ width: 56, height: 56, mr: 2 }}
// // // // // //                         />
// // // // // //                         <Box sx={{ flexGrow: 1 }}>
// // // // // //                           <Typography variant="h6" fontWeight="bold">
// // // // // //                             {interview.candidateName}
// // // // // //                           </Typography>
// // // // // //                           <Typography variant="body2" color="text.secondary">
// // // // // //                             {interview.position}
// // // // // //                           </Typography>
// // // // // //                           <Typography variant="body2" color="text.secondary">
// // // // // //                             {interview.experience}
// // // // // //                           </Typography>
// // // // // //                         </Box>
// // // // // //                         <Chip
// // // // // //                           label={interview.status}
// // // // // //                           color={getStatusColor(interview.status)}
// // // // // //                           size="small"
// // // // // //                           variant="outlined"
// // // // // //                         />
// // // // // //                       </Box>

// // // // // //                       <Box sx={{ mb: 2 }}>
// // // // // //                         <Box
// // // // // //                           sx={{
// // // // // //                             display: "flex",
// // // // // //                             alignItems: "center",
// // // // // //                             gap: 1.5,
// // // // // //                             mb: 1,
// // // // // //                           }}
// // // // // //                         >
// // // // // //                           <CalendarIcon color="action" />
// // // // // //                           <Typography variant="body2">
// // // // // //                             {interview.date}
// // // // // //                           </Typography>
// // // // // //                         </Box>
// // // // // //                         <Box
// // // // // //                           sx={{
// // // // // //                             display: "flex",
// // // // // //                             alignItems: "center",
// // // // // //                             gap: 1.5,
// // // // // //                             mb: 1,
// // // // // //                           }}
// // // // // //                         >
// // // // // //                           <TimeIcon color="action" />
// // // // // //                           <Typography variant="body2">
// // // // // //                             {interview.time}
// // // // // //                           </Typography>
// // // // // //                         </Box>
// // // // // //                         <Box
// // // // // //                           sx={{
// // // // // //                             display: "flex",
// // // // // //                             alignItems: "center",
// // // // // //                             gap: 1.5,
// // // // // //                           }}
// // // // // //                         >
// // // // // //                           <VideoCallIcon color="action" />
// // // // // //                           <Typography variant="body2">
// // // // // //                             {interview.interviewType}
// // // // // //                           </Typography>
// // // // // //                         </Box>
// // // // // //                       </Box>

// // // // // //                       {interview.skills && interview.skills.length > 0 && (
// // // // // //                         <Box sx={{ mb: 2 }}>
// // // // // //                           <Typography
// // // // // //                             variant="body2"
// // // // // //                             fontWeight="medium"
// // // // // //                             gutterBottom
// // // // // //                           >
// // // // // //                             Skills:
// // // // // //                           </Typography>
// // // // // //                           <Box
// // // // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
// // // // // //                           >
// // // // // //                             {interview.skills.map((skill) => (
// // // // // //                               <Chip
// // // // // //                                 key={skill}
// // // // // //                                 label={skill}
// // // // // //                                 size="small"
// // // // // //                                 variant="outlined"
// // // // // //                               />
// // // // // //                             ))}
// // // // // //                           </Box>
// // // // // //                         </Box>
// // // // // //                       )}

// // // // // //                       <Box>
// // // // // //                         <Typography
// // // // // //                           variant="body2"
// // // // // //                           fontWeight="medium"
// // // // // //                           gutterBottom
// // // // // //                         >
// // // // // //                           Notes:
// // // // // //                         </Typography>
// // // // // //                         <Typography
// // // // // //                           variant="body2"
// // // // // //                           color="text.secondary"
// // // // // //                           sx={{ fontStyle: "italic" }}
// // // // // //                         >
// // // // // //                           {interview.notes}
// // // // // //                         </Typography>
// // // // // //                       </Box>
// // // // // //                     </CardContent>

// // // // // //                     <CardActions
// // // // // //                       sx={{
// // // // // //                         justifyContent: "space-between",
// // // // // //                         alignItems: "center",
// // // // // //                         px: 2,
// // // // // //                         pb: 2,
// // // // // //                       }}
// // // // // //                     >
// // // // // //                       <Box sx={{ display: "flex", gap: 1 }}>
// // // // // //                         {interview.status === "scheduled" && (
// // // // // //                           <Button
// // // // // //                             variant="contained"
// // // // // //                             startIcon={<VideoCallIcon />}
// // // // // //                             onClick={() =>
// // // // // //                               handleStartInterview(interview.location)
// // // // // //                             }
// // // // // //                             size="medium"
// // // // // //                           >
// // // // // //                             Start interview
// // // // // //                           </Button>
// // // // // //                         )}
// // // // // //                         {interview.status === "scheduled" && (
// // // // // //                           <Button
// // // // // //                             variant="outlined"
// // // // // //                             startIcon={<EditIcon />}
// // // // // //                             size="medium"
// // // // // //                             onClick={() => handleOpenDialog(interview)}
// // // // // //                           >
// // // // // //                             Edit
// // // // // //                           </Button>
// // // // // //                         )}
// // // // // //                       </Box>

// // // // // //                       <IconButton
// // // // // //                         size="small"
// // // // // //                         color="error"
// // // // // //                         title="Delete Interview"
// // // // // //                         onClick={() => handleDeleteInterview(interview.id)}
// // // // // //                       >
// // // // // //                         <DeleteIcon />
// // // // // //                       </IconButton>
// // // // // //                     </CardActions>
// // // // // //                   </Card>
// // // // // //                 </Grid>
// // // // // //               ))
// // // // // //             ) : (
// // // // // //               <Grid item xs={12}>
// // // // // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // // //                   <ScheduleIcon
// // // // // //                     sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
// // // // // //                   />
// // // // // //                   <Typography variant="h6" color="textSecondary">
// // // // // //                     No {tabValue === 0 ? "scheduled" : "interviews"} found
// // // // // //                   </Typography>
// // // // // //                 </Paper>
// // // // // //               </Grid>
// // // // // //             )}
// // // // // //           </Grid>
// // // // // //         )}
// // // // // //       </Box>

// // // // // //       <Fab
// // // // // //         color="primary"
// // // // // //         aria-label="schedule interview"
// // // // // //         onClick={() => handleOpenDialog()}
// // // // // //         sx={{ position: "fixed", bottom: 32, right: 32 }}
// // // // // //       >
// // // // // //         <AddIcon />
// // // // // //       </Fab>

// // // // // //       <Dialog
// // // // // //         open={openDialog}
// // // // // //         onClose={handleCloseDialog}
// // // // // //         maxWidth="sm"
// // // // // //         fullWidth
// // // // // //       >
// // // // // //         <DialogTitle>
// // // // // //           {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // // //         </DialogTitle>
// // // // // //         <MuiDialogActions>
// // // // // //           <Grid container spacing={3} sx={{ pt: 1, px: 3 }}>
// // // // // //             <Grid item xs={12}>
// // // // // //               <FormControl fullWidth>
// // // // // //                 <InputLabel>Candidate Application</InputLabel>
// // // // // //                 <Select
// // // // // //                   name="applicationId"
// // // // // //                   value={formData.applicationId}
// // // // // //                   onChange={handleInputChange}
// // // // // //                   label="Candidate Application"
// // // // // //                 >
// // // // // //                   {candidates.map((candidate) => (
// // // // // //                     <MenuItem
// // // // // //                       key={candidate.applicationId}
// // // // // //                       value={candidate.applicationId}
// // // // // //                     >
// // // // // //                       <Box
// // // // // //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
// // // // // //                       >
// // // // // //                         <Avatar
// // // // // //                           src={candidate.avatar}
// // // // // //                           sx={{ width: 32, height: 32 }}
// // // // // //                         />
// // // // // //                         <Box>
// // // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // // //                             {candidate.name}
// // // // // //                           </Typography>
// // // // // //                           <Typography variant="caption" color="textSecondary">
// // // // // //                             {candidate.position}
// // // // // //                           </Typography>
// // // // // //                         </Box>
// // // // // //                       </Box>
// // // // // //                     </MenuItem>
// // // // // //                   ))}
// // // // // //                 </Select>
// // // // // //               </FormControl>
// // // // // //             </Grid>

// // // // // //             <Grid item xs={12} sm={6}>
// // // // // //               <TextField
// // // // // //                 fullWidth
// // // // // //                 label="Date"
// // // // // //                 name="date"
// // // // // //                 type="date"
// // // // // //                 value={formData.date}
// // // // // //                 onChange={handleInputChange}
// // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // //                 required
// // // // // //               />
// // // // // //             </Grid>

// // // // // //             <Grid item xs={12} sm={6}>
// // // // // //               <TextField
// // // // // //                 fullWidth
// // // // // //                 label="Time"
// // // // // //                 name="time"
// // // // // //                 type="time"
// // // // // //                 value={formData.time}
// // // // // //                 onChange={handleInputChange}
// // // // // //                 InputLabelProps={{ shrink: true }}
// // // // // //                 required
// // // // // //               />
// // // // // //             </Grid>

// // // // // //             <Grid item xs={12}>
// // // // // //               <FormControl fullWidth>
// // // // // //                 <InputLabel>Type</InputLabel>
// // // // // //                 <Select
// // // // // //                   name="type"
// // // // // //                   value={formData.type}
// // // // // //                   onChange={handleInputChange}
// // // // // //                   label="Type"
// // // // // //                 >
// // // // // //                   <MenuItem value="video">Video</MenuItem>
// // // // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // // // //                 </Select>
// // // // // //               </FormControl>
// // // // // //             </Grid>

// // // // // //             <Grid item xs={12}>
// // // // // //               <TextField
// // // // // //                 fullWidth
// // // // // //                 label="Interview URL"
// // // // // //                 name="interviewUrl"
// // // // // //                 value={formData.interviewUrl}
// // // // // //                 onChange={handleInputChange}
// // // // // //                 placeholder="https://meet.google.com/abc-defg-hij or https://zoom.us/j/123456789"
// // // // // //                 helperText="Enter the meeting URL for the interview"
// // // // // //               />
// // // // // //             </Grid>

// // // // // //             <Grid item xs={12}>
// // // // // //               <TextField
// // // // // //                 fullWidth
// // // // // //                 label="Notes / Feedback"
// // // // // //                 name="notes"
// // // // // //                 value={formData.notes}
// // // // // //                 onChange={handleInputChange}
// // // // // //                 multiline
// // // // // //                 rows={3}
// // // // // //               />
// // // // // //             </Grid>
// // // // // //           </Grid>
// // // // // //         </MuiDialogActions>

// // // // // //         <MuiDialogActions sx={{ p: 3 }}>
// // // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // // //           <Button onClick={handleSubmit} variant="contained">
// // // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // // //           </Button>
// // // // // //         </MuiDialogActions>
// // // // // //       </Dialog>
// // // // // //     </Layout>
// // // // // //   );
// // // // // // }

// // // // // // export default InterviewScheduler;
// // // // // import { useState, useEffect, useCallback } from "react";
// // // // // // ADDED: Import the centralized apiClient
// // // // // import apiClient from "../../context/apiClient";
// // // // // import {
// // // // //   Box,
// // // // //   Typography,
// // // // //   Grid,
// // // // //   Card,
// // // // //   CardContent,
// // // // //   CardActions,
// // // // //   Button,
// // // // //   TextField,
// // // // //   FormControl,
// // // // //   InputLabel,
// // // // //   Select,
// // // // //   MenuItem,
// // // // //   Dialog,
// // // // //   DialogTitle,
// // // // //   DialogActions as MuiDialogActions,
// // // // //   IconButton,
// // // // //   Chip,
// // // // //   Tabs,
// // // // //   Tab,
// // // // //   Paper,
// // // // //   Divider,
// // // // //   Avatar,
// // // // //   Badge,
// // // // //   Skeleton, // ADDED: Import Skeleton component
// // // // //   Fab,
// // // // // } from "@mui/material";
// // // // // import {
// // // // //   Add as AddIcon,
// // // // //   VideoCall as VideoCallIcon,
// // // // //   CalendarMonth as CalendarIcon,
// // // // //   Delete as DeleteIcon,
// // // // //   Edit as EditIcon,
// // // // //   Schedule as ScheduleIcon,
// // // // //   AccessTime as TimeIcon,
// // // // //   ReportProblem as ReportProblemIcon,
// // // // // } from "@mui/icons-material";
// // // // // import Layout from "../common/Layout";
// // // // // import { useNotification } from "../../context/NotificationContext";

// // // // // // The apiClient handles all of this now.

// // // // // const formatApiData = (apiInterview) => {
// // // // //   // Support both new and old API formats
// // // // //   return {
// // // // //     id: apiInterview.id,
// // // // //     applicationId: apiInterview.application || apiInterview.applicationId,
// // // // //     candidateName:
// // // // //       apiInterview.candidate_name ||
// // // // //       apiInterview.application_details?.talent_details?.user?.username ||
// // // // //       apiInterview.interviewer_details?.username ||
// // // // //       "N/A",
// // // // //     position:
// // // // //       apiInterview.job_title ||
// // // // //       apiInterview.application_details?.job_posting_details?.title ||
// // // // //       "N/A",
// // // // //     experience:
// // // // //       apiInterview.experience_level ||
// // // // //       apiInterview.application_details?.job_posting_details
// // // // //         ?.experience_level_display ||
// // // // //       "N/A",
// // // // //     location: apiInterview.location || "N/A",
// // // // //     status: (
// // // // //       apiInterview.status ||
// // // // //       apiInterview.interview_status ||
// // // // //       "unknown"
// // // // //     ).toLowerCase(),
// // // // //     date:
// // // // //       apiInterview.interview_date ||
// // // // //       (apiInterview.scheduled_at
// // // // //         ? new Date(apiInterview.scheduled_at).toLocaleDateString("en-US", {
// // // // //             weekday: "short",
// // // // //             month: "short",
// // // // //             day: "numeric",
// // // // //             year: "numeric",
// // // // //           })
// // // // //         : "N/A"),
// // // // //     time:
// // // // //       apiInterview.interview_time ||
// // // // //       (apiInterview.scheduled_at
// // // // //         ? new Date(apiInterview.scheduled_at).toLocaleTimeString("en-US", {
// // // // //             hour: "2-digit",
// // // // //             minute: "2-digit",
// // // // //             hour12: false,
// // // // //           })
// // // // //         : "N/A"),
// // // // //     interviewType:
// // // // //       apiInterview.interview_mode ||
// // // // //       apiInterview.interview_type ||
// // // // //       (apiInterview.location === "video" ? "Video Interview" : "Interview"),
// // // // //     skills:
// // // // //       apiInterview.skills ||
// // // // //       apiInterview.application_details?.job_posting_details?.required_skills ||
// // // // //       [],
// // // // //     notes:
// // // // //       apiInterview.notes ||
// // // // //       apiInterview.feedback ||
// // // // //       "No specific notes provided for this interview.",
// // // // //     avatar:
// // // // //       apiInterview.application_details?.talent_details?.profile_picture ||
// // // // //       "/placeholder.svg?height=56&width=56",
// // // // //     interviewUrl: apiInterview.location || "",
// // // // //   };
// // // // // };

// // // // // // ADDED: Skeleton component for the interview card
// // // // // const InterviewCardSkeleton = () => (
// // // // //   <Grid item xs={12} md={6} lg={4}>
// // // // //     <Card
// // // // //       sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}
// // // // //     >
// // // // //       <CardContent sx={{ flexGrow: 1 }}>
// // // // //         <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
// // // // //           <Skeleton
// // // // //             variant="circular"
// // // // //             width={56}
// // // // //             height={56}
// // // // //             sx={{ mr: 2, mt: 1 }}
// // // // //           />
// // // // //           <Box sx={{ flexGrow: 1 }}>
// // // // //             <Skeleton variant="text" sx={{ fontSize: "1.25rem" }} width="80%" />
// // // // //             <Skeleton
// // // // //               variant="text"
// // // // //               sx={{ fontSize: "0.875rem" }}
// // // // //               width="60%"
// // // // //             />
// // // // //             <Skeleton
// // // // //               variant="text"
// // // // //               sx={{ fontSize: "0.875rem" }}
// // // // //               width="50%"
// // // // //             />
// // // // //           </Box>
// // // // //           <Skeleton
// // // // //             variant="rectangular"
// // // // //             width={80}
// // // // //             height={24}
// // // // //             sx={{ borderRadius: "16px" }}
// // // // //           />
// // // // //         </Box>
// // // // //         <Box sx={{ mb: 2 }}>
// // // // //           <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
// // // // //             <Skeleton variant="circular" width={24} height={24} />
// // // // //             <Skeleton variant="text" width="70%" />
// // // // //           </Box>
// // // // //           <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
// // // // //             <Skeleton variant="circular" width={24} height={24} />
// // // // //             <Skeleton variant="text" width="40%" />
// // // // //           </Box>
// // // // //           <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// // // // //             <Skeleton variant="circular" width={24} height={24} />
// // // // //             <Skeleton variant="text" width="50%" />
// // // // //           </Box>
// // // // //         </Box>
// // // // //         <Box>
// // // // //           <Skeleton variant="text" width="25%" sx={{ mb: 1 }} />
// // // // //           <Skeleton variant="rectangular" width="100%" height={60} />
// // // // //         </Box>
// // // // //       </CardContent>
// // // // //       <CardActions
// // // // //         sx={{
// // // // //           justifyContent: "space-between",
// // // // //           alignItems: "center",
// // // // //           px: 2,
// // // // //           pb: 2,
// // // // //         }}
// // // // //       >
// // // // //         <Box sx={{ display: "flex", gap: 1 }}>
// // // // //           <Skeleton
// // // // //             variant="rectangular"
// // // // //             width={150}
// // // // //             height={36}
// // // // //             sx={{ borderRadius: "4px" }}
// // // // //           />
// // // // //           <Skeleton
// // // // //             variant="rectangular"
// // // // //             width={70}
// // // // //             height={36}
// // // // //             sx={{ borderRadius: "4px" }}
// // // // //           />
// // // // //         </Box>
// // // // //         <Skeleton variant="circular" width={32} height={32} />
// // // // //       </CardActions>
// // // // //     </Card>
// // // // //   </Grid>
// // // // // );

// // // // // function InterviewScheduler() {
// // // // //   const { showNotification } = useNotification();
// // // // //   const [interviews, setInterviews] = useState([]);
// // // // //   const [candidates, setCandidates] = useState([]);
// // // // //   const [isLoading, setIsLoading] = useState(true);
// // // // //   const [error, setError] = useState(null);
// // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // //   const [tabValue, setTabValue] = useState(0);
// // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // //   const [formData, setFormData] = useState({
// // // // //     applicationId: "",
// // // // //     date: "",
// // // // //     time: "",
// // // // //     type: "video",
// // // // //     notes: "",
// // // // //     interviewUrl: "",
// // // // //   });

// // // // //   const fetchInterviews = useCallback(async () => {
// // // // //     setIsLoading(true);
// // // // //     setError(null);
// // // // //     try {
// // // // //       const response = await apiClient.get("/interviews/");
// // // // //       const formattedData = response.data.map(formatApiData);
// // // // //       setInterviews(formattedData);
// // // // //     } catch (err) {
// // // // //       setError("Could not load interviews.");
// // // // //     } finally {
// // // // //       setIsLoading(false);
// // // // //     }
// // // // //   }, []);

// // // // //   const fetchCandidates = useCallback(async () => {
// // // // //     try {
// // // // //       const response = await apiClient.get("/applications/");
// // // // //       const formattedCandidates = response.data.map((app) => ({
// // // // //         applicationId: app.id,
// // // // //         talentId: app.talent,
// // // // //         name:
// // // // //           `${app.talent_details?.user?.first_name || ""} ${
// // // // //             app.talent_details?.user?.last_name || ""
// // // // //           }`.trim() || `Talent #${app.talent}`,
// // // // //         position: app.job_posting_details?.title || "Applied Position",
// // // // //         avatar:
// // // // //           app.talent_details?.profile_picture ||
// // // // //           "/placeholder.svg?height=40&width=40",
// // // // //       }));
// // // // //       setCandidates(formattedCandidates);
// // // // //     } catch (err) {
// // // // //       showNotification(
// // // // //         "Could not load candidate list for scheduling.",
// // // // //         "warning"
// // // // //       );
// // // // //     }
// // // // //   }, [showNotification]);

// // // // //   useEffect(() => {
// // // // //     fetchInterviews();
// // // // //     fetchCandidates();
// // // // //   }, [fetchInterviews, fetchCandidates]);

// // // // //   const handleInputChange = (e) => {
// // // // //     const { name, value } = e.target;
// // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // //   };

// // // // //   const handleSubmit = async () => {
// // // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // // //       showNotification("Please select a candidate, date, and time.", "error");
// // // // //       return;
// // // // //     }

// // // // //     let locationValue = formData.interviewUrl;
// // // // //     if (!locationValue && selectedInterview && selectedInterview.interviewUrl) {
// // // // //       locationValue = selectedInterview.interviewUrl;
// // // // //     }
// // // // //     if (!locationValue) {
// // // // //       locationValue = formData.type;
// // // // //     }

// // // // //     const payload = {
// // // // //       application: formData.applicationId,
// // // // //       scheduled_at: `${formData.date}T${formData.time}:00Z`,
// // // // //       location: locationValue,
// // // // //       feedback: formData.notes,
// // // // //       interview_status: "Scheduled",
// // // // //       interviewer: 3,
// // // // //     };

// // // // //     try {
// // // // //       if (selectedInterview) {
// // // // //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload);
// // // // //         showNotification("Interview updated successfully!", "success");
// // // // //       } else {
// // // // //         await apiClient.post("/interviews/", payload);
// // // // //         showNotification("Interview scheduled successfully!", "success");
// // // // //       }
// // // // //       fetchInterviews();
// // // // //       handleCloseDialog();
// // // // //     } catch (err) {
// // // // //       showNotification(
// // // // //         err.response?.data?.detail || "An error occurred.",
// // // // //         "error"
// // // // //       );
// // // // //     }
// // // // //   };

// // // // //   const handleDeleteInterview = async (id) => {
// // // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // // //       try {
// // // // //         await apiClient.delete(`/interviews/${id}/`);
// // // // //         setInterviews(interviews.filter((i) => i.id !== id));
// // // // //         showNotification("Interview deleted successfully!", "success");
// // // // //       } catch (err) {
// // // // //         showNotification("Failed to delete interview.", "error");
// // // // //       }
// // // // //     }
// // // // //   };

// // // // //   const handleStartInterview = (interviewUrl) => {
// // // // //     if (
// // // // //       interviewUrl &&
// // // // //       (interviewUrl.startsWith("http://") ||
// // // // //         interviewUrl.startsWith("https://"))
// // // // //     ) {
// // // // //       window.open(interviewUrl, "_blank");
// // // // //     } else {
// // // // //       showNotification("No valid interview URL available.", "warning");
// // // // //     }
// // // // //   };

// // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // //     if (tabValue === 2) return interview.status === "cancelled";
// // // // //     return true;
// // // // //   });

// // // // //   const handleTabChange = (event, newValue) => setTabValue(newValue);

// // // // //   const handleCloseDialog = () => setOpenDialog(false);

// // // // //   const handleOpenDialog = (interview = null) => {
// // // // //     if (interview) {
// // // // //       setSelectedInterview(interview);
// // // // //       setFormData({
// // // // //         applicationId: interview.applicationId,
// // // // //         date: interview.date,
// // // // //         time: interview.time,
// // // // //         type: interview.interviewType === "Video Interview" ? "video" : "audio",
// // // // //         notes: interview.notes,
// // // // //         interviewUrl: interview.interviewUrl || "",
// // // // //       });
// // // // //     } else {
// // // // //       setSelectedInterview(null);
// // // // //       setFormData({
// // // // //         applicationId: "",
// // // // //         date: "",
// // // // //         time: "",
// // // // //         type: "video",
// // // // //         notes: "",
// // // // //         interviewUrl: "",
// // // // //       });
// // // // //     }
// // // // //     setOpenDialog(true);
// // // // //   };

// // // // //   const getStatusColor = (status) => {
// // // // //     switch (status) {
// // // // //       case "scheduled":
// // // // //         return "primary";
// // // // //       case "completed":
// // // // //         return "success";
// // // // //       case "cancelled":
// // // // //         return "error";
// // // // //       default:
// // // // //         return "default";
// // // // //     }
// // // // //   };

// // // // //   const upcomingInterviews = interviews.filter(
// // // // //     (i) => i.status === "scheduled"
// // // // //   ).length;

// // // // //   return (
// // // // //     <Layout title="Interview Scheduler">
// // // // //       <Box sx={{ p: 3, position: "relative" }}>
// // // // //         <Grid container spacing={3} sx={{ mb: 3 }}>
// // // // //           <Grid item xs={12}>
// // // // //             <Typography variant="h4" component="h1" fontWeight="bold">
// // // // //               Interview Management
// // // // //             </Typography>
// // // // //           </Grid>
// // // // //         </Grid>

// // // // //         <Paper sx={{ mb: 3 }}>
// // // // //           <Tabs
// // // // //             value={tabValue}
// // // // //             onChange={handleTabChange}
// // // // //             indicatorColor="primary"
// // // // //             textColor="primary"
// // // // //             centered
// // // // //           >
// // // // //             {/* MODIFIED: Used a composite label to control spacing between badge and text */}
// // // // //             <Tab
// // // // //               label={
// // // // //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // // //                   <Badge badgeContent={upcomingInterviews} color="primary" />
// // // // //                   <span>Scheduled</span>
// // // // //                 </Box>
// // // // //               }
// // // // //             />
// // // // //             <Tab label="Completed" />
// // // // //             <Tab label="Cancelled" />
// // // // //           </Tabs>
// // // // //         </Paper>

// // // // //         {/* MODIFIED: Conditional rendering for loading state */}
// // // // //         {isLoading ? (
// // // // //           <Grid container spacing={3}>
// // // // //             <InterviewCardSkeleton />
// // // // //             <InterviewCardSkeleton />
// // // // //             <InterviewCardSkeleton />
// // // // //           </Grid>
// // // // //         ) : error ? (
// // // // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // //             <ReportProblemIcon
// // // // //               sx={{ fontSize: 64, color: "error.main", mb: 2 }}
// // // // //             />
// // // // //             <Typography variant="h6">{error}</Typography>
// // // // //             <Button
// // // // //               variant="contained"
// // // // //               onClick={fetchInterviews}
// // // // //               sx={{ mt: 2 }}
// // // // //             >
// // // // //               Try Again
// // // // //             </Button>
// // // // //           </Paper>
// // // // //         ) : (
// // // // //           <Grid container spacing={3}>
// // // // //             {filteredInterviews.length > 0 ? (
// // // // //               filteredInterviews.map((interview) => (
// // // // //                 <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // //                   <Card
// // // // //                     sx={{
// // // // //                       height: "100%",
// // // // //                       display: "flex",
// // // // //                       flexDirection: "column",
// // // // //                       p: 1,
// // // // //                     }}
// // // // //                   >
// // // // //                     <CardContent sx={{ flexGrow: 1 }}>
// // // // //                       <Box
// // // // //                         sx={{
// // // // //                           display: "flex",
// // // // //                           alignItems: "flex-start",
// // // // //                           mb: 2,
// // // // //                         }}
// // // // //                       >
// // // // //                         <Avatar
// // // // //                           src={interview.avatar}
// // // // //                           sx={{ width: 56, height: 56, mr: 2 }}
// // // // //                         />
// // // // //                         <Box sx={{ flexGrow: 1 }}>
// // // // //                           <Typography variant="h6" fontWeight="bold">
// // // // //                             {interview.candidateName}
// // // // //                           </Typography>
// // // // //                           <Typography variant="body2" color="text.secondary">
// // // // //                             {interview.position}
// // // // //                           </Typography>
// // // // //                           <Typography variant="body2" color="text.secondary">
// // // // //                             {interview.experience}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Chip
// // // // //                           label={interview.status}
// // // // //                           color={getStatusColor(interview.status)}
// // // // //                           size="small"
// // // // //                           variant="outlined"
// // // // //                         />
// // // // //                       </Box>

// // // // //                       <Box sx={{ mb: 2 }}>
// // // // //                         <Box
// // // // //                           sx={{
// // // // //                             display: "flex",
// // // // //                             alignItems: "center",
// // // // //                             gap: 1.5,
// // // // //                             mb: 1,
// // // // //                           }}
// // // // //                         >
// // // // //                           <CalendarIcon color="action" />
// // // // //                           <Typography variant="body2">
// // // // //                             {interview.date}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Box
// // // // //                           sx={{
// // // // //                             display: "flex",
// // // // //                             alignItems: "center",
// // // // //                             gap: 1.5,
// // // // //                             mb: 1,
// // // // //                           }}
// // // // //                         >
// // // // //                           <TimeIcon color="action" />
// // // // //                           <Typography variant="body2">
// // // // //                             {interview.time}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Box
// // // // //                           sx={{
// // // // //                             display: "flex",
// // // // //                             alignItems: "center",
// // // // //                             gap: 1.5,
// // // // //                           }}
// // // // //                         >
// // // // //                           <VideoCallIcon color="action" />
// // // // //                           <Typography variant="body2">
// // // // //                             {interview.interviewType}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                       </Box>

// // // // //                       {interview.skills && interview.skills.length > 0 && (
// // // // //                         <Box sx={{ mb: 2 }}>
// // // // //                           <Typography
// // // // //                             variant="body2"
// // // // //                             fontWeight="medium"
// // // // //                             gutterBottom
// // // // //                           >
// // // // //                             Skills:
// // // // //                           </Typography>
// // // // //                           <Box
// // // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
// // // // //                           >
// // // // //                             {interview.skills.map((skill) => (
// // // // //                               <Chip
// // // // //                                 key={skill}
// // // // //                                 label={skill}
// // // // //                                 size="small"
// // // // //                                 variant="outlined"
// // // // //                               />
// // // // //                             ))}
// // // // //                           </Box>
// // // // //                         </Box>
// // // // //                       )}

// // // // //                       <Box>
// // // // //                         <Typography
// // // // //                           variant="body2"
// // // // //                           fontWeight="medium"
// // // // //                           gutterBottom
// // // // //                         >
// // // // //                           Notes:
// // // // //                         </Typography>
// // // // //                         <Typography
// // // // //                           variant="body2"
// // // // //                           color="text.secondary"
// // // // //                           sx={{ fontStyle: "italic" }}
// // // // //                         >
// // // // //                           {interview.notes}
// // // // //                         </Typography>
// // // // //                       </Box>
// // // // //                     </CardContent>

// // // // //                     <CardActions
// // // // //                       sx={{
// // // // //                         justifyContent: "space-between",
// // // // //                         alignItems: "center",
// // // // //                         px: 2,
// // // // //                         pb: 2,
// // // // //                       }}
// // // // //                     >
// // // // //                       <Box sx={{ display: "flex", gap: 2 }}>
// // // // //                         {interview.status === "scheduled" && (
// // // // //                           <Button
// // // // //                             variant="contained"
// // // // //                             startIcon={<VideoCallIcon />}
// // // // //                             onClick={() =>
// // // // //                               handleStartInterview(interview.location)
// // // // //                             }
// // // // //                             size="medium"
// // // // //                           >
// // // // //                             Start interview
// // // // //                           </Button>
// // // // //                         )}
// // // // //                         {interview.status === "scheduled" && (
// // // // //                           <Button
// // // // //                             variant="outlined"
// // // // //                             startIcon={<EditIcon />}
// // // // //                             size="medium"
// // // // //                             onClick={() => handleOpenDialog(interview)}
// // // // //                           >
// // // // //                             Edit
// // // // //                           </Button>
// // // // //                         )}
// // // // //                       </Box>

// // // // //                       <IconButton
// // // // //                         size="small"
// // // // //                         color="error"
// // // // //                         title="Delete Interview"
// // // // //                         onClick={() => handleDeleteInterview(interview.id)}
// // // // //                       >
// // // // //                         <DeleteIcon />
// // // // //                       </IconButton>
// // // // //                     </CardActions>
// // // // //                   </Card>
// // // // //                 </Grid>
// // // // //               ))
// // // // //             ) : (
// // // // //               <Grid item xs={12}>
// // // // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // //                   <ScheduleIcon
// // // // //                     sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
// // // // //                   />
// // // // //                   <Typography variant="h6" color="textSecondary">
// // // // //                     No {tabValue === 0 ? "scheduled" : "interviews"} found
// // // // //                   </Typography>
// // // // //                 </Paper>
// // // // //               </Grid>
// // // // //             )}
// // // // //           </Grid>
// // // // //         )}
// // // // //       </Box>

// // // // //       <Fab
// // // // //         color="primary"
// // // // //         aria-label="schedule interview"
// // // // //         onClick={() => handleOpenDialog()}
// // // // //         sx={{ position: "fixed", bottom: 32, right: 32 }}
// // // // //       >
// // // // //         <AddIcon />
// // // // //       </Fab>

// // // // //       <Dialog
// // // // //         open={openDialog}
// // // // //         onClose={handleCloseDialog}
// // // // //         maxWidth="sm"
// // // // //         fullWidth
// // // // //       >
// // // // //         <DialogTitle>
// // // // //           {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // //         </DialogTitle>
// // // // //         <MuiDialogActions>
// // // // //           <Grid container spacing={3} sx={{ pt: 1, px: 3 }}>
// // // // //             <Grid item xs={12}>
// // // // //               <FormControl fullWidth>
// // // // //                 <InputLabel>Candidate Application</InputLabel>
// // // // //                 <Select
// // // // //                   name="applicationId"
// // // // //                   value={formData.applicationId}
// // // // //                   onChange={handleInputChange}
// // // // //                   label="Candidate Application"
// // // // //                 >
// // // // //                   {candidates.map((candidate) => (
// // // // //                     <MenuItem
// // // // //                       key={candidate.applicationId}
// // // // //                       value={candidate.applicationId}
// // // // //                     >
// // // // //                       <Box
// // // // //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
// // // // //                       >
// // // // //                         <Avatar
// // // // //                           src={candidate.avatar}
// // // // //                           sx={{ width: 32, height: 32 }}
// // // // //                         />
// // // // //                         <Box>
// // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // //                             {candidate.name}
// // // // //                           </Typography>
// // // // //                           <Typography variant="caption" color="textSecondary">
// // // // //                             {candidate.position}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                       </Box>
// // // // //                     </MenuItem>
// // // // //                   ))}
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //             </Grid>

// // // // //             <Grid item xs={12} sm={6}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Date"
// // // // //                 name="date"
// // // // //                 type="date"
// // // // //                 value={formData.date}
// // // // //                 onChange={handleInputChange}
// // // // //                 InputLabelProps={{ shrink: true }}
// // // // //                 required
// // // // //               />
// // // // //             </Grid>

// // // // //             <Grid item xs={12} sm={6}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Time"
// // // // //                 name="time"
// // // // //                 type="time"
// // // // //                 value={formData.time}
// // // // //                 onChange={handleInputChange}
// // // // //                 InputLabelProps={{ shrink: true }}
// // // // //                 required
// // // // //               />
// // // // //             </Grid>

// // // // //             <Grid item xs={12}>
// // // // //               <FormControl fullWidth>
// // // // //                 <InputLabel>Type</InputLabel>
// // // // //                 <Select
// // // // //                   name="type"
// // // // //                   value={formData.type}
// // // // //                   onChange={handleInputChange}
// // // // //                   label="Type"
// // // // //                 >
// // // // //                   <MenuItem value="video">Video</MenuItem>
// // // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //             </Grid>

// // // // //             <Grid item xs={12}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Interview URL"
// // // // //                 name="interviewUrl"
// // // // //                 value={formData.interviewUrl}
// // // // //                 onChange={handleInputChange}
// // // // //                 placeholder="https://meet.google.com/abc-defg-hij or https://zoom.us/j/123456789"
// // // // //                 helperText="Enter the meeting URL for the interview"
// // // // //               />
// // // // //             </Grid>

// // // // //             <Grid item xs={12}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Notes / Feedback"
// // // // //                 name="notes"
// // // // //                 value={formData.notes}
// // // // //                 onChange={handleInputChange}
// // // // //                 multiline
// // // // //                 rows={3}
// // // // //               />
// // // // //             </Grid>
// // // // //           </Grid>
// // // // //         </MuiDialogActions>

// // // // //         <MuiDialogActions sx={{ p: 3 }}>
// // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // //           <Button onClick={handleSubmit} variant="contained">
// // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // //           </Button>
// // // // //         </MuiDialogActions>
// // // // //       </Dialog>
// // // // //     </Layout>
// // // // //   );
// // // // // }

// // // // // export default InterviewScheduler;
// // // // // import { useState, useEffect, useCallback } from "react";
// // // // // import apiClient from "../../context/apiClient";
// // // // // import {
// // // // //   Box,
// // // // //   Typography,
// // // // //   Grid,
// // // // //   Card,
// // // // //   CardContent,
// // // // //   CardActions,
// // // // //   Button,
// // // // //   TextField,
// // // // //   FormControl,
// // // // //   InputLabel,
// // // // //   Select,
// // // // //   MenuItem,
// // // // //   Dialog,
// // // // //   DialogTitle,
// // // // //   DialogActions as MuiDialogActions,
// // // // //   IconButton,
// // // // //   Chip,
// // // // //   Tabs,
// // // // //   Tab,
// // // // //   Paper,
// // // // //   Divider,
// // // // //   Avatar,
// // // // //   Badge,
// // // // //   Skeleton,
// // // // //   Fab,
// // // // // } from "@mui/material";
// // // // // import {
// // // // //   Add as AddIcon,
// // // // //   VideoCall as VideoCallIcon,
// // // // //   CalendarMonth as CalendarIcon,
// // // // //   Delete as DeleteIcon,
// // // // //   Edit as EditIcon,
// // // // //   Schedule as ScheduleIcon,
// // // // //   AccessTime as TimeIcon,
// // // // //   ReportProblem as ReportProblemIcon,
// // // // // } from "@mui/icons-material";
// // // // // import Layout from "../common/Layout";
// // // // // import { useNotification } from "../../context/NotificationContext";

// // // // // const formatApiData = (apiInterview) => {
// // // // //   return {
// // // // //     id: apiInterview.id,
// // // // //     applicationId: apiInterview.application || apiInterview.applicationId,
// // // // //     candidateName:
// // // // //       apiInterview.candidate_name ||
// // // // //       apiInterview.application_details?.talent_details?.user?.username ||
// // // // //       apiInterview.interviewer_details?.username ||
// // // // //       "N/A",
// // // // //     position:
// // // // //       apiInterview.job_title ||
// // // // //       apiInterview.application_details?.job_posting_details?.title ||
// // // // //       "N/A",
// // // // //     experience:
// // // // //       apiInterview.experience_level ||
// // // // //       apiInterview.application_details?.job_posting_details
// // // // //         ?.experience_level_display ||
// // // // //       "N/A",
// // // // //     location: apiInterview.location || "N/A",
// // // // //     status: (
// // // // //       apiInterview.status ||
// // // // //       apiInterview.interview_status ||
// // // // //       "unknown"
// // // // //     ).toLowerCase(),
// // // // //     date:
// // // // //       apiInterview.interview_date ||
// // // // //       (apiInterview.scheduled_at
// // // // //         ? new Date(apiInterview.scheduled_at).toLocaleDateString("en-US", {
// // // // //             weekday: "short",
// // // // //             month: "short",
// // // // //             day: "numeric",
// // // // //             year: "numeric",
// // // // //           })
// // // // //         : "N/A"),
// // // // //     time:
// // // // //       apiInterview.interview_time ||
// // // // //       (apiInterview.scheduled_at
// // // // //         ? new Date(apiInterview.scheduled_at).toLocaleTimeString("en-US", {
// // // // //             hour: "2-digit",
// // // // //             minute: "2-digit",
// // // // //             hour12: false,
// // // // //           })
// // // // //         : "N/A"),
// // // // //     interviewType:
// // // // //       apiInterview.interview_mode ||
// // // // //       apiInterview.interview_type ||
// // // // //       (apiInterview.location === "video" ? "Video Interview" : "Interview"),
// // // // //     skills:
// // // // //       apiInterview.skills ||
// // // // //       apiInterview.application_details?.job_posting_details?.required_skills ||
// // // // //       [],
// // // // //     notes:
// // // // //       apiInterview.notes ||
// // // // //       apiInterview.feedback ||
// // // // //       "No specific notes provided for this interview.",
// // // // //     avatar:
// // // // //       apiInterview.application_details?.talent_details?.profile_picture ||
// // // // //       "/placeholder.svg?height=56&width=56",
// // // // //     interviewUrl: apiInterview.location || "",
// // // // //   };
// // // // // };

// // // // // const InterviewCardSkeleton = () => (
// // // // //   <Grid item xs={12} md={6} lg={4}>
// // // // //     <Card
// // // // //       sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}
// // // // //     >
// // // // //       <CardContent sx={{ flexGrow: 1 }}>
// // // // //         <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
// // // // //           <Skeleton
// // // // //             variant="circular"
// // // // //             width={56}
// // // // //             height={56}
// // // // //             sx={{ mr: 2, mt: 1 }}
// // // // //           />
// // // // //           <Box sx={{ flexGrow: 1 }}>
// // // // //             <Skeleton variant="text" sx={{ fontSize: "1.25rem" }} width="80%" />
// // // // //             <Skeleton
// // // // //               variant="text"
// // // // //               sx={{ fontSize: "0.875rem" }}
// // // // //               width="60%"
// // // // //             />
// // // // //             <Skeleton
// // // // //               variant="text"
// // // // //               sx={{ fontSize: "0.875rem" }}
// // // // //               width="50%"
// // // // //             />
// // // // //           </Box>
// // // // //           <Skeleton
// // // // //             variant="rectangular"
// // // // //             width={80}
// // // // //             height={24}
// // // // //             sx={{ borderRadius: "16px" }}
// // // // //           />
// // // // //         </Box>
// // // // //         <Box sx={{ mb: 2 }}>
// // // // //           <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
// // // // //             <Skeleton variant="circular" width={24} height={24} />
// // // // //             <Skeleton variant="text" width="70%" />
// // // // //           </Box>
// // // // //           <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
// // // // //             <Skeleton variant="circular" width={24} height={24} />
// // // // //             <Skeleton variant="text" width="40%" />
// // // // //           </Box>
// // // // //           <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// // // // //             <Skeleton variant="circular" width={24} height={24} />
// // // // //             <Skeleton variant="text" width="50%" />
// // // // //           </Box>
// // // // //         </Box>
// // // // //         <Box>
// // // // //           <Skeleton variant="text" width="25%" sx={{ mb: 1 }} />
// // // // //           <Skeleton variant="rectangular" width="100%" height={60} />
// // // // //         </Box>
// // // // //       </CardContent>
// // // // //       <CardActions
// // // // //         sx={{
// // // // //           justifyContent: "space-between",
// // // // //           alignItems: "center",
// // // // //           px: 2,
// // // // //           pb: 2,
// // // // //         }}
// // // // //       >
// // // // //         <Box sx={{ display: "flex", gap: 1 }}>
// // // // //           <Skeleton
// // // // //             variant="rectangular"
// // // // //             width={150}
// // // // //             height={36}
// // // // //             sx={{ borderRadius: "4px" }}
// // // // //           />
// // // // //           <Skeleton
// // // // //             variant="rectangular"
// // // // //             width={70}
// // // // //             height={36}
// // // // //             sx={{ borderRadius: "4px" }}
// // // // //           />
// // // // //         </Box>
// // // // //         <Skeleton variant="circular" width={32} height={32} />
// // // // //       </CardActions>
// // // // //     </Card>
// // // // //   </Grid>
// // // // // );

// // // // // function InterviewScheduler() {
// // // // //   const { showNotification } = useNotification();
// // // // //   const [interviews, setInterviews] = useState([]);
// // // // //   const [candidates, setCandidates] = useState([]);
// // // // //   const [isLoading, setIsLoading] = useState(true);
// // // // //   const [error, setError] = useState(null);
// // // // //   const [openDialog, setOpenDialog] = useState(false);
// // // // //   const [tabValue, setTabValue] = useState(0);
// // // // //   const [selectedInterview, setSelectedInterview] = useState(null);
// // // // //   const [formData, setFormData] = useState({
// // // // //     applicationId: "",
// // // // //     date: "",
// // // // //     time: "",
// // // // //     type: "video",
// // // // //     notes: "",
// // // // //     interviewUrl: "",
// // // // //   });

// // // // //   const fetchInterviews = useCallback(async () => {
// // // // //     setIsLoading(true);
// // // // //     setError(null);
// // // // //     try {
// // // // //       const response = await apiClient.get("/interviews/");
// // // // //       const formattedData = response.data.map(formatApiData);
// // // // //       setInterviews(formattedData);
// // // // //     } catch (err) {
// // // // //       setError("Could not load interviews.");
// // // // //     } finally {
// // // // //       setIsLoading(false);
// // // // //     }
// // // // //   }, []);

// // // // //   const fetchCandidates = useCallback(async () => {
// // // // //     try {
// // // // //       const response = await apiClient.get("/applications/");
// // // // //       const formattedCandidates = response.data.map((app) => ({
// // // // //         applicationId: app.id,
// // // // //         talentId: app.talent,
// // // // //         name:
// // // // //           `${app.talent_details?.user?.first_name || ""} ${
// // // // //             app.talent_details?.user?.last_name || ""
// // // // //           }`.trim() || `Talent #${app.talent}`,
// // // // //         position: app.job_posting_details?.title || "Applied Position",
// // // // //         avatar:
// // // // //           app.talent_details?.profile_picture ||
// // // // //           "/placeholder.svg?height=40&width=40",
// // // // //       }));
// // // // //       setCandidates(formattedCandidates);
// // // // //     } catch (err) {
// // // // //       showNotification(
// // // // //         "Could not load candidate list for scheduling.",
// // // // //         "warning"
// // // // //       );
// // // // //     }
// // // // //   }, [showNotification]);

// // // // //   useEffect(() => {
// // // // //     fetchInterviews();
// // // // //     fetchCandidates();
// // // // //   }, [fetchInterviews, fetchCandidates]);

// // // // //   const handleInputChange = (e) => {
// // // // //     const { name, value } = e.target;
// // // // //     setFormData((prev) => ({ ...prev, [name]: value }));
// // // // //   };

// // // // //   const handleSubmit = async () => {
// // // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // // //       showNotification("Please select a candidate, date, and time.", "error");
// // // // //       return;
// // // // //     }

// // // // //     let locationValue = formData.interviewUrl;
// // // // //     if (!locationValue && selectedInterview && selectedInterview.interviewUrl) {
// // // // //       locationValue = selectedInterview.interviewUrl;
// // // // //     }
// // // // //     if (!locationValue) {
// // // // //       locationValue = formData.type;
// // // // //     }

// // // // //     const payload = {
// // // // //       application: formData.applicationId,
// // // // //       scheduled_at: `${formData.date}T${formData.time}:00Z`,
// // // // //       location: locationValue,
// // // // //       feedback: formData.notes,
// // // // //       interview_status: "Scheduled",
// // // // //       interviewer: 3,
// // // // //     };

// // // // //     try {
// // // // //       if (selectedInterview) {
// // // // //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload);
// // // // //         showNotification("Interview updated successfully!", "success");
// // // // //       } else {
// // // // //         await apiClient.post("/interviews/", payload);
// // // // //         showNotification("Interview scheduled successfully!", "success");
// // // // //       }
// // // // //       fetchInterviews();
// // // // //       handleCloseDialog();
// // // // //     } catch (err) {
// // // // //       showNotification(
// // // // //         err.response?.data?.detail || "An error occurred.",
// // // // //         "error"
// // // // //       );
// // // // //     }
// // // // //   };

// // // // //   const handleDeleteInterview = async (id) => {
// // // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // // //       try {
// // // // //         await apiClient.delete(`/interviews/${id}/`);
// // // // //         setInterviews(interviews.filter((i) => i.id !== id));
// // // // //         showNotification("Interview deleted successfully!", "success");
// // // // //       } catch (err) {
// // // // //         showNotification("Failed to delete interview.", "error");
// // // // //       }
// // // // //     }
// // // // //   };

// // // // //   const handleStartInterview = (interviewUrl) => {
// // // // //     if (
// // // // //       interviewUrl &&
// // // // //       (interviewUrl.startsWith("http://") ||
// // // // //         interviewUrl.startsWith("https://"))
// // // // //     ) {
// // // // //       window.open(interviewUrl, "_blank");
// // // // //     } else {
// // // // //       showNotification("No valid interview URL available.", "warning");
// // // // //     }
// // // // //   };

// // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // //     if (tabValue === 0) return interview.status === "scheduled";
// // // // //     if (tabValue === 1) return interview.status === "completed";
// // // // //     if (tabValue === 2) return interview.status === "cancelled";
// // // // //     return true;
// // // // //   });

// // // // //   const handleTabChange = (event, newValue) => setTabValue(newValue);

// // // // //   const handleCloseDialog = () => setOpenDialog(false);

// // // // //   const handleOpenDialog = (interview = null) => {
// // // // //     if (interview) {
// // // // //       setSelectedInterview(interview);
// // // // //       setFormData({
// // // // //         applicationId: interview.applicationId,
// // // // //         date: interview.date,
// // // // //         time: interview.time,
// // // // //         type: interview.interviewType === "Video Interview" ? "video" : "audio",
// // // // //         notes: interview.notes,
// // // // //         interviewUrl: interview.interviewUrl || "",
// // // // //       });
// // // // //     } else {
// // // // //       setSelectedInterview(null);
// // // // //       setFormData({
// // // // //         applicationId: "",
// // // // //         date: "",
// // // // //         time: "",
// // // // //         type: "video",
// // // // //         notes: "",
// // // // //         interviewUrl: "",
// // // // //       });
// // // // //     }
// // // // //     setOpenDialog(true);
// // // // //   };

// // // // //   const getStatusColor = (status) => {
// // // // //     switch (status) {
// // // // //       case "scheduled":
// // // // //         return "primary";
// // // // //       case "completed":
// // // // //         return "success";
// // // // //       case "cancelled":
// // // // //         return "error";
// // // // //       default:
// // // // //         return "default";
// // // // //     }
// // // // //   };

// // // // //   const upcomingInterviews = interviews.filter(
// // // // //     (i) => i.status === "scheduled"
// // // // //   ).length;

// // // // //   const tabIndicatorColors = {
// // // // //     0: "primary.main",
// // // // //     1: "success.main",
// // // // //     2: "error.main",
// // // // //   };

// // // // //   return (
// // // // //     <Layout title="Interview Scheduler">
// // // // //       <Box sx={{ p: 3, position: "relative" }}>
// // // // //         <Box sx={{ mb: 2 }}>
// // // // //           <Typography variant="h4" component="h1" fontWeight="bold">
// // // // //             Interview Management
// // // // //           </Typography>
// // // // //         </Box>

// // // // //         <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
// // // // //           <Tabs
// // // // //             value={tabValue}
// // // // //             onChange={handleTabChange}
// // // // //             centered
// // // // //             sx={{
// // // // //               "& .MuiTabs-indicator": {
// // // // //                 backgroundColor: tabIndicatorColors[tabValue],
// // // // //               },
// // // // //             }}
// // // // //           >
// // // // //             <Tab
// // // // //               label={
// // // // //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// // // // //                   <Badge badgeContent={upcomingInterviews} color="primary" />
// // // // //                   <span>Scheduled</span>
// // // // //                 </Box>
// // // // //               }
// // // // //               sx={{
// // // // //                 color: "text.secondary",
// // // // //                 "&.Mui-selected": {
// // // // //                   color: "primary.main",
// // // // //                   fontWeight: 600,
// // // // //                 },
// // // // //               }}
// // // // //             />
// // // // //             <Tab
// // // // //               label="Completed"
// // // // //               sx={{
// // // // //                 color: "text.secondary",
// // // // //                 "&.Mui-selected": {
// // // // //                   color: "success.main",
// // // // //                   fontWeight: 600,
// // // // //                 },
// // // // //               }}
// // // // //             />
// // // // //             <Tab
// // // // //               label="Cancelled"
// // // // //               sx={{
// // // // //                 color: "text.secondary",
// // // // //                 "&.Mui-selected": {
// // // // //                   color: "error.main",
// // // // //                   fontWeight: 600,
// // // // //                 },
// // // // //               }}
// // // // //             />
// // // // //           </Tabs>
// // // // //         </Paper>

// // // // //         {isLoading ? (
// // // // //           <Grid container spacing={3}>
// // // // //             <InterviewCardSkeleton />
// // // // //             <InterviewCardSkeleton />
// // // // //             <InterviewCardSkeleton />
// // // // //           </Grid>
// // // // //         ) : error ? (
// // // // //           <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // //             <ReportProblemIcon
// // // // //               sx={{ fontSize: 64, color: "error.main", mb: 2 }}
// // // // //             />
// // // // //             <Typography variant="h6">{error}</Typography>
// // // // //             <Button
// // // // //               variant="contained"
// // // // //               onClick={fetchInterviews}
// // // // //               sx={{ mt: 2 }}
// // // // //             >
// // // // //               Try Again
// // // // //             </Button>
// // // // //           </Paper>
// // // // //         ) : (
// // // // //           <Grid container spacing={3}>
// // // // //             {filteredInterviews.length > 0 ? (
// // // // //               filteredInterviews.map((interview) => (
// // // // //                 <Grid item xs={12} md={6} lg={4} key={interview.id}>
// // // // //                   <Card
// // // // //                     sx={{
// // // // //                       height: "100%",
// // // // //                       display: "flex",
// // // // //                       flexDirection: "column",
// // // // //                       p: 1,
// // // // //                     }}
// // // // //                   >
// // // // //                     <CardContent sx={{ flexGrow: 1 }}>
// // // // //                       <Box
// // // // //                         sx={{
// // // // //                           display: "flex",
// // // // //                           alignItems: "flex-start",
// // // // //                           mb: 2,
// // // // //                         }}
// // // // //                       >
// // // // //                         <Avatar
// // // // //                           src={interview.avatar}
// // // // //                           sx={{ width: 56, height: 56, mr: 2 }}
// // // // //                         />
// // // // //                         <Box sx={{ flexGrow: 1 }}>
// // // // //                           <Typography variant="h6" fontWeight="bold">
// // // // //                             {interview.candidateName}
// // // // //                           </Typography>
// // // // //                           <Typography variant="body2" color="text.secondary">
// // // // //                             {interview.position}
// // // // //                           </Typography>
// // // // //                           <Typography variant="body2" color="text.secondary">
// // // // //                             {interview.experience}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Chip
// // // // //                           label={interview.status}
// // // // //                           color={getStatusColor(interview.status)}
// // // // //                           size="small"
// // // // //                           variant="outlined"
// // // // //                         />
// // // // //                       </Box>

// // // // //                       <Box sx={{ mb: 2 }}>
// // // // //                         <Box
// // // // //                           sx={{
// // // // //                             display: "flex",
// // // // //                             alignItems: "center",
// // // // //                             gap: 1.5,
// // // // //                             mb: 1,
// // // // //                           }}
// // // // //                         >
// // // // //                           <CalendarIcon color="action" />
// // // // //                           <Typography variant="body2">
// // // // //                             {interview.date}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Box
// // // // //                           sx={{
// // // // //                             display: "flex",
// // // // //                             alignItems: "center",
// // // // //                             gap: 1.5,
// // // // //                             mb: 1,
// // // // //                           }}
// // // // //                         >
// // // // //                           <TimeIcon color="action" />
// // // // //                           <Typography variant="body2">
// // // // //                             {interview.time}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Box
// // // // //                           sx={{
// // // // //                             display: "flex",
// // // // //                             alignItems: "center",
// // // // //                             gap: 1.5,
// // // // //                           }}
// // // // //                         >
// // // // //                           <VideoCallIcon color="action" />
// // // // //                           <Typography variant="body2">
// // // // //                             {interview.interviewType}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                       </Box>

// // // // //                       {interview.skills && interview.skills.length > 0 && (
// // // // //                         <Box sx={{ mb: 2 }}>
// // // // //                           <Typography
// // // // //                             variant="body2"
// // // // //                             fontWeight="medium"
// // // // //                             gutterBottom
// // // // //                           >
// // // // //                             Skills:
// // // // //                           </Typography>
// // // // //                           <Box
// // // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
// // // // //                           >
// // // // //                             {interview.skills.map((skill) => (
// // // // //                               <Chip
// // // // //                                 key={skill}
// // // // //                                 label={skill}
// // // // //                                 size="small"
// // // // //                                 variant="outlined"
// // // // //                               />
// // // // //                             ))}
// // // // //                           </Box>
// // // // //                         </Box>
// // // // //                       )}

// // // // //                       <Box>
// // // // //                         <Typography
// // // // //                           variant="body2"
// // // // //                           fontWeight="medium"
// // // // //                           gutterBottom
// // // // //                         >
// // // // //                           Notes:
// // // // //                         </Typography>
// // // // //                         <Typography
// // // // //                           variant="body2"
// // // // //                           color="text.secondary"
// // // // //                           sx={{ fontStyle: "italic" }}
// // // // //                         >
// // // // //                           {interview.notes}
// // // // //                         </Typography>
// // // // //                       </Box>
// // // // //                     </CardContent>

// // // // //                     <CardActions
// // // // //                       sx={{
// // // // //                         justifyContent: "space-between",
// // // // //                         alignItems: "center",
// // // // //                         px: 2,
// // // // //                         pb: 2,
// // // // //                       }}
// // // // //                     >
// // // // //                       <Box sx={{ display: "flex", gap: 2 }}>
// // // // //                         {interview.status === "scheduled" && (
// // // // //                           <Button
// // // // //                             variant="contained"
// // // // //                             startIcon={<VideoCallIcon />}
// // // // //                             onClick={() =>
// // // // //                               handleStartInterview(interview.location)
// // // // //                             }
// // // // //                             size="medium"
// // // // //                           >
// // // // //                             Start interview
// // // // //                           </Button>
// // // // //                         )}
// // // // //                         {interview.status === "scheduled" && (
// // // // //                           <Button
// // // // //                             variant="outlined"
// // // // //                             startIcon={<EditIcon />}
// // // // //                             size="medium"
// // // // //                             onClick={() => handleOpenDialog(interview)}
// // // // //                           >
// // // // //                             Edit
// // // // //                           </Button>
// // // // //                         )}
// // // // //                       </Box>

// // // // //                       <IconButton
// // // // //                         size="small"
// // // // //                         color="error"
// // // // //                         title="Delete Interview"
// // // // //                         onClick={() => handleDeleteInterview(interview.id)}
// // // // //                       >
// // // // //                         <DeleteIcon />
// // // // //                       </IconButton>
// // // // //                     </CardActions>
// // // // //                   </Card>
// // // // //                 </Grid>
// // // // //               ))
// // // // //             ) : (
// // // // //               <Grid item xs={12}>
// // // // //                 <Paper sx={{ p: 4, textAlign: "center" }}>
// // // // //                   <ScheduleIcon
// // // // //                     sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
// // // // //                   />
// // // // //                   <Typography variant="h6" color="textSecondary">
// // // // //                     No {tabValue === 0 ? "scheduled" : "interviews"} found
// // // // //                   </Typography>
// // // // //                 </Paper>
// // // // //               </Grid>
// // // // //             )}
// // // // //           </Grid>
// // // // //         )}
// // // // //       </Box>

// // // // //       <Fab
// // // // //         color="primary"
// // // // //         aria-label="schedule interview"
// // // // //         onClick={() => handleOpenDialog()}
// // // // //         sx={{ position: "fixed", bottom: 32, right: 32 }}
// // // // //       >
// // // // //         <AddIcon />
// // // // //       </Fab>

// // // // //       <Dialog
// // // // //         open={openDialog}
// // // // //         onClose={handleCloseDialog}
// // // // //         maxWidth="sm"
// // // // //         fullWidth
// // // // //       >
// // // // //         <DialogTitle>
// // // // //           {selectedInterview ? "Edit Interview" : "Schedule New Interview"}
// // // // //         </DialogTitle>
// // // // //         <MuiDialogActions>
// // // // //           <Grid container spacing={3} sx={{ pt: 1, px: 3 }}>
// // // // //             <Grid item xs={12}>
// // // // //               <FormControl fullWidth>
// // // // //                 <InputLabel>Candidate Application</InputLabel>
// // // // //                 <Select
// // // // //                   name="applicationId"
// // // // //                   value={formData.applicationId}
// // // // //                   onChange={handleInputChange}
// // // // //                   label="Candidate Application"
// // // // //                 >
// // // // //                   {candidates.map((candidate) => (
// // // // //                     <MenuItem
// // // // //                       key={candidate.applicationId}
// // // // //                       value={candidate.applicationId}
// // // // //                     >
// // // // //                       <Box
// // // // //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
// // // // //                       >
// // // // //                         <Avatar
// // // // //                           src={candidate.avatar}
// // // // //                           sx={{ width: 32, height: 32 }}
// // // // //                         />
// // // // //                         <Box>
// // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // //                             {candidate.name}
// // // // //                           </Typography>
// // // // //                           <Typography variant="caption" color="textSecondary">
// // // // //                             {candidate.position}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                       </Box>
// // // // //                     </MenuItem>
// // // // //                   ))}
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //             </Grid>

// // // // //             <Grid item xs={12} sm={6}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Date"
// // // // //                 name="date"
// // // // //                 type="date"
// // // // //                 value={formData.date}
// // // // //                 onChange={handleInputChange}
// // // // //                 InputLabelProps={{ shrink: true }}
// // // // //                 required
// // // // //               />
// // // // //             </Grid>

// // // // //             <Grid item xs={12} sm={6}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Time"
// // // // //                 name="time"
// // // // //                 type="time"
// // // // //                 value={formData.time}
// // // // //                 onChange={handleInputChange}
// // // // //                 InputLabelProps={{ shrink: true }}
// // // // //                 required
// // // // //               />
// // // // //             </Grid>

// // // // //             <Grid item xs={12}>
// // // // //               <FormControl fullWidth>
// // // // //                 <InputLabel>Type</InputLabel>
// // // // //                 <Select
// // // // //                   name="type"
// // // // //                   value={formData.type}
// // // // //                   onChange={handleInputChange}
// // // // //                   label="Type"
// // // // //                 >
// // // // //                   <MenuItem value="video">Video</MenuItem>
// // // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //             </Grid>

// // // // //             <Grid item xs={12}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Interview URL"
// // // // //                 name="interviewUrl"
// // // // //                 value={formData.interviewUrl}
// // // // //                 onChange={handleInputChange}
// // // // //                 placeholder="https://meet.google.com/abc-defg-hij or https://zoom.us/j/123456789"
// // // // //                 helperText="Enter the meeting URL for the interview"
// // // // //               />
// // // // //             </Grid>

// // // // //             <Grid item xs={12}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Notes / Feedback"
// // // // //                 name="notes"
// // // // //                 value={formData.notes}
// // // // //                 onChange={handleInputChange}
// // // // //                 multiline
// // // // //                 rows={3}
// // // // //               />
// // // // //             </Grid>
// // // // //           </Grid>
// // // // //         </MuiDialogActions>

// // // // //         <MuiDialogActions sx={{ p: 3 }}>
// // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // //           <Button onClick={handleSubmit} variant="contained">
// // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // //           </Button>
// // // // //         </MuiDialogActions>
// // // // //       </Dialog>
// // // // //     </Layout>
// // // // //   );
// // // // // }

// // // // // export default InterviewScheduler;


// // // // // import { useState, useEffect, useCallback } from "react"
// // // // // import apiClient from "../../context/apiClient"
// // // // // import {
// // // // //   Box,
// // // // //   Typography,
// // // // //   Grid,
// // // // //   Card,
// // // // //   CardContent,
// // // // //   CardActions,
// // // // //   Button,
// // // // //   TextField,
// // // // //   FormControl,
// // // // //   InputLabel,
// // // // //   Select,
// // // // //   MenuItem,
// // // // //   Dialog,
// // // // //   DialogTitle,
// // // // //   DialogContent,
// // // // //   DialogActions as MuiDialogActions,
// // // // //   IconButton,
// // // // //   Chip,
// // // // //   Tabs,
// // // // //   Tab,
// // // // //   Paper,
// // // // //   Avatar,
// // // // //   Badge,
// // // // //   Skeleton,
// // // // //   Fab,
// // // // //   Divider,
// // // // //   Tooltip,
// // // // // } from "@mui/material"
// // // // // import {
// // // // //   Add as AddIcon,
// // // // //   VideoCall as VideoCallIcon,
// // // // //   CalendarMonth as CalendarIcon,
// // // // //   Delete as DeleteIcon,
// // // // //   Edit as EditIcon,
// // // // //   Schedule as ScheduleIcon,
// // // // //   AccessTime as TimeIcon,
// // // // //   ReportProblem as ReportProblemIcon,
// // // // //   LocationOn as LocationOnIcon,
// // // // // } from "@mui/icons-material"
// // // // // import Layout from "../common/Layout"
// // // // // import { useNotification } from "../../context/NotificationContext"

// // // // // const formatApiData = (apiInterview) => {
// // // // //   let scheduledAt = null
// // // // //   let rawDate = ""
// // // // //   let rawTime = ""

// // // // //   if (apiInterview.interview_date && apiInterview.interview_time) {
// // // // //     scheduledAt = new Date(`${apiInterview.interview_date}T${apiInterview.interview_time}:00`)
// // // // //     rawDate = apiInterview.interview_date
// // // // //     rawTime = apiInterview.interview_time
// // // // //   } else if (apiInterview.scheduled_at) {
// // // // //     scheduledAt = new Date(apiInterview.scheduled_at)
// // // // //     rawDate = scheduledAt.toISOString().split("T")[0]
// // // // //     rawTime = scheduledAt.toTimeString().substring(0, 5)
// // // // //   }

// // // // //   const location = apiInterview.location || ""
// // // // //   let address = ""
// // // // //   let mapUrl = ""
// // // // //   let interviewUrl = ""
// // // // //   let formType = "in-person"

// // // // //   const isVideoUrl = location.startsWith("http://") || location.startsWith("https://")

// // // // //   const locationParts = location.split(" | Map: ")
// // // // //   const primaryLocation = locationParts[0]
// // // // //   if (locationParts.length > 1) {
// // // // //     mapUrl = locationParts[1]
// // // // //   }

// // // // //   if (isVideoUrl) {
// // // // //     formType = "video"
// // // // //     interviewUrl = location
// // // // //   } else if (location.toLowerCase() === "remote") {
// // // // //     formType = "video"
// // // // //   } else {
// // // // //     formType = "in-person"
// // // // //     address = primaryLocation
// // // // //   }

// // // // //   return {
// // // // //     id: apiInterview.id,
// // // // //     applicationId: apiInterview.application || apiInterview.applicationId,
// // // // //     candidateName:
// // // // //       apiInterview.candidate_name || apiInterview.application_details?.talent_details?.user?.username || "N/A",
// // // // //     position: apiInterview.job_title || "N/A",
// // // // //     experience: apiInterview.experience_level || "N/A",
// // // // //     status: (apiInterview.status || "unknown").toLowerCase(),
// // // // //     date: scheduledAt
// // // // //       ? scheduledAt.toLocaleDateString("en-US", {
// // // // //           weekday: "short",
// // // // //           month: "short",
// // // // //           day: "numeric",
// // // // //           year: "numeric",
// // // // //         })
// // // // //       : "N/A",
// // // // //     time: scheduledAt
// // // // //       ? scheduledAt.toLocaleTimeString("en-US", {
// // // // //           hour: "2-digit",
// // // // //           minute: "2-digit",
// // // // //           hour12: true,
// // // // //         })
// // // // //       : "N/A",
// // // // //     rawDate: rawDate,
// // // // //     rawTime: rawTime,
// // // // //     interviewType: formType === "video" ? "Video Interview" : "In-Person Interview",
// // // // //     formType: formType,
// // // // //     skills: apiInterview.skills || [],
// // // // //     notes: apiInterview.notes || "No specific notes provided for this interview.",
// // // // //     avatar: apiInterview.application_details?.talent_details?.profile_picture || "/placeholder.svg?height=56&width=56",
// // // // //     address: address,
// // // // //     mapUrl: mapUrl,
// // // // //     interviewUrl: interviewUrl,
// // // // //   }
// // // // // }

// // // // // const InterviewCardSkeleton = () => (
// // // // //   <Grid item xs={12} sm={6} lg={4}>
// // // // //     <Card
// // // // //       sx={{
// // // // //         height: 380,
// // // // //         display: "flex",
// // // // //         flexDirection: "column",
// // // // //         borderRadius: 2,
// // // // //         boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
// // // // //       }}
// // // // //     >
// // // // //       <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
// // // // //         <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // //           <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
// // // // //           <Box sx={{ flexGrow: 1 }}>
// // // // //             <Skeleton variant="text" width="80%" height={24} />
// // // // //             <Skeleton variant="text" width="60%" height={20} />
// // // // //           </Box>
// // // // //           <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
// // // // //         </Box>
// // // // //         <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1, mb: 2 }} />
// // // // //         <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
// // // // //       </CardContent>
// // // // //       <CardActions sx={{ p: 2.5, pt: 0 }}>
// // // // //         <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1, mr: 1 }} />
// // // // //         <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
// // // // //         <Box sx={{ flexGrow: 1 }} />
// // // // //         <Skeleton variant="circular" width={32} height={32} />
// // // // //       </CardActions>
// // // // //     </Card>
// // // // //   </Grid>
// // // // // )

// // // // // function InterviewScheduler() {
// // // // //   const { showNotification } = useNotification()
// // // // //   const [interviews, setInterviews] = useState([])
// // // // //   const [candidates, setCandidates] = useState([])
// // // // //   const [isLoading, setIsLoading] = useState(true)
// // // // //   const [error, setError] = useState(null)
// // // // //   const [openDialog, setOpenDialog] = useState(false)
// // // // //   const [tabValue, setTabValue] = useState(0)
// // // // //   const [selectedInterview, setSelectedInterview] = useState(null)
// // // // //   const [formData, setFormData] = useState({
// // // // //     applicationId: "",
// // // // //     date: "",
// // // // //     time: "",
// // // // //     type: "video",
// // // // //     notes: "",
// // // // //     locationDetail: "",
// // // // //   })

// // // // //   const fetchInterviews = useCallback(async () => {
// // // // //     setIsLoading(true)
// // // // //     setError(null)
// // // // //     try {
// // // // //       const [interviewsRes, legacyInterviewsRes] = await Promise.all([
// // // // //         apiClient.get("/interviews/").catch(() => ({ data: [] })),
// // // // //         // apiClient.get("/interviews/").catch(() => ({ data: [] })),
// // // // //       ])
// // // // //       const combinedData = [...interviewsRes.data, ...legacyInterviewsRes.data]
// // // // //       const uniqueData = Array.from(new Map(combinedData.map((item) => [item.id, item])).values())
// // // // //       const formattedData = uniqueData.map(formatApiData)
// // // // //       setInterviews(formattedData)
// // // // //     } catch (err) {
// // // // //       setError("Could not load interviews.")
// // // // //     } finally {
// // // // //       setIsLoading(false)
// // // // //     }
// // // // //   }, [])

// // // // //   const fetchCandidates = useCallback(async () => {
// // // // //     try {
// // // // //       const response = await apiClient.get("/applications/")
// // // // //       const formattedCandidates = response.data.map((app) => ({
// // // // //         applicationId: app.id,
// // // // //         talentId: app.talent,
// // // // //         name:
// // // // //           `${app.talent_details?.user?.first_name || ""} ${app.talent_details?.user?.last_name || ""}`.trim() ||
// // // // //           `Talent #${app.talent}`,
// // // // //         position: app.job_posting_details?.title || "Applied Position",
// // // // //         avatar: app.talent_details?.profile_picture || "/placeholder.svg?height=40&width=40",
// // // // //       }))
// // // // //       setCandidates(formattedCandidates)
// // // // //     } catch (err) {
// // // // //       showNotification("Could not load candidate list for scheduling.", "warning")
// // // // //     }
// // // // //   }, [showNotification])

// // // // //   useEffect(() => {
// // // // //     fetchInterviews()
// // // // //     fetchCandidates()
// // // // //   }, [fetchInterviews, fetchCandidates])

// // // // //   const handleInputChange = (e) => {
// // // // //     const { name, value } = e.target
// // // // //     setFormData((prev) => ({ ...prev, [name]: value }))
// // // // //   }

// // // // //   const handleSubmit = async () => {
// // // // //     if (!formData.applicationId || !formData.date || !formData.time) {
// // // // //       showNotification("Please select a candidate, date, and time.", "error")
// // // // //       return
// // // // //     }
// // // // //     if (formData.type === "in-person" && !formData.locationDetail.trim()) {
// // // // //       showNotification("Please provide an address for the in-person interview.", "error")
// // // // //       return
// // // // //     }

// // // // //     const locationValue = formData.locationDetail.trim() || (formData.type === "video" ? "video" : "")

// // // // //     const payload = {
// // // // //       application: formData.applicationId,
// // // // //       scheduled_at: `${formData.date}T${formData.time}:00`,
// // // // //       location: locationValue,
// // // // //       feedback: formData.notes,
// // // // //       interview_status: "Scheduled",
// // // // //       interviewer: 3,
// // // // //     }

// // // // //     try {
// // // // //       if (selectedInterview) {
// // // // //         await apiClient.put(`/interviews/${selectedInterview.id}/`, payload)
// // // // //         showNotification("Interview updated successfully!", "success")
// // // // //       } else {
// // // // //         await apiClient.post("/interviews/", payload)
// // // // //         showNotification("Interview scheduled successfully!", "success")
// // // // //       }
// // // // //       fetchInterviews()
// // // // //       handleCloseDialog()
// // // // //     } catch (err) {
// // // // //       showNotification(err.response?.data?.detail || "An error occurred.", "error")
// // // // //     }
// // // // //   }

// // // // //   const handleDeleteInterview = async (id) => {
// // // // //     if (window.confirm("Are you sure you want to delete this interview?")) {
// // // // //       try {
// // // // //         await apiClient.delete(`/interviews/${id}/`)
// // // // //         setInterviews(interviews.filter((i) => i.id !== id))
// // // // //         showNotification("Interview deleted successfully!", "success")
// // // // //       } catch (err) {
// // // // //         showNotification("Failed to delete interview.", "error")
// // // // //       }
// // // // //     }
// // // // //   }

// // // // //   const handleStartInterview = (interviewUrl) => {
// // // // //     if (interviewUrl && (interviewUrl.startsWith("http://") || interviewUrl.startsWith("https://"))) {
// // // // //       window.open(interviewUrl, "_blank")
// // // // //     } else {
// // // // //       showNotification("No valid interview URL available.", "warning")
// // // // //     }
// // // // //   }

// // // // //   const filteredInterviews = interviews.filter((interview) => {
// // // // //     if (tabValue === 0) return interview.status === "scheduled"
// // // // //     if (tabValue === 1) return interview.status === "completed"
// // // // //     if (tabValue === 2) return interview.status === "cancelled"
// // // // //     return true
// // // // //   })

// // // // //   const handleTabChange = (event, newValue) => setTabValue(newValue)

// // // // //   const handleCloseDialog = () => setOpenDialog(false)

// // // // //   const handleOpenDialog = (interview = null) => {
// // // // //     if (interview) {
// // // // //       setSelectedInterview(interview)
// // // // //       setFormData({
// // // // //         applicationId: interview.applicationId,
// // // // //         date: interview.rawDate,
// // // // //         time: interview.rawTime,
// // // // //         type: interview.formType,
// // // // //         notes: interview.notes === "No specific notes provided for this interview." ? "" : interview.notes,
// // // // //         locationDetail: interview.formType === "video" ? interview.interviewUrl : interview.address,
// // // // //       })
// // // // //     } else {
// // // // //       setSelectedInterview(null)
// // // // //       setFormData({
// // // // //         applicationId: "",
// // // // //         date: "",
// // // // //         time: "",
// // // // //         type: "video",
// // // // //         notes: "",
// // // // //         locationDetail: "",
// // // // //       })
// // // // //     }
// // // // //     setOpenDialog(true)
// // // // //   }

// // // // //   const getStatusColor = (status) => {
// // // // //     switch (status) {
// // // // //       case "scheduled":
// // // // //         return "primary"
// // // // //       case "completed":
// // // // //         return "success"
// // // // //       case "cancelled":
// // // // //         return "error"
// // // // //       default:
// // // // //         return "default"
// // // // //     }
// // // // //   }

// // // // //   const getStatusBgColor = (status) => {
// // // // //     switch (status) {
// // // // //       case "scheduled":
// // // // //         return "rgba(25, 118, 210, 0.1)"
// // // // //       case "completed":
// // // // //         return "rgba(46, 125, 50, 0.1)"
// // // // //       case "cancelled":
// // // // //         return "rgba(211, 47, 47, 0.1)"
// // // // //       default:
// // // // //         return "rgba(158, 158, 158, 0.1)"
// // // // //     }
// // // // //   }

// // // // //   const upcomingInterviews = interviews.filter((i) => i.status === "scheduled").length

// // // // //   const tabIndicatorColors = {
// // // // //     0: "primary.main",
// // // // //     1: "success.main",
// // // // //     2: "error.main",
// // // // //   }

// // // // //   const handleOpenMap = (address) => {
// // // // //     const encodedAddress = encodeURIComponent(address)
// // // // //     const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
// // // // //     window.open(mapUrl, "_blank")
// // // // //   }

// // // // //   return (
// // // // //     <Layout title="Interview Scheduler">
// // // // //       <Box sx={{ p: 3, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
// // // // //         <Box sx={{ mb: 3 }}>
// // // // //           <Typography variant="h4" component="h1" fontWeight="700" color="text.primary" sx={{ mb: 1 }}>
// // // // //             Interview Management
// // // // //           </Typography>
// // // // //           <Typography variant="body1" color="text.secondary">
// // // // //             Manage and track all your candidate interviews
// // // // //           </Typography>
// // // // //         </Box>

// // // // //         <Paper
// // // // //           elevation={0}
// // // // //           sx={{
// // // // //             mb: 4,
// // // // //             borderRadius: 3,
// // // // //             border: "none",
// // // // //             background: "rgba(255,255,255,0.95)",
// // // // //             backdropFilter: "blur(10px)",
// // // // //           }}
// // // // //         >
// // // // //           <Tabs
// // // // //             value={tabValue}
// // // // //             onChange={handleTabChange}
// // // // //             centered
// // // // //             sx={{
// // // // //               "& .MuiTabs-indicator": {
// // // // //                 backgroundColor: tabIndicatorColors[tabValue],
// // // // //                 height: 3,
// // // // //               },
// // // // //               "& .MuiTab-root": {
// // // // //                 textTransform: "none",
// // // // //                 fontWeight: 600,
// // // // //                 fontSize: "0.95rem",
// // // // //               },
// // // // //             }}
// // // // //           >
// // // // //             <Tab
// // // // //               label={
// // // // //                 <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
// // // // //                   <Badge badgeContent={upcomingInterviews} color="primary" />
// // // // //                   <span>Scheduled</span>
// // // // //                 </Box>
// // // // //               }
// // // // //               sx={{
// // // // //                 color: "text.secondary",
// // // // //                 "&.Mui-selected": {
// // // // //                   color: "primary.main",
// // // // //                 },
// // // // //               }}
// // // // //             />
// // // // //             <Tab
// // // // //               label="Completed"
// // // // //               sx={{
// // // // //                 color: "text.secondary",
// // // // //                 "&.Mui-selected": {
// // // // //                   color: "success.main",
// // // // //                 },
// // // // //               }}
// // // // //             />
// // // // //             <Tab
// // // // //               label="Cancelled"
// // // // //               sx={{
// // // // //                 color: "text.secondary",
// // // // //                 "&.Mui-selected": {
// // // // //                   color: "error.main",
// // // // //                 },
// // // // //               }}
// // // // //             />
// // // // //           </Tabs>
// // // // //         </Paper>

// // // // //         {isLoading ? (
// // // // //           <Grid container spacing={3}>
// // // // //             <InterviewCardSkeleton />
// // // // //             <InterviewCardSkeleton />
// // // // //             <InterviewCardSkeleton />
// // // // //           </Grid>
// // // // //         ) : error ? (
// // // // //           <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
// // // // //             <ReportProblemIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
// // // // //             <Typography variant="h6" sx={{ mb: 2 }}>
// // // // //               {error}
// // // // //             </Typography>
// // // // //             <Button variant="contained" onClick={fetchInterviews}>
// // // // //               Try Again
// // // // //             </Button>
// // // // //           </Paper>
// // // // //         ) : (
// // // // //           <Grid container spacing={3}>
// // // // //             {filteredInterviews.length > 0 ? (
// // // // //               filteredInterviews.map((interview) => (
// // // // //                 <Grid item xs={12} sm={6} lg={4} key={interview.id}>
// // // // //                   <Card
// // // // //                     sx={{
// // // // //                       height: 380,
// // // // //                       display: "flex",
// // // // //                       flexDirection: "column",
// // // // //                       borderRadius: 2,
// // // // //                       boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
// // // // //                       border: "1px solid #e2e8f0",
// // // // //                       transition: "all 0.2s ease-in-out",
// // // // //                       "&:hover": {
// // // // //                         boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
// // // // //                         transform: "translateY(-2px)",
// // // // //                       },
// // // // //                     }}
// // // // //                   >
// // // // //                     <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
// // // // //                       {/* Header Section */}
// // // // //                       <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
// // // // //                         <Avatar
// // // // //                           src={interview.avatar}
// // // // //                           sx={{
// // // // //                             width: 48,
// // // // //                             height: 48,
// // // // //                             mr: 2,
// // // // //                             border: "2px solid #f1f5f9",
// // // // //                           }}
// // // // //                         />
// // // // //                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
// // // // //                           <Tooltip title={interview.candidateName} placement="top">
// // // // //                             <Typography
// // // // //                               variant="h6"
// // // // //                               fontWeight="600"
// // // // //                               sx={{
// // // // //                                 overflow: "hidden",
// // // // //                                 textOverflow: "ellipsis",
// // // // //                                 whiteSpace: "nowrap",
// // // // //                                 fontSize: "1rem",
// // // // //                                 lineHeight: 1.3,
// // // // //                               }}
// // // // //                             >
// // // // //                               {interview.candidateName}
// // // // //                             </Typography>
// // // // //                           </Tooltip>
// // // // //                           <Tooltip title={interview.position} placement="top">
// // // // //                             <Typography
// // // // //                               variant="body2"
// // // // //                               color="text.secondary"
// // // // //                               sx={{
// // // // //                                 overflow: "hidden",
// // // // //                                 textOverflow: "ellipsis",
// // // // //                                 whiteSpace: "nowrap",
// // // // //                                 fontSize: "0.85rem",
// // // // //                               }}
// // // // //                             >
// // // // //                               {interview.position}
// // // // //                             </Typography>
// // // // //                           </Tooltip>
// // // // //                         </Box>
// // // // //                         <Chip
// // // // //                           label={interview.status}
// // // // //                           size="small"
// // // // //                           sx={{
// // // // //                             backgroundColor: getStatusBgColor(interview.status),
// // // // //                             color: `${getStatusColor(interview.status)}.main`,
// // // // //                             fontWeight: 600,
// // // // //                             fontSize: "0.75rem",
// // // // //                             height: 24,
// // // // //                             textTransform: "capitalize",
// // // // //                           }}
// // // // //                         />
// // // // //                       </Box>

// // // // //                       <Divider sx={{ mb: 2 }} />

// // // // //                       {/* Interview Details */}
// // // // //                       <Box sx={{ mb: 2 }}>
// // // // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // // // //                           <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// // // // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // // // //                             {interview.date}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // // // //                           <TimeIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1.5 }} />
// // // // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // // // //                             {interview.time}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                         <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
// // // // //                           {interview.formType === "video" ? (
// // // // //                             <VideoCallIcon sx={{ fontSize: 18, color: "primary.main", mr: 1.5 }} />
// // // // //                           ) : (
// // // // //                             <LocationOnIcon sx={{ fontSize: 18, color: "secondary.main", mr: 1.5 }} />
// // // // //                           )}
// // // // //                           <Typography variant="body2" fontWeight="500" sx={{ fontSize: "0.85rem" }}>
// // // // //                             {interview.interviewType}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                       </Box>

// // // // //                       {/* Address Section */}
// // // // //                       {interview.address && (
// // // // //                         <Box sx={{ mb: 2 }}>
// // // // //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // // // //                             Address:
// // // // //                           </Typography>
// // // // //                           <Tooltip title={interview.address} placement="top" arrow>
// // // // //                             <Typography
// // // // //                               variant="body2"
// // // // //                               color="text.secondary"
// // // // //                               sx={{
// // // // //                                 fontSize: "0.8rem",
// // // // //                                 lineHeight: 1.3,
// // // // //                                 overflow: "hidden",
// // // // //                                 textOverflow: "ellipsis",
// // // // //                                 whiteSpace: "nowrap",
// // // // //                                 cursor: "pointer",
// // // // //                                 "&:hover": {
// // // // //                                   color: "primary.main",
// // // // //                                 },
// // // // //                               }}
// // // // //                             >
// // // // //                               {interview.address.length > 50
// // // // //                                 ? `${interview.address.substring(0, 50)}...`
// // // // //                                 : interview.address}
// // // // //                             </Typography>
// // // // //                           </Tooltip>
// // // // //                         </Box>
// // // // //                       )}

// // // // //                       {/* Skills Section */}
// // // // //                       {interview.skills && interview.skills.length > 0 && (
// // // // //                         <Box sx={{ mb: 2 }}>
// // // // //                           <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // // // //                             Skills:
// // // // //                           </Typography>
// // // // //                           <Box
// // // // //                             sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "50px", overflow: "hidden" }}
// // // // //                           >
// // // // //                             {interview.skills.slice(0, 3).map((skill) => (
// // // // //                               <Chip
// // // // //                                 key={skill}
// // // // //                                 label={skill}
// // // // //                                 size="small"
// // // // //                                 variant="outlined"
// // // // //                                 sx={{
// // // // //                                   fontSize: "0.7rem",
// // // // //                                   height: 22,
// // // // //                                   "& .MuiChip-label": { px: 1 },
// // // // //                                 }}
// // // // //                               />
// // // // //                             ))}
// // // // //                             {interview.skills.length > 3 && (
// // // // //                               <Chip
// // // // //                                 label={`+${interview.skills.length - 3}`}
// // // // //                                 size="small"
// // // // //                                 variant="filled"
// // // // //                                 color="primary"
// // // // //                                 sx={{
// // // // //                                   fontSize: "0.7rem",
// // // // //                                   height: 22,
// // // // //                                   "& .MuiChip-label": { px: 1 },
// // // // //                                 }}
// // // // //                               />
// // // // //                             )}
// // // // //                           </Box>
// // // // //                         </Box>
// // // // //                       )}

// // // // //                       {/* Notes Section */}
// // // // //                       <Box sx={{ flexGrow: 1 }}>
// // // // //                         <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
// // // // //                           Notes:
// // // // //                         </Typography>
// // // // //                         <Typography
// // // // //                           variant="body2"
// // // // //                           color="text.secondary"
// // // // //                           sx={{
// // // // //                             fontSize: "0.8rem",
// // // // //                             fontStyle: "italic",
// // // // //                             lineHeight: 1.3,
// // // // //                             overflow: "hidden",
// // // // //                             display: "-webkit-box",
// // // // //                             WebkitLineClamp: 2,
// // // // //                             WebkitBoxOrient: "vertical",
// // // // //                             height: "2.6em",
// // // // //                           }}
// // // // //                         >
// // // // //                           {interview.notes}
// // // // //                         </Typography>
// // // // //                       </Box>
// // // // //                     </CardContent>

// // // // //                     <Divider />

// // // // //                     <CardActions sx={{ p: 2.5, pt: 2, justifyContent: "space-between" }}>
// // // // //                       <Box sx={{ display: "flex", gap: 1 }}>
// // // // //                         {/* Video Interview Start Button */}
// // // // //                         {interview.status === "scheduled" && interview.interviewUrl && (
// // // // //                           <Button
// // // // //                             variant="contained"
// // // // //                             size="small"
// // // // //                             startIcon={<VideoCallIcon sx={{ fontSize: 16 }} />}
// // // // //                             onClick={() => handleStartInterview(interview.interviewUrl)}
// // // // //                             sx={{
// // // // //                               fontSize: "0.75rem",
// // // // //                               px: 1.5,
// // // // //                               py: 0.5,
// // // // //                               minWidth: "auto",
// // // // //                               textTransform: "none",
// // // // //                             }}
// // // // //                           >
// // // // //                             Start
// // // // //                           </Button>
// // // // //                         )}

// // // // //                         {/* In-Person Interview Map Button */}
// // // // //                         {interview.status === "scheduled" && interview.address && (
// // // // //                           <Button
// // // // //                             variant="contained"
// // // // //                             color="secondary"
// // // // //                             size="small"
// // // // //                             startIcon={<LocationOnIcon sx={{ fontSize: 16 }} />}
// // // // //                             onClick={() => handleOpenMap(interview.address)}
// // // // //                             sx={{
// // // // //                               fontSize: "0.75rem",
// // // // //                               px: 1.5,
// // // // //                               py: 0.5,
// // // // //                               minWidth: "auto",
// // // // //                               textTransform: "none",
// // // // //                             }}
// // // // //                           >
// // // // //                             Map
// // // // //                           </Button>
// // // // //                         )}

// // // // //                         {/* Edit Button */}
// // // // //                         {interview.status === "scheduled" && (
// // // // //                           <Button
// // // // //                             variant="outlined"
// // // // //                             size="small"
// // // // //                             startIcon={<EditIcon sx={{ fontSize: 16 }} />}
// // // // //                             onClick={() => handleOpenDialog(interview)}
// // // // //                             sx={{
// // // // //                               fontSize: "0.75rem",
// // // // //                               px: 1.5,
// // // // //                               py: 0.5,
// // // // //                               minWidth: "auto",
// // // // //                               textTransform: "none",
// // // // //                             }}
// // // // //                           >
// // // // //                             Edit
// // // // //                           </Button>
// // // // //                         )}
// // // // //                       </Box>

// // // // //                       {/* Delete Button */}
// // // // //                       <IconButton
// // // // //                         size="small"
// // // // //                         color="error"
// // // // //                         onClick={() => handleDeleteInterview(interview.id)}
// // // // //                         sx={{
// // // // //                           "&:hover": {
// // // // //                             backgroundColor: "rgba(211, 47, 47, 0.1)",
// // // // //                           },
// // // // //                         }}
// // // // //                       >
// // // // //                         <DeleteIcon sx={{ fontSize: 18 }} />
// // // // //                       </IconButton>
// // // // //                     </CardActions>
// // // // //                   </Card>
// // // // //                 </Grid>
// // // // //               ))
// // // // //             ) : (
// // // // //               <Grid item xs={12}>
// // // // //                 <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
// // // // //                   <ScheduleIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
// // // // //                   <Typography variant="h6" color="textSecondary">
// // // // //                     No{" "}
// // // // //                     {tabValue === 0
// // // // //                       ? "scheduled interviews"
// // // // //                       : tabValue === 1
// // // // //                         ? "completed interviews"
// // // // //                         : "cancelled interviews"}{" "}
// // // // //                     found
// // // // //                   </Typography>
// // // // //                 </Paper>
// // // // //               </Grid>
// // // // //             )}
// // // // //           </Grid>
// // // // //         )}
// // // // //       </Box>

// // // // //       <Fab
// // // // //         color="primary"
// // // // //         aria-label="schedule interview"
// // // // //         onClick={() => handleOpenDialog()}
// // // // //         sx={{
// // // // //           position: "fixed",
// // // // //           bottom: 32,
// // // // //           right: 32,
// // // // //           boxShadow: "0 4px 16px rgba(25, 118, 210, 0.3)",
// // // // //         }}
// // // // //       >
// // // // //         <AddIcon />
// // // // //       </Fab>

// // // // //       <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
// // // // //         <DialogTitle sx={{ pb: 1 }}>{selectedInterview ? "Edit Interview" : "Schedule New Interview"}</DialogTitle>
// // // // //         <DialogContent>
// // // // //           <Grid container spacing={3} sx={{ pt: 1 }}>
// // // // //             <Grid item xs={12}>
// // // // //               <FormControl fullWidth disabled={!!selectedInterview}>
// // // // //                 <InputLabel>Candidate Application</InputLabel>
// // // // //                 <Select
// // // // //                   name="applicationId"
// // // // //                   value={formData.applicationId}
// // // // //                   onChange={handleInputChange}
// // // // //                   label="Candidate Application"
// // // // //                 >
// // // // //                   {candidates.map((candidate) => (
// // // // //                     <MenuItem key={candidate.applicationId} value={candidate.applicationId}>
// // // // //                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// // // // //                         <Avatar src={candidate.avatar} sx={{ width: 32, height: 32 }} />
// // // // //                         <Box>
// // // // //                           <Typography variant="body2" fontWeight="bold">
// // // // //                             {candidate.name}
// // // // //                           </Typography>
// // // // //                           <Typography variant="caption" color="textSecondary">
// // // // //                             {candidate.position}
// // // // //                           </Typography>
// // // // //                         </Box>
// // // // //                       </Box>
// // // // //                     </MenuItem>
// // // // //                   ))}
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //             </Grid>
// // // // //             <Grid item xs={12} sm={6}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Date"
// // // // //                 name="date"
// // // // //                 type="date"
// // // // //                 value={formData.date}
// // // // //                 onChange={handleInputChange}
// // // // //                 InputLabelProps={{ shrink: true }}
// // // // //                 required
// // // // //               />
// // // // //             </Grid>
// // // // //             <Grid item xs={12} sm={6}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Time"
// // // // //                 name="time"
// // // // //                 type="time"
// // // // //                 value={formData.time}
// // // // //                 onChange={handleInputChange}
// // // // //                 InputLabelProps={{ shrink: true }}
// // // // //                 required
// // // // //               />
// // // // //             </Grid>
// // // // //             <Grid item xs={12}>
// // // // //               <FormControl fullWidth>
// // // // //                 <InputLabel>Type</InputLabel>
// // // // //                 <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
// // // // //                   <MenuItem value="video">Video</MenuItem>
// // // // //                   <MenuItem value="in-person">In-Person</MenuItem>
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //             </Grid>

// // // // //             {formData.type === "video" ? (
// // // // //               <Grid item xs={12}>
// // // // //                 <TextField
// // // // //                   fullWidth
// // // // //                   label="Interview URL"
// // // // //                   name="locationDetail"
// // // // //                   value={formData.locationDetail}
// // // // //                   onChange={handleInputChange}
// // // // //                   placeholder="https://meet.google.com/abc-defg-hij"
// // // // //                   helperText="Enter the meeting URL for the video interview"
// // // // //                 />
// // // // //               </Grid>
// // // // //             ) : (
// // // // //               <Grid item xs={12}>
// // // // //                 <TextField
// // // // //                   fullWidth
// // // // //                   label="Location / Address"
// // // // //                   name="locationDetail"
// // // // //                   value={formData.locationDetail}
// // // // //                   onChange={handleInputChange}
// // // // //                   placeholder="e.g., 123 Main St, Anytown, USA"
// // // // //                   helperText="Enter the physical address for the in-person interview"
// // // // //                   required
// // // // //                 />
// // // // //               </Grid>
// // // // //             )}
// // // // //             <Grid item xs={12}>
// // // // //               <TextField
// // // // //                 fullWidth
// // // // //                 label="Notes / Feedback"
// // // // //                 name="notes"
// // // // //                 value={formData.notes}
// // // // //                 onChange={handleInputChange}
// // // // //                 multiline
// // // // //                 rows={3}
// // // // //               />
// // // // //             </Grid>
// // // // //           </Grid>
// // // // //         </DialogContent>
// // // // //         <MuiDialogActions sx={{ p: 3 }}>
// // // // //           <Button onClick={handleCloseDialog}>Cancel</Button>
// // // // //           <Button onClick={handleSubmit} variant="contained">
// // // // //             {selectedInterview ? "Update Interview" : "Schedule Interview"}
// // // // //           </Button>
// // // // //         </MuiDialogActions>
// // // // //       </Dialog>
// // // // //     </Layout>
// // // // //   )
// // // // // }

// // // // // export default InterviewScheduler
